import { create } from 'zustand';
import { accounts, backend, type Account, type BackendError } from './backend';
import type { ReportedByMe } from './backend/types';
import { readSeen, readCardsSeen, markCardSeen, readRequestsCleared, markRequestCleared } from './seen';
import { effectiveRole, toRole, can, type ProjectRole, type Role } from './permissions';
import { amStaff, currentWorkspace, pickWorkspaceId } from './workspace';
import { companyInAddress } from './company';
import { remember, trail, problems } from './trail';
import { readFilters, writeFilters, normalizeFilters, DEFAULT_FILTERS, type Filters } from './filters';
import { epicReassign } from './epic-ops';
import { DEFAULT_VIEW, type SavedView } from './views';

export type { Role, ProjectRole };
import { rememberAll, thePanel, charterSignoffProblem, charterSignoffRows } from './signatories';
import {
  policyForType,
  policyProblem,
  readPolicy,
  sameSubject,
  sheetFor,
  usesSlots,
  usesStages,
  whyLocked,
  type SheetRow,
  type TheWorld,
} from './signoff-policy';
import { FOR_A_FORTNIGHT, fileKey, fingerprint, isReceived, isSignableFile, isVendorShareable, whyNot } from './documents';
import { composeAar, aarInputsFromState } from './aar';
import { CHARTER_TEMPLATE, charterRisks, type CharterRisk } from './charter';
import { readyToCut } from './releases';
import type {
  Activity, Area, Label, Build, Signatory, Signoff, SignoffCover, BoardRequest, BoardRequestKind, CardAttachment, Comment, Doc, DocumentKind, Issue, IssueStatus, ProjectMeta, Reaction, StatusUpdate, StoredPaymentApproval,
} from './types';
import { liveAreas, liveLabels } from './types';
import type { IdleWindows } from './idle-nudge';
import type { ChecklistPrompts } from './checklist-prompts';
import { bulkDigests, duplicateDraft, type BulkDigest, type BulkDigestItem } from './bulk';
import { carryPendingAreas, upsertById } from './area-merge';
import { carryPendingCardWrites } from './issue-reconcile';
import {
  ORIGINAL_TYPES,
  liveTypes,
  meansOf,
  slugFor,
  type IssueType,
  type Means,
} from './issue-types';
import { numberFromRef, refFor, isBugRef } from './import-plan';
import { FLOW_WINDOW_WEEKS, type FlowMove } from './insights';
import { readInsightsView, writeInsightsView, type InsightsScope } from './insights-scope';
import { C, inProject } from './collections';
import { sampleReadOnlyNote } from './sample-note';
import { asDate } from './utils';
import { whenWorkBegan } from './lifecycle';
import type { Notification } from './notifications';
import { idFrom } from './backend/paths';
import md5 from './backend/hash';
import { groupId as makeGroupId, groupsFor, type Group, type GroupsOf } from './groups';
import type { Viewing } from './viewas';
import {
  FIRST_PROJECT_ID,
  canOpenProject,
  checkedMembers,
  newProjectDoc,
  projectId as makeProjectId,
  buildDeliveryEvidence,
  lineDeliveryBasis,
  deliveredShare,
  deliveryNudgeTrips,
  canMarkPaid,
  retentionCeiling,
  retentionPayBlock,
  lineBaseAmount,
  approvalAuthority,
  correctionNeedsApproval,
  payStampRef,
  payStampKind,
  paymentValueDateIssue,
  civilDay,
  type Project,
  type ProjectDraft,
  type ContractAdjustment,
  type InvoiceLine,
  type ApprovalAuthority,
  type Correction,
  type PaymentValueDateEdit,
  type DeliveryBasis,
  type PaymentChainRow,
} from './projects';
import {
  policyForProject,
  resolvePaymentChain,
  paymentPolicyProblem,
  chainState,
  openPaymentStage,
  paymentAuthority,
  type PaymentAuthority,
} from './payment-approval';
import { authorityToPayPdf, stampInvoiceToPay, asDate as asStampDate, asTime as asStampTime } from './authority-to-pay-pdf';
import { humanDay, shortDay } from './when';
import { cascadeShifts } from './dependencies';
import { fmtIn, fmtMoney, setActiveCurrency, currencyByCode, DEFAULT_CURRENCY } from './money';
import { DEFAULT_CALENDAR, type WorkCalendar, type Holiday } from './work-calendar';
import {
  livePhases,
  seedPhases,
  phaseProblem,
  stepsOf,
  phaseSignable,
  type Phase,
  type PhaseStatus,
  type Step,
  type StepStatus,
  type StepSigner,
  type PhaseEvent,
} from './phases';
import { buildDeliverySnapshot } from './handoff';
import { handoffDismissed } from './handoffDismissals';
import { buildPortfolio, type PortfolioRow } from './portfolio';
import type { Pulse } from './portfolio-trend';
import type { MilestoneRow, MilestonePulse, MilestoneKind, MilestoneTie } from './milestones';
import {
  canDecide,
  canSupersede,
  canWithdraw,
  canEditInPlace,
  type Decision,
  type DecisionDraft,
} from './decisions';
import type { Lesson, LessonDraft, LessonCategory, LessonKind } from './lessons';
import { asLessonKind, hasRecommendation } from './lessons';
import type {
  Risk, Assumption, RegisterIssue, Dependency,
  RiskDraft, AssumptionDraft, IssueDraft, DependencyDraft,
} from './raid';
import { riskBand, risksForCharter } from './raid';
import type { PackClosureInput, PackLessonInput } from './portfolio-pack';
import { richText } from './rich-text';
import { richHasText, type RichDoc } from './rich-text';
import {
  normalizeSavedViews,
  type PortfolioSavedView,
  type PortfolioViewConfig,
} from './portfolio-views';
import { buildAccountability, type Accountability } from './accountability';
import {
  addDays,
  daysBetween,
  nextAfter as nextAfterSprint,
  sprintProblem,
  startProblem,
  today,
  unfinishedIn,
  type Leftovers,
  type Sprint,
} from './sprints';
import {
  deviceId,
  describeDevice,
  describeDeviceParts,
  shouldSignOut,
  takeSignInMethod,
  type Session,
} from './sessions';
import { mentions } from './mentions';
import { collectNew, shouldNotify, show, type NotifyWhen } from './notify';
import {
  alreadyLoggedThisSession,
  alreadyRefusedThisSession,
  markRefusalLogged,
  describeChange,
  describeBulk,
  describeCommentRemoval,
  describeCommentEdit,
  describeUpdateEdit,
  describeUpdateRemoval,
  describeDeletion,
  issueFromSnapshot,
  snapshotOf,
  stampsProject,
  markSessionLogged,
  whereFrom,
  type AuditEntry,
  type AuditKind,
  type AuditOrigin,
} from './audit';
import {
  PULLED_LABEL,
  deviceList,
  deviceText,
  pullProblem,
  releaseProblem,
  toBuild,
  toPulled,
  type Draft,
  type Pull,
} from './releases';
import {
  EMPTY_ACCESS,
  displayName,
  nameFromEmail,
  type Access,
  type Profile,
  type Profiles,
  type ProjectRoles,
  type Roles,
} from './people';
import type { CompanyRole } from './company-role';
import { accessWrite, adminsFromRoles } from './access';
import {
  FEEDBACK_PROJECT_ID,
  feedbackIssue,
  type FeedbackContext,
  type FeedbackDraft,
} from './feedback';
import { forget as forgetBoard, recall as recallBoard, remember as rememberBoard } from './board-cache';

export type View =
  /* MY WORK — the personal, cross-board hub (#457): everything assigned to the
     signed-in person across every board, bucketed to act on, with the epics they
     own and the areas their work sits in. A lone nav row at the very top, for
     everyone; reads the same cross-board cards the Portfolio loads. */
  | 'mywork'
  | 'board'
  /* THE PLAN VIEW — the project worked and signed off by phase, #377. Its own
     destination beside the board because it answers a different question: not
     "what is left to do today" but "which stage are we in, and is it on time". */
  | 'plan'
  /* BUDGET & PAYMENTS — this project's money on one screen: the budget health,
     the forecast against the ledger, the capex/opex split, and the payments
     ledger with its governance, promoted out of the old budget modal and nested
     under Plan (per-project money). The cross-board roll-up stays `payments`. */
  | 'budgetpay'
  /* THE LEARNING & GOVERNANCE SPINE — 3.19. The project's Decision log and its
     Lessons register, project-scoped views beside the Plan (the calls it made,
     and what to carry into the next project); the Lessons library is their
     cross-board companion in the Team group. */
  | 'decisions'
  /* The RAID register — Risks, Assumptions, Issues and Dependencies, one Plan-child
     screen with a tab per register (migration 0193). The single source of truth; the
     charter's Risk-register section renders from it. */
  | 'riskregister'
  | 'lessons'
  | 'lessonslibrary'
  | 'backlog' | 'sprints' | 'releases' | 'documents'
  /* Sign-offs of WORK, in one place — #332. The request appears where the work
     is; this lists what is outstanding. Labelled "Sign-offs" in the nav so it
     is never confused with `requests`, the admin's approve/deny inbox. */
  | 'approvals'
  /* AN ADMIN'S APPROVE/DENY INBOX for who may join the board — #359. A separate
     thing from a work sign-off, and admin-only, so it has its own destination
     rather than sharing the sign-off screen or hiding inside People and access. */
  | 'requests'
  /* THE PORTFOLIO — every project on the tenant, one row each, for the weekly
     meeting. It reads across boards (its own load), where every other view is
     one board at a time; it sits in the Team group beside Insights. */
  | 'portfolio'
  /* THE FINANCE "AUTHORISED TO PAY" QUEUE — every board's approved-not-yet-paid
     invoices in one read-only list, with the proof of authority, plus an export for
     finance. Like the Portfolio it reads across boards and sits in the Team group.
     Deliverable 3 of invoice-approval governance. */
  | 'payments'
  /* THE PAYMENT-APPROVAL CHAIN BUILDER — who authorises a payment, and above what
     value. A workspace-level config (default chain + per-project overrides) reached
     from Settings → Finance & payments and from its own Finances nav row. Editing
     is manager+/admin; the chain it writes governs every payment (executive-approver
     tier). */
  | 'payapproval'
  | 'activity' | 'dashboard' | 'settings' | 'admin' | 'account'
  /* THE HELP PAGE — #501. The dedicated full-screen home of the "how do I…"
     corpus (src/help.ts), reached from the Help row at the foot of the sidebar;
     ⌘K stays the typed quick-jump. Browse by job or search, and every answer
     drills to the real control and flashes it. Its own faint nav hue. */
  | 'help';
/* #301 added Area and None. Area because a board that names its areas will
   want to read the backlog by them; None because grouping is a choice and
   "do not" has to be one of the options. */
export type GroupBy = 'status' | 'epic' | 'area' | 'phase' | 'none';

/**
 * The Portfolio's drilled state, stashed so a card opened from the Portfolio
 * can return to exactly where it left. Mirrors the Portfolio's own Drill shape.
 */
export type PortfolioReturn =
  | { level: 'list' }
  | { level: 'report'; id: string }
  | { level: 'cards'; id: string }
  | { level: 'epic'; name: string }
  | { level: 'area'; name: string };

/**
 * WHICH SCREEN THEY WERE ON, kept per browser — the same way the project is.
 *
 * Refreshing put everybody back on the board. Which project you were in
 * survived, because that was written down; which screen you were looking at did
 * not, because it was only ever a starting value. So somebody reading Activity
 * pressed refresh and lost their place, and the app looked like it had
 * forgotten what they were doing. Vinnie, 17 August: "when refreshing live, it
 * goes back to the board page and does not stay on the current page."
 *
 * VALIDATED ON THE WAY BACK IN, not trusted. This is local storage, so it holds
 * whatever the last version of the app wrote there and whatever anybody typed
 * into it by hand. A name that is no longer a screen falls back to the board
 * rather than rendering nothing — which is what a removed view would otherwise
 * do to anybody who happened to be on it the day it was removed.
 */
/*
 * DECLARED UP HERE, WITH THE OTHER KEY, AND THAT POSITION IS THE WHOLE FIX —
 * #202.
 *
 * `readProjectChoice()` is called while the store's opening state is being
 * built, hundreds of lines above where this used to sit. A `const` is not
 * readable before the line that creates it has run, so the read threw, the
 * `catch` around it answered with the default, and the board somebody was last
 * on was never once looked at. Reloading always landed on the first board in
 * the workspace.
 *
 * WHY IT SURVIVED SO LONG: the catch is there for private browsing, where
 * storage genuinely refuses, and it cannot tell that apart from this. Both
 * arrive as a throw and both get the same sensible-looking answer, so the app
 * behaved exactly as though nobody had ever chosen a board. Nothing failed and
 * nothing was logged.
 *
 * `VIEW_KEY` below has always been fine for the same reason in reverse — it is
 * declared before the reader that uses it. That is the only difference between
 * the two, and it is why one worked and one did not.
 */
const PROJECT_KEY = 'flightdeck-project';
const VIEW_KEY = 'flightdeck-view';
const VIEWS: View[] = [
  'board',
  'plan',
  'backlog',
  'sprints',
  'releases',
  'activity',
  'dashboard',
  'settings',
  'admin',
  'account',
];

/* Exported so the suite can check them without a browser — the fault was in
   what is remembered, not in what is drawn. */
export function readViewChoice(): View {
  /*
   * ABOVE THE STORE, and that is not a tidiness preference.
   *
   * This block first sat beside writeProjectChoice, thirteen hundred lines
   * below the create() call that uses it. Function declarations hoist; the
   * `VIEWS` const does not — so at the moment the initial state was built,
   * VIEWS was still in its temporal dead zone, this threw, and the catch below
   * quietly returned 'board'. Every check passed and the feature did nothing.
   * Caught by dev-preview/view-memory, which reads the store's own starting
   * value; a unit test that imports the store afterwards can never see that
   * moment, which is exactly the moment that was broken.
   *
   * The catch is for local storage being unavailable in private browsing. It is
   * deliberately the LAST thing here now, not a net under a declaration order.
   */
  let held: string | null = null;
  try {
    held = localStorage.getItem(VIEW_KEY);
  } catch {
    return 'board'; // private browsing — nothing was ever written
  }
  return held && (VIEWS as string[]).includes(held) ? (held as View) : 'board';
}

/**
 * FORGET WHERE THEY WERE — the escape hatch on the broken screen, #322.
 *
 * WHAT WENT WRONG WITHOUT THIS. On 30 August 2026 the Releases screen threw on
 * every render. Vinnie reloaded, hard-reloaded, and pressed "Start again from
 * the beginning", and every one of them landed him back on Releases and back on
 * the same broken screen — because starting again reloads the app, and the app
 * reads THIS value and goes straight back to where he was. He got out by
 * opening a private window, which works for exactly one reason: it has no
 * localStorage, so `readViewChoice` finds nothing and returns 'board'.
 *
 * So "start again" now genuinely starts again. It forgets only this — not the
 * board they chose, not their session, nothing on the server.
 */
export function forgetViewChoice(): void {
  try {
    localStorage.removeItem(VIEW_KEY);
  } catch {
    /* private browsing — there was nothing to forget */
  }
}

export function writeViewChoice(view: View): void {
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    /* private browsing — the choice just will not persist */
  }
}

/*
 * WHERE THE APP OPENS — the landing, a per-person choice (#457).
 *
 * My Work is the personal cross-board hub, and the signed-off default is to land
 * there (everything waiting on you, the moment you sign in). But some people live
 * on one board and want it back, and some want to resume wherever they left off —
 * so "Open to" is a setting with three answers, held in local storage like the
 * view memory beside it:
 *   · 'mywork' (the default) → the My Work hub;
 *   · 'board'  → the Board, the old landing;
 *   · 'resume' → wherever they were, i.e. `readViewChoice` and its 'board' fallback.
 *
 * `readLanding` is what the store boots from, NOT `readViewChoice` — the two are
 * kept apart on purpose. `readViewChoice` is the "remember where I was" rule, with
 * its own conservative allow-list (`VIEWS`) and its own tests; the landing sits a
 * layer above it and decides whether that memory is even consulted.
 */
export type OpenTo = 'mywork' | 'board' | 'resume';
const OPEN_TO_KEY = 'flightdeck-open-to';

export function readOpenTo(): OpenTo {
  try {
    const v = localStorage.getItem(OPEN_TO_KEY);
    if (v === 'mywork' || v === 'board' || v === 'resume') return v;
  } catch {
    /* private browsing — fall through to the default */
  }
  return 'mywork';
}

export function writeOpenTo(choice: OpenTo): void {
  try {
    localStorage.setItem(OPEN_TO_KEY, choice);
  } catch {
    /* private browsing — the choice just will not persist */
  }
}

/** The view the app opens on, honouring the "Open to" setting (default: My work). */
export function readLanding(): View {
  const pref = readOpenTo();
  return pref === 'resume' ? readViewChoice() : pref;
}


function uid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/*
 * PATHS, NOT REFERENCES. These used to hand back Firestore DocumentReferences,
 * which meant every caller was holding something Firebase-shaped. They are
 * strings now, and the backend turns a string into whatever its own database
 * needs — see src/backend/types.ts.
 */
const boardPath = () => `${C.projects}/${useApp.getState().projectId}`;

const issuePath = (id: string) => `${inProject(useApp.getState().projectId).issues}/${id}`;
const sprintsPath = () => inProject(useApp.getState().projectId).sprints;
const areasPath = () => inProject(useApp.getState().projectId).areas;
const labelsPath = () => inProject(useApp.getState().projectId).labels;
const phasesPath = () => inProject(useApp.getState().projectId).phases;
const milestonesPath = () => inProject(useApp.getState().projectId).milestones;
const decisionsPath = () => inProject(useApp.getState().projectId).decisions;
const risksPath = () => inProject(useApp.getState().projectId).risks;
const assumptionsPath = () => inProject(useApp.getState().projectId).assumptions;
const registerIssuesPath = () => inProject(useApp.getState().projectId).registerIssues;
const dependenciesPath = () => inProject(useApp.getState().projectId).dependencies;
/** The signed-in email, for a RAID item's madeBy (same resolution as logDecision). */
const raidMe = () =>
  useApp.getState().user?.email?.toLowerCase() ?? accounts.current()?.email?.toLowerCase() ?? '';
/** The next per-board sequence number (R-01, A-01, …) and the next list position —
 *  both max+1, computed from the current list, like a decision's position. */
const nextNum = (list: { num?: number }[]) => list.reduce((n, x) => Math.max(n, x.num ?? 0), 0) + 1;
const nextPos = (list: { position?: number }[]) => list.reduce((n, x) => Math.max(n, x.position ?? -1), -1) + 1;
const phaseStepsPath = () => inProject(useApp.getState().projectId).phaseSteps;
const phaseEventsPath = () => inProject(useApp.getState().projectId).phaseEvents;
const typesPath = () => inProject(useApp.getState().projectId).issueTypes;

/*
 * DOCUMENTS JUST CREATED, NOT YET SEEN IN A LIVE SNAPSHOT — Vinnie, 1 Oct 2026.
 *
 * `draftDocument` inserts the new row optimistically so the create flow can open
 * its editor at once. But the realtime watcher REPLACES `documents` wholesale
 * with whatever the collection pushes, and the first snapshot after a write can
 * land BEFORE the row is readable (read-after-write lag) — wiping the optimistic
 * row for a beat. That beat is enough to collapse the just-opened editor back to
 * the list (the create-then-open bug). So a freshly created id is held here until
 * a snapshot actually contains it; while it is pending, the watcher keeps the
 * optimistic row rather than letting a stale snapshot drop it. Cleared the moment
 * a snapshot confirms the row, or when the draft is deleted. Module-level so it is
 * never persisted — it is transient reconciliation state, not board data.
 */
const pendingDocs = new Set<string>();
/* The same read-after-write guard for the 3.19 spine's two collections — a
   logged/superseded decision and a recorded lesson are shown the instant they are
   saved, and held here so a stale first snapshot cannot drop them before the row is
   readable. Keyed by a client uuid, so the optimistic id equals the persisted one. */
const pendingDecisions = new Set<string>();
const pendingLessons = new Set<string>();
// One pending set per RAID register — an optimistic row a stale first snapshot has
// not caught up to is kept until the snapshot confirms it, exactly as pendingDecisions.
const pendingRisks = new Set<string>();
const pendingAssumptions = new Set<string>();
const pendingRegisterIssues = new Set<string>();
const pendingDependencies = new Set<string>();

/**
 * A line in a phase's append-only history — #377. Written whenever a step or a
 * phase is signed off or changed, with who and when. The summary is the sentence
 * a person reads with the actor's name in front ("signed off … as PM"); the
 * table (0093) has no update or delete policy, so nothing can rewrite it.
 */
async function recordPhaseEvent(phaseId: string, kind: string, summary: string): Promise<void> {
  const id = uid();
  await backend.set(`${phaseEventsPath()}/${id}`, {
    id,
    phaseId,
    actor: useApp.getState().user?.email ?? '',
    kind,
    summary,
    at: Date.now(),
  });
}

/*
 * A PROJECT JUST CREATED, NOT YET IN THE LIST — the fix for landing on the wrong
 * board after making one.
 *
 * `createProject` switches to the new board with setProject, but the board list
 * arrives over realtime a moment later. In that gap the projects watcher sees a
 * chosen id that is not in the list it holds and, taking it for a board that was
 * archived or belongs to someone else, falls back to the first board — so the
 * person who just made a project lands on an unrelated one. Then the new board
 * arrives, the chosen id is now the fallback, and nothing corrects it back.
 *
 * This names the board we are deliberately waiting for, so the watcher leaves the
 * choice alone until it appears. Cleared the moment it does (or if the person
 * switches somewhere else first). Module-level, because both the creator and the
 * watcher live in this file and it is not state any screen renders.
 */
let awaitingProjectId: string | null = null;

/**
 * Write one project's budget ledger into the local project lists at once.
 *
 * The projects list is owned by the realtime watcher, which lands the echo of a
 * write a moment after it. The ledger is edited a line at a time, so between two
 * quick edits the second would otherwise read the pre-edit array and overwrite
 * the line the first just added. Setting the new array locally the instant it is
 * computed means each edit builds on the last; the realtime echo then confirms
 * the same value. (Across sessions the whole-array write stays last-writer-wins,
 * like `stakeholders` — a documented v1 limit, not this race.)
 */
function setInvoicesLocal(
  set: (
    fn: (s: { projects: Project[]; archivedProjects: Project[] }) => Partial<{
      projects: Project[];
      archivedProjects: Project[];
    }>,
  ) => void,
  id: string,
  invoices: InvoiceLine[],
) {
  const patch = (p: Project): Project => (p.id === id ? { ...p, invoices } : p);
  set((s) => ({ projects: s.projects.map(patch), archivedProjects: s.archivedProjects.map(patch) }));
}

/*
 * WHICH WORKSPACE WE ARE IN, for a tenant-level read or write (0117).
 *
 * The workspace in the ADDRESS comes first, because that is what my_tenant()
 * actually resolves to: the address sets the `x-flightdeck-company` header the
 * policies read (src/supabase.ts), so for a member of MORE THAN ONE workspace
 * who has switched to a non-home one, the row the policy lets us touch is the
 * VIEWED workspace — not the `tenant` claim in the token, which still names
 * their home. Keying a tenant read or write by the home id while the policy
 * scopes to the viewed one meant the read matched nothing and fell silently to
 * defaults: a non-home workspace lost its currency, exchange rates, working
 * calendar and payment-approval chain (all ride the one tenant row), and a
 * defaulted calendar made the forecast optimistically wrong with no signal.
 *
 * When the address names NO company (a single-company deployment, or before the
 * address has settled) the old order stands: the token names it, and failing
 * that the OLDEST membership — which is the same fallback my_tenant() uses
 * (0011/0085), so the id chosen here and the row the policy permits still match.
 */
async function resolveWorkspaceId(): Promise<string> {
  const viewedSlug = companyInAddress();
  const claimed = currentWorkspace();
  // The list is needed to resolve the viewed slug to an id, and as the oldest-
  // membership fallback; skip the round trip only when the address names no
  // company AND the token already carries the claim (the common single-
  // workspace path, unchanged from before).
  const mine = viewedSlug || !claimed ? await accounts.workspaces().catch(() => []) : [];
  return pickWorkspaceId(viewedSlug, claimed, mine);
}

/**
 * SOMEBODY TURNED AWAY, RECORDING THAT THEY WERE.
 *
 * The one security event worth having was the one that could not be written.
 * `recordAudit` is no use here — it is refused for exactly the reason this
 * entry exists, because the writer is not on the access list. The rules have a
 * narrow opening for this one kind, about oneself, and this is what goes
 * through it: nothing but the five fields it permits.
 *
 * Once per visit. The denial handler fires on every listener that is refused —
 * projects, the access list, the roles — so without the guard a single turned
 * away visit writes half a dozen identical lines and buries the trail under
 * the very event it exists to surface.
 *
 * Failure here is swallowed on purpose. Somebody being turned away is already
 * looking at a refusal screen; a second error about the recording of the first
 * tells them nothing they can act on.
 */
/**
 * SET ONCE WHEN THIS BROWSER IS BEING CLOSED FROM ELSEWHERE — #305.
 *
 * The check lives in a listener that fires whenever the profile row moves, and
 * signing out is not instant. Without this, one press of "sign out everywhere"
 * on another machine writes a line here for every callback in the gap.
 */
let leavingAfterSignOutElsewhere = false;

async function recordRefusal(): Promise<void> {
  const me = accounts.current()?.email?.toLowerCase();
  if (!me || alreadyRefusedThisSession(me)) return;
  markRefusalLogged(me);
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    await backend.set(`${C.audit}/${id}`, {
      id,
      at: Date.now(),
      who: me,
      kind: 'refused',
      what: `tried to open ${whereFrom()} and was turned away — not on the access list`,
    });
  } catch {
    /* nothing they could do about it */
  }
}

/**
 * YOUR OWN ROLE, WHICH IS NOW A PROPERTY OF THE BOARD YOU ARE LOOKING AT.
 *
 * Four things decide it and each arrives on its own schedule: the access list,
 * your default role, this board's answer, and which board is open. Every one of
 * them used to recompute it in its own way, which is how the screen ends up
 * showing rights you no longer have — right in the database, wrong in front of
 * you, and it comes good on its own before anybody can reproduce it.
 *
 * So there is one function, and everything that can change the answer calls it.
 */
/**
 * AM I AN ADMIN OF THE WORKSPACE ON SCREEN — including as staff visiting one.
 *
 * ONE PLACE, BECAUSE THIS IS THE FIFTH TIME. Support access has now broken the
 * terms gate, the password gate, "not approved for this board", the onboarding
 * wizard and the board list, and every one of them was the same mistake: a
 * question about the person answered by looking for their row in the workspace
 * on screen. A support visit is the first thing in this app that has no such
 * row, so every one of those answered "no" and drew the wrong screen.
 *
 * Anything asking whether *I* may do an admin thing should call this, so the
 * sixth one is a call rather than another fix.
 */
/**
 * WHAT LED TO IT, appended to every report.
 *
 * The errors go FIRST and the steps after, because the two answer different
 * questions and only one of them usually gets read. An error with a file and a
 * line in it is often the whole diagnosis; the steps are what is needed when
 * there is no error at all, which is the harder and more common case.
 *
 * Empty when there is nothing to say, rather than a heading over nothing.
 */
function whatLedToIt(): string {
  const broke = problems();
  const steps = trail();
  if (!broke.length && !steps) return '';
  const parts = [''];
  if (broke.length) {
    parts.push(`Errors seen (${broke.length}):`, ...broke.map((e) => `  ${e.what}`), '');
  }
  if (steps) parts.push('What they did just before:', steps);
  return parts.join('\n');
}

function iAmAdminHere(admins: string[], me: string): boolean {
  return amStaff() || admins.includes(me);
}

/*
 * READ-ONLY SAMPLE, ENFORCED AT THE WRITE — the guarantee behind the look.
 *
 * Every user-facing mutating action calls this first. If the board the change
 * would land on is a worked sample, the write is refused and the person gets
 * the gentle note (the shared one, so it matches a control's own click), with a
 * way straight on to a board of their own. Returns true when the write must NOT
 * proceed, so the call site is `if (sampleBlocks()) return;`.
 *
 * KEYED ON THE BOARD, NOT THE ROLE. It asks only whether the TARGET board is a
 * sample — never lowers anyone's role — so it blocks the sample and nothing
 * else: a real board edited from the cross-board My Work (pass its own id)
 * writes normally, and opening the sample never touches the nav or the
 * cross-board screens the way an earlier role-force did. Most actions change
 * the active board, so the id defaults to `projectId`; cross-board writers pass
 * the board they target. System writes — audit, the activity feed, phase
 * events — do not call this: they are the app recording, not a person changing
 * the sample.
 */
function sampleBlocks(boardId?: string): boolean {
  const s = useApp.getState();
  const board = s.projects.find((p) => p.id === (boardId ?? s.projectId));
  if (!board?.sample) return false;
  void sampleReadOnlyNote(board.name).then((start) => {
    if (start) useApp.getState().setStartingBoard(true);
  });
  return true;
}

/** The silent half of the same test, for the few write actions that answer a
 *  caller inline (an error string the form shows) rather than with the dialog —
 *  no note is raised, the caller's own message carries it. True = a sample. */
function sampleNoWrite(boardId?: string): boolean {
  const s = useApp.getState();
  return !!s.projects.find((p) => p.id === (boardId ?? s.projectId))?.sample;
}

function recomputeMyRole(): void {
  const s = useApp.getState();
  /*
   * A SUPPORT VISIT IS ADMIN, BECAUSE THE DATABASE ALREADY SAYS SO.
   *
   * Everything below reads the visitor's row in this workspace, and a staff
   * account visiting a customer has none — so it fell through to `viewer`,
   * the safe default, and the screen said "you are a Viewer on this board"
   * while the policies would have allowed every write. Wrong in the direction
   * that wastes a support call: the person is told they cannot do the thing
   * they were let in to do.
   *
   * NOT A NEW PERMISSION. is_admin() in 0012 grants this and the database
   * enforces it whatever this line says; a browser that lied here would only
   * draw buttons that then failed. This makes the screen agree with the answer
   * rather than deciding it.
   */
  const email = s.user?.email?.toLowerCase() ?? accounts.current()?.email?.toLowerCase();
  /*
   * A SAMPLE BOARD IS READ-ONLY FOR EVERYONE, BUT NOT BY LOWERING YOUR ROLE.
   *
   * An earlier version forced `shownRole` to "viewer" on a sample board to make
   * every editing control gate itself off. It worked for board controls, but
   * `shownRole` is also what the nav and the cross-board screens read (App.tsx
   * `navVisible`, and the Portfolio / Authorised-to-pay / Payment-approval
   * render gates) — so a real admin who merely OPENED the sample lost those
   * rows and hit "not open to your role". The role must stay true to the board.
   *
   * Read-only is enforced two other ways instead, both keyed on the board's
   * `sample` mark, not the person's role: the store's write actions refuse and
   * answer with the gentle note (`sampleBlocks`, below), which is the guarantee
   * and means no control is ever a dead click; and the board's own controls
   * render visibly inert (`useSampleBoard`), which is the look the mock asks
   * for. The pair is per-tenant, so a client guard is enough — the way an
   * archived board is handled in the app, not the database.
   */
  if (amStaff()) {
    useApp.setState({ role: 'admin', shownRole: s.viewing ? s.viewing.role : 'admin' });
    return;
  }
  const mine = effectiveRole({
      email,
      admins: s.access.admins,
      defaults: s.roles,
      on: s.projectRoles,
      legacy: s.legacyRoles,
      projectId: s.projectId,
  });
  // A preview in progress keeps its own answer; otherwise the screen is you.
  useApp.setState({ role: mine, shownRole: s.viewing ? s.viewing.role : mine });
}

export interface AppState {
  user: Account | null;
  authReady: boolean;
  emailVerified: boolean; // confirmed ownership of the email address
  denied: boolean; // signed in and verified, but not on the access list
  /** Signed in and genuinely a member, but the token predates a change and cannot reach the workspace. */
  stale: boolean;
  /**
   * Their workspace has been switched off by FlightDeck, and the reason they
   * are shown. Null when it has not. See 0028 — a suspended workspace refuses
   * everything, so without this it is indistinguishable from a broken sign-in.
   */
  suspended: { name: string; why: string } | null;
  loaded: boolean; // first data snapshot received
  /*
   * WHETHER THESE TWO HAVE BEEN ASKED FOR YET — #252, and NOT what came back.
   *
   * `loaded` stopped meaning "the app knows things" on 23 August, when the
   * board began painting from the copy the browser kept. That copy holds the
   * cards and nothing about the PERSON, so `loaded` goes true while `profiles`
   * and `projects` are still empty — and empty is exactly what "has not agreed
   * to the terms" and "has no projects" look like. Both interruptions were
   * therefore drawn on every refresh, to people who had done neither.
   *
   * True the moment the answer arrives, INCLUDING an empty or failed one:
   * asked-and-nothing is a real answer and must still gate. Only
   * not-yet-asked is silent.
   */
  profilesKnown: boolean;
  /*
   * profilesKnown says an answer ARRIVED (success or error); profilesOk says
   * the answer SUCCEEDED. The terms and password gates read profilesOk, never
   * profilesKnown: a failed profiles read leaves an empty map that reads like
   * "has agreed to nothing / set no password", and believing it ejected
   * established people to the Terms of use or the password screen on a passing
   * network/realtime blip. A failed read is "not known", not "no".
   */
  profilesOk: boolean;
  projectsKnown: boolean;
  role: Role; // least privilege until the access list says otherwise
  /**
   * THE ROLE THE SCREEN SHOULD USE — yours, unless a preview is running.
   *
   * Every component that hides a control reads THIS, not `role`. Two reasons
   * it is a stored field rather than each screen working it out: ten of them
   * ask, and "sometimes mine, sometimes theirs" repeated ten times is how a
   * preview eventually leaks into a decision that was never meant to be a
   * preview. And it is only ever about what is DRAWN — every read and write
   * still goes out as you. See src/viewas.ts.
   */
  shownRole: Role;
  access: Access;
  profiles: Profiles;
  roles: Roles;
  /**
   * What each person may do on each board, where a board has an answer of its
   * own. Anybody absent here falls back to `roles` above — see effectiveRole.
   */
  projectRoles: ProjectRoles;
  /** The teams that exist. Membership lives on each person's roles document. */
  groups: Group[];
  /** Which groups each person is in, by email. */
  groupsOf: GroupsOf;
  /**
   * Whose eyes the SCREEN is using. Null is your own.
   *
   * Deliberately not `user` and deliberately not `role`. Every read and write
   * still goes out as you and is still judged against you — see src/viewas.ts.
   * Keeping it in a field of its own is what makes that impossible to blur.
   */
  viewing: Viewing | null;
  /** The pre-split roles map, read only while a board is being migrated. */
  legacyRoles: Record<string, string>;
  audit: AuditEntry[];
  /** Why the activity list is empty, when it is. Empty string means it is not. */
  auditProblem: string;
  /** The admin-only network origin of each sign-in — IP and an approximate place
      (0150). RLS hands these to admins alone; for anyone else the list stays
      empty and no origin is ever shown. Merged onto a sign-in line by auditId. */
  auditOrigin: AuditOrigin[];
  /** Where this person is signed in. Theirs alone — see firestore.rules. */
  sessions: Session[];
  /**
   * WHY THE LIST OF DEVICES IS EMPTY, when it is.
   *
   * Empty because there is nothing to show and empty because the question was
   * refused are the same array, and until this existed the screen asserted the
   * innocent one: "Nothing recorded yet. This browser will appear here
   * shortly." It said that for three weeks while nothing was being written.
   *
   * Empty string means the list is trustworthy. Anything else is what went
   * wrong, in the words the reader can act on.
   */
  sessionsProblem: string;
  /** Which project is being looked at. */
  projectId: string;
  projects: Project[];
  /**
   * BOARDS THAT HAVE BEEN PUT AWAY — #327.
   *
   * Kept apart from `projects` rather than filtered out of it, because every
   * screen in the app reads `projects` and expects it to mean "boards you can
   * open". An archived board is not one of those: it is not in the switcher,
   * it is not somewhere the app will fall back to, and nothing routes to it.
   *
   * IT IS STILL HELD IN STATE, and that is the whole point of archiving rather
   * than deleting. A board that vanished with no list to find it in again is a
   * one-way door wearing a reversible word. This is the list the Settings
   * screen offers "Bring it back" from.
   *
   * Same visibility rule as `projects`: a board reserved for named people is
   * not shown to anybody else merely because it was archived.
   */
  archivedProjects: Project[];

  /*
   * THE PORTFOLIO ROLL-UP — a derived row per project, tenant-wide (#385). Held
   * apart from `projects` because it is a computed pulse, not the board list: it
   * needs every board's issues and phases, which the app otherwise loads one
   * board at a time. `loadPortfolio` reads them across the tenant in two group
   * queries, derives each row (see portfolio.ts), and drops them here. On-demand
   * for v1 — the Portfolio screen loads it on open; a persisted snapshot is a
   * later increment.
   */
  portfolio: PortfolioRow[];
  /** The same cards read by the person — who is accountable and what they are
   *  doing. Derived alongside the roll-up in `loadPortfolio`. */
  accountability: Accountability;
  /** The cards and phases the roll-up was built from, kept by board, so the
   *  Portfolio screen can open a project row in place and show its real cards
   *  without going to the board or reading again. Same two group reads that
   *  build the roll-up already load these; v1 just stopped dropping them. */
  portfolioIssues: Map<string, Issue[]>;
  portfolioPhases: Map<string, Phase[]>;
  /** Each board's areas, kept by board — the by-area lens resolves a card's local
   *  area id to its name through the list for the card's own board. */
  portfolioAreas: Map<string, Area[]>;
  /** The daily health pulses (migration 0122) kept by board, so the roll-up can
   *  draw a trend arrow and a project's report can show its history strip. Read
   *  alongside the roll-up in `loadPortfolio`; empty on a backend or a tenant
   *  with no pulses recorded yet (the trend then reads "New"). */
  portfolioPulses: Map<string, Pulse[]>;
  /** The standalone milestone rows (migration 0124) kept by board, so the roll-up,
   *  the Milestones lens and a project's report can derive its milestone set. The
   *  go-live and promoted phase ends need no row — they come from the board itself
   *  and its phases (see `projectMilestones`). Empty on a tenant with none. */
  portfolioMilestones: Map<string, MilestoneRow[]>;
  /** The daily milestone forecast snapshots (0124) kept by board, so a project's
   *  drill can plot each milestone's slip over time. Job-written and read-only;
   *  empty until the daily snapshot has recorded a day or two. */
  milestonePulses: Map<string, MilestonePulse[]>;
  /** LEARNING & CLOSURE (#491) — the signed after-action reports across every board,
   *  pre-flattened for the steering pack's Learning & closure page. The composer
   *  windows these to the pack's cycle; empty until the portfolio is loaded. */
  portfolioClosures: PackClosureInput[];
  /** The recommendation-bearing lessons across every board (RLS-scoped),
   *  pre-flattened for the same page. */
  portfolioLessons: PackLessonInput[];
  portfolioLoading: boolean;
  portfolioError?: string;
  /** When the roll-up was last read, so the screen can show its freshness. */
  portfolioAt?: number;
  loadPortfolio: () => Promise<void>;

  /** Keep the cross-board card cache (`portfolioIssues`) live while a screen that
   *  needs it is open — My Work subscribes on mount and drops it on unmount — so an
   *  assignment or status change on any board shows AT ONCE, no reload. Returns the
   *  unsubscribe. Scoped on purpose: the tenant-wide listener exists only while
   *  someone is looking, not as a standing cost. */
  livePortfolioIssues: () => () => void;

  /**
   * THE CURRENT BOARD'S STATUS-CHANGE LOG over the cumulative-flow window —
   * fetched ON DEMAND when Insights is opened, not at board load.
   *
   * The board read attaches only the last few days of moves to each card
   * (readMovesSince, called with a 7-day window), which is enough for the
   * movement marks but nowhere near enough history to draw a flow diagram. So
   * the CFD asks for its own wider window the first time it is shown, and the
   * board's startup read stays light for everyone who never opens it. Null until
   * asked for; `flowMovesBoard` records which board it was read for, so switching
   * boards refetches rather than charting the wrong board's history.
   */
  flowMoves: FlowMove[] | null;
  flowMovesLoading: boolean;
  flowMovesBoard?: string;
  loadFlowMoves: () => Promise<void>;

  /**
   * WHAT INSIGHTS IS LOOKING AT — the board that is open or all of them, and how
   * far back. Per browser (see insights-scope), so it is this person's view and
   * does not follow them to another device or change anyone else's screen.
   *
   * Choosing All boards pulls the roll-up in if it is not already loaded — the
   * flow reads run over every board's cards then, flow only; the portfolio keeps
   * RAG, schedule and budget across boards.
   */
  insightsScope: InsightsScope;
  insightsWindow: number;
  setInsightsScope: (scope: InsightsScope) => void;
  setInsightsWindow: (window: number) => void;

  /**
   * THE PORTFOLIO'S SAVED VIEWS, and the id of the one this person opens on.
   *
   * The same "per person, follows them" store as `savedViews`, for the roll-up's
   * own toolbar — a preset lens plus dimension filters, a grouping and a sort.
   * Held on the person (migration 0121) and hydrated in the profiles listener.
   * `portfolioDefault` is a view id — a built-in lens or one of these — or ''
   * when nothing is pinned and the screen falls back to a built-in. The ACTIVE
   * view (which tab is selected right now) is transient and stays in the
   * roll-up, exactly as the Backlog keeps its active tab in the browser.
   */
  portfolioViews: PortfolioSavedView[];
  portfolioDefault: string;
  /** Save the current toolbar setup as a new named view; resolves to its id so
   *  the roll-up can make it the active one. */
  savePortfolioView: (name: string, config: PortfolioViewConfig) => Promise<string>;
  /** Overwrite a saved view's captured setup (the "Save" on an edited own view). */
  updatePortfolioView: (id: string, config: PortfolioViewConfig) => Promise<void>;
  /** Rename a saved view. */
  renamePortfolioView: (id: string, name: string) => Promise<void>;
  /** Delete a saved view; if it was the pinned default, the pin falls back. */
  forgetPortfolioView: (id: string) => Promise<void>;
  /** Pin the view Portfolio opens on — any lens, built-in or saved. */
  setPortfolioDefault: (id: string) => Promise<void>;

  meta: ProjectMeta;
  members: string[];
  issues: Issue[];
  builds: Build[];
  /**
   * THE SIGNATURES ON THIS BOARD'S RELEASES, all of them, not one release's.
   * Watched like builds and sprints are: a screen filters to the release it is
   * showing, and a sign-off given in another window appears here without a
   * reload — which for an approval is the difference between a live record and
   * a stale one.
   */
  signoffs: Signoff[];
  /** Refusals that were dismissed off the open panel — migration 0094. Kept apart
      from `signoffs` (they are withdrawn, so they count nowhere active); the
      Sign-offs screen lists them under settled with their reason. */
  dismissedSignoffs: Signoff[];
  /** Requests that an admin add somebody to a board — #359. Everyone sees only
      what RLS lets them: their own, or all of them if they administer. */
  boardRequests: BoardRequest[];
  /** Who covers whom while a signer is away — configurable sign-offs R1 (0130).
      The whole workspace's covers, so a sign-off sheet can show "X may sign,
      delegated" for anyone; RLS keeps it to the tenant. */
  signoffCover: SignoffCover[];
  /** The signed-in person's own call-outs, across every board they are on — the
      feed behind the bell (track A, #388). RLS hands each person only theirs;
      only the database writes them, and the app only ever marks one read. */
  notifications: Notification[];
  /** Light reactions on card-feed entries — emoji on a comment or update (0143).
      One cross-board list, gathered like the notification feed and narrowed by RLS
      to reactions on boards the person can open; the card and My Work merge them
      onto the feed by the entry id they point at. `addReaction`/`removeReaction`
      toggle the signed-in person's own, optimistically. */
  reactions: Reaction[];
  addReaction: (issueId: string, targetId: string, emoji: string) => void;
  removeReaction: (issueId: string, targetId: string, emoji: string) => void;
  /** Which of my own decided requests I have cleared from my list — kept per
      browser (seen.ts), held here too so the People-and-access nav badge and
      the list read one source and the badge drops the moment I clear a row. */
  requestsCleared: Record<string, number>;
  documents: Doc[];
  /**
   * THIS BOARD'S SPRINTS — every one of them, running, coming and finished.
   *
   * Kept whole rather than as "the current one", because velocity is the last
   * six to ten and a retrospective reads the one that just closed. Which one is
   * running is a question asked of this list — see `running` in src/sprints.ts —
   * rather than a second field that can disagree with it.
   *
   * Empty on a board that works in flow, and empty is also what a board with
   * sprints turned on but none planned looks like. The two are told apart by
   * the board's own setting, never by this being empty.
   */
  sprints: Sprint[];

  /*
   * THE PARTS OF THE PRODUCT this board's work divides into — #269.
   *
   * Empty is the ordinary starting state and is not a fault: a board that has
   * never set any areas up has none, and every card reads "No area". Screens
   * must be legible in that state rather than showing an empty chart.
   */
  areas: Area[];
  /*
   * Areas this client just created whose row the areas subscription has not yet
   * echoed back. The realtime watcher REPLACES the whole list with the server's
   * snapshot, and that snapshot can arrive mid-flight from just BEFORE the insert
   * — wiping the freshly-made area, which left a card unable to select an area it
   * had just created (it snapped back to "No area"). These ids let the watcher
   * CARRY a just-made area forward until the server's snapshot includes it, then
   * drop it. Short-lived; pruned the moment the row appears, and on a timeout
   * guard so a failed insert never leaves a ghost.
   */
  pendingAreaIds: string[];
  /** The board's managed labels — name + optional colour, a registry by name (#462). */
  labels: Label[];
  /** Why the labels read failed, in words — the #289 lesson, as for areas. */
  labelsProblem: string;
  /** What sort of work a card can be, in this board's own words — #302. */
  issueTypes: IssueType[];
  /*
   * WHY THE READ FAILED, IN WORDS, rather than an empty list — the lesson from
   * #289, where Devices and Security activity both showed nothing at all and
   * the screen could not tell "there is none" from "I could not ask". An empty
   * list is a claim about the data; a refused read is a claim about the
   * connection, and drawing the second as the first is how a fault stays hidden.
   */
  areasProblem: string;
  /** The stages this project runs in — #377. Ordered, retire-not-delete. */
  phases: Phase[];
  /** Why the phases read failed, in words — the #289 lesson, as for areas. */
  phasesProblem: string;
  /** The active board's standalone milestones — the Plan register writes these,
   *  and a billable one is a payment point invoices claim against (milestone
   *  payments). Loaded live on board open, like phases. */
  milestones: MilestoneRow[];
  /** The active board's decision log — 3.19 Learning & Governance spine (D1).
   *  Loaded live on board open, like milestones; the Decisions tab reads and writes
   *  these. Empty on a board with none. */
  decisions: Decision[];
  /** Whether the active board's decision log has had its first snapshot (or a
   *  refused/empty read) yet — false while it is still loading. The Decisions tab
   *  waits on this before drawing its "no decisions logged yet" first-run state, so a
   *  board with retired-but-present decisions never flashes the never-logged view on
   *  a cold load. Reset to false on every board (re)subscribe. */
  decisionsLoaded: boolean;
  /** The active board's RAID register — Risks, Assumptions, Issues and Dependencies
   *  (migration 0193). Loaded live on board open, like decisions; the Risk register
   *  screen reads and writes these, and the charter renders its risks from `risks`.
   *  Empty on a board with none. */
  risks: Risk[];
  assumptions: Assumption[];
  registerIssues: RegisterIssue[];
  dependencies: Dependency[];
  /** Whether each register has had its first snapshot (or a refused/empty read) yet —
   *  false while loading, so the screen waits before drawing its empty state. Reset on
   *  every board (re)subscribe. */
  raidLoaded: boolean;
  /** A lesson handed from the Lessons screen to the Risk register to seed a risk
   *  from (#490, the learning loop). Set by startRiskSeed just before navigating to
   *  the register, which opens the editable seed preview and clears it. */
  seedRiskLesson: Lesson | null;
  /** The workspace lessons register — 3.19 Learning & Governance spine (L1). ONE
   *  tenant-wide collection (like reactions): workspace-wide lessons plus board
   *  lessons the person can open. The Lessons tab filters to its board; the library
   *  shows them all. Empty until loaded. */
  lessons: Lesson[];
  /** The fluid steps worked inside the phases — #377, the Plan view. Flat across
   *  the board; the view groups them by phase. */
  phaseSteps: Step[];
  /** The append-only history of every phase's sign-offs and changes — #377. */
  phaseEvents: PhaseEvent[];

  view: View;
  groupBy: GroupBy;
  /** Which backlog view is selected — see src/views.ts. #301. */
  viewId: string;
  /** Counts beside the group headings. Hides nothing either way. */
  showCounts: boolean;
  /**
   * The views this person saved, newest last.
   *
   * ON THE PERSON, NOT THE BROWSER, by Vinnie's decision of 30 August 2026:
   * *"per person, but follows them"*. Filters stay in localStorage — they are a
   * transient state of one screen — but a view somebody named and expects to
   * find again is not, and finding it only on the laptop you built it on is
   * the kind of thing that quietly teaches people not to use a feature.
   */
  savedViews: SavedView[];
  lastSeen: number;
  filters: Filters;
  search: string;
  selectedId: string | null;
  creating: boolean;
  /** When a card is opened straight into editing one field — the card-face
   *  "+ What's this about?" nudge opens it to the summary, and Needs Attention's
   *  "Fix →" opens it onto the field that needs the change — which field the modal
   *  should start editing and focus. The modal consumes it on open and clears it. */
  openEditField: 'summary' | 'dates' | 'assignee' | 'sprint' | 'size' | 'status' | null;
  /**
   * A CARD EDIT THAT DID NOT SAVE, in the person's words, or null when the last
   * one stuck. The write is optimistic and the modal closes the instant Save is
   * pressed, so a write the database later refuses used to revert the card in
   * silence — the exact shape of "I set the dates, saved, and they came back".
   * Held here so a banner can say it did not save, and why, after the card has
   * already closed. Cleared when the next edit is attempted or the person
   * dismisses it. See `updateIssue`.
   */
  saveProblem: string | null;
  /**
   * A BRIEF LINE CONFIRMING A COMMAND JUST RAN, or null. The command palette acts
   * and closes in one keystroke, so without this a quiet action — set a status,
   * assign a card, switch a project — left nothing on screen to say it happened.
   * Set by whatever ran the command, shown as a small toast, and cleared on a
   * timer by the toast itself. UI-only; never persisted.
   */
  flash: string | null;
  /**
   * THE ONE NEXT STEP, after a "how do I…" answer sends you to a screen. Set by the
   * help palette as it drills to the real control; shown as a calm, dismissible
   * coach-mark naming the example-first next action ("Open Documents → produce the
   * PDF → send it…"). Unlike `flash` it does NOT time out — a sentence needs
   * reading — and is cleared by Escape, a click on it, or choosing another screen.
   * UI-only; never persisted. (Help & learnability initiative, design rule P9.)
   */
  coach: string | null;
  /**
   * TRUE WHILE THE OPEN CARD IS BEING EDITED. The card's editor holds its own copy
   * of the fields it edits (status, priority, assignee among them) and writes them
   * on Save, so a quick change made ELSEWHERE while the form is open would be
   * clobbered when it saves. Lifted out of the modal so the command palette can
   * see it and withhold those same quick actions while the full editor is open.
   */
  issueEditing: boolean;
  /**
   * WHERE THE PORTFOLIO WAS WHEN A CARD WAS OPENED FROM IT — so the full card
   * has a way back to exactly that spot (same drill), not just a modal close
   * that would strand the viewer on a board they never navigated to. Non-null
   * only while a card opened via the Portfolio is on screen; the Portfolio
   * reads it on the way back and clears it. `setView` deliberately leaves it
   * alone so it survives the board→portfolio hop; `openIssue` clears it so a
   * card opened any other way never wears the "← Portfolio" return.
   */
  portfolioReturn: PortfolioReturn | null;
  setPortfolioReturn: (r: PortfolioReturn | null) => void;

  /** A one-shot request to open the go-live handoff dialog on a specific board —
   *  set by the board's "Mark it live" ribbon and by the Portfolio's overdue
   *  nudge (which switches to that board first). HandoffPrompt opens when this
   *  equals the current board and clears it on dismiss/confirm; it survives the
   *  board switch (setProject leaves it alone), so a person never has to wait for
   *  or chase the auto-prompt (#435, Vinnie 22 Oct). */
  handoffRequestBoard: string | null;
  setHandoffRequest: (boardId: string | null) => void;

  setGroupBy: (g: GroupBy) => void;
  setViewId: (id: string) => void;
  setShowCounts: (on: boolean) => void;
  saveView: (name: string) => Promise<void>;
  forgetView: (id: string) => Promise<void>;
  setLastSeen: (t: number) => void;
  setFilters: (patch: Partial<Filters>) => void;
  resetFilters: () => void;
  setView: (v: View) => void;
  /** A one-shot: open the Budget & payments screen straight into its edit form.
   *  The Plan's "Set budget" / "Add a forecast" deep-links set it; BudgetPay reads
   *  and clears it on mount, so a later plain visit stays in view mode. */
  budgetEnterEdit: boolean;
  setBudgetEnterEdit: (v: boolean) => void;
  /**
   * THE SCREEN A DRILL-DOWN CAME FROM, so there is a way back from it.
   *
   * Vinnie, 27 August 2026: *"when you drill down and change screens there
   * should be a way back to where you were rather than finding it again in a
   * menu."* Clicking a bar on Insights sets a filter and moves you to the
   * Backlog — and from there the only route back was the menu, which is not a
   * route back, it is starting again.
   *
   * Null unless a drill-down put you here, and cleared the moment you choose a
   * screen yourself: an offer to return to somewhere you left on purpose,
   * shown indefinitely, is clutter rather than help.
   */
  cameFrom: View | null;
  /** The My Work tab last open ('todo' | 'inbox'), kept on the store so it
      survives leaving for a card and coming back: "Back to My work" from a card
      opened in the Inbox returns to the Inbox, not To do (it used to always
      reset to To do because the tab was component-local state). */
  myworkTab: 'todo' | 'inbox';
  setMyworkTab: (t: 'todo' | 'inbox') => void;
  /**
   * ── THE ONE THING TO REVEAL ON ARRIVAL — #350 ─────────────────────────────
   *
   * Sending somebody to a SCREEN is not sending them to the THING. Approvals
   * listed a sprint waiting on a signature and then landed the reader on a
   * Sprints screen that may hold a year of them, collapsed, to find it
   * themselves. Vinnie, 4 September 2026, walking [[#332]]: *"it takes you to
   * that specific sprint waiting on the approval with it open so that you can
   * immediately go to approve the sprint."*
   *
   * Null unless a drill-down named something, and cleared the moment somebody
   * chooses a screen for themselves — for the same reason `cameFrom` is. It is
   * an instruction about one arrival, not a preference.
   */
  revealId: string | null;
  /**
   * Bumped on EVERY drill, so a flash re-fires even when the drill names the same
   * control you are already looking at. The generic reveal effect keys on this,
   * not on `revealId` alone: re-selecting the same `revealId` is no state change,
   * so without the counter a second "Take me there" to a control already on screen
   * did nothing (L99). Kept separate from `revealId` so clearing it is unnecessary
   * — screens that read `revealId` to hold a section open are undisturbed.
   */
  revealSeq: number;
  /** Whether the changelog / What's new dialog is open — shared so the version
   *  chip and the What's new callout open the one surface. */
  changelogOpen: boolean;
  setChangelogOpen: (open: boolean) => void;
  /** Move to `view`, remembering where from, and offer the way back. */
  drillTo: (view: View, reveal?: string) => void;
  /**
   * Open whatever a decision or lesson's Reveal link points at — the card it
   * affected, the document (change request / after-action report), or the screen
   * (budget line, scope, sign-offs). One routing table so a link chip in either
   * view reaches its target the same way. `kind` matches the composers' targets
   * (decisions.ts / lessons.ts); an unknown kind is a no-op.
   */
  revealLink: (link: { kind: string; id: string }) => void;
  /**
   * Show the backlog narrowed to these issues, and nothing else.
   *
   * Sent from Next up, so that "3 things are holding this up" opens those three
   * rather than the whole list. A search cannot express a set, and a link to an
   * unfiltered list left the reader to work out which of twenty-five rows the
   * number had meant.
   */
  focus: string[] | null;
  /**
   * WHAT THE FOCUSED SET HAS IN COMMON, as a phrase the bar can finish the
   * sentence with — "holding work up", "being worked on right now".
   *
   * The bar used to say "holding work up" whatever had sent you there, because
   * blockers were the only route in. In flight is a second route, and a bar
   * that describes the wrong journey is worse than one that says nothing.
   */
  focusWhy: string;
  showInBacklog: (ids: string[], why: string) => void;
  clearFocus: () => void;
  setSearch: (s: string) => void;
  /**
   * WHETHER THE START-A-BOARD SCREEN IS OPEN.
   *
   * On the store rather than inside the project switcher, because WHERE a
   * full-screen dialog is rendered decides how big it is. `.overlay` is
   * positioned against its nearest positioned ancestor, so rendered from inside
   * the sidebar it filled the SIDEBAR — a full-screen form squeezed into 216
   * pixels. Mounted at the app root beside the issue modal, it covers the app,
   * which is what it was always drawn to do.
   */
  startingBoard: boolean;
  setStartingBoard: (open: boolean) => void;
  openIssue: (id: string, editField?: 'summary' | 'dates' | 'assignee' | 'sprint' | 'size' | 'status') => void;
  /** The open card calls this once it has consumed `openEditField`. */
  clearOpenEditField: () => void;
  /** When this browser last opened each card — see markCardSeen. */
  cardsSeen: Record<string, number>;
  createStatus: IssueStatus | null;
  /**
   * Closed for this session.
   *
   * The markers are written to the database, and a write takes a moment to come
   * back. Without this the wizard reopens for that moment, which reads as it
   * refusing to close.
   */
  setupDismissed: boolean;
  /**
   * Whether the questions are on screen.
   *
   * Nothing puts them there but a person: the wizard used to open itself on any
   * project nobody had set up, which threw six questions over whatever was
   * already on the screen the moment you switched project.
   */
  setupForced: boolean;
  /**
   * Ask everything, rather than only what is outstanding.
   *
   * True from Settings, where the point is to go through it again. False from
   * the board's offer, where asking questions already answered would be padding
   * out the very thing somebody is being persuaded to start.
   */
  setupAll: boolean;
  dismissSetup: () => void;
  /** From Settings: every question, whatever has been answered. */
  openSetup: () => void;
  /** From the board's offer: only what is still outstanding. */
  startSetup: () => void;
  closeModal: () => void;
  /** Dismiss the "did not save" banner (see `saveProblem`). */
  clearSaveProblem: () => void;
  /** Show a brief confirmation toast (see `flash`); '' or null clears it. */
  showFlash: (text: string | null) => void;
  /** Show the help coach-mark's next step (see `coach`); '' or null clears it. */
  showCoach: (text: string | null) => void;
  /** The open card's editor reports whether it is editing (see `issueEditing`). */
  setIssueEditing: (v: boolean) => void;
  startCreate: (status?: IssueStatus) => void;

  // Resolves true when the card was actually written, false when it was not —
  // so the quick-add bar keeps what was typed and says so instead of clearing
  // the line on a silent failure (L148).
  createIssue: (draft: Omit<Issue, 'id' | 'ref' | 'createdAt' | 'updatedAt' | 'comments'>) => Promise<boolean>;
  /**
   * Bring a whole spreadsheet in at once — see src/import-plan.ts for what
   * turns a file into these, and Import.tsx for the three questions asked first.
   *
   * NOT createIssue in a loop. Each card here already knows its reference,
   * because the board is taking on the numbering the file arrived with, so
   * asking the counter 78 times would both be 78 round trips and hand out
   * numbers nobody wants.
   */
  importIssues: (
    rows: { draft: Omit<Issue, 'id' | 'createdAt' | 'updatedAt' | 'comments'> }[],
    scheme: { prefix: string; width: number; next: number },
    /*
     * Called after each batch with how many have landed. An import of 78 cards
     * takes long enough that a button reading "Importing…" and nothing else
     * leaves somebody wondering whether it has stopped — asked for by Vinnie on
     * 20 August 2026 while walking it.
     */
    onProgress?: (done: number, total: number) => void,
  ) => Promise<{ made: number; failed: number; why: string }>;
  /**
   * File a report on the FlightDeck board, from wherever you are.
   *
   * Returns the reference it was given, so the person is told where their
   * report went rather than that it "was sent". Throws if it could not be
   * written — losing something somebody took the trouble to type, quietly, is
   * the one outcome this must not have.
   */
  sendFeedback: (draft: FeedbackDraft, ctx: FeedbackContext) => Promise<string>;
  /**
   * Who reported each issue, by issue id — empty string for "nobody did".
   * Filled in as cards are opened; see loadReporter for why the misses are kept.
   */
  reporters: Record<string, string>;
  loadReporter: (issue: string) => Promise<void>;
  /**
   * Answer the reporter. Separate from addComment ON PURPOSE — one is a note
   * between us, the other leaves the building. Says whether the mail went.
   */
  answerReport: (
    issue: string,
    text: string,
    about?: 'fixed' | 'closed'
  ) => Promise<{ emailed: boolean; why?: string; alreadyTold?: boolean }>;
  /**
   * Tell the reporter a card moved, where the move is one of the two they care
   * about. Does nothing when nobody reported it, which is almost always.
   */
  tellReporter: (issue: string, status: IssueStatus) => Promise<void>;
  /**
   * Which ticket is waiting on a sentence before its reporter is told it has
   * been closed. Empty when nothing is. See tellReporter for why it stops here.
   */
  tellingWhyClosed: string;
  sayNothingAboutClosing: () => void;
  tellThemWhyClosed: (text: string) => Promise<{ emailed: boolean; why?: string }>;
  /**
   * My own reports and what was said back — the only read that crosses out of
   * the workspace I am standing in. Answers empty rather than throwing: it
   * draws a screen somebody opened, not a button they pressed.
   */
  myReports: () => Promise<ReportedByMe[]>;
  updateIssue: (id: string, patch: Partial<Issue>) => void;
  /**
   * PUT A CARD ON HOLD / TAKE IT OFF — an explicit, deliberate pause (project-card-hold).
   * `holdIssue` stamps `heldAt` (and an optional reason); while held the card is skipped
   * by the idle nudge, reads a neutral "On hold", and sits out of the slip counts.
   * `resumeIssue` clears the hold and moves `startsOn`/`dueDate` out by the time it was
   * held, so the deadline resumes with the same slack — the push cascades to dependents.
   * Both route through `updateIssue`, so the optimistic UI, the audit trail and (on
   * resume) the date cascade all fire exactly as they do for any other card change.
   */
  holdIssue: (id: string, reason?: string) => void;
  resumeIssue: (id: string) => void;
  /**
   * CLEAN UP AN EPIC — move every card on this board that carries epic `from` to
   * epic `to`, in one go. An epic is only a name on its cards (free text, no stored
   * object), so this one move is the whole of epic management from Settings:
   * rename (`to` a new name), merge (`to` a name already in use), delete→reassign
   * (`to` another epic) and delete→clear (`to` is ''). Each card is edited through
   * updateIssue, so the change lands in that card's history like any other edit.
   */
  reassignEpic: (from: string, to: string) => void;
  /**
   * POST A DATED STATUS UPDATE ON A CARD — the status log (0138).
   *
   * The writer types `text` ("what changed"); the author and time are stamped.
   * `status` is passed only when the update also moves the card, and a move with
   * no words is still a valid update. The newest words are mirrored into
   * `stands` so the portfolio pack and the card's notes show the current state.
   * `quiet` posts it as a Note rather than an Update — recorded, but no call-out
   * to the card's people (a move always tells them, so `quiet` is ignored on one).
   * Awaitable — somebody is watching the box they typed into.
   */
  postStatusUpdate: (
    id: string,
    input: { text: string; status?: IssueStatus; nextStep?: string; quiet?: boolean; overrideNote?: string }
  ) => Promise<void>;
  /**
   * CARDS TO NUDGE FOR A RELEASE PRIORITY — #6, transient and UI-only.
   *
   * A card pulled into a phase or added to a sprint with no MoSCoW level yet is
   * added here, and the Plan and Sprints screens show the inline nudge on it.
   * Not persisted and never touched by realtime — it is a per-session prompt,
   * cleared the moment the level is set or the nudge is dismissed.
   */
  moscowNudge: string[];
  dismissMoscowNudge: (id: string) => void;
  /**
   * CARD ASSISTS THE PERSON HAS WAVED AWAY, this session only (project-idle-nudge).
   * A one-tap size/due suggestion that someone dismissed should stay gone while the
   * card is open, so the key is `${issueId}:size` / `${issueId}:due` and the surface
   * hides the strip whose key is in here. Ephemeral like `moscowNudge` — a reload
   * offers the suggestion afresh, because by then the card may have changed.
   */
  assistDismiss: string[];
  dismissAssist: (key: string) => void;
  /**
   * Move a card to a column. Optimistic — see the note on the implementation —
   * and awaitable, so a caller that needs to say what happened can.
   *
   * `opts.overrideNote` is an OPTIONAL trailing argument, so every existing
   * caller is unaffected. When present, the card's own `auto` move line carries
   * that note instead of being blank — this is how a deliberate-start surface
   * records that the card was started while still waiting on an unfinished
   * predecessor (the soft start-gate; see Board.tsx / IssueModal.tsx).
   */
  moveIssue: (id: string, status: IssueStatus, opts?: { overrideNote?: string }) => Promise<void>;
  /**
   * Move several at once, and tell the caller how many would not go.
   *
   * NOT A LOOP OVER `moveIssue`. In a row each write waits for the one before
   * it, which is the crawl this is here to avoid.
   */
  moveIssues: (ids: string[], status: IssueStatus) => Promise<{ refused: number; why: string }>;
  /* ── BULK CARD CHANGES — the floating bulk bar (project-bulk-card-changes) ───
   *
   * These are the view-agnostic board actions the BulkBar drives, and the entry
   * point the planned ⌘Z Undo slice reuses: each writes exactly ONE audit line
   * via recordBulk (one action = one line = one undo), each reports enough for the
   * result toast, and the two that touch people coalesce their call-outs to ONE
   * digest per recipient (never N). The caller passes the human LABEL for the
   * audit line and the toast, so the store stays clear of STATUS_LABELS/display
   * wording the view already owns.
   */
  /** Move a set and tell the caller which went, which were refused (by their own
   *  card rules), and the first reason — so the toast can say "6 moved · 1 couldn't"
   *  and "Show it" can filter to the refused ones. Fires one move digest per owner. */
  bulkMove: (
    ids: string[],
    status: IssueStatus,
    label: string,
  ) => Promise<{ moved: string[]; refused: string[]; why: string; notified: boolean }>;
  /** Assign a set to one person (the doer / Responsible). One audit line, and one
   *  "assigned you N cards" digest to that person (never to yourself, never N). */
  bulkAssign: (ids: string[], who: string, label: string) => Promise<{ notified: boolean }>;
  /** Set the same field on a set — Area / Epic / Priority. No call-out (these are
   *  not a hand-off), one audit line. The patch is the field to write on every card. */
  bulkPatch: (ids: string[], patch: Partial<Issue>, label: string) => void;
  /** Copy a set — each card's work, not its history (see duplicateDraft). One line. */
  bulkDuplicate: (ids: string[], label: string) => Promise<void>;
  /** Delete a set, keeping each card's restorable snapshot (as deleteIssue does),
   *  plus one bulk summary line. Returns the deleted cards so Undo can restore them. */
  bulkDelete: (ids: string[], label: string) => Promise<Issue[]>;
  /** Write ONE digest row per recipient for a bulk hand-off. A browser cannot write
   *  a call-out FOR another person (notifications have no client insert — 0105/0106),
   *  so this hands the shapes to `backend.bulkNotify`, which calls the SECURITY DEFINER
   *  RPC. A no-op until that backend method + its migration exist. */
  bulkNotify: (digests: BulkDigest[]) => Promise<boolean>;
  /** UNDO a field/status/assignee batch — put each card's PRIOR value back (the
   *  values differ per card, so it is not a single bulkPatch). One audit line. This
   *  is the reversal the result-toast Undo and the future ⌘Z slice both call. */
  bulkRestore: (entries: { id: string; patch: Partial<Issue> }[], label: string) => void;
  /** UNDO a delete batch — write the deleted cards back exactly as they were (same
   *  id/ref), from the copies bulkDelete returned. One audit line. */
  restoreIssues: (issues: Issue[], label: string) => Promise<void>;
  /**
   * SPRINTS. Every one of these returns the reason it did not happen, or an
   * empty string — rather than throwing, or returning a boolean that leaves the
   * screen to invent the sentence. The refusals here are things a person needs
   * telling ("Sprint 2 is still running"), not faults.
   */
  /* ── Areas — #269 ──────────────────────────────────────────────────────
   *
   * Each returns a sentence when it refuses and '' when it does not, the same
   * shape as the sprint actions below, so a screen never has to invent the
   * wording for a refusal it did not cause.
   */
  createType: (draft: { name: string; means: Means }) => Promise<string>;
  editType: (id: string, patch: { name?: string; means?: Means }) => Promise<string>;
  moveType: (id: string, direction: -1 | 1) => Promise<string>;
  retireType: (id: string) => Promise<string>;
  restoreType: (id: string) => Promise<string>;
  createArea: (draft: { name: string; colour: string }) => Promise<{ id: string } | { error: string }>;
  editArea: (id: string, patch: { name?: string; colour?: string; lead?: string | null }) => Promise<string>;
  moveArea: (id: string, direction: -1 | 1) => Promise<string>;
  /**
   * What "remove" means, and it does NOT delete — Vinnie's decision, 26 August
   * 2026. The area stops being offered and keeps its place on every card that
   * already carries it, so a chart drawn in June still reads the same in
   * December. Migration 0067 makes this the only available path by giving the
   * table no delete policy at all.
   */
  retireArea: (id: string) => Promise<string>;
  /** Put a retired area back on offer. Refused if the name is now taken. */
  restoreArea: (id: string) => Promise<string>;
  /* ── Labels — #462. A registry by name: name + optional colour, no order. ── */
  createLabel: (draft: { name: string; colour?: string | null }) => Promise<{ id: string } | { error: string }>;
  editLabel: (id: string, patch: { name?: string; colour?: string | null }) => Promise<string>;
  /** "Remove" retires, never deletes — as for areas, so counts still add up. */
  retireLabel: (id: string) => Promise<string>;
  /** Put a retired label back on offer. Refused if the name is now taken. */
  restoreLabel: (id: string) => Promise<string>;
  /* ── Phases — #377. Mirror areas: ordered, retire-not-delete. ───────────── */
  createPhase: (draft: { name: string; targetEnd?: string | null; status?: PhaseStatus }) => Promise<string>;
  editPhase: (
    id: string,
    patch: { name?: string; targetEnd?: string | null; status?: PhaseStatus }
  ) => Promise<string>;
  movePhase: (id: string, direction: -1 | 1) => Promise<string>;
  /** What "remove" means for a phase — retire, never delete, exactly as an area
   *  (0092 gives the table no delete policy). Kept on every issue that names it. */
  retirePhase: (id: string) => Promise<string>;
  /** Put a retired phase back on offer. Refused if the name is now taken. */
  restorePhase: (id: string) => Promise<string>;
  /* ── The Plan's standalone milestones — #374 + milestone payments. ───────── */
  /** Add a standalone milestone to the active board. A billable one needs a
   *  bill value. Returns '' or why it was refused. */
  createMilestone: (draft: {
    name: string;
    kind: MilestoneKind;
    targetDay?: string | null;
    billable?: boolean;
    billValue?: number | null;
    forecastTie?: MilestoneTie | null;
  }) => Promise<string>;
  /** Edit a standalone milestone's fields. Turning billable off clears its value. */
  editMilestone: (
    id: string,
    patch: {
      name?: string;
      kind?: MilestoneKind;
      targetDay?: string | null;
      billable?: boolean;
      billValue?: number | null;
      forecastTie?: MilestoneTie | null;
    }
  ) => Promise<string>;
  /** Remove a standalone milestone. Unlike a phase it holds no cards, so it is a
   *  real delete. Returns '' or why it was refused. */
  deleteMilestone: (id: string) => Promise<string>;

  /* ── The decision log — 3.19 Learning & Governance spine (D1). ───────────────
     Supersede-never-edit: an open draft edits in place; a decided decision is
     changed by superseding it. Writes are gated server-side by the decisions RLS
     (a writer with rank on the board, or its owner), like milestones. */
  /** Log a decision on the active board. Defaults to 'decided' (a decision MADE,
   *  stamped now/by me); pass status 'open' to log a pending one. Returns '' or why
   *  it was refused, like createMilestone. */
  logDecision: (draft: DecisionDraft) => Promise<string>;
  /** Move an open decision to 'decided' (stamps who/when). Returns '' or an error. */
  decideDecision: (id: string, when?: { decidedBy?: string; decidedAt?: number }) => Promise<string>;
  /** Edit an OPEN decision in place (title/detail). A decided one must be superseded
   *  instead. Returns '' or an error. */
  editDecision: (id: string, patch: { title?: string; detail?: RichDoc }) => Promise<string>;
  /** Supersede a decided decision with a new one (ADR): writes the replacement
   *  linking back, and flips the old one to 'superseded'. Returns '' or an error. */
  supersedeDecision: (id: string, draft: DecisionDraft) => Promise<string>;
  /** Withdraw an open or decided decision — it stays in the record, out of the live
   *  log, with `reason` kept as the why (required; captured by the withdraw prompt).
   *  Returns '' or an error. */
  withdrawDecision: (id: string, reason: string) => Promise<string>;

  /* ── The RAID register — Risks, Assumptions, Issues, Dependencies (0193). ─────
     Each add returns the new row's id (or '' when refused/blocked, like a sample
     board); edit takes a partial patch of the item's own fields; remove takes the id.
     Writes are gated server-side by each register's RLS (a writer with rank on the
     board, or its owner). The two convert loops cross registers and stamp provenance. */
  addRisk: (draft: RiskDraft) => Promise<string>;
  editRisk: (id: string, patch: Partial<Omit<Risk, 'id' | 'boardId' | 'num' | 'madeBy' | 'createdAt'>>) => Promise<string>;
  removeRisk: (id: string) => Promise<string>;
  addAssumption: (draft: AssumptionDraft) => Promise<string>;
  editAssumption: (id: string, patch: Partial<Omit<Assumption, 'id' | 'boardId' | 'num' | 'madeBy' | 'createdAt'>>) => Promise<string>;
  removeAssumption: (id: string) => Promise<string>;
  /** Convert an assumption to a risk (the invalidated-assumption loop): creates a
   *  risk carrying the assumption's title/impact, then flips the assumption to
   *  'invalidated' with `convertedToRiskId` set. Returns the new risk id or ''. */
  convertAssumptionToRisk: (id: string) => Promise<string>;
  addIssue: (draft: IssueDraft) => Promise<string>;
  editIssue: (id: string, patch: Partial<Omit<RegisterIssue, 'id' | 'boardId' | 'num' | 'madeBy' | 'createdAt'>>) => Promise<string>;
  removeIssue: (id: string) => Promise<string>;
  /** Realise a risk as an issue (the realised-risk loop): creates an issue carrying
   *  the risk's title with `realisedFromRiskId` provenance. The action lives on the
   *  risk row; the risk itself is left as-is. Returns the new issue id or ''. */
  realiseRiskAsIssue: (riskId: string) => Promise<string>;
  addDependency: (draft: DependencyDraft) => Promise<string>;
  editDependency: (id: string, patch: Partial<Omit<Dependency, 'id' | 'boardId' | 'num' | 'madeBy' | 'createdAt'>>) => Promise<string>;
  removeDependency: (id: string) => Promise<string>;
  /** Hand a lesson to the Risk register to seed a risk from, and open it there
   *  (#490). Navigates to the register; the register opens the seed preview. */
  startRiskSeed: (lesson: Lesson) => void;
  /** Clear the pending lesson seed (the register calls this once it has opened the
   *  preview, so returning to the register does not reopen it). */
  clearRiskSeed: () => void;

  /* ── The lessons register — 3.19 Learning & Governance spine (L1). ───────────
     Workspace-wide: a draft with no boardId is a library lesson any tenant member
     may add; with a boardId it is raised on that board (a board writer). Writes are
     gated server-side by the lessons RLS. */
  /** Record a lesson. Omit boardId for a workspace-wide one; set it to raise it on a
   *  board. Returns '' or why it was refused. */
  createLesson: (draft: LessonDraft) => Promise<string>;
  /** Edit a lesson's fields (the author, a workspace admin, or a board writer may).
   *  Returns '' or an error. */
  editLesson: (
    id: string,
    patch: {
      title?: string;
      category?: Lesson['category'];
      kind?: Lesson['kind'];
      body?: RichDoc;
      /** A rich recommendation to set, or null to clear it (toggle off). */
      recommendation?: RichDoc | null;
      owner?: string | null;
    },
  ) => Promise<string>;
  /** Remove a lesson. Returns '' or an error. */
  deleteLesson: (id: string) => Promise<string>;

  /* ── The Plan view: steps worked inside a phase, and its history — #377. ──── */
  /** Add a step to a phase. Returns '' or why it was refused. */
  addStep: (phaseId: string, name: string) => Promise<string>;
  /** Edit a step's name, assignee, due day, work status or blocked reason. */
  editStep: (
    id: string,
    patch: { name?: string; assignee?: string; due?: string | null; status?: StepStatus; blockedReason?: string }
  ) => Promise<string>;
  /** Retire a step — remove means retire, never delete (the phases decision). */
  retireStep: (id: string) => Promise<string>;
  /** Set who has to sign a step off, replacing the list. */
  setStepSigners: (id: string, signers: StepSigner[]) => Promise<string>;
  /** Record that one signer has signed a step off. */
  signStep: (id: string, person: string) => Promise<string>;
  /** Close a phase — allowed only when every step is signed off (the gate). */
  signPhase: (phaseId: string) => Promise<string>;
  createSprint: (draft: { name: string; goal?: string; startsOn: string; endsOn: string }) => Promise<string>;
  editSprint: (id: string, patch: Partial<Sprint>) => Promise<string>;
  startSprint: (id: string) => Promise<string>;
  /**
   * Finish a sprint, saying what happens to work that did not get done.
   *
   * THE ANSWER IS REQUIRED, with no default, because there is no safe one —
   * carrying and returning to the backlog mean different things about whether
   * the work is still committed, and every chart drawn afterwards reads
   * differently. A default here would be the software quietly answering a
   * question only the team can. See `closingAsks` in src/sprints.ts.
   */
  closeSprint: (id: string, leftovers: Leftovers) => Promise<string>;
  deleteSprint: (id: string) => Promise<void>;
  /** Put a card into a sprint, or back in the backlog with an empty id. */
  putInSprint: (issueId: string, sprintId: string) => Promise<void>;
  /** Whether this board works in timeboxes. Off is flow, which is the default. */
  setCadence: (cadence: 'builds' | 'sprints') => Promise<void>;
  /** Whether this board is run as a bounded project or a continuous service. */
  setDeliveryMode: (mode: 'project' | 'continuous') => Promise<void>;
  /** The board-wide override of how a new card auto-assigns its creator (A). */
  setCardDefaults: (mode: import('./company-role').CardStance) => Promise<void>;
  /**
   * HAND THE PROJECT OVER — mark it live and move it to service (#435 surface 2).
   * Freezes the as-delivered snapshot (scope counted here from the live cards; the
   * plan, budget and actual go-live passed in by the confirm prompt, which holds
   * the charter rollup), stamps `actualGoLive`, and flips delivery to continuous —
   * one write, optimistic like setDeliveryMode so the header and nav turn on the
   * click frame. `provisional` records that it was confirmed before fully ready (a
   * phase still open, or ahead of the planned date). The DB trail-stamps the flip. */
  markLiveAndMoveToService: (opts: {
    actualGoLive: number;
    plannedGoLive?: number | null;
    budgetAtGoLive?: number | null;
    provisional: boolean;
  }) => Promise<void>;
  /** Undo the handoff — put the board back to in-delivery project mode, KEEPING the
   *  frozen delivery record (decision 3: a revert never discards what shipped). Sets
   *  deliveryMode project and clears actualGoLive; deliverySnapshot stays. Audited. */
  revertFromService: () => Promise<void>;
  /** Whether this board shows the optional In Staging column — #471. Off is the
   *  default; turning it off while cards sit in staging is guided in the UI
   *  (the cards are moved to a chosen column first). */
  setStagingEnabled: (on: boolean) => Promise<void>;
  /** Hide (or restore) the Done column while In Staging is on — 0183. Off by
   *  default, so the board keeps Done and scrolls to it; on drops the Done column
   *  while In Staging is shown. Only the board column is affected. The flip is
   *  audited on the row (record_board_control_change). */
  setHideDoneInStaging: (on: boolean) => Promise<void>;
  /** Show (or hide) the backlog as a column on the board — 0218. Off by default; the
   *  backlog lives on its own view. On splices a muted, capped Backlog column at the
   *  front (left of To-Do). Only the board column is affected — no card moves, and the
   *  backlog is never counted as open/WIP. The flip is audited (record_board_control_change). */
  setBacklogOnBoard: (on: boolean) => Promise<void>;
  /** Turn STRICT separation of duties on or off for scope changes — when on, only
   *  the designated approver may record one (an admin stand-in is hard-blocked);
   *  when off (default) a stand-in is allowed but documented. The flip is audited. */
  setRequireSeparateScopeApprover: (on: boolean) => Promise<void>;
  /** Turn the hard finish-to-start dependency block on/off for this board — admin
   *  only (the app gates it); the flip is recorded on the board's control trail. */
  setDepHardBlock: (on: boolean) => Promise<void>;
  /** Turn the card size/date assists on/off for this board — admin only
   *  (configureAssists); default on. The flip is recorded on the board's control
   *  trail (0205). */
  setCardAssists: (on: boolean) => Promise<void>;
  /** Turn the Insights Data quality panel on/off for this board — admin only
   *  (configureAssists); default on. The flip is recorded on the board's control
   *  trail (0205). */
  setDataQualityPanel: (on: boolean) => Promise<void>;
  /** Show the completion odds to everyone on this board, not only a PM-and-above — admin
   *  only (configureAssists); default off. The flip is recorded on the board's control
   *  trail (0220). */
  setShowOddsToEveryone: (on: boolean) => Promise<void>;
  /** Set who sees each half of the Delivery signal — admin only. 0225. */
  setScheduleSignalAudience: (aud: 'off' | 'managers' | 'all') => Promise<void>;
  setBudgetSignalAudience: (aud: 'off' | 'managers' | 'all') => Promise<void>;
  /** Set this board's idle-nudge windows — admin only (configureAssists). Null
   *  restores the app defaults. The change is recorded on the board's control
   *  trail (0213). */
  setIdleWindows: (windows: IdleWindows | null) => Promise<void>;
  /** Set this board's checklist prompts — admin only (configureAssists). Null
   *  restores the app defaults (everything on). The change is recorded on the
   *  board's control trail (0214). */
  setChecklistPrompts: (prompts: ChecklistPrompts | null) => Promise<void>;
  /** Configure the delivery-% nudge for this board (#483) — whether it runs and the
   *  bar it runs at (1–100%). A NUDGE, not a block: it warns when a payment is
   *  approved below the bar and records the fact; it never stops the approval.
   *  Gated to the admin/manager tier; the flip is audited. Returns '' or an error. */
  setDeliveryNudge: (config: { on: boolean; pct: number }) => Promise<string>;
  /**
   * Approve a change to the contract value on a project that already has one — a
   * scope adjustment. Gated to the designated approver or an admin/manager (like a
   * payment); a stand-in is allowed but recorded as a control exception unless
   * STRICT separation of duties is on, which hard-blocks it. Records
   * who/when/old→new/why (and the stand-in, when there is one) to the audit trail,
   * and moves the budget baseline — in full, or keeping a held contingency when the
   * caller passes an explicit `newBaseline`. Optionally references a signed
   * variation document. Returns why not, or '' when done.
   */
  adjustContractValue: (
    newValue: number,
    reason: string,
    documentRef?: string,
    newBaseline?: number
  ) => Promise<string>;
  /** Freeze the panel onto a release and start asking. Returns why not, or ''. */
  requestSignoff: (buildId: string) => Promise<string>;
  /**
   * ASK FOR APPROVAL OF A FINISHED FORTNIGHT — #332.
   *
   * Returns a message, empty when it worked, like every other action here.
   * `quietly` is set by the automatic raise: finishing a sprint must not fail
   * because nobody has set a rule yet.
   */
  requestSprintSignoff: (sprintId: string, quietly?: boolean) => Promise<string>;
  /** Clear a REFUSED sprint sign-off off the open panel, keeping the refusal as a
      settled/dismissed record. Manager/owner only. See migration 0094. */
  dismissSprintSignoff: (sprintId: string) => Promise<string>;
  /** Dismiss a refused sprint sign-off AND ask for a fresh round. */
  reRaiseSprintSignoff: (sprintId: string) => Promise<string>;
  /**
   * A SPRINT'S SIGN-OFF DOCUMENT, EITHER PHASE — the one-ledger flow.
   *
   * A slots sprint is signed off through documents, not a checklist on the
   * sprint: a 'change' request authorises the work at planning, a 'deliverables'
   * record confirms it at the end. This finds that document for the sprint and
   * phase, or creates it as a draft, and returns its id so the caller can open
   * it. `problem` is set instead when it could not be made. Only meaningful on a
   * slots board; the rank model keeps the inline sprint sign-off.
   */
  sprintSignoffDoc: (
    sprintId: string,
    kind: 'change' | 'deliverables'
  ) => Promise<{ id: string; problem: string }>;
  /** An admin fills in a row somebody could not sign. Returns why not, or ''. */
  /** Answer the row that names you. Being named is the permission — see 0055. */
  answerMyRow: (
    signoffId: string,
    answer: 'approved' | 'rejected',
    said?: string
  ) => Promise<string>;
  signOnBehalf: (
    signoffId: string,
    reason: string,
    answer?: 'approved' | 'rejected',
    said?: string
  ) => Promise<string>;
  /** Sign a row for a person who has an ACTIVE cover naming you (0130/0131) — the
      nominee's version of signOnBehalf. No typed reason: the cover IS the reason, so
      it is stamped automatically. Returns why not, or ''. */
  signAsCover: (
    signoffId: string,
    answer?: 'approved' | 'rejected',
    said?: string
  ) => Promise<string>;
  /**
   * Hand a signatory a link of their own. Returns the whole address, ONCE —
   * only its hash is stored, so it cannot be looked up afterwards.
   */
  /**
   * The link, and what became of it. `emailed` says whether FlightDeck posted
   * it; when it did not, `why` is the sentence to put on screen and the manager
   * sends `url` themselves — which is what everybody did before it could.
   */
  issueSigningLink: (
    signoffId: string
  ) => Promise<{ url: string; problem: string; emailed: boolean; to: string; why: string }>;
  /** Mark a signed release as gone to live. Returns why not, or ''. */
  cutTheRelease: (buildId: string) => Promise<string>;
  markGoneLive: (buildId: string) => Promise<string>;
  /**
   * Draft a new document. Returns the new id with an empty `problem` on success,
   * or an empty id and the reason to show on failure — the same `{ id, problem }`
   * shape as `supersedeDocument`, so a caller that wants to open the new document
   * (the Plan's "Start a charter") has its id, while one that only reports failure
   * reads `problem`.
   */
  draftDocument: (kind: DocumentKind, title: string, sprint?: string) => Promise<{ id: string; problem: string }>;
  saveDocument: (id: string, patch: Partial<Doc>) => Promise<string>;
  /**
   * SHARE A DOCUMENT WITH THE BOARD'S VENDOR, OR STOP — Feature B. Sets the one
   * visible_to_vendors flag, separately from saveDocument because it is NOT a
   * content edit: it applies whatever the document's state (a signed-off spec is
   * exactly the kind of thing one shares), so the draft-only guard must not stop it.
   * A governance kind is refused here and again by the database (0198); the flip is
   * gated to a manager+ and written to History server-side. Returns '' or the reason.
   */
  setDocVendorVisibility: (id: string, visible: boolean) => Promise<string>;
  /** `signatories` is read only for a charter, whose signatory list is named on
   *  the document (its sponsor + whoever else must approve it) rather than taken
   *  from the board's delivery panel; every other kind ignores it. */
  sendDocumentForSignature: (id: string, signatories?: Signatory[]) => Promise<string>;
  markDocumentSigned: (id: string) => Promise<string>;
  supersedeDocument: (id: string) => Promise<{ id: string; problem: string }>;
  withdrawDocument: (id: string) => Promise<string>;
  /** Clear a REFUSED document off the open Sign-offs panel, keeping the refusal
      as a settled/dismissed record. Manager/owner only. See migration 0094. */
  dismissDocument: (id: string) => Promise<string>;
  /** Dismiss a refused document AND send it out again for a fresh round. */
  reRaiseDocument: (id: string) => Promise<string>;
  deleteDocument: (id: string) => Promise<string>;
  uploadDocumentFile: (id: string, file: File) => Promise<string>;
  documentFileUrl: (id: string) => Promise<string>;
  /**
   * A CARD'S OWN FILES (dossier v3 §6, migration 0223). `attachCardFile` puts the
   * file in the private bucket under a `card-<id>` key and appends its metadata to
   * `issue.attachments`; `removeCardFile` deletes the object and drops the entry;
   * `cardFileUrl` issues a short-lived address to read one back. Each returns '' on
   * success or a human reason on refusal, like the document trio above.
   */
  attachCardFile: (issueId: string, file: File) => Promise<string>;
  removeCardFile: (issueId: string, attId: string) => Promise<string>;
  cardFileUrl: (issueId: string, attId: string) => Promise<string>;
  /**
   * WHO HOLDS THE PEN ON THE VERSION NUMBER — Vinnie's switch, 15 August.
   * On, the tool that pushes to this board sets it. Off, a person types it when
   * they cut a release.
   */
  setAssistantVersions: (on: boolean) => Promise<void>;
  /**
   * Record a release. Returns '' when it worked, or the reason in the words to
   * show the person — the same shape as `createSprint`.
   */
  cutRelease: (draft: Draft) => Promise<string>;
  /**
   * Mark a release withdrawn or rolled back, or put it back if that was a
   * mistake. Returns '' when it worked, or the reason to show the person.
   *
   * THE WORK IT CARRIED COMES BACK with it — see `notYetReleased`. A build
   * nobody can install has not shipped anything.
   */
  pullRelease: (buildId: string, pull: Pull) => Promise<string>;
  putReleaseBack: (buildId: string) => Promise<string>;
  /**
   * CHANGE WHAT A RELEASE WENT ONTO, after it has been recorded.
   *
   * Not an afterthought — it is what makes the device list true. A release
   * reaches its devices over the following days: the Fold by cable two days
   * later, the S26 over the air overnight. A list that could only be written at
   * the moment of cutting would be wrong about every release by the next
   * morning, and nobody would trust the field.
   */
  setReleaseDevices: (buildId: string, devices: string[]) => Promise<string>;
  deleteIssue: (id: string) => Promise<void>;
  /**
   * Add a comment. `replyTo` threads it under a specific feed entry — a status
   * update or another comment — so a reply answers that entry instead of sitting
   * at the bottom of the card. Its `parentAuthor` is carried so the server can
   * tell that person; a top-level comment leaves `replyTo` out.
   */
  addComment: (
    id: string,
    author: string,
    text: string,
    replyTo?: { parentId: string; parentAuthor: string }
  ) => void;
  /**
   * REPLY TO A CARD FROM MY WORK'S INBOX, without leaving it (#457).
   *
   * Posts a plain comment to an issue on ANOTHER board — the board the call-out
   * came from, by id, not the one currently open. (The call-out carries the board
   * and the card, but not the id of the comment that named you, so it cannot
   * thread under it; it lands as a top-level comment, which is what the inbox
   * reply is.) Awaitable and returns '' on success or the reason to show on the
   * row, because unlike `addComment` the card is usually not on screen to show an
   * optimistic row that could be quietly taken back.
   */
  replyFromInbox: (
    boardId: string,
    issueId: string,
    author: string,
    text: string
  ) => Promise<string>;
  /**
   * THE CARD OPEN IN THE MY WORK PANEL — the workstation (#2/#6).
   *
   * My Work opens a card in a right-docked panel WITHOUT switching board: no
   * `setProject`, so none of the ~21-subscription board load the switch costs. The
   * panel reads the one card from `portfolioIssues` for its facts and standing log,
   * and `openCardInline` fetches the full card (its comments nested) by board id in
   * one `get`. Every inline action — reply, react, post an update, move the status,
   * mark done — writes to the card's own board BY ID (the proven `replyFromInbox`
   * route), never through the active board. Structural edits (area, phase, sprint,
   * the blocker typeahead, title, delete) are not here; they open the full card.
   */
  inlineCardBoard: string | null;
  inlineCardId: string | null;
  /** The full card (comments nested), once loaded; null while loading or closed. */
  inlineCard: Issue | null;
  inlineCardLoading: boolean;
  inlineCardError: string;
  openCardInline: (boardId: string, issueId: string) => void;
  closeCardInline: () => void;
  /** Post an update/note, optionally moving the status — the card's board, by id. */
  postUpdateInline: (
    input: { text: string; status?: IssueStatus; nextStep?: string; quiet?: boolean; overrideNote?: string }
  ) => Promise<void>;
  /** Reply/comment on the panel card — the card's board, by id. Returns '' or why. */
  addCommentInline: (
    author: string,
    text: string,
    replyTo?: { parentId: string; parentAuthor: string }
  ) => Promise<string>;
  /**
   * Why the last comment did not save, when it did not.
   *
   * Empty when all is well. A comment is shown the moment it is typed, so the
   * one thing that must never happen quietly is it being taken back off again.
   */
  commentError: string;
  removeComment: (id: string, commentId: string, by: string) => void;
  /**
   * CHANGE THE WORDS OF A COMMENT OR REPLY that is already on the card — the
   * author's own, or anybody's for an admin. A single-row edit; the card shows a
   * quiet "edited" mark afterwards. Optimistic, and put back exactly as it was if
   * the write is refused, like every other thing somebody watches change.
   */
  editComment: (id: string, commentId: string, text: string) => void;
  /**
   * CHANGE THE WORDS OF A STATUS UPDATE already in the card's log — "awaiting
   * feedback" becoming "feedback received". Its author (always a member or above,
   * since only they could post it) or an admin. Editing never re-notifies: the
   * log is not growing, so the fan-out trigger stays quiet (migration 0138).
   */
  editStatusUpdate: (id: string, entryId: string, text: string, nextStep?: string) => Promise<void>;
  /**
   * Take a note or plain update off a card — the author's own, or anyone's for
   * an admin (canRemoveComment). A status MOVE is never removable: it is the
   * card's real history. Patches the standsLog the same authorised way
   * editStatusUpdate does. See CardFeed.
   */
  removeStatusUpdate: (id: string, entryId: string, by: string) => Promise<void>;

  setMeta: (patch: Partial<ProjectMeta>) => void;
  /**
   * Choose how THIS board labels its issues — plain `#267`, or the project key
   * `CSM-267`. Re-labels the cards already on the board so it keeps one scheme;
   * the number inside the label never changes, so anything signed off against a
   * card still points at the same card. See numberFromRef / refFor.
   */
  setNumbering: (usePrefix: boolean) => Promise<void>;
  /** The customer logo on this board's generated documents. Returns why not, or ''. */
  uploadProjectLogo: (file: File) => Promise<string>;
  projectLogoUrl: () => Promise<string>;
  writeAccess: (emails: string[], admins: string[]) => Promise<void>;
  setPersonAdmin: (email: string, isAdmin: boolean) => Promise<void>;
  recordAudit: (
    kind: AuditKind,
    what: string,
    extra?: Partial<Pick<AuditEntry, 'snapshot' | 'projectId'>>
  ) => Promise<void>;
  /**
   * Sign out every browser this account is open in, including this one.
   *
   * An INSTRUCTION, not a revocation — see src/sessions.ts for why that is the
   * most a browser can do, and why saying so matters.
   */
  signOutEverywhere: () => Promise<void>;
  /** Take one machine off the list, and out. */
  endSession: (id: string) => Promise<void>;
  /** Settle one call-out — stamp when it was read. Only ever moves a row from
      unread to read; a row already read is left alone. */
  markNotificationRead: (id: string) => Promise<void>;
  /** Mark a call-out HANDLED — replied to, or marked done in My Work (0144).
      Stamps handled_at (and read_at too, since handling it reads it). The inbox
      then clears it; the notification centre keeps it. */
  markNotificationDone: (id: string) => Promise<void>;
  /** Undo a mark-done — clears handled_at so the call-out returns to "Needs my
      action" (still read). The Done row's Undo uses it, so marking done is never
      a one-way move with no way back. */
  markNotificationActive: (id: string) => Promise<void>;
  /** Settle every unread call-out at once — the "Mark all read" button. */
  markAllNotificationsRead: () => Promise<void>;
  /** Put a deleted issue back, from the copy kept in the trail. */
  restoreIssue: (entry: AuditEntry) => Promise<void>;
  /**
   * One change applied to many issues, recorded as the single act it was.
   *
   * Each issue still gets its own line in its own history — that is what makes
   * a change readable on the card. This adds the fact those lines belong
   * together, which is the part that was missing when twenty blockers went in
   * one press and the trail showed twenty unrelated edits.
   */
  recordBulk: (what: string, issues: Issue[]) => void;
  setProject: (id: string) => void;
  /**
   * Start a board of your own, and open it.
   *
   * Returns the new board's id. Throws with a readable reason if the write is
   * refused — this is a form somebody has typed into, so a silent failure
   * would lose what they wrote and tell them nothing.
   */
  createProject: (
    name: string,
    key: string,
    /* The rest of the wizard's draft — #387. Absent for the two-field path. */
    def?: Omit<ProjectDraft, 'name' | 'key'>
  ) => Promise<string>;
  /**
   * PUT A BOARD AWAY, OR BRING IT BACK — #327.
   *
   * Archive rather than delete, and that was the decision rather than the
   * button. A board carries issues, comments, activity, releases and sign-offs,
   * and a sign-off is a record somebody put their name to. Deleting takes all
   * of it, permanently, on one click from one person. The same argument that
   * made Remove mean *retire* for areas and types applies here with far more
   * weight, so it is the same answer.
   *
   * Nothing is removed and nothing is renamed. The board stops being offered,
   * and comes back exactly as it was.
   *
   * Returns what to tell somebody — empty when it simply worked — the same as
   * every other settings action here.
   */
  archiveProject: (id: string, archived: boolean) => Promise<string>;
  /**
   * TAKE THE READ-ONLY SAMPLE PAIR OUT OF, OR BACK INTO, THE BOARD LIST
   * (remove/restore mock 01–03).
   *
   * A tenant that has finished with the Example Co. examples can put them away,
   * and bring them back whenever it wants a reference again. Both reuse the
   * board-archive path scoped to the sample pair — no new delete mechanic, so
   * nothing is ever lost: removing archives both sample boards, restoring brings
   * them back. Remove is the admin-only "Remove these" / "Remove them"; restore
   * is "Bring them back" (Settings) and the empty-picker "Or bring back the
   * examples". Each returns what to tell somebody, '' when it simply worked.
   */
  removeSamples: () => Promise<string>;
  restoreSamples: () => Promise<string>;
  /**
   * CHANGE A PROJECT'S OWN DEFINITION AFTER IT IS MADE — #387.
   *
   * The create-project wizard captures the go-live date, budget baseline and the
   * rest of the charter (projects.ts), but there was no way to change any of it
   * afterwards — so a board made without a go-live was stuck on "No go-live date
   * set" forever and nothing could ever read against it. This patches the board doc
   * with any subset of those definition fields (camelCase, mapped to columns by
   * the backend), records an audit, and lets the projects watcher reflect it.
   * Gated at the UI to who may edit settings; the row's own rules gate the write.
   * Returns what to tell somebody — empty when it simply worked.
   */
  updateProject: (
    id: string,
    patch: Partial<
      Pick<
        Project,
        | 'goLive'
        | 'goLiveAnchor'
        | 'startDate'
        | 'description'
        | 'priority'
        | 'projectType'
        | 'budget'
        | 'contractValue'
        | 'fundingSource'
        | 'costBasis'
        | 'capex'
        | 'capexNote'
        | 'opex'
        | 'opexNote'
        | 'actualSpend'
        | 'forecast'
        | 'retentionRate'
        | 'sponsor'
        | 'stakeholders'
        | 'risks'
        | 'successMeasure'
        | 'vendor'
        | 'scopeIn'
        | 'scopeOut'
        | 'projectManager'
        | 'payApprover'
        | 'assumptions'
        | 'constraints'
        | 'contractRef'
        | 'vendorContact'
      >
    >
  ) => Promise<string>;
  /**
   * Put a project on hold, or take it off — migration 0115.
   *
   * Parking is a decision someone makes, so it is its own action rather than a
   * field on the details form: it stamps `heldAt`, records the reason and when
   * it will be picked up, and reads back as the "On hold" state on the Plan and
   * the portfolio. Lifting clears all three. Both leave a one-line audit trail.
   */
  putProjectOnHold: (projectId: string, reason: string, pickUp: string) => Promise<string>;
  liftProjectHold: (projectId: string) => Promise<string>;
  /*
   * THE BUDGET LEDGER (0116). One invoice/commitment per line; the pool totals
   * are derived by summing them, so nothing is ever overwritten. A line's status
   * moves committed → paid; it is never duplicated. The whole array is written
   * back each time, like `stakeholders`.
   */
  addBudgetLine: (projectId: string, line: Omit<InvoiceLine, 'id'>, file?: File) => Promise<string>;
  updateBudgetLine: (
    projectId: string,
    lineId: string,
    patch: Partial<Omit<InvoiceLine, 'id'>>,
  ) => Promise<string>;
  removeBudgetLine: (projectId: string, lineId: string) => Promise<string>;
  /**
   * CORRECT A LINE'S CLASSIFICATION — change its capex/opex pool or its phase after
   * it was first set (line-correction governance). A reason is always required. Up
   * to & including approved-but-not-paid it applies at once; once the line is PAID
   * it is raised as a pending correction for an admin to approve.
   */
  correctLine: (
    projectId: string,
    lineId: string,
    field: 'pool' | 'phase' | 'milestone',
    to: string,
    reason: string,
  ) => Promise<string>;
  /**
   * SET WHAT A LINE IS BILLED AGAINST — a billable milestone, or '' for none. A
   * classification like the phase, so it is FREE while the line is not yet paid
   * (applies at once, no reason). Once the line is PAID, changing it is a governed
   * correction instead (Reclassify, which needs a reason) — this op refuses a paid
   * line and says so. Never touches the spend rollup. Returns '' or why refused.
   */
  setBilledAgainst: (projectId: string, lineId: string, milestoneId: string) => Promise<string>;
  /** Set or clear what a line's delivery is measured against — a phase, an epic, or a
   *  milestone (#483). Editable until the line is accepted (its evidence freezes then). */
  setDeliveryBasis: (projectId: string, lineId: string, basis: DeliveryBasis | null) => Promise<string>;
  /**
   * DECIDE A PAID-LINE CORRECTION — an admin/manager who did NOT raise it approves
   * it (applying the change) or rejects it (with a reason). Separation of duties:
   * the person who raised a correction may not decide it.
   */
  decideCorrection: (
    projectId: string,
    lineId: string,
    correctionId: string,
    approve: boolean,
    reason?: string,
  ) => Promise<string>;
  /*
   * THE ACCEPTANCE GATE — invoice-acceptance governance. Each moves a line one
   * step forward and records who/when; the guards refuse a move out of order, so
   * the gate holds on the write path, not just in the drawer. `payBudgetLine`
   * refuses unless the line is BOTH accepted AND approved (canMarkPaid).
   */
  receiveBudgetLine: (projectId: string, lineId: string) => Promise<string>;
  acceptBudgetLine: (projectId: string, lineId: string) => Promise<string>;
  /**
   * SEND A LINE FOR APPROVAL — invoice-approval governance. Routes an accepted line
   * to its designated approver and notifies them. Refuses until the supplier
   * invoice is attached (the upload gate), so no approval is ever sought on a line
   * with no document behind it.
   */
  requestPayApproval: (projectId: string, lineId: string) => Promise<string>;
  /**
   * APPROVE A LINE FOR PAYMENT — only the designated approver, their active cover,
   * or an admin/manager may (approvalAuthority). The accepter of the work cannot;
   * an admin may override that, or stand in for an absent approver, only with a
   * `reason`, which is recorded as a control exception.
   */
  approveBudgetLine: (projectId: string, lineId: string, reason?: string) => Promise<string>;
  payBudgetLine: (projectId: string, lineId: string) => Promise<string>;
  /**
   * Set or correct the finance payment date (`paymentValueDate`) on a PAID line —
   * the real value date from the finance system, kept distinct from `paidAt` (the
   * in-app event). Admin/manager (ledger-write) only. A change to an already-set
   * date needs a reason. The date can never predate the approval (hard block); a
   * future date is accepted (the drawer warns first). Returns '' on success, else a
   * message to show. (#481.)
   */
  setPaymentValueDate: (projectId: string, lineId: string, value: string, reason?: string) => Promise<string>;
  /** The current user's authority to approve this line right now — the one rule the
   *  drawer reads to draw the Approve control's state, the same `approveBudgetLine`
   *  enforces. Null when the board or line is gone. */
  payApprovalFor: (projectId: string, lineId: string) => ApprovalAuthority | null;
  /**
   * RAISE THE PAYMENT-APPROVAL CHAIN on an accepted line — the configurable-chain
   * path (executive-approver tier). Resolves the project's policy against the real
   * people and the line's base-currency amount, and FREEZES the ordered chain onto
   * `line.payChain` so later changes to roster or policy never re-shuffle a payment
   * already in flight. Refuses until the supplier invoice is attached, and returns
   * the first-run / mis-configured message when no usable chain is set. No-op (returns
   * '') when a chain is already frozen. Used in place of `requestPayApproval` on a
   * board whose workspace has a payment-approval chain.
   */
  raisePaymentChain: (projectId: string, lineId: string) => Promise<string>;
  /**
   * AUTHORISE THE STAGE OPEN NOW — the acting user signs their own row on the open
   * stage (`answer` 'approved' or 'rejected'). Only a named required signer of the
   * open stage may; separation of duties holds (the accepter of the work may not,
   * unless an admin overrides with a `reason`). When the final required stage is met
   * the line becomes `approved`; a refusal ends the chain. Enforced here AND in the
   * database (0182), so a hidden button is never the whole of it.
   */
  authorisePaymentStage: (projectId: string, lineId: string, answer: 'approved' | 'rejected', reason?: string) => Promise<string>;
  /** The current user's authority to authorise this line's OPEN payment stage right
   *  now — the one rule the drawer reads to draw the Authorise control's state, the
   *  same `authorisePaymentStage` enforces. Null when the board/line is gone or the
   *  line carries no chain. */
  paymentAuthorityFor: (projectId: string, lineId: string) => PaymentAuthority | null;
  /** PREVIEW the chain a payment WOULD pass before it is raised — the project's
   *  policy resolved against the real people and this line's base amount, WITHOUT
   *  freezing anything. `problem` is the first-run / mis-configured message (empty
   *  when the chain is usable). The drawer draws the stages and decides between
   *  "Send for authorisation" and the blocked first-run state from this. Null when
   *  the board or line is gone. */
  previewPaymentChain: (projectId: string, lineId: string) => { rows: PaymentChainRow[]; problem: string } | null;
  /** Nudge the people the open stage is waiting on — records the reminder on the
   *  line's history (the same audit-only channel `requestPayApproval` uses until the
   *  authorise-by-email flow lands). Refuses when the line is not mid-chain. */
  remindPaymentChain: (projectId: string, lineId: string) => Promise<string>;
  /** Attach the supplier's invoice document to a line (0116 storage pattern). */
  attachInvoiceFile: (projectId: string, lineId: string, file: File) => Promise<string>;
  /** A short-lived signed address for reading an attached invoice document. */
  budgetLineFileUrl: (projectId: string, lineId: string) => Promise<string>;
  /** Build the "Authority to pay" PDF for an approved line and return a blob URL to
   *  open it. Shared by the line drawer and the finance queue so both produce the
   *  same document. '' when the line is not approved or the page cannot be built. */
  authorityToPayUrl: (projectId: string, lineId: string) => Promise<string>;
  /*
   * THE WORKSPACE'S CURRENCY (0117) — the ISO code every amount is drawn with.
   * A workspace-wide setting, not a per-project one, so it lives here rather than
   * on a project. `loadWorkspaceCurrency` reads it on sign-in and points money.ts
   * at it; `setWorkspaceCurrency` is the admin-only change, applied at once and
   * written to the tenant row (the database refuses it to anyone but an admin,
   * and refuses any other column). See src/money.ts.
   */
  currency: string;
  loadWorkspaceCurrency: () => Promise<void>;
  setWorkspaceCurrency: (code: string) => Promise<void>;
  /*
   * THE WORKSPACE EXCHANGE RATES (0118) — base-per-foreign by ISO code, e.g.
   * { USD: 6.78 } means "US$1 = TT$6.78" on a TTD-base workspace. This is the
   * CURRENT rate a committed foreign line converts at (a paid line is frozen at
   * the rate captured on it); changing a rate re-flows open commitments. Loaded
   * alongside the currency and changed by an admin (setWorkspaceRate), written to
   * the tenant's `fx_rates` column under the same column-privilege guard.
   */
  fxRates: Record<string, number>;
  setWorkspaceRate: (code: string, rate: number | null) => Promise<void>;
  /**
   * THE WORKSPACE PAYMENT-APPROVAL CHAIN (0182) — the ordered stages a payment
   * passes before it may be paid: a workspace DEFAULT plus optional per-project
   * overrides ({ default, overrides }). `null` until it loads on sign-in, and a
   * null stored value means NO chain is set (payments blocked, never a silent
   * any-admin). Loaded alongside the currency; changed by an admin on the Payment
   * approval screen (setPaymentApproval), written to the tenant's
   * `payment_approval` column under the same column-privilege guard as fx_rates.
   */
  paymentApproval: StoredPaymentApproval | null;
  setPaymentApproval: (stored: StoredPaymentApproval) => Promise<string>;
  /**
   * THE WORKSPACE WORKING CALENDAR (0216) — the working week (which weekdays count)
   * and the holidays over it, read by the working-day engine (src/work-calendar.ts)
   * so the forecast, the idle nudge, the stale-to-do signal, progress-vs-schedule and
   * dependency cascades never assume work on a day nobody is in. Loaded alongside the
   * currency; `DEFAULT_CALENDAR` (Mon–Fri, no holidays) until it loads and whenever a
   * workspace never set one. Changed by an admin (setWorkCalendar), written to the
   * tenant's `work_calendar` column under the same column-privilege guard as fx_rates.
   */
  workCalendar: WorkCalendar;
  setWorkCalendar: (cal: WorkCalendar) => Promise<void>;
  /** Who may open one project. An empty list means everyone — see projects.ts. */
  setProjectMembers: (projectId: string, members: string[]) => Promise<void>;
  /**
   * Put somebody on ONE project.
   *
   * Two steps, and both are needed: the workspace list is who may sign in at
   * all, and the project's members are who may open that board. Somebody new
   * gets both; somebody already in the workspace gets only the second.
   */
  addToProject: (projectId: string, email: string, role: Role, person?: { first: string; last: string }) => Promise<void>;
  /**
   * Take somebody off ONE project, leaving every other alone.
   *
   * This is the thing that did not exist: removing a person removed them from
   * the workspace, so taking them off one board took them off all of them.
   */
  removeFromProject: (projectId: string, email: string) => Promise<void>;
  setPersonProfile: (email: string, patch: Profile) => Promise<void>;
  /**
   * The per-notification switches (0126). `notificationSwitches` reads the mutes
   * — the signed-in person's own, or, for an admin, a named member's. `muteKind`
   * switches one of the reader's own off or back on. `adminSetKind` is the
   * People-and-access override: an admin turns a kind on for a member and
   * optionally locks it. All proxy the backend; the caller holds the result.
   */
  notificationSwitches: (
    email?: string
  ) => Promise<{ kind: string; muted: boolean; lockedBy: string }[]>;
  /** When each person last signed in — one time per address, for the People
   *  roster (people-access A). Reads a privacy-preserving admin-only RPC (0210);
   *  an empty list for a non-admin or a backend without it, so the roster shows a
   *  dash. The caller holds the result; nothing is stored. */
  signInTimes: () => Promise<{ email: string; at: number }[]>;
  muteKind: (kind: string, muted: boolean) => Promise<string>;
  adminSetKind: (email: string, kind: string, on: boolean, lock: boolean) => Promise<string>;
  /**
   * Add somebody to the workspace.
   *
   * Returns the temporary password when one was asked for, and an empty string
   * otherwise. It is returned rather than stored because it is never stored:
   * this is the only moment it can be read, which is what the screen showing it
   * has to say out loud.
   */
  addPerson: (
    email: string,
    role: Role,
    person?: { first: string; last: string },
    options?: {
      title?: string;
      boards?: { id: string; role: ProjectRole }[];
      tempPassword?: boolean;
      /**
       * The person's workspace company role, set AT CREATION. Passed to the
       * invite function so people.company_role is written in the same server call
       * as the board grants — an External / Vendor person is a vendor from the
       * first row that exists, never a full internal member for a window.
       */
      companyRole?: CompanyRole;
      /** An assistant, listed in the Assistants tab rather than People. */
      assistant?: boolean;
      /** Finishing an approved assistant request — passed straight to the
          server, which reads the request and lets a non-admin add this one
          assistant. See requestId on accounts.invite. */
      requestId?: string;
    }
  ) => Promise<string>;
  /** Send somebody their invitation again, changing nothing about them. */
  resendInvite: (email: string) => Promise<void>;
  /**
   * Reset somebody's password — issue a fresh temporary one and force them to
   * choose again. Unlike `resendInvite` (which deliberately changes nothing),
   * this invalidates their current password: the invite path, given a temp
   * password for an account that already exists, is a RESET on the server
   * (a new temp password + `password_set_at` cleared). Returns the temporary
   * password to show the admin once, exactly like the add flow.
   */
  resetPassword: (email: string) => Promise<string>;
  /**
   * Correct the address a person signs in with — the one identity change the
   * browser cannot make itself (it re-keys the auth login AND ~30 denormalised
   * columns across ~26 tables in one server call). Admin-only, gated server-side
   * by the change-email edge function exactly as invite is. Returns '' on
   * success, or a message to show the admin when it is refused. Their roles,
   * boards and history are unchanged — only the identity moves.
   */
  changeEmail: (oldEmail: string, newEmail: string) => Promise<string>;
  /**
   * Deactivate a person, or turn them back on — 0208. Suspends sign-in while
   * KEEPING every role and board, so it is a reversible pause for offboarding or
   * a halted contract, distinct from `removePerson` (permanent). Writes
   * people.suspended_at (a moment, or null to reactivate). Admin-only; the trail
   * records both directions.
   */
  setSuspended: (email: string, suspended: boolean) => Promise<void>;
  /*
   * ── ASKING AN ADMIN TO ADD SOMEBODY — #359 ────────────────────────────────
   * Only admins invite, so everyone else files a request an admin acts on.
   */
  /** File a request that an admin add a person or an assistant to a board. */
  requestToAdd: (input: {
    boardId: string;
    kind: BoardRequestKind;
    email: string;
    displayName: string;
    role: 'viewer' | 'reviewer' | 'member';
    note?: string;
  }) => Promise<string>;
  /** An admin approves or declines. Approving a PERSON adds them outright;
      approving an ASSISTANT leaves the requester to complete it. A decline may
      carry a short reason, shown to the asker (G6); an approval may lift or
      lower the board role from what was asked, within the cap (G9). */
  decideRequest: (
    id: string,
    approved: boolean,
    reason?: string,
    grantRole?: 'viewer' | 'reviewer' | 'member'
  ) => Promise<string>;
  /** The asker takes back their own request, while it is still pending (G5). */
  withdrawRequest: (id: string) => Promise<string>;
  /** The asker clears a decided request from their own list — per browser, the
      record and the admin's log untouched (#359). */
  clearRequest: (id: string) => void;
  /**
   * SET (or change) who covers a signer while they are away — configurable
   * sign-offs R1 (0130). `delegator` is the covered person; leave it out to cover
   * yourself. `reason` is required when covering someone else (an admin act); the
   * database enforces it too. `until` is an optional end (ms), null = on until
   * turned off. Returns '' on success, or a message to show. The cover spans every
   * board the delegator is on — there is no board argument. `active` is for the
   * caller that means to turn cover ON (pass true); leave it out and an existing
   * cover KEEPS its on/off state, so changing the date or the nominee never wakes a
   * paused cover. A brand-new cover (no existing row) starts on.
   */
  setCover: (input: {
    delegate: string;
    delegator?: string;
    until?: number | null;
    reason?: string;
    active?: boolean;
  }) => Promise<string>;
  /** Turn a person's cover off. Keeps the row (active=false) so their last nominee
      is remembered when they turn it back on. `delegator` defaults to me; an admin
      may pass someone else's. Returns '' on success or a message. */
  clearCover: (delegator?: string) => Promise<string>;
  /** The asker finishes an approved assistant request: the account is minted on
      their behalf and its one-time password comes back here, on their screen,
      never the admin's. Empty string means no password came back. */
  fulfilAssistantRequest: (id: string) => Promise<string>;
  /**
   * Remember that this person has a password of their own, so they are never
   * asked again. The password itself is Supabase's business and never ours.
   */
  setMyPassword: () => Promise<void>;
  /**
   * What to say about the LAST person added, when it is not simply "done".
   *
   * Empty when an invitation went out normally. Otherwise the reason nobody was
   * emailed — most often that they already had an account and were sent a
   * sign-in link instead.
   */
  lastInviteNote: string;
  setPersonRole: (email: string, role: ProjectRole) => Promise<void>;
  /** Look at the board as somebody else would see it. Grants nothing. */
  previewAs: (email: string) => void;
  stopPreview: () => void;

  /** What somebody may do on ONE board, leaving every other alone. */
  setRoleOn: (email: string, projectId: string, role: ProjectRole) => Promise<void>;

  /** Make a team. Returns its id. */
  createGroup: (name: string) => Promise<string>;
  renameGroup: (groupId: string, name: string) => Promise<void>;
  /** Remove the team itself, and take it off every person and every board. */
  deleteGroup: (groupId: string) => Promise<void>;
  /** Put somebody in a team, or take them out. */
  setInGroup: (email: string, groupId: string, isIn: boolean) => Promise<void>;
  /** Say which teams may open one board. */
  setProjectGroups: (projectId: string, groupIds: string[]) => Promise<void>;
  /** Stop this board answering for itself, and follow their default again. */
  clearRoleOn: (email: string, projectId: string) => Promise<void>;
  removePerson: (email: string) => Promise<void>;
  signOutUser: () => void;
  resendVerification: () => Promise<void>;
  refreshVerification: () => Promise<boolean>;
}

/**
 * WHAT SOMEBODY IS TOLD WHEN THE THING THEY REPORTED IS FIXED.
 *
 * WRITTEN ONCE, HERE, rather than left to whoever moves the card. Not to save
 * typing — so that it is always the same, always warm, and never accidentally
 * curt because it was written at the end of a long day.
 *
 * IT DOES NOT PROMISE A DATE. "It will be in the next update" is the obvious
 * sentence to add and it is one this app cannot know is true; a promise nobody
 * checked is worse than no promise, because they will hold us to it.
 */
const WHEN_IT_IS_FIXED =
  'Good news — the problem you reported is fixed. Thank you for telling us about it; ' +
  'it would have been a great deal harder to find without you.';

// whenWorkBegan — the rule for stamping startedAt on the first exit from the
// queue — now lives in src/lifecycle.ts (pure, so it can be read and tested on
// its own). It is imported at the top of this file and used unchanged below.

/**
 * ONE STATUS UPDATE, COMPUTED ONCE — shared by `postStatusUpdate` (the card open
 * on its own board) and `postUpdateInline` (the card open in the My Work panel on
 * another board). Both must produce byte-for-byte the same entry and the same
 * stamped patch — the log line, the standing note, the move and its `startedAt` —
 * so a status update reads identically wherever it was posted. Returns null when
 * there is nothing to record (no words, no next step, no move).
 */
function buildStatusUpdate(
  before: Issue,
  input: { text: string; status?: IssueStatus; nextStep?: string; quiet?: boolean; overrideNote?: string },
  me: string,
  now: number,
): { entry: StatusUpdate; stamped: Partial<Issue>; moved?: IssueStatus } | null {
  const text = input.text.trim();
  const nextStep = input.nextStep?.trim() || undefined;
  // A move rides on the entry only when the picker left the status it was on.
  const moved = input.status && input.status !== before.status ? input.status : undefined;
  if (!text && !nextStep && !moved) return null;
  // A Note is recorded quietly; a move always tells the card's people, so the
  // quiet flag is dropped on one (the trigger would otherwise skip the call-out).
  const markQuiet = input.quiet === true && !moved;
  const entry: StatusUpdate = {
    id: uid(),
    at: now,
    by: me,
    text,
    ...(moved ? { status: moved } : {}),
    ...(nextStep ? { nextStep } : {}),
    ...(markQuiet ? { quiet: true } : {}),
  };
  // When a deliberate start went through the SOFT gate while a predecessor was
  // still open, the person's entry already carries the move; the override is
  // recorded as a separate `auto` line (no status of its own, so the move is not
  // drawn twice) saying it was started anyway and by whom. `auto`, so the fan-out
  // trigger raises no call-out — the same contract as the Board and modal paths.
  const overrideLine: StatusUpdate | null =
    moved && input.overrideNote
      ? { id: uid(), at: now, by: me, text: input.overrideNote, auto: true }
      : null;
  const standsLog = overrideLine
    ? [...(before.standsLog ?? []), entry, overrideLine]
    : [...(before.standsLog ?? []), entry];
  const began = moved ? whenWorkBegan(before, moved) : {};
  const stamped: Partial<Issue> = {
    standsLog,
    ...(text ? { stands: text } : {}),
    ...(moved ? { status: moved } : {}),
    ...began,
    updatedAt: now,
  };
  return { entry, stamped, moved };
}

/**
 * THE BOARD AS IT STANDS, for reading a sign-off rule against — #332.
 *
 * Gathered in one place rather than at each call site, so "who signs" can
 * never be answered from a different set of facts than "is this rule usable".
 * The owner is assembled from the three meta fields the panel has always used.
 */
function theWorld(s: AppState): TheWorld {
  return {
    owner: {
      name: (s.meta.projectOwnerName ?? '').trim(),
      position: (s.meta.projectOwnerPosition ?? '').trim(),
      email: (s.meta.projectOwnerEmail ?? '').trim(),
    },
    access: s.access,
    roles: s.roles,
    projectRoles: s.projectRoles,
    profiles: s.profiles,
    projectId: s.projectId,
  };
}

/**
 * THE PERSON RAISING A SIGN-OFF, AS THE DEVELOPER SLOT — the redesign.
 *
 * On a slots board the developer signs by raising: whoever is signed in fills
 * the developer row and it is written already-attested. Their position on the
 * sheet is the slot they fill — "Developer" — not their workspace rank, because
 * it is the developer line on the change request they are putting their name to.
 * Undefined when nobody is signed in, which a preview handles by drawing no
 * developer row rather than an empty one.
 */
function theDeveloper(s: AppState): Signatory | undefined {
  const me = (s.user?.email ?? '').trim().toLowerCase();
  if (!me) return undefined;
  return { name: displayName(me, s.profiles), position: 'Developer', email: me };
}

/**
 * MINT ONE ROW'S SIGNING LINK AND POST IT — the token half of #216, in one place.
 *
 * Pulled out of `issueSigningLink` so the two callers cannot drift: the "Send a
 * link" button, which re-sends on demand, and a release sign-off, which now
 * sends each approver their link the moment it is raised. It writes the hash
 * onto the row FIRST, so the link works whatever the mail does; a failed post is
 * reported back, never thrown, exactly as the manual path always behaved.
 *
 * `askedAgainst` freezes what the signatory is answering about — a document's
 * file fingerprint, or '' for a sprint or release, which have no file. A refused
 * write (only a manager may issue one) throws, and the caller decides what that
 * means: fatal for the button, shrugged off for the auto-send.
 */
async function mintSigningLink(
  projectId: string,
  rowId: string,
  askedAgainst: string
): Promise<{ url: string; emailed: boolean; to: string; why: string }> {
  const raw = crypto.getRandomValues(new Uint8Array(32));
  /* base64url, so the whole thing survives being pasted into an email, a
     calendar entry and whatever else it passes through on the way. */
  const token = btoa(String.fromCharCode(...raw))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  const hash = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  const now = Date.now();
  await backend.patch(`${inProject(projectId).signoffs}/${rowId}`, {
    linkTokenHash: hash,
    linkIssuedAt: now,
    linkExpiresAt: now + FOR_A_FORTNIGHT,
    askedAgainst,
  });

  const url = `${window.location.origin}/sign/${token}`;
  let emailed = false;
  let to = '';
  let why = '';
  try {
    const r = (await backend.sendSigningLink?.(rowId, token)) ?? {
      emailed: false,
      why: 'This backend cannot send email.',
    };
    emailed = r.emailed;
    to = String(r.to ?? '');
    why = String(r.why ?? '');
  } catch (e) {
    why = (e as { message?: string })?.message || 'FlightDeck could not send it just now.';
  }
  return { url, emailed, to, why };
}

export const useApp = create<AppState>()((set, get) => ({
  user: null,
  authReady: false,
  emailVerified: false,
  denied: false,
  stale: false,
  suspended: null,
  loaded: false,
  cardsSeen: readCardsSeen(),
  lastInviteNote: '',
  commentError: '',
  role: 'viewer',
  shownRole: 'viewer',
  access: EMPTY_ACCESS,
  profiles: {},
  profilesKnown: false,
  profilesOk: false,
  reporters: {},
  tellingWhyClosed: '',
  roles: {},
  projectRoles: {},
  groups: [],
  groupsOf: {},
  viewing: null,
  legacyRoles: {},
  audit: [],
  auditProblem: '',
  auditOrigin: [],
  sessions: [],
  sessionsProblem: '',
  projectId: readProjectChoice(),
  projects: [],
  archivedProjects: [],
  projectsKnown: false,
  portfolio: [],
  accountability: { people: [], unassigned: null },
  portfolioIssues: new Map(),
  inlineCardBoard: null,
  inlineCardId: null,
  inlineCard: null,
  inlineCardLoading: false,
  inlineCardError: '',
  portfolioPhases: new Map(),
  portfolioAreas: new Map(),
  portfolioPulses: new Map(),
  portfolioMilestones: new Map(),
  milestonePulses: new Map(),
  portfolioClosures: [],
  portfolioLessons: [],
  portfolioLoading: false,
  flowMoves: null,
  flowMovesLoading: false,
  insightsScope: readInsightsView().scope,
  insightsWindow: readInsightsView().window,
  portfolioViews: [],
  portfolioDefault: '',

  meta: { name: 'FlightDeck', key: 'FD', nextNumber: 1, repoUrl: '' },
  members: [],
  issues: [],
  builds: [],
  signoffs: [],
  dismissedSignoffs: [],
  boardRequests: [],
  signoffCover: [],
  notifications: [],
  reactions: [],
  requestsCleared: readRequestsCleared(),
  documents: [],
  moscowNudge: [],
  assistDismiss: [],
  sprints: [],
  areas: [],
  pendingAreaIds: [],
  labels: [],
  labelsProblem: '',
  /* ⚠️ THE FOUR ORIGINALS UNTIL THE BOARD'S OWN ARRIVE, never an empty list.
     Empty would draw a picker with nothing in it and a card with no type at
     all, for the half-second before the network answers — which reads as work
     having lost its classification. */
  issueTypes: ORIGINAL_TYPES,
  areasProblem: '',
  phases: [],
  phasesProblem: '',
  milestones: [],
  decisions: [],
  decisionsLoaded: false,
  risks: [],
  assumptions: [],
  registerIssues: [],
  dependencies: [],
  raidLoaded: false,
  seedRiskLesson: null,
  lessons: [],
  phaseSteps: [],
  phaseEvents: [],

  view: readLanding(),
  focus: null,
  focusWhy: '',
  groupBy: 'status',
  viewId: DEFAULT_VIEW,
  showCounts: true,
  savedViews: [],
  lastSeen: readSeen(),
  filters: readFilters(),
  search: '',
  selectedId: null,
  creating: false,
  openEditField: null,
  saveProblem: null,
  flash: null,
  coach: null,
  issueEditing: false,
  createStatus: null,
  portfolioReturn: null,
  handoffRequestBoard: null,
  budgetEnterEdit: false,
  setupDismissed: false,
  setupForced: false,
  setupAll: false,

  myworkTab: 'todo',
  setMyworkTab: (myworkTab) => set({ myworkTab }),

  // Choosing a view yourself always clears a focus. Otherwise the backlog would
  // still be narrowed the next time you opened it, with no memory of why.
  cameFrom: null,
  revealId: null,
  revealSeq: 0,
  changelogOpen: false,
  // The default until the workspace's own currency loads on sign-in (0117).
  currency: DEFAULT_CURRENCY,
  fxRates: {},
  paymentApproval: null,
  workCalendar: DEFAULT_CALENDAR,

  setChangelogOpen: (open) => set({ changelogOpen: open }),

  drillTo: (view, reveal) =>
    set((s) => {
      remember('saw', view);
      /* Not written as the remembered screen, for the same reason showInBacklog
         is not: a filtered list you were SENT to is not where a refresh should
         put you back. */
      return {
        view,
        /* Drilling to the screen you are ALREADY on (revealing a section in
           place — e.g. the Plan's "Set scope in Project details" opens the
           About editor without leaving the Plan) is not a journey, so it must
           not plant a "← Back to Plan" trail back to this same screen. Keep the
           real origin you arrived from instead. */
        cameFrom: view === s.view ? s.cameFrom : s.view,
        revealId: reveal ?? null,
        // Count every drill, so re-revealing the same control re-flashes it (L99).
        revealSeq: s.revealSeq + 1,
        // A "Show me" from the What's new dialog drills AND closes it, so the
        // person lands on the highlighted control, not back on the dialog.
        changelogOpen: false,
        selectedId: null,
        creating: false,
        focus: null,
        focusWhy: '',
      };
    }),

  revealLink: (link) => {
    // One routing table for a decision/lesson Reveal chip. A card opens in the
    // modal over wherever you are (openIssue); a document is revealed on the
    // Documents screen (it honours revealId, opening the doc); a budget line and
    // scope are revealed on the Plan; a sign-off lands on the Sign-offs screen.
    const { drillTo, openIssue } = get();
    switch (link.kind) {
      case 'issue':
        openIssue(link.id);
        break;
      case 'change-request':
      case 'aar':
      case 'document':
        drillTo('documents', link.id);
        break;
      case 'lesson':
        // A lesson Reveal lands on the Lessons register and flashes the card —
        // e.g. from the after-action report's "captured during this project" list.
        drillTo('lessons', link.id);
        break;
      case 'budget':
        drillTo('plan', `budget:${link.id}`);
        break;
      case 'scope':
        drillTo('plan', 'about');
        break;
      case 'signoff':
        drillTo('approvals');
        break;
      default:
        break;
    }
  },

  setView: (view) => {
    // Which screen they were on, for the trail a report carries — see src/trail.ts.
    remember('saw', view);
    /* Written here rather than in a subscriber, so it records a screen somebody
       CHOSE. showInBacklog below deliberately does not: being sent to the
       backlog to look at three particular issues is not a place to be returned
       to on the next refresh, because the reason for being there is gone. */
    writeViewChoice(view);
    /* Choosing a screen ends the drill-down, so the way back goes with it. */
    set({ view, cameFrom: null, revealId: null, selectedId: null, creating: false, focus: null, focusWhy: '', coach: null });
  },

  /* A DRILL-DOWN AS MUCH AS A FILTERED BAR IS. This route — "show me the six
     items that blocker is holding" — landed on the backlog with no way back,
     because only setFilters-then-drillTo recorded where you came from and this
     sets the view itself. Same journey, same offer to return. */
  showInBacklog: (ids, why) =>
    set((s) => {
      /* A focus set is an EXACT list of cards, so any filter pill left on from an
         earlier drill would contradict it — and "Show the whole backlog" would
         then reveal that filtered subset rather than the whole (the interaction-
         witness caught both, 24 Sep 2026). Clear the filters here, keeping only
         the sort (which hides nothing), so the focus reads cleanly and clearing
         it returns the whole backlog. */
      const filters = { ...DEFAULT_FILTERS, sort: s.filters.sort };
      writeFilters(filters);
      return { view: 'backlog', cameFrom: s.view === 'backlog' ? s.cameFrom : s.view, revealId: null, focus: ids, focusWhy: why, selectedId: null, creating: false, filters };
    }),
  clearFocus: () => set({ focus: null, focusWhy: '' }),
  setGroupBy: (groupBy) => set({ groupBy }),
  /**
   * ── PICKING A VIEW, AND WHAT HAPPENS TO THE FILTERS — #301 ───────────────
   *
   * A SAVED VIEW LOADS ITS FILTERS INTO THE BAR, rather than narrowing the list
   * from somewhere the reader cannot see. The pills then say exactly what is
   * being applied and every one of them can be taken off, which is the whole
   * point of saving one — Vinnie, 30 August 2026: *"it gives them the
   * flexibility to customize the way they work."* A view that filtered
   * invisibly would be the old bar's defect wearing a nicer label.
   *
   * A BUILT-IN LEAVES THEM ALONE, because the four of them ask a question the
   * filters cannot ("is it mine", "is it blocked") and the two are meant to
   * combine: Open plus your two areas is the ordinary thing to want, and
   * emptying the bar every time somebody changed tab would fight them for it.
   */
  setViewId: (viewId) =>
    set((s) => {
      const saved = s.savedViews.find((v) => v.id === viewId);
      if (!saved) return { viewId };
      writeFilters(saved.filters);
      return { viewId, filters: saved.filters };
    }),
  setShowCounts: (showCounts) => set({ showCounts }),
  /*
   * ── SAVING A VIEW — #301 ────────────────────────────────────────────────
   *
   * Against the person, so it follows them to another machine, and against the
   * board, so a view naming an area does not appear on a board that has none.
   *
   * WRITTEN THROUGH THE PROFILE rather than a table of its own. It is a list
   * belonging to one person, read only by them, never joined against and never
   * counted — which is a column, not an entity. A table would have brought a
   * policy, a migration for the policy, and a second place for "who may see
   * this" to be got wrong.
   */
  saveView: async (name) => {
    if (sampleBlocks()) return;
    const me = get().user?.email ?? '';
    const board = get().projectId;
    if (!me || !board) return;
    const view: SavedView = {
      id: `v${Date.now().toString(36)}`,
      name: name.slice(0, 40),
      board,
      filters: get().filters,
    };
    const next = [...get().savedViews, view];
    set({ savedViews: next, viewId: view.id });
    await backend.patch(`${C.profiles}/${me}`, { savedViews: next });
  },
  forgetView: async (id) => {
    const me = get().user?.email ?? '';
    if (!me) return;
    const next = get().savedViews.filter((v) => v.id !== id);
    /* Standing on a view that is being removed would leave the screen pointed
       at nothing, which draws an empty backlog and looks like an empty board. */
    set({ savedViews: next, viewId: get().viewId === id ? DEFAULT_VIEW : get().viewId });
    await backend.patch(`${C.profiles}/${me}`, { savedViews: next });
  },

  /*
   * ── THE PORTFOLIO'S SAVED VIEWS ──────────────────────────────────────────
   *
   * The same shape as saveView/forgetView above, for the roll-up's own toolbar,
   * written through the person's profile so a view follows them between
   * machines. The captured config is stored whole; the ACTIVE selection stays
   * in the roll-up, so nothing here touches which tab is on screen.
   */
  savePortfolioView: async (name, config) => {
    const me = get().user?.email ?? '';
    if (!me) return '';
    const view: PortfolioSavedView = { id: `pv${Date.now().toString(36)}`, name: name.slice(0, 40), ...config };
    const next = [...get().portfolioViews, view];
    set({ portfolioViews: next });
    await backend.patch(`${C.profiles}/${me}`, { portfolioViews: next });
    return view.id;
  },
  updatePortfolioView: async (id, config) => {
    const me = get().user?.email ?? '';
    if (!me) return;
    const next = get().portfolioViews.map((v) => (v.id === id ? { ...v, ...config } : v));
    set({ portfolioViews: next });
    await backend.patch(`${C.profiles}/${me}`, { portfolioViews: next });
  },
  renamePortfolioView: async (id, name) => {
    const me = get().user?.email ?? '';
    if (!me) return;
    const next = get().portfolioViews.map((v) => (v.id === id ? { ...v, name: name.slice(0, 40) } : v));
    set({ portfolioViews: next });
    await backend.patch(`${C.profiles}/${me}`, { portfolioViews: next });
  },
  forgetPortfolioView: async (id) => {
    const me = get().user?.email ?? '';
    if (!me) return;
    const next = get().portfolioViews.filter((v) => v.id !== id);
    /* If the removed view was the pinned default, drop the pin so the screen
       falls back to a built-in rather than opening on a view that is gone. */
    const def = get().portfolioDefault === id ? '' : get().portfolioDefault;
    set({ portfolioViews: next, portfolioDefault: def });
    await backend.patch(`${C.profiles}/${me}`, { portfolioViews: next, portfolioDefault: def || null });
  },
  setPortfolioDefault: async (id) => {
    const me = get().user?.email ?? '';
    if (!me) return;
    set({ portfolioDefault: id });
    await backend.patch(`${C.profiles}/${me}`, { portfolioDefault: id });
  },
  setLastSeen: (lastSeen) => set({ lastSeen }),

  setFilters: (patch) =>
    set((s) => {
      const filters = { ...s.filters, ...patch };
      writeFilters(filters);
      return { filters };
    }),

  resetFilters: () => {
    writeFilters(DEFAULT_FILTERS);
    set({ filters: DEFAULT_FILTERS });
  },
  setSearch: (search) => set({ search }),
  /*
   * Opening a card is what "I have read this" means, so it is recorded here
   * rather than anywhere the card is drawn — every route in goes through this
   * one function, and a marker cleared in only some of them is worse than none.
   *
   * Held in state as well as in storage so the stripe goes the moment it is
   * opened. Reading localStorage on every render would work and would also make
   * every card ask the disk a question it already knows the answer to.
   */
  startingBoard: false,
  setStartingBoard: (startingBoard) => set({ startingBoard }),
  openIssue: (selectedId, editField) =>
    // Clear any Portfolio return here: only openFull re-arms it (right after this
    // call), so a card opened any other way never carries a stale "← Portfolio".
    // `editField` (from the card-face summary nudge) opens the card straight into
    // editing that field; a plain open passes nothing and lands in read mode.
    set({ selectedId, creating: false, portfolioReturn: null, openEditField: editField ?? null, cardsSeen: markCardSeen(selectedId, Date.now()) }),
  closeModal: () => set({ selectedId: null, creating: false, createStatus: null, openEditField: null }),
  clearOpenEditField: () => set({ openEditField: null }),
  clearSaveProblem: () => set({ saveProblem: null }),
  showFlash: (text) => set({ flash: text || null }),
  showCoach: (text) => set({ coach: text || null }),
  setIssueEditing: (v) => set({ issueEditing: v }),
  setPortfolioReturn: (portfolioReturn) => set({ portfolioReturn }),
  setHandoffRequest: (handoffRequestBoard) => set({ handoffRequestBoard }),
  setBudgetEnterEdit: (budgetEnterEdit) => set({ budgetEnterEdit }),
  /**
   * Opens the new-issue form, optionally starting in a chosen column.
   *
   * The status is held on the store rather than passed to the modal, because the
   * modal is mounted once at the top of the tree and reads what it needs — there
   * is nothing to pass it through. Cleared by closeModal, or a `+` in one column
   * would still be in force the next time somebody opened the form from the top
   * bar.
   */
  dismissSetup: () => set({ setupDismissed: true, setupForced: false, setupAll: false }),
  /** Run the questions again, from Settings. */
  openSetup: () => set({ setupDismissed: false, setupForced: true, setupAll: true }),
  /** Start them from the board's offer — only what has not been answered. */
  startSetup: () => set({ setupDismissed: false, setupForced: true, setupAll: false }),

  startCreate: (status) => set({ creating: true, selectedId: null, createStatus: status ?? null }),

  /**
   * THE NUMBER COMES FROM THE BOARD, NOT FROM THIS BROWSER'S IDEA OF IT.
   *
   * It used to read `meta.nextNumber` out of local state, write the issue, and
   * then patch the board to move the counter on. Two things wrong with that, and
   * the second is why a reviewer could not raise an issue properly:
   *
   *   Two people pressing at once both read the same number and both write it —
   *   two issues wearing one reference. The counter needs to be taken, not read.
   *
   *   Patching the board needs a MANAGER. Raising an issue needs a reviewer. So
   *   an ordinary member's issue was written and the counter's advance refused,
   *   and the next issue took the same number. Silently, because both writes
   *   were fired and forgotten.
   *
   * Asking for the number is now one call that does both halves and checks the
   * right actually required — see Backend.nextNumber.
   */
  importIssues: async (rows, scheme, onProgress) => {
    let made = 0;
    let failed = 0;
    let why = '';

    /*
     * EIGHT AT A TIME, NOT ALL SEVENTY-EIGHT.
     *
     * Firing every write at once is how a hundred-row import gets rate-limited
     * half way through and leaves a board holding some of somebody's backlog.
     * allSettled rather than all, because one refused row must not throw away
     * the seventy-seven that went in — the person is told the count either way.
     */
    for (let i = 0; i < rows.length; i += 8) {
      const batch = rows.slice(i, i + 8);
      const done = await Promise.allSettled(
        batch.map((r) => {
          const id = uid();
          return backend.set(issuePath(id), {
            ...r.draft,
            id,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            comments: [],
          } as Issue);
        }),
      );
      for (const d of done) {
        if (d.status === 'fulfilled') made += 1;
        else { failed += 1; why ||= (d.reason as BackendError)?.message || String(d.reason); }
      }
      // Counts everything ATTEMPTED, not everything that worked — a bar that
      // stalls on a refused row reads as the import having hung.
      onProgress?.(made + failed, rows.length);
    }

    /*
     * THE BOARD TAKES ON THEIR NUMBERING LAST, and only if something arrived.
     * Written after the cards rather than before so a run that is refused
     * outright does not leave a board numbering LH080 with no LH-anything on it.
     */
    if (made > 0) {
      get().setMeta({
        refPrefix: scheme.prefix,
        refWidth: scheme.width,
        nextNumber: scheme.next,
      });
      /*
       * ONE LINE FOR THE IMPORT, not one per card — #305. Seventy-eight cards
       * arriving at once is one act by one person, and writing it seventy-eight
       * times would bury the board's own history under the day it was filled.
       * The same argument as describeBulk, which exists for exactly this.
       *
       * The refused rows are named in the count because "made 70" reads as a
       * success when 8 were turned away, and the person is already being told
       * both on screen.
       */
      await get().recordAudit(
        'project',
        `imported ${made} card${made === 1 ? '' : 's'}${failed ? `, and ${failed} were refused` : ''}`
      );
    }
    return { made, failed, why };
  },

  holdIssue: (id, reason) => {
    const issue = get().issues.find((i) => i.id === id);
    if (!issue || issue.heldAt != null) return; // already held, or gone
    // A real timestamp so the badge can age ("on hold 3 days") and resume can measure
    // how long it sat; the seam turns the millisecond count into timestamptz. Mirrors
    // putProjectOnHold, but a card's hold rides through updateIssue like any field.
    get().updateIssue(id, { heldAt: Date.now(), holdReason: (reason ?? '').trim() });
  },

  resumeIssue: (id) => {
    const issue = get().issues.find((i) => i.id === id);
    if (!issue || issue.heldAt == null) return; // not held
    // The deadline paused in real time, so it resumes with the same slack: move the
    // dates out by the CALENDAR days the card sat held. daysBetween counts both ends,
    // so the elapsed span is one less; a hold lifted the same day moves nothing.
    const elapsed = Math.max(0, daysBetween(today(issue.heldAt), today()) - 1);
    const patch: Partial<Issue> = { heldAt: null, holdReason: '' };
    if (elapsed > 0) {
      if (issue.startsOn) patch.startsOn = addDays(issue.startsOn, elapsed);
      if (issue.dueDate) patch.dueDate = addDays(issue.dueDate, elapsed);
    }
    // Through updateIssue so a pushed due date cascades to dependents (and, once the
    // cascade-notify trigger lands, tells them) exactly as a hand-moved date would.
    get().updateIssue(id, patch);
  },

  createIssue: async (draft) => {
    set({ creating: false });
    if (sampleBlocks()) return false;
    try {
      const number = await backend.nextNumber(boardPath());
      const { meta } = get();
      const issue: Issue = {
        ...draft,
        /* A uuid, not a short id, because Postgres keys issues on one and reads
           them back as one — the app's own short ids are hashed on the way in
           (see idFrom in backend/paths.ts) and come back as a DIFFERENT uuid.
           That drift is invisible for most fields, but the release-priority
           nudge (#6) is keyed by this id: seeded here with the short id, it
           stopped matching the card the moment the realtime echo replaced it
           with the persisted uuid, so the nudge vanished ~a second after a card
           was made straight into a phase. A uuid passes through unchanged, so
           the id the nudge holds is the id the card keeps. Same reason as the
           board-request id below. */
        id: crypto.randomUUID(),
        // Most boards number with '#'. One that arrived with its own scheme
        // carries on in it — see ProjectMeta.refPrefix. A bug carries the 'B'
        // token the same as an imported or renumbered one (refFor / isBugRef),
        // so a card raised in the app numbers 'FDD-B4', not 'FDD-4'. "Bug" is the
        // type's MEANING ('fixing'), not the literal slug, so a board that renamed
        // Bug or added its own fixing-type still gets the B — the same test that
        // draws the bug mark (meansOf / meansMark).
        ref: refFor({ prefix: meta.refPrefix || '#', width: meta.refWidth ?? 0, next: 0 }, number, meansOf(draft.kind, get().issueTypes) === 'fixing'),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        comments: [],
      };
      await backend.set(issuePath(issue.id), issue);
      /*
       * SHOW IT NOW, don't wait for the round trip. The realtime watcher replaces
       * `issues` with the authoritative set a beat later — this same row, keyed by
       * id, so no duplicate. Without this, a card made straight into a phase is
       * only seen once the echo arrives, and the release-priority nudge set just
       * below — keyed to this id — lands on a row that does not exist yet and is
       * lost by the time the echo mounts it. Mirrors draftDocument's optimistic
       * insert, which fixed the same race for a freshly-opened charter. #6.
       */
      set((s) => ({ issues: [issue, ...s.issues.filter((i) => i.id !== issue.id)] }));
      /* THE RELEASE-PRIORITY NUDGE — #6. A card made straight into a phase (the
         Plan's own add-a-card) is being scoped as it is born, and starts with no
         level, so nudge it the same as one pulled in. */
      if (issue.phase && !issue.moscow) {
        set({ moscowNudge: [...new Set([...get().moscowNudge, issue.id])] });
      }
      return true;
    } catch {
      // Somebody typed this and pressed a button. Losing it without a word is
      // the one outcome worth refusing — so signal the miss to the caller (the
      // quick-add bar keeps the line and says it could not be added). This used
      // to set `commentError`, which only ever renders inside an open card's
      // feed, so the quick-add failure was invisible AND it later surfaced a
      // stray "Not posted" on the next card opened. L148.
      return false;
    }
  },

  /**
   * A REPORT WRITTEN ONTO A BOARD THAT IS NOT THE ONE YOU ARE LOOKING AT.
   *
   * Everything else here writes to the open project, through `issueRef`. This
   * cannot: the whole point is that somebody in the Sandbox says what they
   * found and it lands on FlightDeck's own board, without them switching
   * project, finding the right one, or knowing it exists.
   *
   * IN A TRANSACTION, WHICH THE REST OF THE APP DOES NOT DO. Taking the next
   * number is read-then-write, and two people pressing at once both read the
   * same number and both write it — two issues wearing one reference. Elsewhere
   * that is unlikely, because one person is working one board. Here it is the
   * expected case: a group of testers is precisely who this is for, and they
   * are all filing onto the same board at the same time. The transaction
   * retries on collision, so the second press waits and takes the next one.
   *
   * AWAITED, NOT FIRED OFF. `void` is right for a board change somebody can see
   * happen; it is wrong for something typed into a box that then disappears. If
   * this cannot be written the person has to be told while their words are
   * still on screen.
   */
  sendFeedback: async (draft, ctx) => {
    const me = get().user?.email ?? '';
    const issue = feedbackIssue(draft, ctx, displayName(me, get().profiles), get().issueTypes);

    /*
     * A REPORT FROM SOMEBODY ELSE'S WORKSPACE GOES THROUGH A SERVER.
     *
     * Everything below writes the issue where the reporter is standing, which
     * was right when there was one workspace and is wrong now: a tester in
     * their own company would file a fault onto their own board, where nobody
     * here would ever read it. The rules will not let a browser write into our
     * workspace — that is the property being sold — so the crossing is done by
     * the one thing allowed to do it.
     *
     * Kept as a branch rather than a replacement because the Firebase build
     * still has one workspace and no server, and there it is still correct to
     * write it here.
     */
    if (backend.reportFeedback) {
      return backend.reportFeedback({
        title: issue.title,
        // Optional on an issue; always a string over the wire, so the
        // server never has to decide what a missing one means.
        summary: issue.summary ?? '',
        description: `${issue.description}${whatLedToIt()}`,
        kind: issue.kind,
        priority: issue.priority,
      });
    }

    const board = `${C.projects}/${FEEDBACK_PROJECT_ID}`;
    const id = uid();
    // The number and the board's own advance in one go — see Backend.nextNumber.
    const number = await backend.nextNumber(board);
    await backend.set(`${inProject(FEEDBACK_PROJECT_ID).issues}/${id}`, {
      ...issue,
      id,
      ref: `#${number}`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      comments: [],
    });
    return `#${number}`;
  },

  /**
   * WHO SENT THIS CARD IN, cached once per issue.
   *
   * Asked as a card opens, and remembered — including the answer "nobody",
   * which is the common one. Without caching the miss, every card on every
   * board would ask again on every open, forever, to be told the same thing.
   */
  loadReporter: async (issue) => {
    if (issue in get().reporters) return;
    if (!backend.whoReported) return;
    const who = await backend.whoReported(issue).catch(() => '');
    set((s) => ({ reporters: { ...s.reporters, [issue]: who } }));
  },

  /**
   * ANSWER THE PERSON WHO SENT IT — deliberately not `addComment`.
   *
   * The two write to the same place and look almost the same on screen, and
   * that is exactly why they are separate functions with separate names. One
   * is a note to each other. The other leaves the building.
   *
   * AWAITED, like sendFeedback and for the same reason: somebody is watching a
   * box they typed into, and a reply that failed silently is worse than one
   * that never sent.
   */
  answerReport: async (issue, text, about) => {
    if (!backend.answerReport) throw new Error('Replying is not set up on this copy.');
    const said = await backend.answerReport(issue, text, about);
    /*
     * SHOWN ON THE CARD, NOT SAVED AGAIN. `addComment` would write a second
     * row — the server already wrote the real one, which is the only one marked
     * as the reporter's to see. This is that same row appearing locally so the
     * answer lands where it was typed rather than after a reload, and the
     * watcher replaces it with the real one moments later.
     */
    const me = get().user?.email ?? '';
    const shown: Comment = { id: uid(), author: me, text, createdAt: Date.now(), toReporter: true };
    set((s) => ({
      issues: s.issues.map((i) =>
        i.id === issue ? { ...i, comments: [...(i.comments ?? []), shown] } : i
      ),
    }));
    return said;
  },

  postStatusUpdate: async (id, input) => {
    if (sampleBlocks()) return;
    const before = get().issues.find((i) => i.id === id);
    if (!before) return;
    const me = get().user?.email ?? '';
    // The entry, the standing note, the move and its started-stamp — computed by
    // the one helper the My Work panel also uses, so a status update is the same
    // whether it was posted on the card's board or from the cross-board panel.
    const built = buildStatusUpdate(before, input, me, Date.now());
    if (!built) return; // no words, no next step, no move — nothing to record
    const { stamped, moved } = built;
    // Optimistic, and reverted whole if the write is refused — the same contract
    // as updateIssue, because somebody is watching the box they typed into.
    set({ issues: get().issues.map((i) => (i.id === id ? { ...i, ...stamped } : i)), saveProblem: null });
    try {
      await backend.patch(issuePath(id), stamped);
    } catch (e) {
      set({ issues: get().issues.map((i) => (i.id === id ? before : i)) });
      const why = e instanceof Error ? e.message : String(e);
      const which = before.ref ?? 'the card';
      remember('broke', `a status update on ${which} was not saved — ${why}`);
      set({ saveProblem: `Your status update on ${which} was not saved. ${why}` });
      throw e;
    }
    // A move carried on the update tells the reporter, exactly as dragging or
    // editing the card would — the record of the move is the same either way.
    if (moved) void get().tellReporter(id, moved);
  },

  reassignEpic: (from, to) => {
    // One edit per affected card, through updateIssue, so each card records the
    // epic change in its own history (a bulk rewrite would leave no trail). Trim
    // the target the way the card field does; '' means "No epic".
    const changes = epicReassign(get().issues, from, to.trim());
    for (const ch of changes) get().updateIssue(ch.id, { epic: ch.epic });
  },

  updateIssue: (id, patch) => {
    if (sampleBlocks()) return;
    const before = get().issues.find((i) => i.id === id);
    const entries = before ? describeChange(before, patch, get().user?.email ?? 'someone') : [];
    /*
     * THE STRUCTURED HALF, on the edit path as well as the drag path. These are
     * two ways of doing one thing, and recording history on only one of them
     * gives a burndown that is right for teams who drag and wrong for teams who
     * edit — which is worse than no burndown, because it looks like a chart.
     * The same omission stopped the closing prompt firing earlier today.
     */
    if (patch.status && before && before.status !== patch.status) {
      for (const e of entries) {
        if (e.what.includes('status')) {
          e.fromStatus = before.status;
          e.toStatus = patch.status;
        }
      }
    }
    /*
     * THE SAME, FOR SIZE — see migration 0020. Sizes have numbers behind them
     * now, so re-sizing a card changes what every past day of a sprint was
     * worth. Nothing reads these yet; they are recorded from today so that the
     * chart which eventually needs them has a year of history to read rather
     * than a year of silence.
     *
     * `!== undefined` RATHER THAN TRUTHY. Clearing a size sends an empty string,
     * and that is the change most worth recording: work that quietly left every
     * points total on the board.
     */
    if (patch.size !== undefined && before && before.size !== patch.size) {
      for (const e of entries) {
        if (e.what.includes('size')) {
          e.fromSize = before.size ?? '';
          e.toSize = patch.size;
        }
      }
    }
    /*
     * ── THE CARD CHANGES FIRST ───────────────────────────────────────────────
     *
     * The same fix `moveIssue` already carries, and the note there says why it
     * was needed: writing and then waiting for the database to echo the change
     * back through the listener makes the app feel slow. Dragging got it.
     * Editing and pressing Save did not, and it is the worse of the two to
     * leave out — a drag at least moves under your hand while it waits.
     *
     * Vinnie, 23 August 2026: *"when I change the difficulty in 231 it takes a
     * while to be reflected, I have to open it several times for me to see the
     * change... Changes to any card or screen is supposed to be reflected
     * immediately, with no delay."* Measured: the write itself took 188ms, and
     * the re-read of every card on the board that followed it took 2.5 seconds.
     * Nothing was slow to SAVE. The screen was waiting to be told what it
     * already knew.
     *
     * Put back exactly as it was if the write is refused, so a rule saying no
     * is a change that visibly reverts rather than one that quietly stuck on
     * screen and nowhere else.
     */
    /*
     * A STATUS CHANGE WRITES ITS OWN LOG LINE (0138). Editing the card to a new
     * column is a move with no words — the status log would be silent about it
     * unless the record is written here. An `auto` line carries the move so the
     * timeline reads "Moved to In Review" without anyone narrating it; the fan-out
     * trigger skips auto lines, so a bare move raises no call-out (the reporter is
     * already told below). Skipped when the caller already carries its own log —
     * `postStatusUpdate` writes the narrated entry itself.
     */
    const statusMoved = !!(patch.status && before && before.status !== patch.status);
    const autoLog =
      statusMoved && patch.standsLog === undefined
        ? [
            ...(before!.standsLog ?? []),
            { id: uid(), at: Date.now(), by: get().user?.email ?? '', text: '', status: patch.status!, auto: true } as StatusUpdate,
          ]
        : undefined;
    const stamped = {
      ...patch,
      updatedAt: Date.now(),
      ...(patch.status ? whenWorkBegan(before, patch.status) : {}),
      ...(autoLog ? { standsLog: autoLog } : {}),
    };
    // Clear any earlier failure the moment a new edit is attempted — a retry
    // that is still in flight should not sit under the banner from the last one.
    set({ issues: get().issues.map((i) => (i.id === id ? { ...i, ...stamped } : i)), saveProblem: null });
    // Mark the card pending at the version we just set, so a lagged read-back
    // cannot revert it before the server catches up (src/issue-reconcile.ts). The
    // stamp matches the optimistic row's updatedAt the watcher compares against.
    pendingCardWrites.set(id, stamped.updatedAt);

    void backend.patch(issuePath(id), stamped).catch((e: unknown) => {
      /*
       * ── A SAVE THAT DID NOT SAVE MUST SAY SO ──────────────────────────────
       *
       * The write is optimistic and the modal closes the instant Save is
       * pressed, so this catch fires with the card already gone. It used to
       * revert the card and rethrow into a dropped promise — the throw reached
       * nobody, and the only trace was an "Uncaught (in promise)" in a console
       * the person editing never opens. From the seat it looked exactly like
       * "I set the dates, saved, went back, and they had changed back": the
       * database refused the write (a row a policy hides, an id the board does
       * not hold — PostgREST answers both with the same silence), the card
       * reverted, and nothing said why.
       *
       * So the revert stays, and the reason is put on screen. `before?.ref`
       * names the card the way the person does; the database's own message
       * says what it refused. Recorded to the trail as well, for the diagnostics
       * screen, because a message dismissed is a message gone.
       */
      if (before) set({ issues: get().issues.map((i) => (i.id === id ? before : i)) });
      // The write failed and the card is back to `before`; stop guarding it, or
      // the watcher would hold the reverted copy against the server for the window.
      pendingCardWrites.delete(id);
      const why = e instanceof Error ? e.message : String(e);
      const which = before?.ref ?? 'the card';
      remember('broke', `an edit to ${which} was not saved — ${why}`);
      set({ saveProblem: `Your change to ${which} was not saved. ${why}` });
      throw e;
    });
    /*
     * ── THE DATE CASCADE — a later due date pushes everything that waits on it.
     *
     * A finish-to-start dependency means a dependent cannot start until this card
     * finishes (src/dependencies.ts). So when THIS card's due date moves LATER,
     * every transitive dependent that would now overlap the new finish slides
     * forward out of its way, keeping its own planned duration. The pure
     * `cascadeShifts` works all of that out — forward-only, cycle-safe, a dateless
     * dependent skipped and its branch stopped — and hands the moves back in
     * topological order. We only APPLY them; we add no recursion of our own.
     * Calling `updateIssue` per dependent would re-enter this very block and shift
     * the chain a second time, so each dependent is written here directly, through
     * the same optimistic set-then-patch path the card itself took above.
     *
     * Only on a due date that genuinely moved LATER (including first-set, where
     * there was no finish to respect before). A due pulled EARLIER or CLEARED
     * cascades nothing — cascadeShifts is forward-only, but we also skip calling
     * it so an edit that never pushed a finish out pays nothing.
     */
    const prevDue = before?.dueDate ?? '';
    const dueMovedLater =
      typeof patch.dueDate === 'string' &&
      patch.dueDate.length > 0 &&
      !!before &&
      patch.dueDate > prevDue; // '' sorts before any real day, so none→date counts
    if (dueMovedLater && before) {
      const me = get().user?.email ?? '';
      const newDue = patch.dueDate as string;
      // Resolved once, from the board as it now stands (this card's new due is
      // already applied above, and cascadeShifts lays it over the top regardless),
      // so the whole transitive set is known before any dependent is touched.
      const shifts = cascadeShifts(get().issues, before.ref, newDue, get().workCalendar);
      for (const sh of shifts) {
        const dep = get().issues.find((i) => i.ref === sh.ref);
        if (!dep) continue;
        // Patch only the dates this card actually carries — a due-only dependent
        // moves its due alone, and writing `startsOn: null` would wrongly CLEAR a
        // date the card never had. A shift with nothing to write is skipped.
        const datePatch: { startsOn?: string; dueDate?: string } = {};
        if (sh.toStartsOn != null) datePatch.startsOn = sh.toStartsOn;
        if (sh.toDueDate != null) datePatch.dueDate = sh.toDueDate;
        if (datePatch.startsOn === undefined && datePatch.dueDate === undefined) continue;
        const at = Date.now();
        // An `auto` line, exactly as a bare status move writes one (0138): auto, so
        // the fan-out trigger raises no call-out, and it records WHY it moved.
        const depLog = [
          ...(dep.standsLog ?? []),
          { id: uid(), at, by: me, text: `Moved with ${before.ref} (now due ${shortDay(newDue)})`, auto: true, cascade: true } as StatusUpdate,
        ];
        const depStamped = { ...datePatch, standsLog: depLog, updatedAt: at };
        set({ issues: get().issues.map((i) => (i.id === dep.id ? { ...i, ...depStamped } : i)) });
        const depBefore = dep;
        void backend.patch(issuePath(dep.id), depStamped).catch((e: unknown) => {
          // Put the dependent back exactly as it was if its own write is refused,
          // the same contract the card above keeps for itself.
          set({ issues: get().issues.map((i) => (i.id === depBefore.id ? depBefore : i)) });
          const why = e instanceof Error ? e.message : String(e);
          remember('broke', `${depBefore.ref} could not be moved with ${before.ref} — ${why}`);
          set({ saveProblem: `${depBefore.ref} could not be moved with ${before.ref}. ${why}` });
        });
        /*
         * The dependent's doer is told their dates moved — in their feed, the same
         * way every call-out is raised: by a database trigger from the row change,
         * not from here (`raise_notification` is Postgres-only). The `cascade: true`
         * flag on the auto line just written is the trigger's signal; it raises a
         * `moved_with` call-out to the card's assignee. See migration 0203
         * (notify_on_cascade_move) and the `blocked_on_you` fan-out (0159) it mirrors.
         */
      }
    }
    /*
     * NOT WRITTEN HERE ANY MORE — the database writes it. See migration 0030.
     *
     * These lines used to append the history entries this browser had just
     * composed, which made the record a courtesy performed by one particular
     * client. push-tracker never performed it, so Vinnie's command-line edits
     * on 17 August left thirteen hours of changes with no trace at all, and
     * nothing anywhere saying so.
     *
     * `describeChange` above is still called, because the ENTRIES it builds are
     * shown on the card immediately — an optimistic line the watcher replaces
     * with the real row a moment later. What is gone is the write.
     */
    /*
     * A CARD CHANGES STATUS TWO WAYS, and this is the one that was missed.
     * Dragging it goes through moveIssue; editing it and pressing Save comes
     * through here — same outcome, same person waiting to be told, and telling
     * them only when somebody happened to drag rather than edit would be a
     * silence nobody could explain. Found by closing a ticket from the card and
     * watching nothing happen.
     */
    if (patch.status && before && before.status !== patch.status) {
      void get().tellReporter(id, patch.status);
    }

    /*
     * THE RELEASE-PRIORITY NUDGE — #6. Setting the level answers the question,
     * so the card leaves the nudge set. Being pulled into a PHASE with no level
     * yet is the moment scope is decided, so it joins the set — the Plan renders
     * the inline prompt on it. Only a real phase change of an unweighed card,
     * never on any other edit.
     */
    if (patch.moscow) {
      set({ moscowNudge: get().moscowNudge.filter((x) => x !== id) });
    } else if (
      typeof patch.phase === 'string' &&
      patch.phase &&
      before &&
      !before.moscow &&
      before.phase !== patch.phase
    ) {
      set({ moscowNudge: [...new Set([...get().moscowNudge, id])] });
    }
  },

  dismissMoscowNudge: (id) => {
    set({ moscowNudge: get().moscowNudge.filter((x) => x !== id) });
  },

  dismissAssist: (key) => {
    set({ assistDismiss: [...new Set([...get().assistDismiss, key])] });
  },

  moveIssue: async (id, status, opts) => {
    if (sampleBlocks()) return;
    const before = get().issues.find((i) => i.id === id);
    if (!before || before.status === status) return;
    const stamp = Date.now();
    const began = whenWorkBegan(before, status);

    /* A STATUS CHANGE WRITES ITS OWN LOG LINE (0138) — the same `auto` line the
       edit path writes, so dragging a card and editing it leave the identical
       record. No words, so the standing note is untouched and the fan-out trigger
       raises nothing; the reporter is told below as before.

       When the move is a deliberate start THROUGH THE SOFT GATE — started while a
       predecessor is still open — that same auto line carries the override note
       instead of being blank, so the card's history says it was started anyway
       and by whom. Still `auto`, so it raises no call-out. */
    const standsLog = [
      ...(before.standsLog ?? []),
      { id: uid(), at: stamp, by: get().user?.email ?? '', text: opts?.overrideNote ?? '', status, auto: true } as StatusUpdate,
    ];

    /*
     * ── THE CARD MOVES FIRST ────────────────────────────────────────────────
     *
     * This used to write and wait for the database to echo the change back
     * through the listener before the card went anywhere — the same fault that
     * made adding to a sprint feel slow, in the one place where the delay is
     * most obvious, because you are holding the card when it happens.
     *
     * Put back exactly where it was if the write is refused, so a rule saying
     * no is a card that visibly returns rather than one that never moved.
     */
    set({
      issues: get().issues.map((i) => (i.id === id ? { ...i, status, standsLog, ...began } : i)),
    });

    try {
      await backend.patch(issuePath(id), { status, standsLog, updatedAt: stamp, ...began });
    } catch (e) {
      set({
        issues: get().issues.map((i) =>
          i.id === id ? { ...i, status: before.status, standsLog: before.standsLog, startedAt: before.startedAt } : i
        ),
      });
      throw e;
    }
    /* The status entry is written by the database now — 0030. Dragging a card
       and editing it are two ways of doing one thing, and this was the half
       that a browser happened to record. */
    // Only a real move is news. Re-dropping a card in the column it already
    // sits in must not write to anybody, let alone email them.
    void get().tellReporter(id, status);
  },

  moveIssues: async (ids, status) => {
    const done = await Promise.allSettled(ids.map((id) => get().moveIssue(id, status)));
    const refused = done.filter((r) => r.status === 'rejected');
    const first = (refused[0] as PromiseRejectedResult | undefined)?.reason as Error | undefined;
    return { refused: refused.length, why: first?.message ?? '' };
  },

  /* ── BULK CARD CHANGES — impls (project-bulk-card-changes) ──────────────────
   *
   * Each mirrors the established shape: parallel, not a serial crawl; one audit
   * line for the whole act; and a single digest per recipient where a person is
   * handed work. They are the board entry point the future Undo slice reuses.
   */
  bulkMove: async (ids, status, label) => {
    // Which cards WERE somewhere else — a card already in the target is not "moved"
    // and its owner is not told it was. Captured before the writes.
    const was = new Map(ids.map((id) => [id, get().issues.find((i) => i.id === id)?.status]));
    const results = await Promise.allSettled(ids.map((id) => get().moveIssue(id, status)));
    const moved: string[] = [];
    const refused: string[] = [];
    let why = '';
    results.forEach((r, i) => {
      const id = ids[i];
      if (r.status === 'fulfilled') {
        if (was.get(id) !== status) moved.push(id);
      } else {
        refused.push(id);
        if (!why) why = (r.reason as Error | undefined)?.message ?? '';
      }
    });
    const movedIssues = get().issues.filter((i) => moved.includes(i.id));
    // One audit line for the whole move (recordBulk ignores a single card — that is
    // just a normal move, already recorded per card).
    get().recordBulk(label, movedIssues);
    // One "moved N of your cards" digest per owner — never N alerts, never to me.
    const board = get().projectId;
    const items: BulkDigestItem[] = movedIssues
      .map((i) => ({
        recipient: i.responsible?.trim() || i.assignee?.trim() || '',
        ref: i.ref,
        title: i.title,
        boardId: board,
      }))
      .filter((it) => it.recipient);
    const notified = await get().bulkNotify(bulkDigests(get().user?.email ?? '', 'moved_bulk', items));
    return { moved, refused, why, notified };
  },

  bulkAssign: async (ids, who, label) => {
    const assignee = who.trim();
    const target = get().issues.filter((i) => ids.includes(i.id));
    // The doer is the Responsible seat — the "who is doing this" the board leads with.
    for (const i of target) get().updateIssue(i.id, { responsible: assignee });
    get().recordBulk(label, target);
    // ONE digest to the person who got the cards — not one per card, and not to me.
    const board = get().projectId;
    const items: BulkDigestItem[] = target.map((i) => ({
      recipient: assignee,
      ref: i.ref,
      title: i.title,
      boardId: board,
    }));
    const notified = await get().bulkNotify(bulkDigests(get().user?.email ?? '', 'assigned_bulk', items));
    return { notified };
  },

  bulkPatch: (ids, patch, label) => {
    const target = get().issues.filter((i) => ids.includes(i.id));
    for (const i of target) get().updateIssue(i.id, patch);
    get().recordBulk(label, target);
  },

  bulkDuplicate: async (ids, label) => {
    // The copies are made in the selection's own order, so a set of three cards
    // duplicated reads as three new cards in the same order they were ticked.
    const target = ids
      .map((id) => get().issues.find((i) => i.id === id))
      .filter((i): i is Issue => !!i);
    for (const i of target) await get().createIssue(duplicateDraft(i));
    get().recordBulk(label, target);
  },

  bulkDelete: async (ids, label) => {
    const gone = ids
      .map((id) => get().issues.find((i) => i.id === id))
      .filter((i): i is Issue => !!i);
    // deleteIssue keeps each card's restorable snapshot in the trail; the bulk line
    // on top of it says the N were one act (and is what Undo reverts in one step).
    for (const i of gone) await get().deleteIssue(i.id);
    get().recordBulk(label, gone);
    return gone;
  },

  bulkNotify: async (digests) => {
    if (!digests.length || !backend.bulkNotify) return false;
    // A browser has no insert on notifications (0105) — writing a call-out FOR
    // another person is a SECURITY DEFINER job (0106). The backend method calls the
    // matching RPC; AWAIT it and report whether it actually wrote, so the result
    // toast claims "notified once" ONLY when the digest truly landed. Until the RPC
    // (and its migration) exist the call fails and we return false — so the toast is
    // honest, and no bulk change ever fans out N alerts in the meantime (the per-card
    // status trigger is already silent on these: moves write `auto` lines, and
    // assign/area/epic/priority raise nothing today).
    try {
      await backend.bulkNotify(digests);
      return true;
    } catch {
      return false;
    }
  },

  bulkRestore: (entries, label) => {
    for (const e of entries) get().updateIssue(e.id, e.patch);
    const touched = get().issues.filter((i) => entries.some((e) => e.id === i.id));
    get().recordBulk(label, touched);
  },

  restoreIssues: async (issues, label) => {
    const where = get().projectId;
    if (sampleBlocks(where)) return;
    for (const issue of issues) {
      await backend.set(`${inProject(where).issues}/${issue.id}`, issue);
    }
    get().recordBulk(label, issues);
  },

  /* ── Areas — #269 ───────────────────────────────────────────────────────── */

  /*
   * ── THE BOARD'S TYPES — #302 ─────────────────────────────────────────────
   *
   * Deliberately the same five actions as areas, in the same order, doing the
   * same things: the two features are the same shape and a reader who knows one
   * should not have to learn the other. Where they differ, the difference is
   * commented.
   */
  createType: async (draft) => {
    if (sampleBlocks()) return '';
    const name = draft.name.trim();
    if (!name) return 'A type needs a name.';
    const clash = liveTypes(get().issueTypes).some(
      (t) => t.name.toLowerCase() === name.toLowerCase()
    );
    if (clash) return `There is already a type called “${name}”.`;
    const id = uid();
    const last = get().issueTypes.reduce((n, t) => Math.max(n, t.position), -1);
    /*
     * ⚠️ THE SLUG IS MADE ONCE, HERE, AND NEVER AGAIN. It is what `issues.kind`
     * holds, so remaking it on a rename would orphan every card using it —
     * which is precisely what the foreign key in 0072 would refuse, loudly, at
     * the worst possible moment. Checked against EVERY slug including retired
     * ones, because the database's uniqueness on slug does not exempt them.
     */
    await backend.set(`${typesPath()}/${id}`, {
      id,
      slug: slugFor(name, get().issueTypes.map((t) => t.slug)),
      name,
      means: draft.means,
      position: last + 1,
      createdAt: Date.now(),
    });
    return '';
  },

  editType: async (id, patch) => {
    if (sampleBlocks()) return '';
    const mine = get().issueTypes.find((t) => t.id === id);
    if (!mine) return 'That type is not on this board.';
    const name = patch.name?.trim();
    if (name !== undefined && !name) return 'A type needs a name.';
    if (name) {
      const clash = liveTypes(get().issueTypes).some(
        (t) => t.id !== id && t.name.toLowerCase() === name.toLowerCase()
      );
      if (clash) return `There is already a type called “${name}”.`;
    }
    /* THE SLUG IS NOT IN THIS PATCH, and that is what makes renaming free:
       one row changes and not one card moves. */
    await backend.patch(`${typesPath()}/${id}`, {
      ...(name ? { name } : {}),
      ...(patch.means ? { means: patch.means } : {}),
    });
    return '';
  },

  moveType: async (id, direction) => {
    if (sampleBlocks()) return '';
    const order = liveTypes(get().issueTypes);
    const at = order.findIndex((t) => t.id === id);
    if (at < 0) return 'That type is not on this board.';
    const swapWith = order[at + direction];
    if (!swapWith) return '';
    const mine = order[at];
    await backend.patch(`${typesPath()}/${mine.id}`, { position: swapWith.position });
    await backend.patch(`${typesPath()}/${swapWith.id}`, { position: mine.position });
    return '';
  },

  retireType: async (id) => {
    const mine = get().issueTypes.find((t) => t.id === id);
    if (!mine) return 'That type is not on this board.';
    if (mine.retiredAt) return '';
    /*
     * ⚠️ THE LAST LIVE ONE CANNOT GO, and areas have no equivalent rule because
     * an area is optional and a type is not. Every card must be something: with
     * no live types the new-card form would have nothing to offer and no card
     * could be raised at all — a board bricked from its own settings screen.
     */
    if (liveTypes(get().issueTypes).length <= 1) {
      return 'A board needs at least one type — every card has to be something.';
    }
    await backend.patch(`${typesPath()}/${id}`, { retiredAt: Date.now() });
    return '';
  },

  restoreType: async (id) => {
    if (sampleBlocks()) return '';
    const mine = get().issueTypes.find((t) => t.id === id);
    if (!mine) return 'That type is not on this board.';
    if (!mine.retiredAt) return '';
    const clash = liveTypes(get().issueTypes).some(
      (t) => t.name.toLowerCase() === mine.name.toLowerCase()
    );
    if (clash) return `“${mine.name}” is in use again. Rename that one first, or rename this.`;
    await backend.patch(`${typesPath()}/${id}`, { retiredAt: null });
    return '';
  },

  createArea: async (draft) => {
    if (sampleNoWrite()) return { error: 'This is a sample board — start a board of your own to add an area.' };
    const name = draft.name.trim();
    if (!name) return { error: 'An area needs a name.' };
    /*
     * CHECKED AGAINST THE LIVE ONES ONLY, which matches what the database
     * enforces: `unique nulls not distinct (tenant_id, board_id, name,
     * retired_at)` leaves retired rows out on purpose. Retiring "Back-end" and
     * later wanting the name back is an ordinary thing to want, and refusing it
     * would make tidying up feel like a punishment.
     */
    const clash = liveAreas(get().areas).some((a) => a.name.toLowerCase() === name.toLowerCase());
    if (clash) return { error: `There is already an area called “${name}”.` };
    // A REAL uuid, not uid(). `areas.id` is a uuid column, and this id is handed
    // straight back and written onto a card as `issues.area` (also uuid) — a path
    // that does NOT hash it. A uid() short id reaches that column raw and Postgres
    // refuses it ("invalid input syntax for type uuid"), so a new card that creates
    // an area cannot be saved; it also would not match the stored row (whose own
    // write DOES hash it), showing the area twice. A uuid is passed through
    // unchanged by the backend's id hashing (its UUID.test guard), so the id the
    // app holds equals the stored row equals the card's reference.
    const id = crypto.randomUUID();
    const last = get().areas.reduce((n, a) => Math.max(n, a.position), -1);
    await backend.set(`${areasPath()}/${id}`, {
      id,
      name,
      colour: draft.colour,
      position: last + 1,
      createdAt: Date.now(),
    });
    // Optimistic local insert, so the new area is usable the instant it is made —
    // the card that created it can select it by id and show its name without
    // waiting for the subscription. It is also marked PENDING, so the watcher
    // carries it forward if the next server snapshot arrived mid-flight from
    // before the insert (otherwise it would wipe the new row and the card would
    // snap back to "No area"); the mark clears the moment the row is echoed.
    set((s) => ({
      // Idempotent: the realtime echo can beat this insert's own write-ack and
      // already hold the server row, so append only if the id is not present —
      // otherwise the area shows twice until the next snapshot (upsertById).
      areas: upsertById(s.areas, { id, name, colour: draft.colour, position: last + 1 }),
      pendingAreaIds: s.pendingAreaIds.includes(id) ? s.pendingAreaIds : [...s.pendingAreaIds, id],
    }));
    // Safety net: if the subscription never echoes the row (a failed insert),
    // drop the pending mark after a grace so it can never ghost the list.
    setTimeout(() => set((s) => ({ pendingAreaIds: s.pendingAreaIds.filter((x) => x !== id) })), 15000);
    return { id };
  },

  editArea: async (id, patch) => {
    if (sampleBlocks()) return '';
    const mine = get().areas.find((a) => a.id === id);
    if (!mine) return 'That area is not on this board.';
    const name = patch.name?.trim();
    if (name !== undefined && !name) return 'An area needs a name.';
    if (name) {
      const clash = liveAreas(get().areas).some(
        (a) => a.id !== id && a.name.toLowerCase() === name.toLowerCase()
      );
      if (clash) return `There is already an area called “${name}”.`;
    }
    // Reflect it AT ONCE in the list, before the round-trip — otherwise the new
    // name/colour/lead only appears when the realtime echo returns a second or
    // two later, which reads as a laggy control. The same optimism createArea
    // uses; the realtime echo then writes the identical value, so nothing fights.
    set((s) => ({
      areas: s.areas.map((a) =>
        a.id === id
          ? {
              ...a,
              ...(name ? { name } : {}),
              ...(patch.colour ? { colour: patch.colour } : {}),
              ...(patch.lead !== undefined ? { lead: patch.lead || null } : {}),
            }
          : a
      ),
    }));
    await backend.patch(`${areasPath()}/${id}`, {
      ...(name ? { name } : {}),
      ...(patch.colour ? { colour: patch.colour } : {}),
      // lead is set OR cleared, so '' / null is a real value to write, not a
      // no-op — only an absent key means "leave the lead as it is".
      ...(patch.lead !== undefined ? { lead: patch.lead || null } : {}),
    });
    return '';
  },

  /*
   * MOVING ONE SWAPS TWO POSITIONS rather than renumbering the list. Rewriting
   * every row to move one of them is a great many writes for one drag, and each
   * of those writes can fail on its own — leaving an order nobody chose.
   */
  moveArea: async (id, direction) => {
    if (sampleBlocks()) return '';
    const order = liveAreas(get().areas);
    const at = order.findIndex((a) => a.id === id);
    if (at < 0) return 'That area is not on this board.';
    const swapWith = order[at + direction];
    if (!swapWith) return '';
    const mine = order[at];
    await backend.patch(`${areasPath()}/${mine.id}`, { position: swapWith.position });
    await backend.patch(`${areasPath()}/${swapWith.id}`, { position: mine.position });
    return '';
  },

  retireArea: async (id) => {
    const mine = get().areas.find((a) => a.id === id);
    if (!mine) return 'That area is not on this board.';
    if (mine.retiredAt) return '';
    await backend.patch(`${areasPath()}/${id}`, { retiredAt: Date.now() });
    return '';
  },

  restoreArea: async (id) => {
    if (sampleBlocks()) return '';
    const mine = get().areas.find((a) => a.id === id);
    if (!mine) return 'That area is not on this board.';
    if (!mine.retiredAt) return '';
    /*
     * The name may have been taken while it was away — the database allows that
     * on purpose. Say so here rather than letting the write come back as a
     * constraint name nobody can act on.
     */
    const clash = liveAreas(get().areas).some(
      (a) => a.name.toLowerCase() === mine.name.toLowerCase()
    );
    if (clash) return `“${mine.name}” is in use again. Rename that one first, or rename this.`;
    await backend.patch(`${areasPath()}/${id}`, { retiredAt: null });
    return '';
  },

  /* ── Labels — #462. Areas' actions, one entity along, minus order and lead:
   * create/rename-or-recolour/retire/restore. A registry by NAME — the card keeps
   * the name in issue.labels; this holds its optional colour. ─────────────────── */
  createLabel: async (draft) => {
    if (sampleNoWrite()) return { error: 'This is a sample board — start a board of your own to add a label.' };
    const name = draft.name.trim();
    if (!name) return { error: 'A label needs a name.' };
    // Live ones only, matching the DB's `unique nulls not distinct` — a retired
    // name can be taken up again.
    const clash = liveLabels(get().labels).some((l) => l.name.toLowerCase() === name.toLowerCase());
    if (clash) return { error: `There is already a label called “${name}”.` };
    // A REAL uuid, not uid() — `labels.id` is a uuid column (see createArea). A
    // uid() short id is hashed only on the row's own write, so the id handed back
    // would not match the stored row and the label would show twice. A uuid is
    // passed through unchanged, so optimistic id == stored id.
    const id = crypto.randomUUID();
    await backend.set(`${labelsPath()}/${id}`, {
      id,
      name,
      // Colour is optional; only write it when one was chosen, so "no colour"
      // stays null rather than an empty string.
      ...(draft.colour ? { colour: draft.colour } : {}),
      createdAt: Date.now(),
    });
    // Idempotent for the same reason as createArea: the realtime echo can beat
    // this insert's write-ack, so append only if the id is not already present.
    set((s) => ({ labels: upsertById(s.labels, { id, name, colour: draft.colour ?? null }) }));
    return { id };
  },

  editLabel: async (id, patch) => {
    if (sampleBlocks()) return '';
    const mine = get().labels.find((l) => l.id === id);
    if (!mine) return 'That label is not on this board.';
    const name = patch.name?.trim();
    if (name !== undefined && !name) return 'A label needs a name.';
    if (name) {
      const clash = liveLabels(get().labels).some(
        (l) => l.id !== id && l.name.toLowerCase() === name.toLowerCase()
      );
      if (clash) return `There is already a label called “${name}”.`;
    }
    // Optimistic, as editArea is — the new name/colour shows at once; the realtime
    // echo then writes the identical value, so nothing fights.
    set((s) => ({
      labels: s.labels.map((l) =>
        l.id === id
          ? {
              ...l,
              ...(name ? { name } : {}),
              // colour is SET or CLEARED, so '' / null is a real value to write;
              // only an absent key means "leave the colour as it is".
              ...(patch.colour !== undefined ? { colour: patch.colour || null } : {}),
            }
          : l
      ),
    }));
    await backend.patch(`${labelsPath()}/${id}`, {
      ...(name ? { name } : {}),
      ...(patch.colour !== undefined ? { colour: patch.colour || null } : {}),
    });
    return '';
  },

  retireLabel: async (id) => {
    const mine = get().labels.find((l) => l.id === id);
    if (!mine) return 'That label is not on this board.';
    if (mine.retiredAt) return '';
    await backend.patch(`${labelsPath()}/${id}`, { retiredAt: Date.now() });
    return '';
  },

  restoreLabel: async (id) => {
    if (sampleBlocks()) return '';
    const mine = get().labels.find((l) => l.id === id);
    if (!mine) return 'That label is not on this board.';
    if (!mine.retiredAt) return '';
    const clash = liveLabels(get().labels).some(
      (l) => l.name.toLowerCase() === mine.name.toLowerCase()
    );
    if (clash) return `“${mine.name}” is in use again. Rename that one first, or rename this.`;
    await backend.patch(`${labelsPath()}/${id}`, { retiredAt: null });
    return '';
  },

  /* ── Phases — #377 ─────────────────────────────────────────────────────────
   * The area actions, one entity along: create/rename/reorder/retire/restore,
   * plus a status and a target day a phase has and an area does not, and
   * assigning an issue to a phase. Validation mirrors the database's own rules
   * (phaseProblem), so the screen says what is wrong before the write is refused.
   */
  createPhase: async (draft) => {
    if (sampleBlocks()) return '';
    const wrong = phaseProblem(draft.name, get().phases);
    if (wrong) return wrong;
    const id = uid();
    const last = get().phases.reduce((n, p) => Math.max(n, p.position), -1);
    await backend.set(`${phasesPath()}/${id}`, {
      id,
      name: draft.name.trim(),
      position: last + 1,
      status: draft.status ?? 'planned',
      // Only write a target when one was given — absent means "nobody has said".
      ...(draft.targetEnd ? { targetEnd: draft.targetEnd } : {}),
      createdAt: Date.now(),
    });
    return '';
  },

  editPhase: async (id, patch) => {
    if (sampleBlocks()) return '';
    const mine = get().phases.find((p) => p.id === id);
    if (!mine) return 'That phase is not on this board.';
    if (patch.name !== undefined) {
      const wrong = phaseProblem(patch.name, get().phases, id);
      if (wrong) return wrong;
    }
    const next: Record<string, unknown> = {};
    if (patch.name !== undefined) next.name = patch.name.trim();
    if (patch.status !== undefined) next.status = patch.status;
    // null clears the target back to "nobody has said"; a string sets it.
    if (patch.targetEnd !== undefined) next.targetEnd = patch.targetEnd || null;
    if (Object.keys(next).length) await backend.patch(`${phasesPath()}/${id}`, next);
    return '';
  },

  movePhase: async (id, direction) => {
    if (sampleBlocks()) return '';
    const order = livePhases(get().phases);
    const at = order.findIndex((p) => p.id === id);
    if (at < 0) return 'That phase is not on this board.';
    const swapWith = order[at + direction];
    if (!swapWith) return '';
    const mine = order[at];
    await backend.patch(`${phasesPath()}/${mine.id}`, { position: swapWith.position });
    await backend.patch(`${phasesPath()}/${swapWith.id}`, { position: mine.position });
    return '';
  },

  retirePhase: async (id) => {
    const mine = get().phases.find((p) => p.id === id);
    if (!mine) return 'That phase is not on this board.';
    if (mine.retiredAt) return '';
    await backend.patch(`${phasesPath()}/${id}`, { retiredAt: Date.now() });
    return '';
  },

  restorePhase: async (id) => {
    if (sampleBlocks()) return '';
    const mine = get().phases.find((p) => p.id === id);
    if (!mine) return 'That phase is not on this board.';
    if (!mine.retiredAt) return '';
    // The name may have been taken while it was away — say so here rather than
    // letting the write come back as a constraint name nobody can act on.
    const clash = livePhases(get().phases).some(
      (p) => p.name.trim().toLowerCase() === mine.name.trim().toLowerCase()
    );
    if (clash) return `"${mine.name}" is in use again. Rename that one first, or rename this.`;
    await backend.patch(`${phasesPath()}/${id}`, { retiredAt: null });
    return '';
  },

  /* ── The Plan's standalone milestones — #374 + milestone payments. ───────── */

  createMilestone: async (draft) => {
    if (sampleBlocks()) return '';
    const name = draft.name.trim();
    if (!name) return 'Give the milestone a name.';
    // A billable milestone is a payment point, so it must say what it is worth.
    if (draft.billable && !(draft.billValue != null && draft.billValue > 0))
      return 'A billable milestone needs a bill value.';
    const id = uid();
    const last = get().milestones.reduce((n, m) => Math.max(n, m.position ?? -1), -1);
    await backend.set(`${milestonesPath()}/${id}`, {
      id,
      name,
      kind: draft.kind,
      position: last + 1,
      billable: !!draft.billable,
      // Only a billable milestone carries a value; otherwise it is a dated marker.
      ...(draft.billable ? { billValue: draft.billValue } : {}),
      ...(draft.targetDay ? { targetDay: draft.targetDay } : {}),
      // The work it gates, for the cadence forecast (0215) — only when set.
      ...(draft.forecastTie ? { forecastTie: draft.forecastTie } : {}),
      createdAt: Date.now(),
    });
    return '';
  },

  editMilestone: async (id, patch) => {
    if (sampleBlocks()) return '';
    const mine = get().milestones.find((m) => m.id === id);
    if (!mine) return 'That milestone is not on this board.';
    const name = patch.name !== undefined ? patch.name.trim() : mine.name;
    if (!name) return 'Give the milestone a name.';
    // The resulting billable state and value, after this patch is applied.
    const billable = patch.billable !== undefined ? patch.billable : !!mine.billable;
    const billValue = patch.billValue !== undefined ? patch.billValue : mine.billValue ?? null;
    if (billable && !(billValue != null && billValue > 0))
      return 'A billable milestone needs a bill value.';
    const next: Record<string, unknown> = {};
    if (patch.name !== undefined) next.name = name;
    if (patch.kind !== undefined) next.kind = patch.kind;
    if (patch.targetDay !== undefined) next.targetDay = patch.targetDay || null;
    if (patch.billable !== undefined) next.billable = billable;
    // Turning billable off clears the value; on/changed writes it.
    if (patch.billable !== undefined || patch.billValue !== undefined)
      next.billValue = billable ? billValue : null;
    // The forecast tie, when the patch touches it — null clears it back to "from the plan".
    if (patch.forecastTie !== undefined) next.forecastTie = patch.forecastTie || null;
    if (Object.keys(next).length) await backend.patch(`${milestonesPath()}/${id}`, next);
    return '';
  },

  deleteMilestone: async (id) => {
    const mine = get().milestones.find((m) => m.id === id);
    if (!mine) return 'That milestone is not on this board.';
    await backend.remove(`${milestonesPath()}/${id}`);
    return '';
  },

  /* ── The decision log — 3.19 Learning & Governance spine (D1). ──────────────
     Writes are gated server-side by the decisions RLS (a writer with rank on the
     board, or its owner), as for milestones; the Decisions tab hides the controls
     from everyone else. Supersede-never-edit: an open draft edits in place; a
     decided decision is changed only by superseding it. */
  logDecision: async (draft) => {
    if (sampleBlocks()) return '';
    const title = draft.title.trim();
    if (!title) return 'Give the decision a title.';
    const me = get().user?.email?.toLowerCase() ?? accounts.current()?.email?.toLowerCase() ?? '';
    // A client uuid, not a short id — passed through the write layer unchanged, so the
    // optimistic row's id equals the one the snapshot reads back (see draftDocument).
    const id = crypto.randomUUID();
    const now = Date.now();
    const decided = (draft.status ?? 'decided') === 'decided';
    const last = get().decisions.reduce((n, d) => Math.max(n, d.position ?? -1), -1);
    const row = {
      id,
      title,
      ...(richHasText(draft.detail) ? { detail: draft.detail } : {}),
      status: decided ? 'decided' : 'open',
      // A decision may be logged after the fact, for a call someone else made on
      // an earlier date — honour an explicit decider/date, else stamp me + now.
      ...(decided ? { decidedAt: draft.decidedAt ?? now, decidedBy: draft.decidedBy?.trim().toLowerCase() || me } : {}),
      madeBy: me,
      origin: draft.origin,
      ...(draft.originRef ? { originRef: draft.originRef } : {}),
      ...(draft.links && draft.links.length ? { links: draft.links } : {}),
      position: last + 1,
      createdAt: now,
      updatedAt: now,
    };
    await backend.set(`${decisionsPath()}/${id}`, row);
    // The History line is the decisions trigger's (record_decision_change, 0176) —
    // written where every writer is seen (browser, CLI, script), not just here.
    // SHOW IT NOW, don't wait for the realtime echo (the same optimistic insert
    // draftDocument does) — held in pendingDecisions so a stale first snapshot cannot
    // drop it before the row is readable; the watcher replaces this list keyed by id.
    pendingDecisions.add(id);
    set((s) => ({ decisions: [...s.decisions.filter((d) => d.id !== id), row as unknown as Decision] }));
    return '';
  },

  decideDecision: async (id, when) => {
    if (sampleBlocks()) return '';
    const mine = get().decisions.find((d) => d.id === id);
    if (!mine) return 'That decision is not on this board.';
    if (!canDecide(mine)) return 'Only an open decision can be marked decided.';
    const me = get().user?.email?.toLowerCase() ?? accounts.current()?.email?.toLowerCase() ?? '';
    const now = Date.now();
    // Honour an explicit decider/date (a call made earlier, or by someone else),
    // else stamp me + now — the same rule as logDecision.
    const patch = {
      status: 'decided' as const,
      decidedAt: when?.decidedAt ?? now,
      decidedBy: when?.decidedBy?.trim().toLowerCase() || me,
      updatedAt: now,
    };
    await backend.patch(`${decisionsPath()}/${id}`, patch);
    // History line: the decisions trigger (record_decision_change, 0176).
    // SHOW IT NOW — the row is already in the snapshot, so the echo just corrects it.
    set((s) => ({ decisions: s.decisions.map((d) => (d.id === id ? { ...d, ...patch } : d)) }));
    return '';
  },

  editDecision: async (id, patch) => {
    if (sampleBlocks()) return '';
    const mine = get().decisions.find((d) => d.id === id);
    if (!mine) return 'That decision is not on this board.';
    if (!canEditInPlace(mine)) return 'A decided decision can’t be edited — supersede it with a new one.';
    const next: Record<string, unknown> = {};
    if (patch.title !== undefined) {
      const t = patch.title.trim();
      if (!t) return 'Give the decision a title.';
      next.title = t;
    }
    if (patch.detail !== undefined) next.detail = richHasText(patch.detail) ? patch.detail : null;
    if (Object.keys(next).length) {
      next.updatedAt = Date.now();
      await backend.patch(`${decisionsPath()}/${id}`, next);
      // SHOW IT NOW — mirror the patch into local state (a cleared detail is null on
      // the wire, undefined in the Decision), so the edit shows before the echo lands.
      set((s) => ({
        decisions: s.decisions.map((d) => {
          if (d.id !== id) return d;
          const merged: Decision = { ...d, updatedAt: next.updatedAt as number };
          if (next.title !== undefined) merged.title = next.title as string;
          if (next.detail !== undefined) merged.detail = (next.detail as RichDoc | null) ?? undefined;
          return merged;
        }),
      }));
    }
    return '';
  },

  supersedeDecision: async (id, draft) => {
    if (sampleBlocks()) return '';
    const old = get().decisions.find((d) => d.id === id);
    if (!old) return 'That decision is not on this board.';
    if (!canSupersede(old)) return 'Only a decided decision can be superseded.';
    const title = draft.title.trim();
    if (!title) return 'Give the replacing decision a title.';
    const me = get().user?.email?.toLowerCase() ?? accounts.current()?.email?.toLowerCase() ?? '';
    // A client uuid (see logDecision): optimistic id === persisted id.
    const newId = crypto.randomUUID();
    const now = Date.now();
    const last = get().decisions.reduce((n, d) => Math.max(n, d.position ?? -1), -1);
    const row = {
      id: newId,
      title,
      ...(richHasText(draft.detail) ? { detail: draft.detail } : {}),
      status: 'decided',
      decidedAt: draft.decidedAt ?? now,
      decidedBy: draft.decidedBy?.trim().toLowerCase() || me,
      madeBy: me,
      supersedes: id,
      origin: draft.origin,
      ...(draft.originRef ? { originRef: draft.originRef } : {}),
      ...(draft.links && draft.links.length ? { links: draft.links } : {}),
      position: last + 1,
      createdAt: now,
      updatedAt: now,
    };
    // Write the replacement first (it names the old via `supersedes`), then flip the
    // old one. If the flip is lost to a crossed write, liveDecisions still drops the
    // old row via that back-reference — the log never shows both.
    await backend.set(`${decisionsPath()}/${newId}`, row);
    await backend.patch(`${decisionsPath()}/${id}`, { status: 'superseded', updatedAt: now });
    // History line: the decisions trigger (record_decision_change, 0176) writes the
    // supersede once, on the replacement row's insert — not on the old row's flip.
    // SHOW IT NOW — flip the old row and add the replacement (held in pendingDecisions
    // until a snapshot carries it) so the log updates before the echo lands.
    pendingDecisions.add(newId);
    set((s) => ({
      decisions: [
        ...s.decisions
          .filter((d) => d.id !== newId)
          .map((d) => (d.id === id ? { ...d, status: 'superseded' as const, updatedAt: now } : d)),
        row as unknown as Decision,
      ],
    }));
    return '';
  },

  withdrawDecision: async (id, reason) => {
    if (sampleBlocks()) return '';
    const mine = get().decisions.find((d) => d.id === id);
    if (!mine) return 'That decision is not on this board.';
    if (!canWithdraw(mine)) return 'That decision can’t be withdrawn.';
    const why = reason.trim();
    if (!why) return 'Say why the decision is being withdrawn.';
    const now = Date.now();
    await backend.patch(`${decisionsPath()}/${id}`, { status: 'withdrawn', withdrawnReason: why, updatedAt: now });
    // History line: the decisions trigger (record_decision_change, 0176/0180) writes
    // "withdrew a decision — … (reason)". SHOW IT NOW — the echo corrects this row a
    // beat later.
    set((s) => ({ decisions: s.decisions.map((d) => (d.id === id ? { ...d, status: 'withdrawn' as const, withdrawnReason: why, updatedAt: now } : d)) }));
    return '';
  },

  /* ── The RAID register — Risks, Assumptions, Issues, Dependencies (0193). ─────
     Each add builds a camelCase row (the seam maps it to the table's columns and
     derives board_id from the path, exactly as logDecision does — so boardId is NOT
     in the written row, only on the optimistic one). The row is shown now and held in
     a pending set until the snapshot confirms it, so a stale first read can't drop it.
     The History line is each register's trigger (record_*_change, 0193). */
  addRisk: async (draft) => {
    if (sampleBlocks()) return '';
    const title = draft.title.trim();
    if (!title) return '';
    const id = crypto.randomUUID();
    const now = Date.now();
    const list = get().risks;
    const row: Record<string, unknown> = {
      id, num: nextNum(list), title,
      ...(draft.response?.trim() ? { response: draft.response.trim() } : {}),
      ...(draft.likelihood ? { likelihood: draft.likelihood } : {}),
      ...(draft.impact ? { impact: draft.impact } : {}),
      ...(draft.category?.trim() ? { category: draft.category.trim() } : {}),
      ...(draft.strategy ? { strategy: draft.strategy } : {}),
      status: draft.status ?? 'open',
      ...(draft.owner?.trim() ? { owner: draft.owner.trim() } : {}),
      ...(draft.nextReviewAt ? { nextReviewAt: draft.nextReviewAt } : {}),
      ...(draft.seededFromLessonId ? { seededFromLessonId: draft.seededFromLessonId } : {}),
      ...(draft.links?.length ? { links: draft.links } : {}),
      madeBy: raidMe(), position: nextPos(list), createdAt: now, updatedAt: now,
    };
    await backend.set(`${risksPath()}/${id}`, row);
    pendingRisks.add(id);
    set((s) => ({ risks: [...s.risks.filter((r) => r.id !== id), { ...row, boardId: get().projectId } as unknown as Risk] }));
    return id;
  },
  editRisk: async (id, patch) => {
    if (sampleBlocks()) return '';
    if (!get().risks.find((r) => r.id === id)) return 'That risk is not on this board.';
    const next = { ...patch, updatedAt: Date.now() };
    await backend.patch(`${risksPath()}/${id}`, next);
    set((s) => ({ risks: s.risks.map((r) => (r.id === id ? ({ ...r, ...next } as Risk) : r)) }));
    return '';
  },
  removeRisk: async (id) => {
    if (sampleBlocks()) return '';
    if (!get().risks.find((r) => r.id === id)) return '';
    await backend.remove(`${risksPath()}/${id}`);
    pendingRisks.delete(id);
    set((s) => ({ risks: s.risks.filter((r) => r.id !== id) }));
    return '';
  },

  addAssumption: async (draft) => {
    if (sampleBlocks()) return '';
    const title = draft.title.trim();
    if (!title) return '';
    const id = crypto.randomUUID();
    const now = Date.now();
    const list = get().assumptions;
    const row: Record<string, unknown> = {
      id, num: nextNum(list), title,
      ...(draft.impactIfFalse?.trim() ? { impactIfFalse: draft.impactIfFalse.trim() } : {}),
      ...(draft.confidence ? { confidence: draft.confidence } : {}),
      ...(draft.owner?.trim() ? { owner: draft.owner.trim() } : {}),
      status: draft.status ?? 'open',
      ...(draft.validateByAt ? { validateByAt: draft.validateByAt } : {}),
      ...(draft.links?.length ? { links: draft.links } : {}),
      madeBy: raidMe(), position: nextPos(list), createdAt: now, updatedAt: now,
    };
    await backend.set(`${assumptionsPath()}/${id}`, row);
    pendingAssumptions.add(id);
    set((s) => ({ assumptions: [...s.assumptions.filter((a) => a.id !== id), { ...row, boardId: get().projectId } as unknown as Assumption] }));
    return id;
  },
  editAssumption: async (id, patch) => {
    if (sampleBlocks()) return '';
    if (!get().assumptions.find((a) => a.id === id)) return 'That assumption is not on this board.';
    const next = { ...patch, updatedAt: Date.now() };
    await backend.patch(`${assumptionsPath()}/${id}`, next);
    set((s) => ({ assumptions: s.assumptions.map((a) => (a.id === id ? ({ ...a, ...next } as Assumption) : a)) }));
    return '';
  },
  removeAssumption: async (id) => {
    if (sampleBlocks()) return '';
    if (!get().assumptions.find((a) => a.id === id)) return '';
    await backend.remove(`${assumptionsPath()}/${id}`);
    pendingAssumptions.delete(id);
    set((s) => ({ assumptions: s.assumptions.filter((a) => a.id !== id) }));
    return '';
  },
  convertAssumptionToRisk: async (id) => {
    if (sampleBlocks()) return '';
    const a = get().assumptions.find((x) => x.id === id);
    if (!a) return '';
    const riskId = await get().addRisk({
      boardId: a.boardId,
      title: a.title,
      response: a.impactIfFalse,
      status: 'open',
      owner: a.owner,
      links: [{ kind: 'assumption', id: a.id, label: a.title }],
    });
    if (!riskId) return '';
    await get().editAssumption(id, { status: 'invalidated', convertedToRiskId: riskId });
    return riskId;
  },

  addIssue: async (draft) => {
    if (sampleBlocks()) return '';
    const title = draft.title.trim();
    if (!title) return '';
    const id = crypto.randomUUID();
    const now = Date.now();
    const list = get().registerIssues;
    const row: Record<string, unknown> = {
      id, num: nextNum(list), title,
      ...(draft.nextStep?.trim() ? { nextStep: draft.nextStep.trim() } : {}),
      priority: draft.priority ?? 'medium',
      ...(draft.owner?.trim() ? { owner: draft.owner.trim() } : {}),
      status: draft.status ?? 'open',
      raisedAt: draft.raisedAt ?? now,
      ...(draft.realisedFromRiskId ? { realisedFromRiskId: draft.realisedFromRiskId } : {}),
      ...(draft.links?.length ? { links: draft.links } : {}),
      madeBy: raidMe(), position: nextPos(list), createdAt: now, updatedAt: now,
    };
    await backend.set(`${registerIssuesPath()}/${id}`, row);
    pendingRegisterIssues.add(id);
    set((s) => ({ registerIssues: [...s.registerIssues.filter((i) => i.id !== id), { ...row, boardId: get().projectId } as unknown as RegisterIssue] }));
    return id;
  },
  editIssue: async (id, patch) => {
    if (sampleBlocks()) return '';
    if (!get().registerIssues.find((i) => i.id === id)) return 'That issue is not on this board.';
    const next = { ...patch, updatedAt: Date.now() };
    await backend.patch(`${registerIssuesPath()}/${id}`, next);
    set((s) => ({ registerIssues: s.registerIssues.map((i) => (i.id === id ? ({ ...i, ...next } as RegisterIssue) : i)) }));
    return '';
  },
  removeIssue: async (id) => {
    if (sampleBlocks()) return '';
    if (!get().registerIssues.find((i) => i.id === id)) return '';
    await backend.remove(`${registerIssuesPath()}/${id}`);
    pendingRegisterIssues.delete(id);
    set((s) => ({ registerIssues: s.registerIssues.filter((i) => i.id !== id) }));
    return '';
  },
  realiseRiskAsIssue: async (riskId) => {
    if (sampleBlocks()) return '';
    const r = get().risks.find((x) => x.id === riskId);
    if (!r) return '';
    // The issue takes the risk's urgency: a critical/high risk realises as a
    // critical/high issue, else medium. The PM can change it after.
    const band = riskBand(r);
    const priority = band === 'critical' ? 'critical' : band === 'high' ? 'high' : 'medium';
    return get().addIssue({
      boardId: r.boardId,
      title: r.title,
      priority,
      status: 'open',
      owner: r.owner,
      realisedFromRiskId: r.id,
      links: [{ kind: 'risk', id: r.id, label: r.title }],
    });
  },

  addDependency: async (draft) => {
    if (sampleBlocks()) return '';
    const title = draft.title.trim();
    if (!title) return '';
    const id = crypto.randomUUID();
    const now = Date.now();
    const list = get().dependencies;
    const row: Record<string, unknown> = {
      id, num: nextNum(list), title,
      ...(draft.note?.trim() ? { note: draft.note.trim() } : {}),
      direction: draft.direction ?? 'inbound',
      partyKind: draft.partyKind ?? 'external',
      partyName: draft.partyName?.trim() ?? '',
      ...(draft.partyBoardId ? { partyBoardId: draft.partyBoardId } : {}),
      ...(draft.owner?.trim() ? { owner: draft.owner.trim() } : {}),
      status: draft.status ?? 'ontrack',
      ...(draft.neededByAt ? { neededByAt: draft.neededByAt } : {}),
      ...(draft.links?.length ? { links: draft.links } : {}),
      madeBy: raidMe(), position: nextPos(list), createdAt: now, updatedAt: now,
    };
    await backend.set(`${dependenciesPath()}/${id}`, row);
    pendingDependencies.add(id);
    set((s) => ({ dependencies: [...s.dependencies.filter((d) => d.id !== id), { ...row, boardId: get().projectId } as unknown as Dependency] }));
    return id;
  },
  editDependency: async (id, patch) => {
    if (sampleBlocks()) return '';
    if (!get().dependencies.find((d) => d.id === id)) return 'That dependency is not on this board.';
    const next = { ...patch, updatedAt: Date.now() };
    await backend.patch(`${dependenciesPath()}/${id}`, next);
    set((s) => ({ dependencies: s.dependencies.map((d) => (d.id === id ? ({ ...d, ...next } as Dependency) : d)) }));
    return '';
  },
  removeDependency: async (id) => {
    if (sampleBlocks()) return '';
    if (!get().dependencies.find((d) => d.id === id)) return '';
    await backend.remove(`${dependenciesPath()}/${id}`);
    pendingDependencies.delete(id);
    set((s) => ({ dependencies: s.dependencies.filter((d) => d.id !== id) }));
    return '';
  },

  startRiskSeed: (lesson) => {
    set({ seedRiskLesson: lesson });
    get().setView('riskregister');
  },
  clearRiskSeed: () => set({ seedRiskLesson: null }),

  /* ── The lessons register — 3.19 Learning & Governance spine (L1). ──────────
     One tenant-wide collection (C.lessons), board_id nullable. Writes are gated
     server-side by the lessons RLS (author / workspace admin / board writer). */
  createLesson: async (draft) => {
    if (sampleBlocks()) return '';
    const title = draft.title.trim();
    if (!title) return 'Give the lesson a title.';
    if (!richHasText(draft.body)) return 'Say what happened.';
    const me = get().user?.email?.toLowerCase() ?? accounts.current()?.email?.toLowerCase() ?? '';
    // A client uuid (see logDecision): optimistic id === persisted id.
    const id = crypto.randomUUID();
    const now = Date.now();
    const last = get().lessons.reduce((n, l) => Math.max(n, l.position ?? -1), -1);
    const hasRec = richHasText(draft.recommendation);
    const row = {
      id,
      ...(draft.boardId ? { boardId: draft.boardId } : {}),
      title,
      category: draft.category,
      kind: draft.kind,
      body: draft.body,
      // Only carried when the recommendation toggle held real text — its presence
      // is exactly what shows the "Recommendation" badge.
      ...(hasRec ? { recommendation: draft.recommendation } : {}),
      ...(draft.owner?.trim() ? { owner: draft.owner.trim() } : {}),
      ...(draft.links && draft.links.length ? { links: draft.links } : {}),
      madeBy: me,
      position: last + 1,
      createdAt: now,
      updatedAt: now,
    };
    await backend.set(`${C.lessons}/${id}`, row);
    // The History line is the lessons trigger's (record_lesson_change, 0176) —
    // written for a create / edit / remove, whoever makes it, and covering the
    // workspace-wide lesson (null board) too.
    // SHOW IT NOW — held in pendingLessons so a stale first snapshot cannot drop it.
    pendingLessons.add(id);
    set((s) => ({ lessons: [...s.lessons.filter((l) => l.id !== id), row as unknown as Lesson] }));
    return '';
  },

  editLesson: async (id, patch) => {
    if (sampleBlocks()) return '';
    const mine = get().lessons.find((l) => l.id === id);
    if (!mine) return 'That lesson is gone.';
    const next: Record<string, unknown> = {};
    if (patch.title !== undefined) {
      const t = patch.title.trim();
      if (!t) return 'Give the lesson a title.';
      next.title = t;
    }
    if (patch.category !== undefined) next.category = patch.category;
    if (patch.kind !== undefined) next.kind = patch.kind;
    if (patch.body !== undefined) {
      if (!richHasText(patch.body)) return 'Say what happened.';
      next.body = patch.body;
    }
    if (patch.recommendation !== undefined) {
      // Optional: a recommendation with real text is stored; an empty one or an
      // explicit null (the toggle turned off) clears it, written as null on the wire.
      next.recommendation = richHasText(patch.recommendation ?? undefined) ? patch.recommendation : null;
    }
    if (patch.owner !== undefined) next.owner = patch.owner?.trim() ? patch.owner.trim() : null;
    if (Object.keys(next).length) {
      next.updatedAt = Date.now();
      await backend.patch(`${C.lessons}/${id}`, next);
      // SHOW IT NOW — mirror the patch locally (a cleared recommendation/owner is
      // null on the wire, undefined on the Lesson) so the edit shows before the echo.
      set((s) => ({
        lessons: s.lessons.map((l) => {
          if (l.id !== id) return l;
          const merged: Lesson = { ...l, updatedAt: next.updatedAt as number };
          if (next.title !== undefined) merged.title = next.title as string;
          if (next.category !== undefined) merged.category = next.category as LessonCategory;
          if (next.kind !== undefined) merged.kind = next.kind as LessonKind;
          if (next.body !== undefined) merged.body = next.body as RichDoc;
          if (next.recommendation !== undefined)
            merged.recommendation = (next.recommendation as RichDoc | null) ?? undefined;
          if (next.owner !== undefined) merged.owner = (next.owner as string | null) ?? undefined;
          return merged;
        }),
      }));
    }
    return '';
  },

  deleteLesson: async (id) => {
    if (sampleBlocks()) return '';
    const mine = get().lessons.find((l) => l.id === id);
    if (!mine) return 'That lesson is gone.';
    await backend.remove(`${C.lessons}/${id}`);
    // SHOW IT NOW — drop it locally and clear any pending insert, so a stale snapshot
    // cannot resurrect a just-removed lesson (the watcher honours pendingLessons).
    pendingLessons.delete(id);
    set((s) => ({ lessons: s.lessons.filter((l) => l.id !== id) }));
    return '';
  },

  /* ── The Plan view: steps inside a phase, and its history — #377. ───────── */

  addStep: async (phaseId, name) => {
    if (sampleBlocks()) return '';
    const trimmed = name.trim();
    if (!trimmed) return 'Give the step a name.';
    if (!get().phases.some((p) => p.id === phaseId)) return 'That phase is not on this board.';
    const id = uid();
    const last = stepsOf(get().phaseSteps, phaseId).reduce((n, s) => Math.max(n, s.position), -1);
    await backend.set(`${phaseStepsPath()}/${id}`, {
      id,
      phaseId,
      name: trimmed,
      assignee: '',
      status: 'todo' as StepStatus,
      blockedReason: '',
      signers: [] as StepSigner[],
      position: last + 1,
      addedAt: Date.now(),
    });
    await recordPhaseEvent(phaseId, 'added_step', `added step ${trimmed}`);
    return '';
  },

  editStep: async (id, patch) => {
    if (sampleBlocks()) return '';
    const mine = get().phaseSteps.find((s) => s.id === id);
    if (!mine) return 'That step is not on this board.';
    if (patch.name !== undefined && !patch.name.trim()) return 'Give the step a name.';
    const next: Record<string, unknown> = {};
    if (patch.name !== undefined) next.name = patch.name.trim();
    if (patch.assignee !== undefined) next.assignee = patch.assignee;
    // null clears the due day back to "nobody has said"; a string sets it.
    if (patch.due !== undefined) next.due = patch.due || null;
    if (patch.status !== undefined) next.status = patch.status;
    if (patch.blockedReason !== undefined) next.blockedReason = patch.blockedReason;
    if (Object.keys(next).length) await backend.patch(`${phaseStepsPath()}/${id}`, next);
    return '';
  },

  retireStep: async (id) => {
    const mine = get().phaseSteps.find((s) => s.id === id);
    if (!mine) return 'That step is not on this board.';
    if (mine.retiredAt) return '';
    await backend.patch(`${phaseStepsPath()}/${id}`, { retiredAt: Date.now() });
    return '';
  },

  setStepSigners: async (id, signers) => {
    if (sampleBlocks()) return '';
    const mine = get().phaseSteps.find((s) => s.id === id);
    if (!mine) return 'That step is not on this board.';
    await backend.patch(`${phaseStepsPath()}/${id}`, { signers });
    return '';
  },

  signStep: async (id, person) => {
    if (sampleBlocks()) return '';
    const mine = get().phaseSteps.find((s) => s.id === id);
    if (!mine) return 'That step is not on this board.';
    const who = (mine.signers ?? []).find((s) => s.person === person);
    if (!who) return 'That person is not asked to sign this step.';
    if (who.signedAt) return '';
    const signers = (mine.signers ?? []).map((s) =>
      s.person === person ? { ...s, signedAt: new Date().toISOString() } : s
    );
    await backend.patch(`${phaseStepsPath()}/${id}`, { signers });
    await recordPhaseEvent(
      mine.phaseId,
      'signed_step',
      `signed off ${mine.name}${who.role ? ` as ${who.role}` : ''}`
    );
    return '';
  },

  signPhase: async (phaseId) => {
    if (sampleBlocks()) return '';
    const phase = get().phases.find((p) => p.id === phaseId);
    if (!phase) return 'That phase is not on this board.';
    if (!phaseSignable(get().phaseSteps, phaseId))
      return 'Every step has to be signed off before the phase can be.';
    await backend.patch(`${phasesPath()}/${phaseId}`, { status: 'done' as PhaseStatus });
    await recordPhaseEvent(phaseId, 'signed_phase', `signed off the ${phase.name} phase`);
    return '';
  },

  /* ── Sprints ───────────────────────────────────────────────────────────── */

  createSprint: async (draft) => {
    if (sampleBlocks()) return '';
    const problem = sprintProblem(draft, get().sprints);
    if (problem) return problem;
    const id = uid();
    await backend.set(`${sprintsPath()}/${id}`, {
      id,
      name: draft.name.trim(),
      goal: (draft.goal ?? '').trim(),
      startsOn: draft.startsOn,
      endsOn: draft.endsOn,
      /*
       * PLANNED, NEVER RUNNING. Creating one that starts immediately would be a
       * convenience that quietly stops the sprint before it from being closed
       * properly — and the database refuses a second running sprint, so it
       * would fail with a constraint name rather than a sentence. Starting is
       * its own decision, with its own button.
       */
      state: 'planned',
      createdAt: Date.now(),
    });
    return '';
  },

  editSprint: async (id, patch) => {
    if (sampleBlocks()) return '';
    const mine = get().sprints.find((s) => s.id === id);
    if (!mine) return 'That sprint is not on this board any more.';
    // Checked against the whole list including itself, which is why the id is
    // passed: a sprint always overlaps its own dates.
    const problem = sprintProblem({ ...mine, ...patch }, get().sprints, id);
    if (problem) return problem;
    await backend.patch(`${sprintsPath()}/${id}`, patch);
    /*
     * SHOW IT NOW, don't wait for the round trip — the same optimistic set
     * updateIssue and reRaiseSprintSignoff already do. The realtime watcher
     * replaces `sprints` with the authoritative set a beat later, this same
     * row keyed by id, so no duplicate and no drift. Without this, an edited
     * end date (or any changed field) sat stale until the screen was manually
     * refreshed, because the view reads `sprints` reactively but nothing here
     * moved it.
     */
    set({ sprints: get().sprints.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
    return '';
  },

  startSprint: async (id) => {
    if (sampleBlocks()) return '';
    const all = get().sprints;
    const mine = all.find((s) => s.id === id);
    if (!mine) return 'That sprint is not on this board any more.';
    /*
     * ASKED HERE FIRST even though the database enforces it. The unique index is
     * what makes "one sprint is running" true; this is what makes the refusal a
     * sentence somebody can act on rather than `sprints_one_running`.
     */
    const problem = startProblem(mine, all);
    if (problem) return problem;
    await backend.patch(`${sprintsPath()}/${id}`, { state: 'active', startedAt: Date.now() });
    return '';
  },

  requestSprintSignoff: async (sprintId, quietly = false) => {
    if (sampleBlocks()) return '';
    const sprint = get().sprints.find((x) => x.id === sprintId);
    if (!sprint) return quietly ? '' : 'That sprint is not on this board any more.';
    /*
     * THE LATCH. Finishing, reopening and finishing a sprint again must not
     * send the same people a second sheet — so the date on the sprint decides,
     * not whether rows happen to exist. See migration 0075.
     */
    if (sprint.signoffRequestedAt)
      return quietly ? '' : 'Approval has already been asked for on this sprint.';

    /*
     * ⚠️ THE TYPED NAMES ARE THE BOARD'S EXISTING PANEL, NOT A SECOND LIST.
     *
     * The rule stores RANKS and the sprint switch; who is typed in comes from
     * `meta.signatories`, which is the list Settings has always shown under
     * "Who signs work off here". Storing names in the rule as well would give
     * the product two lists of signatories, and a product with two lists is one
     * where somebody edits the wrong one and cannot see why the sheet is wrong.
     *
     * The owner is not read from here either — they arrive through the `owner`
     * rank, so removing them from the panel and removing them from the rule are
     * the same act rather than two that can disagree.
     */
    /* THE SPRINT'S OWN POLICY — its override if the board set one, else the board
       default. `policyForType` reads the same field, and a board with no sprint
       override resolves byte-for-byte what `readPolicy` did before. */
    const policy = { ...policyForType(get().meta.signoffPolicy, 'sprint'), named: get().meta.signatories ?? [] };
    const world = theWorld(get());
    const wrong = policyProblem(policy, world);
    /*
     * ⚠️ A MISSING RULE NEVER STOPS A SPRINT FINISHING. The automatic raise is
     * a convenience on top of closing a fortnight of work; refusing to close it
     * because Settings is incomplete would hold the board hostage to a setting
     * somebody has not reached yet. Said out loud when a person pressed the
     * button, silent when the sprint raised it for itself.
     */
    if (wrong) return quietly ? '' : wrong;

    /*
     * THE WHOLE CHAIN AT ONCE, EACH ROW CARRYING ITS OWN STEP — #346.
     *
     * Every step's rows are written now rather than the first step's now and
     * the rest later, and that is deliberate: an auditor has to be able to see
     * what the sheet was going to ask before anybody answered, and a sheet that
     * grows a row after the fact cannot be told from one somebody added. What
     * the step gates is when a row may be ANSWERED, not when it exists.
     */
    const who = sheetFor(policy, world, theDeveloper(get()));
    const at = Date.now();
    /* WHO IS SENDING THIS OUT, stamped on every row so a decision has somebody
       to tell (0105). Frozen here for the same reason the signatory is: the
       record is who actually raised it, read once. */
    const raisedBy = (get().user?.email ?? '').trim().toLowerCase();
    /* THE ROWS THAT STILL NEED A SIGNATURE AND CAN BE REACHED — kept as they are
       written, so the sprint can post each approver their link the moment it is
       raised, exactly as a release does (requestSignoff). The developer attested
       by raising and has nothing to answer; a row with no address cannot be
       emailed and is left for the in-app sheet and the day-later reminder. */
    const toPost: { id: string; name: string; position: string }[] = [];
    try {
      /*
       * The rows first, the date last — the same order and the same reason as
       * `requestSignoff` above: written the other way round, a failure halfway
       * leaves a sprint marked as asked with nobody to ask, which reads as a
       * sign-off everybody ignored rather than as a write that did not finish.
       */
      for (const [seq, row] of who.entries()) {
        const person = row.who;
        const rowId = uid();
        const email = (person.email ?? '').trim().toLowerCase();
        await backend.set(`${inProject(get().projectId).signoffs}/${rowId}`, {
          sprintId,
          seq,
          raisedBy,
          /* WHICH STEP THIS ROW IS, written at the time. A finished sheet looks
             the same whether the steps were asked in order or all at once, so
             this is the only moment the order can be recorded at all. */
          step: row.step,
          /* THE FROZEN STAGE ORDER — configurable sign-offs R1. On the stage
             model each row carries the dense rank of its stage; the board's stage
             definition is editable after this, so the order asked in is written
             onto the row now or it cannot be enforced later. Absent on the rank
             and slots models, whose rows order by `step` alone. */
          ...(row.stageSeq != null ? { stageSeq: row.stageSeq } : {}),
          ...(row.stageMode != null ? { stageMode: row.stageMode } : {}),
          name: person.name.trim(),
          position: person.position.trim(),
          /* FROZEN HERE. The rule is read once, at this moment, and what it
             produced is the record — an auditor reading this next March must
             see who it actually went to, not who would be asked then. */
          signatoryEmail: email,
          /* ATTESTED ON RAISING — the developer slot. The person raising this
             signs it by raising it, so their row is written already-signed and
             agreed; there is nothing to chase. Every other row waits for a
             person. An attested row gates nothing: step '' sorts first and a
             signed row never blocks another (see the trigger in 0077, which
             fires only on an answer being given, never on an insert). */
          signedAt: row.attested ? at : null,
          ...(row.attested ? { answer: 'approved' as const } : {}),
        });
        if (!row.attested && email)
          toPost.push({ id: rowId, name: person.name.trim(), position: person.position.trim() });
      }
      await backend.patch(`${sprintsPath()}/${sprintId}`, { signoffRequestedAt: at });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'Approval could not be asked for. Nothing was recorded — try again.'
      );
    }
    // The ask is recorded by the database on the sprint row now —
    // record_sprint_signoff_request (migration 0168) counts the rows and writes
    // the line, kind 'project' (the same kind the release sign-off uses; a
    // `signoff` kind of its own would need a badge and a label for the same sort
    // of thing said about a different subject). Recorded once, by any writer.

    /*
     * AND POST THE APPROVERS THEIR LINK NOW — the same on-raise send a release
     * gets (requestSignoff, Vinnie's 29 September fix), so a sprint approval no
     * longer goes out in silence: each approver is emailed a signing link the
     * moment it is raised, which reaches someone with no account too. Outside the
     * write above and each on its own — a link that will not mint must not undo a
     * request that was correctly made; a failed send is chased by the day-later
     * reminder and re-sendable by hand, the same fallback the release path has.
     */
    for (const p of toPost) {
      try {
        const r = await mintSigningLink(get().projectId, p.id, '');
        void get().recordAudit(
          'project',
          r.emailed
            ? `emailed ${p.name} (${p.position}) a signing link for "${sprint.name}"`
            : `sent ${p.name} (${p.position}) a signing link for "${sprint.name}"`
        );
      } catch {
        /* Reported by the reminder and re-sendable from the sheet; never fatal
           to a sign-off that has already been raised. */
      }
    }
    return '';
  },

  /**
   * CLEARING A REFUSED SPRINT SIGN-OFF — Vinnie, 11 September 2026, migration 0094.
   *
   * The sprint has no document to move back to draft; its sheet is latched by
   * `signoffRequestedAt` (0075). Clearing that latch is the withdraw/dismiss move,
   * and the trigger in 0094 does the row-work when it goes back to null — keeping
   * the refused rows (stamped dismissed) so the refusal shows under settled, and
   * withdrawing the rest. Manager/owner is enforced by that trigger; the checks
   * here are so the person reads a sentence rather than a raised error.
   */
  dismissSprintSignoff: async (sprintId) => {
    if (sampleBlocks()) return '';
    const sprint = get().sprints.find((x) => x.id === sprintId);
    if (!sprint) return 'That sprint is not on this board any more.';
    if (!sprint.signoffRequestedAt) return 'Approval has not been asked for on this sprint.';
    /* Only a REFUSAL is dismissed — a sprint still waiting is not "dealt with"
       and keeping a "was refused" record of it would be a lie. */
    const refused = get().signoffs.filter((r) => r.sprintId === sprintId && r.answer === 'rejected');
    if (refused.length === 0)
      return 'Only a refused sprint sign-off can be dismissed.';
    try {
      /* The latch back to null; the trigger keeps the refused rows (withdrawn +
         dismissed) and withdraws the rest, all in one statement. */
      await backend.patch(`${sprintsPath()}/${sprintId}`, { signoffRequestedAt: null });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'The refusal could not be dismissed. Nothing was changed — try again.'
      );
    }
    // Recorded by the database on the sprint row (the latch going back to null) —
    // record_sprint_signoff_request (migration 0168), which tells a dismissal
    // from any other clearing by the refused row that stands.
    return '';
  },

  /**
   * DEAL WITH A REFUSED SPRINT BY ASKING AGAIN. Dismiss clears the refusal and
   * releases the latch; then a fresh round is raised on the same sprint.
   */
  reRaiseSprintSignoff: async (sprintId) => {
    const cleared = await get().dismissSprintSignoff(sprintId);
    if (cleared) return cleared;
    /* Release the latch locally so the fresh request below is not refused by a
       still-latched copy while the realtime round-trip catches up. If the raise
       fails, the sprint is left cleared (no request), which can be raised again. */
    set({
      sprints: get().sprints.map((s) => (s.id === sprintId ? { ...s, signoffRequestedAt: null } : s)),
    });
    const asked = await get().requestSprintSignoff(sprintId);
    if (asked) return `The refusal was cleared, but a fresh request could not be sent: ${asked}`;
    return '';
  },

  sprintSignoffDoc: async (sprintId, kind) => {
    const sprint = get().sprints.find((x) => x.id === sprintId);
    if (!sprint) return { id: '', problem: 'That sprint is not on this board any more.' };

    /* ONE OF EACH PHASE PER SPRINT, NOT A NEW ONE EACH TIME. A superseded draft
       is skipped — it has been replaced — but any live document of this phase
       for this sprint is the record, so a second press opens it rather than
       making a duplicate that splits the record in two. */
    const existing = get().documents.find(
      (d) => d.kind === kind && d.sprint === sprintId && d.state !== 'superseded'
    );
    if (existing) return { id: existing.id, problem: '' };

    /* A real uuid, not a short id — see draftDocument: documents.id is a uuid
       and the write layer passes a uuid through but hashes a short id into a
       different one, orphaning the optimistic row from the row read back. */
    const id = crypto.randomUUID();
    /* Named after the sprint and its phase, so the Documents list reads as the
       record of that fortnight rather than as "Change request" over and over. */
    const title = `${sprint.name} — ${kind === 'change' ? 'authorisation' : 'deliverables'}`;
    try {
      await backend.set(`${inProject(get().projectId).documents}/${id}`, {
        kind,
        title,
        body: '',
        state: 'draft',
        version: 1,
        sprint: sprintId,
        createdAt: Date.now(),
      });
    } catch (e) {
      return {
        id: '',
        problem:
          (e as { message?: string })?.message ||
          'That document could not be created. Nothing was saved — try again.',
      };
    }
    // The "drafted" line is written by the database now — record_document_change
    // (migration 0160) — on the INSERT itself, naming the document by its kind and
    // title (the title already carries the sprint and whether it is the
    // authorisation or the deliverables). Recording it here too would double it.
    return { id, problem: '' };
  },

  closeSprint: async (id, leftovers) => {
    const mine = get().sprints.find((s) => s.id === id);
    if (!mine) return 'That sprint is not on this board any more.';
    if (mine.state !== 'active') return 'That sprint is not running.';

    /*
     * THE WORK MOVES BEFORE THE SPRINT CLOSES, and the order is the whole of it.
     * Closing first and then moving leaves a window where the cards belong to a
     * finished fortnight — and if anything fails in between, that is where they
     * stay, with nothing on any screen saying so. This way a failure leaves the
     * sprint running, which is a state somebody can see and press again.
     */
    const left = unfinishedIn(get().issues, id);
    if (left.length) {
      const into = leftovers === 'carry' ? nextAfterSprint(get().sprints) : undefined;
      /*
       * ASKED FOR AND NOT AVAILABLE IS A REFUSAL, not a quiet fall back to the
       * backlog. The two answers mean different things about whether the work is
       * still committed, so substituting one for the other is exactly the silent
       * wrong number this is built to prevent.
       */
      if (leftovers === 'carry' && !into)
        return 'There is no planned sprint to carry the work into. Plan one, or send it back to the backlog.';
      // One at a time rather than in parallel: each writes an activity entry as
      // well as the card, and a half-applied batch is worse than a slow one.
      for (const i of left) await get().putInSprint(i.id, into?.id ?? '');
    }

    await backend.patch(`${sprintsPath()}/${id}`, { state: 'closed', closedAt: Date.now() });

    /*
     * ── AND IT ASKS FOR ITSELF — #332 ──────────────────────────────────────
     *
     * Vinnie's first ask of the whole card, 1 September 2026: *"Once a sprint
     * has been finished it should automatically send for sign-off"*. Nothing
     * prompted one before, so it only happened when somebody remembered.
     *
     * ⚠️ AFTER THE CLOSE AND UNABLE TO UNDO IT. The sprint is finished the
     * moment the line above lands; this is a thing that happens next. Raising
     * first would let a sign-off exist against a fortnight that then failed to
     * close, and `quietly` means a board with no rule yet finishes exactly as
     * it did before this existed.
     */
    const finishPolicy = readPolicy(get().meta.signoffPolicy);
    if (finishPolicy.onSprintFinish) {
      /* ⚠️ TWO MODELS PART HERE. A slots board's approval is a deliverables
         document the developer edits and then sends — so finishing drafts that
         document and leaves it for them, rather than sending anything out; the
         sprint reads as awaiting sign-off until the document is sent and signed.
         A rank board keeps exactly what #332 built: the sign-off raised for
         itself. `usesSlots` is the same switch every other path reads. */
      if (usesSlots(finishPolicy)) void get().sprintSignoffDoc(id, 'deliverables');
      else void get().requestSprintSignoff(id, true);
    }
    return '';
  },

  deleteSprint: async (id) => {
    if (sampleBlocks()) return;
    // The cards come back to the backlog rather than going with it — held by
    // the foreign key, not by a loop here. See migration 0019.
    await backend.remove(`${sprintsPath()}/${id}`);
  },

  putInSprint: async (issueId, sprintId) => {
    const before = get().issues.find((i) => i.id === issueId);
    const was = before?.sprint ?? '';
    if (was === sprintId) return;
    const stamp = Date.now();

    /*
     * ── THE SCREEN MOVES FIRST ─────────────────────────────────────────────
     *
     * This used to write and wait. The card stayed in the list until the
     * database echoed the change back through the listener, which on a full
     * board is a second or two of a button that appears to have done nothing —
     * so people press it again. Vinnie, 19 August 2026: "I would like it to be
     * more responsive… This needs to run smoothly and cannot have any lagging."
     *
     * It also hid a fault. When the write silently changed nothing, the screen
     * not moving was indistinguishable from the screen being slow. Moving first
     * and putting the card BACK on failure makes the two impossible to confuse:
     * a refusal is now a card that visibly returns, with a reason beside it.
     */
    set({
      issues: get().issues.map((i) =>
        i.id === issueId ? { ...i, sprint: sprintId || undefined, addedToSprintAt: sprintId ? stamp : undefined } : i
      ),
    });

    /* THE RELEASE-PRIORITY NUDGE — #6. Being added to a sprint with no level yet
       is a scope decision, so nudge it; leaving a sprint is not. The Sprints
       screen renders the same inline prompt the Plan does. */
    if (sprintId && before && !before.moscow) {
      set({ moscowNudge: [...new Set([...get().moscowNudge, issueId])] });
    }

    try {
      await backend.patch(issuePath(issueId), {
        sprint: sprintId || null,
        /*
         * STAMPED ON THE WAY IN, cleared on the way out. This is the whole of
         * committed-versus-added, and it can only be recorded at the moment it
         * happens — see the field's own note in src/types.ts.
         */
        addedToSprintAt: sprintId ? stamp : null,
        updatedAt: stamp,
      });
    } catch (e) {
      /* Put it back exactly as it was, and let the screen say why. */
      set({
        issues: get().issues.map((i) =>
          i.id === issueId ? { ...i, sprint: before?.sprint, addedToSprintAt: before?.addedToSprintAt } : i
        ),
      });
      throw e;
    }
    /* Written by the database — 0030 covers sprint_id too, and looks the names
       up rather than trusting whatever this browser had loaded. */
  },

  /**
   * ASK THE PANEL TO SIGN A RELEASE.
   *
   * The rows are COPIED here and never follow the panel again. Somebody signs
   * against a named list of people; adding a fifth afterwards would change what
   * they agreed to, retroactively, without asking them.
   *
   * Asked once. A second request would either duplicate the rows or silently
   * discard signatures already given, and both are worse than being told no.
   */
  requestSignoff: async (buildId) => {
    if (sampleBlocks()) return '';
    const build = get().builds.find((b) => b.id === buildId);
    if (!build) return 'That release is not on this board.';
    if (build.signoffRequestedAt) return 'Sign-off has already been asked for on this release.';

    /*
     * ── ONE SHEET, WHICHEVER MODEL THE BOARD IS ON — the redesign ────────────
     *
     * A slots board resolves a release from the same rule as a sprint, minus the
     * tester (see below): the developer attested on raising, then the approver,
     * then the owner where the board turns it on, each carrying its step. A board
     * still on the rank model keeps the release's own flat panel — the owner and
     * everybody named, all at once, with no chain — which is what a release has
     * always used and is untouched.
     */
    /* THE RELEASE'S OWN POLICY — its override if the board set one, else the board
       default (`policyForType(..., 'release')`). A board with no release override
       resolves exactly what `readPolicy` did before. */
    const board = { ...policyForType(get().meta.signoffPolicy, 'release'), named: get().meta.signatories ?? [] };
    /*
     * A RELEASE IS NOT RE-TESTED, WHICHEVER MODEL THE BOARD IS ON — the tester
     * is off the release sheet. The UAT tester signs the SPRINT's work; a
     * release is the internal go-ahead to cut it, approved by the approver and,
     * where the board names one, the owner (Vinnie, 29 September 2026). So the
     * tester comes off both ways the board can name one:
     *   · slots  — the UAT slot forced off, leaving developer, approver, owner.
     *   · rank / named panel — the signatory TAGGED as the tester (`step: 'uat'`)
     *     dropped from the panel. A plain board tags nobody, so nothing is
     *     dropped and its release panel is untouched.
     */
    const policy = board.slots ? { ...board, slots: { ...board.slots, uat: false } } : board;
    /* A STAGES board does not force anything above: a release signs through the
       stage set configured for the RELEASE type (its override, or the default).
       If such a board wants no tester on a release, it configures the release
       type without one — the drop is a configuration choice, not a code rule
       (Vinnie, 30 September 2026). Slots and rank/named panels keep the fixed
       tester-drop above. */
    const rows: SheetRow[] = usesSlots(policy) || usesStages(policy)
      ? sheetFor(policy, theWorld(get()), theDeveloper(get()))
      : thePanel(get().meta)
          .filter((who) => (who.step ?? '') !== 'uat')
          .map((who) => ({ step: '' as const, who }));

    if (usesSlots(policy) || usesStages(policy)) {
      const wrong = policyProblem(policy, theWorld(get()));
      if (wrong) return wrong;
    }
    if (rows.length === 0)
      return 'Nobody is named as signing work off here. Add them in Settings first.';

    const at = Date.now();
    /* WHO IS SENDING THIS OUT — stamped on every row so a decision has somebody
       to tell (0105). See requestSprintSignoff for why it is frozen here. */
    const raisedBy = (get().user?.email ?? '').trim().toLowerCase();
    /* THE ROWS THAT STILL NEED A SIGNATURE AND CAN BE REACHED — kept as they are
       written, so the release can post each approver their link the moment it is
       raised. The developer attested by raising and has nothing to answer; a row
       with no address cannot be emailed and is left for the in-app sheet. Name
       and position ride along for the audit line, matching issueSigningLink. */
    const toPost: { id: string; name: string; position: string }[] = [];
    try {
      /*
       * The rows first, the date last. Written the other way round, a failure
       * halfway leaves a release marked as asked with nobody to ask — which
       * reads as a sign-off that everybody ignored rather than as a write that
       * did not finish.
       */
      for (const [seq, row] of rows.entries()) {
        const who = row.who;
        const id = uid();
        const email = (who.email ?? '').trim().toLowerCase();
        await backend.set(`${inProject(get().projectId).signoffs}/${id}`, {
          build: build.build,
          seq,
          raisedBy,
          step: row.step,
          /* THE FROZEN STAGE ORDER — configurable sign-offs R1; see
             requestSprintSignoff for why it is written at the moment of raising.
             Absent on the rank and slots models. */
          ...(row.stageSeq != null ? { stageSeq: row.stageSeq } : {}),
          ...(row.stageMode != null ? { stageMode: row.stageMode } : {}),
          name: who.name.trim(),
          position: who.position.trim(),
          /* COPIED, LIKE THE POSITION. The panel is a default and this is the
             record — a signatory who is given an account tomorrow does not
             retroactively become able to sign something asked for today. */
          signatoryEmail: email,
          /* ATTESTED ON RAISING — the developer slot; see requestSprintSignoff
             for why this gates nothing. Empty on the flat panel, where nobody
             is attested and every row waits for a person. */
          signedAt: row.attested ? at : null,
          ...(row.attested ? { answer: 'approved' as const } : {}),
        });
        if (!row.attested && email)
          toPost.push({ id, name: who.name.trim(), position: who.position.trim() });
      }
      await backend.patch(`${inProject(get().projectId).builds}/${build.build}`, {
        signoffRequestedAt: at,
      });
      /*
       * REMEMBERED HERE, BECAUSE THIS IS WHERE SOMEBODY BECOMES SOMEBODY WHO
       * SIGNS. The roster used to be written by the Settings screen on every
       * keystroke, which recorded a new person for each character typed into a
       * position. "Signed here before" now means what it says.
       */
      const known = rememberAll(get().meta.signatoriesKnown ?? [], rows.map((r) => r.who));
      if (known.length !== (get().meta.signatoriesKnown ?? []).length)
        get().setMeta({ signatoriesKnown: known });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'Sign-off could not be asked for. Nothing was recorded — try again.'
      );
    }
    // The ask is recorded by the database on the release row now —
    // record_build_change (migration 0168) counts the rows and writes the line —
    // so a sign-off asked for by any writer is recorded once.

    /*
     * AND POST THE APPROVERS THEIR LINK NOW — the release's own rule, Vinnie's
     * 29 September fix. A release sign-off used to be raised in silence: the
     * approver was named on a sheet nobody told them about, and the release sat
     * unasked until a manager happened to open it and press "Send a link". So it
     * emails each approver the moment it is raised — reusing the manual path's
     * mint-and-post, which the sheet's button keeps for a re-send.
     *
     * OUTSIDE THE WRITE ABOVE, and each on its own. The sign-off is asked for
     * the moment its rows exist; a link that will not mint or a post office that
     * is shut must not undo a request that was correctly made. A row whose mail
     * fails is chased by the day-later reminder and can be re-sent by hand — the
     * same fallback the manual button has always had.
     */
    for (const p of toPost) {
      try {
        const r = await mintSigningLink(get().projectId, p.id, '');
        void get().recordAudit(
          'project',
          r.emailed
            ? `emailed ${p.name} (${p.position}) a signing link for "${build.version || build.id}"`
            : `sent ${p.name} (${p.position}) a signing link for "${build.version || build.id}"`
        );
      } catch {
        /* Reported by the reminder and re-sendable from the sheet; never fatal
           to a sign-off that has already been raised. */
      }
    }
    return '';
  },

  /**
   * RECORD A SIGNATURE SOMEBODY ELSE COULD NOT GIVE.
   *
   * Allowed because being locked out of your own release when an approver is on
   * leave is worse than a row honestly marked as weaker. Never disguised: the
   * database refuses this write unless it names the person doing it and carries
   * a reason, so a row recorded this way cannot be made to look like the
   * signatory's own act.
   */
  /**
   * ANSWERING YOUR OWN ROW.
   *
   * A DIFFERENT ACT FROM `signOnBehalf`, not a special case of it. That one
   * records what somebody else told you; this one is the signature itself. The
   * shapes differ where it matters: nothing about anybody else goes on the row,
   * and there is no "why can they not answer it themselves" to ask, because
   * they are answering it themselves.
   *
   * BEING NAMED IS THE PERMISSION, whatever your rank. Vinnie, 22 August, on a
   * developer asked to accept their own work: *"will they be able to sign even
   * as members and a reviewer? This is an important distinction that must be
   * clarified."* They can. Rank governs only answering FOR somebody.
   *
   * The database says the same in 0055, and it is the one that decides: the
   * policy matches the row's own `signatory_email` against who is signed in,
   * and a trigger refuses to let that column change afterwards.
   */
  answerMyRow: async (signoffId, answer, said = '') => {
    const row = get().signoffs.find((s) => s.id === signoffId);
    if (!row) return 'That signature is not on this board.';
    if (row.signedAt) return 'That row is already answered.';
    const me = (get().user?.email ?? '').trim().toLowerCase();
    if (!me || (row.signatoryEmail ?? '').trim().toLowerCase() !== me)
      return 'That row belongs to somebody else.';
    const theirs = said.trim();
    if (answer === 'rejected' && !theirs) return 'Say why you are rejecting it.';
    /*
     * ⚠️ THE ORDER IS ENFORCED HERE, NOT ONLY DRAWN — #346.
     *
     * The sheet hides the buttons on a step that is not open yet, and a screen
     * that hides a control is not a rule: this is the app's own write path and
     * anything that reaches it must be judged. Said in the same words the sheet
     * uses, so somebody who got here another way is told the same thing.
     */
    const locked = whyLocked(row.step ?? '', get().signoffs.filter((r) => sameSubject(r, row)), get().meta.signoffPolicy);
    if (locked) return locked;
    try {
      await backend.patch(`${inProject(get().projectId).signoffs}/${row.id}`, {
        signedAt: Date.now(),
        answer,
        why: answer === 'rejected' ? theirs : '',
      });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That could not be recorded. Only the person a row names may answer it.'
      );
    }
    // The History line is written by the database now, on the signature itself —
    // record_signoff_signature (migration 0165), so a row answered by any path,
    // not only this one, is recorded once and the same way.
    return '';
  },

  signOnBehalf: async (signoffId, reason, answer = 'approved', said = '') => {
    if (sampleBlocks()) return '';
    const row = get().signoffs.find((s) => s.id === signoffId);
    if (!row) return 'That signature is not on this board.';
    if (row.signedAt) return 'That row is already answered.';
    const why = reason.trim();
    if (!why) return 'Say why they could not answer it themselves.';
    /* TWO REASONS, AND THEY ARE DIFFERENT THINGS. `reason` is why the admin is
       pressing this; `said` is what the signatory actually gave for refusing.
       Collapsing them would put the admin's words in the signatory's mouth. */
    const theirs = said.trim();
    if (answer === 'rejected' && !theirs) return 'Say why they rejected it.';
    /* THE SAME GATE AS `answerMyRow` — #346. Recording somebody else's answer
       out of turn is the same act as answering out of turn, and a manager is
       the one person with the rank to do it by accident on every board. */
    const locked = whyLocked(row.step ?? '', get().signoffs.filter((r) => sameSubject(r, row)), get().meta.signoffPolicy);
    if (locked) return locked;
    const me = get().user?.email ?? '';
    try {
      await backend.patch(`${inProject(get().projectId).signoffs}/${row.id}`, {
        signedAt: Date.now(),
        answer,
        /* AN APPROVAL CARRIES NO "WHY THEY SAID NO". Somebody who typed a
           rejection reason, then changed the answer to approved, would
           otherwise leave that sentence behind on the row — invisible on
           screen, and read back by anything that goes looking. */
        why: answer === 'rejected' ? theirs : '',
        onBehalfBy: me,
        onBehalfReason: why,
      });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That could not be recorded. Only an admin may answer on somebody\'s behalf.'
      );
    }
    /*
     * THE REASON GOES INTO THE HISTORY, not only onto the row — because the row
     * does not always survive. Withdrawing a request clears every signature
     * against it, so a rejection recorded only there would take its reason with
     * it, and the reason is the whole point of recording a no. The line is
     * written by the database on the signature itself now —
     * record_signoff_signature (migration 0165) — which reads on_behalf_by and
     * on_behalf_reason off the row and tells an on-behalf answer apart from a
     * delegated-cover one.
     */
    return '';
  },

  /**
   * SIGN FOR SOMEBODY YOU COVER — the nominee's path (0130/0131).
   *
   * The same write as signOnBehalf — it fills a blank row, stamps on_behalf_by =
   * me and a reason, and never overwrites a real signature — but it is authorised
   * by an ACTIVE cover naming the signer rather than by being an admin, and the
   * reason is not typed: the cover is the reason, so it is stamped automatically.
   * The database decides with the same facts (signoffs_sign_as_cover, 0131); this
   * is the near side of it, so a missing or expired cover is said in words rather
   * than returned as a raw refusal.
   */
  signAsCover: async (signoffId, answer = 'approved', said = '') => {
    if (sampleBlocks()) return '';
    const row = get().signoffs.find((s) => s.id === signoffId);
    if (!row) return 'That signature is not on this board.';
    if (row.signedAt) return 'That row is already answered.';
    const me = (get().user?.email ?? '').trim().toLowerCase();
    const sig = (row.signatoryEmail ?? '').trim().toLowerCase();
    if (!me || !sig) return 'That row cannot be covered.';
    /* I must hold an active, unexpired cover for the person this row names. The
       database enforces the same (0131); checking here gives a plain message
       rather than a bare refusal, and keeps the button honest. */
    const now = Date.now();
    const cover = get().signoffCover.find(
      (c) => c.active && c.delegate === me && c.delegator === sig && (c.until == null || c.until >= now)
    );
    if (!cover) return 'You are not covering this person right now.';
    const theirs = said.trim();
    if (answer === 'rejected' && !theirs) return 'Say why they rejected it.';
    /* The same step-order gate as the other two signing paths — #346. */
    const locked = whyLocked(row.step ?? '', get().signoffs.filter((r) => sameSubject(r, row)), get().meta.signoffPolicy);
    if (locked) return locked;
    const reason = `Delegated cover — signing while ${row.name} is away`;
    try {
      await backend.patch(`${inProject(get().projectId).signoffs}/${row.id}`, {
        signedAt: Date.now(),
        answer,
        why: answer === 'rejected' ? theirs : '',
        onBehalfBy: me,
        onBehalfReason: reason,
      });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That could not be recorded. Your cover for this person may have ended.'
      );
    }
    // Recorded by the database on the signature — record_signoff_signature
    // (migration 0165). The stamped reason above is exactly how it tells a
    // delegated-cover answer apart from a plain on-behalf one.
    return '';
  },

  /**
   * A LINK OF THEIR OWN — #216.
   *
   * The people who sign a scope of works are a client's sponsor, an executive
   * director, a department head. None of them will be given an account, so
   * until now the only way to record their answer was somebody in the office
   * typing it for them. That is a note saying they agreed. This is the thing
   * itself, and the sheet can say which it was.
   *
   * THE TOKEN IS MADE HERE AND NEVER STORED. What goes to the database is
   * sha256 of it, so a copy of the table is not a set of working links, and
   * there is nothing on the server that could be handed to the wrong person.
   * The consequence is deliberate and worth saying out loud: a link cannot be
   * looked up later. Somebody who mislaid theirs gets a NEW one, and issuing it
   * kills the one before — which is what "send it again" already means to
   * everybody who has ever used email.
   *
   * TWO WEEKS, Vinnie's call on 22 August: long enough for somebody on leave,
   * short enough that a forwarded email next quarter is dead.
   */
  issueSigningLink: async (signoffId) => {
    const row = get().signoffs.find((s) => s.id === signoffId);
    const no = (problem: string) => ({ url: '', problem, emailed: false, to: '', why: '' });
    if (!row) return no('That signature is not on this board.');
    if (row.signedAt) return no('That row has already been answered.');
    /* A document has a file whose fingerprint we freeze against; a sprint or a
       release has no file, so there is nothing to fingerprint and no document to
       find. Look one up only when the row is against a document, and refuse only
       a row that is against nothing at all. */
    const doc = row.documentId ? get().documents.find((d) => d.id === row.documentId) : undefined;
    if (row.documentId && !doc) return no('That document is not on this board.');
    if (!row.documentId && !row.sprintId && row.build == null)
      return no('There is nothing on this row to send a link for.');
    /* What the link is for, named for the audit line — the document's title, the
       sprint's name, or the release version, whichever this row is against. */
    const subjectTitle =
      doc?.title ??
      (row.sprintId ? get().sprints.find((s) => s.id === row.sprintId)?.name : undefined) ??
      (row.build != null
        ? get().builds.find((b) => b.build === row.build)?.version || `Build ${row.build}`
        : undefined) ??
      'the sign-off';

    /*
     * MINT IT, WRITE ITS HASH, AND POST IT — all in `mintSigningLink`, shared
     * with the release auto-send so the two paths cannot drift.
     *
     * `askedAgainst` is WHAT THEY ARE BEING ASKED ABOUT, not merely which
     * document. A file replaced after the link went out no longer matches, and
     * the page says so rather than collecting an approval for something else. A
     * sprint or a release has no file, so there is nothing to fingerprint.
     *
     * A FAILURE TO MINT is the manager being told only a manager may send one;
     * the hash was not written, so nothing is left half-done. A failure to POST
     * is not a failure of the link — the hash is written, the link works, and
     * the manager is told it did not go and sends it themselves.
     */
    let sent: { url: string; emailed: boolean; to: string; why: string };
    try {
      sent = await mintSigningLink(get().projectId, row.id, doc?.fileHash ?? '');
    } catch (e) {
      return no(
        (e as { message?: string })?.message ||
          'That link could not be made. Only a manager can send one.'
      );
    }

    void get().recordAudit(
      'project',
      sent.emailed
        ? `emailed ${row.name} (${row.position}) a signing link for "${subjectTitle}"`
        : `sent ${row.name} (${row.position}) a signing link for "${subjectTitle}"`
    );
    return { url: sent.url, problem: '', emailed: sent.emailed, to: sent.to, why: sent.why };
  },

  /**
   * SAY THAT A SIGNED RELEASE WENT OUT.
   *
   * The check below is a courtesy, not the guarantee: the database refuses this
   * write while any row is outstanding, whatever this function believes. Both
   * exist because the screen should explain rather than let somebody press a
   * button and read a Postgres error — but the one that matters is the one
   * nothing can walk around.
   */
  /**
   * CUT IT — the act approval earns.
   *
   * Separate from going live because they are separate days: a release is
   * approved on Tuesday and deployed on Thursday night, and a record that
   * cannot tell those apart cannot answer either question afterwards.
   */
  cutTheRelease: async (buildId) => {
    if (sampleBlocks()) return '';
    const build = get().builds.find((b) => b.id === buildId);
    if (!build) return 'That release is not on this board.';
    if (build.cutAt) return 'That release has already been cut.';
    const mine = get().signoffs.filter((r) => r.build === build.build);
    if (!readyToCut(mine)) {
      const waiting = mine.filter((r) => !r.signedAt).length;
      return `${waiting} ${waiting === 1 ? 'signature is' : 'signatures are'} still outstanding, so this cannot be cut yet.`;
    }
    try {
      await backend.patch(`${inProject(get().projectId).builds}/${build.build}`, {
        cutAt: Date.now(),
      });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That release could not be cut. Nothing was changed — try again.'
      );
    }
    /* "cut …" is written by the database now (record_build_change, 0161). */
    return '';
  },

  markGoneLive: async (buildId) => {
    if (sampleBlocks()) return '';
    const build = get().builds.find((b) => b.id === buildId);
    if (!build) return 'That release is not on this board.';
    if (build.wentLiveAt) return 'That release is already marked as gone to live.';
    /* The database says the same thing — 0050 — and this is so the reason is a
       sentence rather than a raise. */
    if (!build.cutAt) return 'This release has not been cut yet, so it cannot go live.';
    const waiting = get().signoffs.filter((r) => r.build === build.build && !r.signedAt);
    if (waiting.length)
      return `${waiting.length} ${waiting.length === 1 ? 'signature is' : 'signatures are'} still outstanding.`;
    const no = get().signoffs.filter((r) => r.build === build.build && r.answer === 'rejected');
    if (no.length)
      return `${no.map((r) => r.name).join(', ')} rejected this release, so it cannot go live.`;
    try {
      await backend.patch(`${inProject(get().projectId).builds}/${build.build}`, {
        wentLiveAt: Date.now(),
      });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That could not be recorded. Nothing was changed — try again.'
      );
    }
    /* "marked … as gone to live" is written by the database now (0161). */
    return '';
  },

  /**
   * A NEW DOCUMENT, ALWAYS AS A DRAFT — the policy in 0044 refuses any other
   * starting state, so nobody can create one that is already approved.
   */
  draftDocument: async (kind, title, sprint) => {
    const name = title.trim();
    if (!name) return { id: '', problem: 'A document needs a title before it can be saved.' };
    /*
     * A REAL UUID, NOT A SHORT id — because documents.id is a Postgres uuid
     * (0044) and the write layer PASSES A UUID THROUGH but HASHES a short id
     * into a different uuid (paths.ts). A short id therefore never equals the
     * id the row is read back under: the optimistic row (short id) and the
     * snapshot row (hashed uuid) are two different keys, so the list showed the
     * new document twice, the open editor stayed bound to the orphaned short id
     * (so a produced PDF never registered until reload), and the storage folder
     * was a non-uuid that the bucket policy's `::uuid` cast rejected (produce
     * again / delete → 400). A client uuid is written and read back unchanged,
     * so optimistic === persisted and all three faults go away.
     */
    const id = crypto.randomUUID();
    const createdAt = Date.now();
    const row: Doc = {
      id,
      kind,
      title: name,
      body: '',
      state: 'draft',
      version: 1,
      createdAt,
      ...(sprint ? { sprint } : {}),
      // A charter authored in-app starts on the standard section skeleton, so a
      // PM is not staring at a blank page. An uploaded charter overwrites this
      // with a file; every other kind never carries it.
      ...(kind === 'charter' ? { charter: CHARTER_TEMPLATE } : {}),
      // An after-action report opens already written: its first three sections are
      // composed from the board at creation (the fourth is the person's), so a
      // closing PM edits a draft, not a blank page. Frozen into `aar` here; the
      // editor regenerates nothing (a closing retrospective is a point-in-time
      // record, unlike a change request). See aar.ts / migration 0174.
      ...(kind === 'aar'
        ? {
            aar: composeAar(
              aarInputsFromState({
                project: get().projects.find((p) => p.id === get().projectId),
                issues: get().issues,
                decisions: get().decisions,
                documents: get().documents,
                builds: get().builds,
              }),
            ),
          }
        : {}),
    };
    try {
      await backend.set(`${inProject(get().projectId).documents}/${id}`, row);
    } catch (e) {
      return {
        id: '',
        problem:
          (e as { message?: string })?.message ||
          'That document could not be created. Nothing was saved — try again.',
      };
    }
    /*
     * SHOW IT THE INSTANT IT IS SAVED, don't wait for the realtime echo. The
     * write has persisted; the collection subscription will replace this list
     * with the authoritative set (this same row, so no duplicate — it is keyed
     * by id and sorted the same way) a beat later. Without this, a caller that
     * opens the new document immediately — a fresh charter opening its editor —
     * finds it missing from `documents` and falls back to the list, and for a
     * charter the "already there" guard is briefly blind, so a second could be
     * made. Newest first, matching the subscription's own order.
     */
    /* Hold it until a live snapshot confirms it, so the stale first snapshot
       after this write cannot drop it and collapse the editor the create flow
       is about to open — see `pendingDocs`. */
    pendingDocs.add(id);
    set((s) => ({
      documents: [row, ...s.documents.filter((d) => d.id !== id)].sort(
        (a, b) => b.createdAt - a.createdAt
      ),
    }));
    /* The History line — "drafted …" — is the database's now (record_document_change, 0160);
       written once, in the one place that sees every writer. */
    return { id, problem: '' };
  },

  /**
   * EDIT, WHICH THE DATABASE ALLOWS ONLY WHILE IT IS A DRAFT. Checked here too
   * so the refusal is a sentence rather than a row that matches no policy —
   * an UPDATE matching nothing is a success that did nothing.
   */
  saveDocument: async (id, patch) => {
    if (sampleBlocks()) return '';
    const doc = get().documents.find((d) => d.id === id);
    if (!doc) return 'That document is not on this board.';
    if (doc.state !== 'draft')
      return 'This document has been sent for signature, so it can no longer be changed. Supersede it with a new version instead.';
    try {
      await backend.patch(`${inProject(get().projectId).documents}/${id}`, {
        ...patch,
        updatedAt: Date.now(),
      });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That change could not be saved. Try again.'
      );
    }
    return '';
  },

  setDocVendorVisibility: async (id, visible) => {
    if (sampleBlocks()) return '';
    const prev = get().documents;
    const doc = prev.find((d) => d.id === id);
    if (!doc) return 'That document is not on this board.';
    // A governance document is internal — the control is never offered for one, but
    // guard here too so a stale call cannot try (and the database would refuse, 0198).
    if (visible && !isVendorShareable(doc.kind))
      return 'This kind of document is internal and cannot be shared with vendors.';
    // Optimistic: flip the switch the instant it is pressed rather than after the
    // documents-collection echo round-trips (~2.5s, Vinnie 23 Oct). The documents
    // watcher reconciles to the stored row when its broadcast lands; a rejected
    // write rolls the switch back and the caller shows the error.
    const at = Date.now();
    set({
      documents: prev.map((d) => (d.id === id ? { ...d, visibleToVendors: visible, updatedAt: at } : d)),
    });
    try {
      await backend.patch(`${inProject(get().projectId).documents}/${id}`, {
        visibleToVendors: visible,
        updatedAt: at,
      });
    } catch (e) {
      set({ documents: prev });
      return (
        (e as { message?: string })?.message ||
        'That change could not be saved. Try again.'
      );
    }
    return '';
  },

  /**
   * PUT THE DOCUMENT ITSELF IN — #200.
   *
   * The file is the document. Vinnie, 21 August: nobody types a scope of works
   * into a text box, and the person asked to sign has to be able to read it.
   *
   * THE ROW IS WRITTEN AFTER THE FILE, and that order is the whole of the error
   * handling. The other way round, a failed upload leaves a document claiming a
   * file that is not there — which reads as a missing document rather than as a
   * failed upload, and would be sent out for signature.
   */
  uploadDocumentFile: async (id, file) => {
    const doc = get().documents.find((d) => d.id === id);
    if (!doc) return 'That document is not on this board.';
    if (doc.state !== 'draft')
      return 'This document has been sent for signature, so the file can no longer be changed. Supersede it with a new version instead.';

    const why = whyNot(file, isReceived(doc.kind), doc.kind);
    if (why) return why;

    const board = idFrom('board', get().projectId, md5.md5);
    const key = fileKey(board, id, file.name);
    try {
      await backend.putFile(key, file, file.type);
      await backend.patch(`${inProject(get().projectId).documents}/${id}`, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        filePath: key,
        /* WHAT IT ACTUALLY IS, recorded at the moment it goes up — #216. A
           signing link is issued against this, so a file replaced afterwards
           cannot be signed as though it were the one that was sent. */
        fileHash: await fingerprint(file),
        uploadedAt: Date.now(),
        updatedAt: Date.now(),
      });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That file could not be uploaded. Nothing was changed — try again.'
      );
    }
    /* "attached …" is written by the database now (record_document_change, 0160). */
    return '';
  },

  /**
   * AN ADDRESS FOR READING IT, GOOD FOR A FEW MINUTES.
   *
   * Never a permanent one. The bucket is private so that no address exists that
   * works without the request being judged, and a short life means a link that
   * leaks stops working on its own rather than being a copy of the document
   * loose on the internet.
   */
  documentFileUrl: async (id) => {
    const doc = get().documents.find((d) => d.id === id);
    if (!doc?.filePath) return '';
    try {
      return await backend.fileUrl(doc.filePath);
    } catch {
      return '';
    }
  },

  /* ── A CARD'S OWN FILES (dossier v3 §6, migration 0223) ─────────────────────
   *
   * The same private bucket and the same board-scoped rule the documents use
   * (0048 / 0223), under a `board/card-<id>/<attId>-<name>` key. The attachment
   * id rides in the key so two files of the same name on one card never collide
   * (the bucket upserts by key). The bytes go up first; the metadata lands on the
   * card through updateIssue, so the write is optimistic and audited exactly like
   * every other card edit, and a refused storage write leaves the card untouched.
   */
  attachCardFile: async (issueId, file) => {
    const issue = get().issues.find((i) => i.id === issueId);
    if (!issue) return 'That card is not on this board.';
    if (file.size === 0) return `${file.name} is empty.`;
    if (file.size > 26214400)
      return `${file.name} is larger than 25 MB — attach a smaller file or a link to it.`;
    // A card is a place for the few artefacts it is ABOUT, not a file store: cap the
    // count and the total so one card can never balloon the row (read whole, as JSON,
    // on every board load) or run up storage. Generous for real use; a hard ceiling.
    const existing = issue.attachments ?? [];
    if (existing.length >= 20)
      return 'This card already has 20 files — remove one before adding another.';
    if (existing.reduce((n, a) => n + (a.size || 0), 0) + file.size > 104857600)
      return 'This card’s files would go over 100 MB in total — remove one before adding another.';

    const board = idFrom('board', get().projectId, md5.md5);
    const attId = crypto.randomUUID();
    const key = fileKey(board, `card-${issueId}`, `${attId}-${file.name}`);
    try {
      await backend.putFile(key, file, file.type);
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That file could not be attached. Nothing was changed — try again.'
      );
    }
    const entry: CardAttachment = {
      id: attId,
      name: file.name,
      size: file.size,
      type: file.type,
      path: key,
      by: get().user?.email ?? '',
      at: Date.now(),
    };
    get().updateIssue(issueId, { attachments: [...(issue.attachments ?? []), entry] });
    return '';
  },

  removeCardFile: async (issueId, attId) => {
    const issue = get().issues.find((i) => i.id === issueId);
    const att = issue?.attachments?.find((a) => a.id === attId);
    if (!issue || !att) return 'That file is no longer on this card.';
    try {
      await backend.removeFile(att.path);
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That file could not be removed — try again.'
      );
    }
    get().updateIssue(issueId, { attachments: (issue.attachments ?? []).filter((a) => a.id !== attId) });
    return '';
  },

  cardFileUrl: async (issueId, attId) => {
    const att = get().issues.find((i) => i.id === issueId)?.attachments?.find((a) => a.id === attId);
    if (!att) return '';
    try {
      return await backend.fileUrl(att.path);
    } catch {
      return '';
    }
  },

  /**
   * SEND IT FOR SIGNATURE, which copies the panel onto the document and freezes
   * it there — 0039's argument, applied to a document: somebody signing against
   * four named people must not have a fifth added afterwards.
   *
   * The rows first and the state last, for the reason `requestSignoff` gives:
   * written the other way round, a failure halfway leaves a document marked as
   * awaiting signature with nobody to ask.
   */
  sendDocumentForSignature: async (id, signatories) => {
    const doc = get().documents.find((d) => d.id === id);
    if (!doc) return 'That document is not on this board.';
    if (doc.state !== 'draft') return 'This document has already been sent for signature.';
    /*
     * NOTHING IS SENT OUT EMPTY. The trigger in 0049 refuses it too, and that
     * is what makes it true — this is so the reason is a sentence rather than a
     * raise. Four people asked to approve a title is the worst thing this
     * screen could do.
     */
    if (!doc.filePath)
      return 'This document has no file yet, so there is nothing to sign. Upload it first.';
    /*
     * ONLY A FILE A SIGNATORY CAN READ GOES OUT. An uploaded Test Script may be
     * an Excel file (Vinnie, 15 Oct 2026); a spreadsheet has no in-app view the
     * people signing could read, so it is kept for the record and never sent.
     * The screen hides the button for one, so this is the belt to that braces —
     * the same reason-not-a-raise rule as the empty-document guard above.
     */
    if (!isSignableFile(doc))
      return 'This file cannot be sent for sign-off — a signatory could not read it. Upload a PDF or Word version to get it signed.';

    /*
     * A CHARTER SIGNS THROUGH THE BOARD'S CONFIGURED SIGN-OFF, like every other
     * kind — configurable sign-offs R1, #445. Vinnie, 1 Oct: "It does not send
     * it for team review, I have to add the names individually." A board that
     * has set up charter stages (its 'charter' override, or the board default)
     * routes the charter through them — the people named in the stages sign it,
     * with nothing to hand-type and nothing to lose by leaving the screen.
     *
     * A board that has configured NO sign-off at all falls back to the charter's
     * own list, seeded from the sponsor on the send screen — so a project that
     * authorises its charter by its sponsor alone is unchanged. That is the only
     * case the `signatories` argument is still read.
     */
    let rows: SheetRow[];
    if (doc.kind === 'charter') {
      const policy = {
        ...policyForType(get().meta.signoffPolicy, 'charter'),
        named: get().meta.signatories ?? [],
      };
      if (usesSlots(policy) || usesStages(policy)) {
        const wrong = policyProblem(policy, theWorld(get()));
        if (wrong) return wrong;
        rows = sheetFor(policy, theWorld(get()), theDeveloper(get()));
        if (rows.length === 0)
          return 'Nobody is named to sign the charter off here. Set who signs it in Settings first.';
      } else {
        const why = charterSignoffProblem(signatories ?? []);
        if (why) return why;
        rows = charterSignoffRows(signatories ?? []).map((who) => ({ step: '' as const, who }));
      }
    } else {
      /* ONE SHEET, WHICHEVER MODEL THE BOARD IS ON — the same branch releases and
         sprints take. A slots board signs the document through its slots (the
         developer attested on sending, then the tester(s), approver, owner); a
         stages board signs it through the stage set configured for the
         document's kind (its override, or the default); a rank board keeps the
         document's flat panel, unchanged. */
      /* THE DOCUMENT'S OWN POLICY — its kind's override if the board set one, else
         the board default (`policyForType(..., doc.kind)`). A board with no override
         for this kind resolves exactly what `readPolicy` did before. (A charter is
         handled in the branch above — it signs by its own list, never the board
         policy — so its kind's override is not consulted here; see the report.) */
      const policy = { ...policyForType(get().meta.signoffPolicy, doc.kind), named: get().meta.signatories ?? [] };
      rows = usesSlots(policy) || usesStages(policy)
        ? sheetFor(policy, theWorld(get()), theDeveloper(get()))
        : thePanel(get().meta).map((who) => ({ step: '' as const, who }));

      if (usesSlots(policy) || usesStages(policy)) {
        const wrong = policyProblem(policy, theWorld(get()));
        if (wrong) return wrong;
      }
      if (rows.length === 0)
        return 'Nobody is named as signing work off here. Add them in Settings first.';
    }

    const at = Date.now();
    /* WHO IS SENDING THIS OUT — stamped on every row so a decision has somebody
       to tell (0105). See requestSprintSignoff for why it is frozen here. */
    const raisedBy = (get().user?.email ?? '').trim().toLowerCase();
    /* THE ROWS THAT STILL NEED A SIGNATURE AND CAN BE REACHED — so the document
       can post each approver their link the moment it is sent, exactly as a
       release does (requestSignoff). The developer attested by sending; a row
       with no address is left for the in-app sheet and the day-later reminder. */
    const toPost: { id: string; name: string; position: string }[] = [];
    try {
      /* IN ORDER, seq 0 FIRST, one at a time. Migration 0091 leans on this: the
         insert of seq 0 is what sweeps away an earlier send that was abandoned
         mid-way, and it can only do that safely while none of this send's own
         rows are in yet. Awaiting each keeps that true — do not make this loop
         insert in parallel without reading that migration's comment first. */
      for (const [seq, row] of rows.entries()) {
        const who = row.who;
        const rowId = uid();
        const email = (who.email ?? '').trim().toLowerCase();
        await backend.set(`${inProject(get().projectId).signoffs}/${rowId}`, {
          documentId: id,
          seq,
          raisedBy,
          step: row.step,
          /* THE FROZEN STAGE ORDER — configurable sign-offs R1; see
             requestSprintSignoff for why it is written at the moment of raising.
             Absent on the rank and slots models. */
          ...(row.stageSeq != null ? { stageSeq: row.stageSeq } : {}),
          ...(row.stageMode != null ? { stageMode: row.stageMode } : {}),
          name: who.name.trim(),
          position: who.position.trim(),
          signatoryEmail: email,
          /* ATTESTED ON SENDING — the developer slot; see requestSprintSignoff
             for why this gates nothing. Empty on the flat panel. */
          signedAt: row.attested ? at : null,
          ...(row.attested ? { answer: 'approved' as const } : {}),
        });
        if (!row.attested && email)
          toPost.push({ id: rowId, name: who.name.trim(), position: who.position.trim() });
      }
      await backend.patch(`${inProject(get().projectId).documents}/${id}`, {
        state: 'awaiting',
        updatedAt: at,
      });
      /*
       * REMEMBERED HERE, BECAUSE THIS IS WHERE SOMEBODY BECOMES SOMEBODY WHO
       * SIGNS. The roster used to be written by the Settings screen on every
       * keystroke, which recorded a new person for each character typed into a
       * position. "Signed here before" now means what it says.
       */
      const known = rememberAll(get().meta.signatoriesKnown ?? [], rows.map((r) => r.who));
      if (known.length !== (get().meta.signatoriesKnown ?? []).length)
        get().setMeta({ signatoriesKnown: known });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'Sign-off could not be asked for. Nothing was recorded — try again.'
      );
    }
    /* "asked N people to sign …" is written by the database now (record_document_change, 0160),
       on the move to "awaiting", counting the signature rows this send just wrote. */

    /*
     * AND POST THE SIGNATORIES THEIR LINK NOW — the same on-raise send a release
     * gets (requestSignoff, Vinnie's 29 September fix), so a document no longer
     * goes out for signature in silence: each signatory is emailed a signing link
     * the moment it is sent, which reaches someone with no account too. The link
     * is frozen against the file's fingerprint, like the manual "Send a link"
     * button (issueSigningLink). Outside the write above and each on its own — a
     * link that will not mint must not undo a send correctly made; a failed send
     * is chased by the day-later reminder and re-sendable by hand.
     */
    for (const p of toPost) {
      try {
        const r = await mintSigningLink(get().projectId, p.id, doc.fileHash ?? '');
        void get().recordAudit(
          'project',
          r.emailed
            ? `emailed ${p.name} (${p.position}) a signing link for "${doc.title}"`
            : `sent ${p.name} (${p.position}) a signing link for "${doc.title}"`
        );
      } catch {
        /* Reported by the reminder and re-sendable from the sheet; never fatal
           to a send that has already been made. */
      }
    }
    return '';
  },

  /**
   * SIGNED OFF. The trigger in 0046 refuses this while anybody is outstanding,
   * so the count below is what the person is told rather than what decides it.
   */
  markDocumentSigned: async (id) => {
    if (sampleBlocks()) return '';
    const doc = get().documents.find((d) => d.id === id);
    if (!doc) return 'That document is not on this board.';
    const waiting = get().signoffs.filter((r) => r.documentId === id && !r.signedAt);
    if (waiting.length)
      return `${waiting.length} ${waiting.length === 1 ? 'signature is' : 'signatures are'} still outstanding.`;
    /* EVERYBODY ANSWERING IS NOT EVERYBODY AGREEING. 0052 refuses this write;
       this is so the reason is a sentence, and names who to go and read. */
    const no = get().signoffs.filter((r) => r.documentId === id && r.answer === 'rejected');
    if (no.length)
      return `${no.map((r) => r.name).join(', ')} rejected this, so it cannot be marked as signed off. Supersede it with a version that answers them.`;
    try {
      await backend.patch(`${inProject(get().projectId).documents}/${id}`, {
        state: 'signed',
        updatedAt: Date.now(),
      });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That could not be recorded. Nothing was changed — try again.'
      );
    }
    /* "recorded … as signed off" is the database's now (record_document_change, 0160);
       the automatic close-out writes its own line in 0087. */
    return '';
  },

  /**
   * TAKING THE REQUEST BACK, while it is still only a request.
   *
   * Sent is not signed. Until somebody has actually put their name to it, four
   * unanswered requests are four unanswered emails, and un-asking is ordinary
   * work — it used to mean superseding, which left a permanent trail of a
   * version nobody ever wanted. The trigger in 0051 refuses it the moment one
   * signature exists; the count here is so the person reads a sentence.
   */
  withdrawDocument: async (id) => {
    if (sampleBlocks()) return '';
    const doc = get().documents.find((d) => d.id === id);
    if (!doc) return 'That document is not on this board.';
    if (doc.state !== 'awaiting') return 'This document has not been sent for signature.';
    /*
     * AN APPROVAL BLOCKS THIS. A REJECTION DOES NOT.
     *
     * This read `signedAt` alone, which was right until a signature could mean
     * two things. 0052 changed the rule in the trigger and this was left
     * behind, so the screen offered "Withdraw the request" on a rejected
     * document and the store refused it — the walk found it by trying to send
     * the thing out again and discovering it had never come back to draft.
     *
     * The rule now lives in three places — this, the trigger, and what the
     * screen offers — and they have to agree. The trigger is the one that is
     * true; these two exist so nobody is offered a button that is about to be
     * refused, and so the refusal reads as a sentence when it happens anyway.
     */
    const agreed = get().signoffs.filter(
      (r) => r.documentId === id && r.signedAt && r.answer !== 'rejected'
    );
    if (agreed.length)
      return `${agreed.length === 1 ? 'Somebody has' : `${agreed.length} people have`} already approved this, so the request cannot be withdrawn. Supersede it with a new version instead.`;
    try {
      /* The signature rows go with it, in the trigger rather than here — one
         statement, so a document can never be back in draft while rows still
         ask people to sign the version it used to be. */
      await backend.patch(`${inProject(get().projectId).documents}/${id}`, {
        state: 'draft',
        updatedAt: Date.now(),
      });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'The request could not be withdrawn. Nothing was changed — try again.'
      );
    }
    /* "withdrew the request to sign …" is written by the database now (0160). */
    return '';
  },

  /**
   * CLEARING A REFUSAL OFF THE PANEL — Vinnie, 11 September 2026, migration 0094.
   *
   * A rejected request used to sit on the Sign-offs screen for ever: anything not
   * fully approved counts as open, so "✕ Not approved · needs re-raising" stayed
   * in the open list with only Review beside it. Dismissing it is the honest end
   * — somebody read the refusal, dealt with it, and takes it off the board, while
   * the record of who refused and why is KEPT.
   *
   * IT RIDES ON THE SAME withdraw MOVE THE STORE ALREADY MAKES (awaiting→draft),
   * so nothing new decides who may do it: the trigger in 0094 (the 0088 guard,
   * verbatim, with only the withdraw branch changed) still requires manager or
   * owner and still refuses it once anybody has approved. The one difference the
   * trigger makes is that the refused rows are KEPT and stamped
   * withdrawn+dismissed instead of being deleted, so the screen can show the
   * refusal under "settled" with its reason. The checks here exist so the person
   * reads a sentence rather than a raised database error.
   */
  dismissDocument: async (id) => {
    if (sampleBlocks()) return '';
    const doc = get().documents.find((d) => d.id === id);
    if (!doc) return 'That document is not on this board.';
    if (doc.state !== 'awaiting') return 'This document has not been sent for signature.';
    /* Only a REFUSAL is dismissed. A request still waiting is withdrawn, not
       dismissed — dismissing keeps a "was refused" record, which is a lie about
       a request nobody has answered.
       NO APPROVAL BLOCK HERE (unlike withdrawDocument): a refused sheet is
       settled as "no", so there is no approval to protect — and on a slots board
       the raiser's own attested row would otherwise block clearing the very
       refusal this is for. The trigger in 0094 clears a refusal regardless. */
    const refused = get().signoffs.filter((r) => r.documentId === id && r.answer === 'rejected');
    if (refused.length === 0)
      return 'Only a refused request can be dismissed. Withdraw the request instead.';
    try {
      /* State back to draft; the trigger keeps the refused rows (withdrawn +
         dismissed) and clears the rest — one statement, so the document can never
         be draft while rows still ask people to sign the version it used to be. */
      await backend.patch(`${inProject(get().projectId).documents}/${id}`, {
        state: 'draft',
        updatedAt: Date.now(),
      });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'The refusal could not be dismissed. Nothing was changed — try again.'
      );
    }
    /* "dismissed the refused request to sign …" is written by the database now (0160). */
    return '';
  },

  /**
   * DEAL WITH A REFUSAL BY ASKING AGAIN — the second half of #0094's pair.
   *
   * Dismiss clears the refusal and leaves the document in draft; re-raising goes
   * one step further and sends the same document out for a FRESH round. The old
   * refusal is kept (dismissed) as the record of the round that failed, and a new
   * sheet is drawn — so the trail reads "refused, then re-raised" rather than a
   * silent second attempt.
   */
  reRaiseDocument: async (id) => {
    const cleared = await get().dismissDocument(id);
    if (cleared) return cleared;
    /* Reflect the draft state locally so the send below is not refused by a
       still-'awaiting' copy while the realtime round-trip catches up — the
       subscription confirms it a moment later. If the send fails, the document is
       left in draft (dismissed), which is a state somebody can send again. */
    set({
      documents: get().documents.map((d) => (d.id === id ? { ...d, state: 'draft' } : d)),
    });
    const sent = await get().sendDocumentForSignature(id);
    if (sent) return `The refusal was cleared, but a fresh request could not be sent: ${sent}`;
    return '';
  },

  /**
   * THROWING A DRAFT AWAY — and the file with it.
   *
   * THE FILE FIRST, and the row after. The other order deletes the row and then
   * fails, which leaves a file in the bucket that nothing on the board points
   * at any more: invisible, unreachable, and still counted against the storage
   * a customer pays for. This way a failure leaves a document that still names
   * its file, which is a thing somebody can look at and try again.
   */
  deleteDocument: async (id) => {
    if (sampleBlocks()) return '';
    const doc = get().documents.find((d) => d.id === id);
    if (!doc) return 'That document is not on this board.';
    if (doc.state !== 'draft')
      return 'This document has been sent for signature. Withdraw the request first.';
    try {
      if (doc.filePath) await backend.removeFile(doc.filePath);
      await backend.remove(`${inProject(get().projectId).documents}/${id}`);
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That could not be deleted. Nothing was removed — try again.'
      );
    }
    /* A deleted draft must not be resurrected by the watcher's pending-row
       keep — drop it from the pending set, and from the local list now so the
       removal shows at once rather than waiting on the echo. */
    pendingDocs.delete(id);
    set((s) => ({ documents: s.documents.filter((d) => d.id !== id) }));
    /* "deleted the draft …" is written by the database now (0160). */
    return '';
  },

  /**
   * A NEW VERSION, AND THE OLD ONE LEFT EXACTLY AS IT WAS SIGNED.
   *
   * The new row first. The other order would mark the old one superseded and
   * then fail, leaving a project whose only requirements document says it has
   * been replaced by something that does not exist.
   */
  supersedeDocument: async (id) => {
    const doc = get().documents.find((d) => d.id === id);
    if (!doc) return { id: '', problem: 'That document is not on this board.' };
    if (doc.state !== 'signed')
      return { id: '', problem: 'Only a signed document is superseded. A draft is edited instead.' };

    /* A real uuid, not a short id — see draftDocument: a short id is hashed to a
       different uuid by the write layer, so the new version would be orphaned
       from the row it is read back as (duplicate row, storage-path cast failure). */
    const next = crypto.randomUUID();
    try {
      await backend.set(`${inProject(get().projectId).documents}/${next}`, {
        kind: doc.kind,
        /* The same title, body and — for a change request — the narratives the
           person already wrote, so the new version is edited rather than started
           again. The SCOPE is deliberately not copied: it is produced fresh from
           the board as it stands now, which is the whole reason to re-do it. */
        title: doc.title,
        body: doc.body,
        state: 'draft',
        version: doc.version + 1,
        supersedes: doc.id,
        createdAt: Date.now(),
        ...(doc.written ? { written: doc.written } : {}),
        ...(doc.sprint ? { sprint: doc.sprint } : {}),
      });
      await backend.patch(`${inProject(get().projectId).documents}/${id}`, {
        state: 'superseded',
        updatedAt: Date.now(),
      });
    } catch (e) {
      return {
        id: '',
        problem:
          (e as { message?: string })?.message || 'That version could not be created. Try again.',
      };
    }
    /* "superseded … with version N" is written by the database now (0160). */
    return { id: next, problem: '' };
  },

  setCadence: async (cadence) => {
    if (sampleBlocks()) return;
    await backend.patch(boardPath(), { cadence });
    // Recorded by the database on the row — record_board_control_change (0167).
  },

  setDeliveryMode: async (mode) => {
    if (sampleBlocks()) return;
    // Optimistic, and reverted if the write is refused. The mode card's "in use"
    // badge and the nav (Plan + the Finances group, gated on meta via
    // isProjectMode) must flip on the click frame, not a network round-trip later,
    // so the set MUST precede the await — placing it after is exactly L90's lag.
    const was = get().meta.deliveryMode;
    set((s) => ({ meta: { ...s.meta, deliveryMode: mode } }));
    try {
      await backend.patch(boardPath(), { deliveryMode: mode });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, deliveryMode: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0167).
  },
  setCardDefaults: async (mode) => {
    if (sampleBlocks()) return;
    // Optimistic, reverted on refusal — the same pattern as setDeliveryMode. It
    // only changes how the NEXT new card pre-fills, so nothing else on screen
    // depends on it flipping instantly, but the control's own selected state must.
    const was = get().meta.cardDefaults;
    set((s) => ({ meta: { ...s.meta, cardDefaults: mode } }));
    try {
      await backend.patch(boardPath(), { cardDefaults: mode });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, cardDefaults: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0195).
  },

  markLiveAndMoveToService: async (opts) => {
    if (sampleBlocks()) return;
    // The as-delivered scope is counted HERE from the live cards, the one place
    // that holds them all: shipped = the Done cards; deferred = everything else
    // that was committed (todo/in-progress/in-review/staging and the cut ones),
    // which is carried into service. Backlog is uncommitted — never part of the
    // project's delivery scope — so it is excluded from both counts.
    const committed = get().issues.filter((i) => i.status !== 'backlog');
    const scopeShipped = committed.filter((i) => i.status === 'done').length;
    const scopeDeferred = committed.length - scopeShipped;
    const snapshot = buildDeliverySnapshot({
      plannedGoLive: opts.plannedGoLive,
      actualGoLive: opts.actualGoLive,
      scopeShipped,
      scopeDeferred,
      budgetAtGoLive: opts.budgetAtGoLive,
      provisional: opts.provisional,
      nowMs: Date.now(),
    });
    // Optimistic, reverted if refused — the header's "In service" pill and the nav
    // (Plan + Finances, gated on isProjectMode) must flip on the click frame, so the
    // set precedes the await, exactly like setDeliveryMode (L90). One write carries
    // all three: the frozen go-live, the record, and the flip to continuous.
    const was = {
      deliveryMode: get().meta.deliveryMode,
      actualGoLive: get().meta.actualGoLive,
      deliverySnapshot: get().meta.deliverySnapshot,
    };
    set((s) => ({
      meta: {
        ...s.meta,
        deliveryMode: 'continuous',
        actualGoLive: opts.actualGoLive,
        deliverySnapshot: snapshot,
      },
    }));
    try {
      await backend.patch(boardPath(), {
        deliveryMode: 'continuous',
        actualGoLive: opts.actualGoLive,
        deliverySnapshot: snapshot,
      });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, ...was } }));
      throw e;
    }
    // The DB trail-stamps the go-live — record_board_control_change (0192).
  },

  revertFromService: async () => {
    if (sampleBlocks()) return;
    // Undo the handoff: back to in-delivery project mode, KEEPING the frozen
    // delivery record (decision 3 — a revert never discards what shipped). Only
    // the mode and the live stamp move; deliverySnapshot is left untouched.
    const was = {
      deliveryMode: get().meta.deliveryMode,
      actualGoLive: get().meta.actualGoLive,
    };
    set((s) => ({ meta: { ...s.meta, deliveryMode: 'project', actualGoLive: undefined } }));
    try {
      await backend.patch(boardPath(), { deliveryMode: 'project', actualGoLive: null });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, ...was } }));
      throw e;
    }
    // The DB trail-stamps the flip — record_board_control_change (0192).
  },

  setStagingEnabled: async (on) => {
    if (sampleBlocks()) return;
    // Optimistic + revert on refusal, like setDeliveryMode — the lane and the
    // settings toggle flip on the click frame. Turning OFF while cards sit in
    // staging is guided in Settings (the cards are moved to a chosen column
    // first, via moveIssue); this action only flips the board flag.
    const was = get().meta.stagingEnabled;
    set((s) => ({ meta: { ...s.meta, stagingEnabled: on } }));
    try {
      await backend.patch(boardPath(), { stagingEnabled: on });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, stagingEnabled: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0181).
  },

  setHideDoneInStaging: async (on) => {
    if (sampleBlocks()) return;
    // Optimistic + revert on refusal, exactly like setStagingEnabled above. Only
    // the board column is affected; no card moves, so there is nothing to guide.
    const was = get().meta.hideDoneInStaging;
    set((s) => ({ meta: { ...s.meta, hideDoneInStaging: on } }));
    try {
      await backend.patch(boardPath(), { hideDoneInStaging: on });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, hideDoneInStaging: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0183).
  },

  setBacklogOnBoard: async (on) => {
    if (sampleBlocks()) return;
    // Optimistic + revert on refusal, exactly like setHideDoneInStaging above. Only
    // the board column is affected; no card moves (the backlog stays its own view),
    // so there is nothing to guide.
    const was = get().meta.backlogOnBoard;
    set((s) => ({ meta: { ...s.meta, backlogOnBoard: on } }));
    try {
      await backend.patch(boardPath(), { backlogOnBoard: on });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, backlogOnBoard: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0218).
  },

  setRequireSeparateScopeApprover: async (on) => {
    if (sampleBlocks()) return;
    // Optimistic, reverted if refused — the toggle flips on the click frame, not a
    // round-trip later, so the set precedes the await (L90, same as above).
    const was = get().meta.requireSeparateScopeApprover;
    set((s) => ({ meta: { ...s.meta, requireSeparateScopeApprover: on } }));
    try {
      await backend.patch(boardPath(), { requireSeparateScopeApprover: on });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, requireSeparateScopeApprover: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0167).
  },

  setDepHardBlock: async (on) => {
    if (sampleBlocks()) return;
    // Optimistic, reverted if refused — same shape as the other board toggles.
    const was = get().meta.depHardBlock;
    set((s) => ({ meta: { ...s.meta, depHardBlock: on } }));
    try {
      await backend.patch(boardPath(), { depHardBlock: on });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, depHardBlock: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0202).
  },

  setCardAssists: async (on) => {
    if (sampleBlocks()) return;
    // Optimistic, reverted if refused — same shape as the other board toggles.
    const was = get().meta.cardAssists;
    set((s) => ({ meta: { ...s.meta, cardAssists: on } }));
    try {
      await backend.patch(boardPath(), { cardAssists: on });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, cardAssists: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0205).
  },

  setDataQualityPanel: async (on) => {
    if (sampleBlocks()) return;
    // Optimistic, reverted if refused — same shape as the other board toggles.
    const was = get().meta.dataQualityPanel;
    set((s) => ({ meta: { ...s.meta, dataQualityPanel: on } }));
    try {
      await backend.patch(boardPath(), { dataQualityPanel: on });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, dataQualityPanel: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0205).
  },

  setShowOddsToEveryone: async (on) => {
    if (sampleBlocks()) return;
    // Optimistic, reverted if refused — same shape as the other board toggles.
    const was = get().meta.showOddsToEveryone;
    set((s) => ({ meta: { ...s.meta, showOddsToEveryone: on } }));
    try {
      await backend.patch(boardPath(), { showOddsToEveryone: on });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, showOddsToEveryone: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0220).
  },

  setScheduleSignalAudience: async (aud) => {
    if (sampleBlocks()) return;
    // Optimistic, reverted if refused — same shape as the other board toggles.
    const was = get().meta.scheduleSignalAudience;
    // Narrowing the schedule audience narrows budget with it (budget can never be
    // wider than schedule) — mirror the bound the DB and the picker enforce, so the
    // optimistic view matches what lands.
    const wasBudget = get().meta.budgetSignalAudience;
    const RANK = { off: 0, managers: 1, all: 2 } as const;
    const budgetNext =
      wasBudget && RANK[wasBudget] > RANK[aud] ? aud : wasBudget;
    set((s) => ({ meta: { ...s.meta, scheduleSignalAudience: aud, budgetSignalAudience: budgetNext } }));
    try {
      await backend.patch(boardPath(), budgetNext !== wasBudget
        ? { scheduleSignalAudience: aud, budgetSignalAudience: budgetNext }
        : { scheduleSignalAudience: aud });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, scheduleSignalAudience: was, budgetSignalAudience: wasBudget } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0225).
  },

  setBudgetSignalAudience: async (aud) => {
    if (sampleBlocks()) return;
    // Optimistic, reverted if refused — same shape as the other board toggles.
    const was = get().meta.budgetSignalAudience;
    set((s) => ({ meta: { ...s.meta, budgetSignalAudience: aud } }));
    try {
      await backend.patch(boardPath(), { budgetSignalAudience: aud });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, budgetSignalAudience: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0225).
  },

  setIdleWindows: async (windows) => {
    if (sampleBlocks()) return;
    // Optimistic, reverted if refused — same shape as the other board toggles.
    const was = get().meta.idleWindows;
    set((s) => ({ meta: { ...s.meta, idleWindows: windows } }));
    try {
      await backend.patch(boardPath(), { idleWindows: windows });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, idleWindows: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0213).
  },

  setChecklistPrompts: async (prompts) => {
    if (sampleBlocks()) return;
    // Optimistic, reverted if refused — same shape as the other board toggles.
    const was = get().meta.checklistPrompts;
    set((s) => ({ meta: { ...s.meta, checklistPrompts: prompts } }));
    try {
      await backend.patch(boardPath(), { checklistPrompts: prompts });
    } catch (e) {
      set((s) => ({ meta: { ...s.meta, checklistPrompts: was } }));
      throw e;
    }
    // Recorded by the database on the row — record_board_control_change (0214).
  },

  setDeliveryNudge: async (config) => {
    if (sampleBlocks()) return '';
    const s = get();
    const me = s.user?.email?.toLowerCase() ?? accounts.current()?.email?.toLowerCase();
    if (!me) return 'You are not signed in.';
    const pid = s.projectId;
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === pid);
    if (!board) return 'That board is not one of yours to change.';
    // Gated to the admin/manager (ledger-write) tier — the same tier that governs
    // the payment ledger. A general viewer cannot change how the board governs.
    const myRole: Role = amStaff()
      ? 'admin'
      : effectiveRole({ email: me, admins: s.access.admins, defaults: s.roles, on: s.projectRoles, legacy: s.legacyRoles, projectId: pid });
    if (!(myRole === 'admin' || can(myRole, 'editSettings'))) {
      return 'Only a manager or admin can change the delivery nudge.';
    }
    if (config.on) {
      const pct = Math.round(config.pct);
      if (!(pct >= 1 && pct <= 100)) return 'Set the bar between 1% and 100%.';
      config = { on: true, pct };
    } else {
      // Keep the chosen bar when turning off, so turning it back on restores it.
      config = { on: false, pct: Math.max(1, Math.min(100, Math.round(config.pct) || board.deliveryNudge?.pct || 100)) };
    }
    await backend.patch(boardPath(), { deliveryNudge: config });
    const applyLocal = (p: Project): Project => (p.id === pid ? { ...p, deliveryNudge: config } : p);
    set((st) => ({
      projects: st.projects.map(applyLocal),
      archivedProjects: st.archivedProjects.map(applyLocal),
    }));
    await get().recordAudit(
      'project',
      config.on
        ? `turned on the delivery nudge — warn when a payment is approved below ${config.pct}% delivered`
        : 'turned off the delivery nudge',
      { projectId: pid },
    );
    return '';
  },

  adjustContractValue: async (newValue, reason, documentRef, newBaseline) => {
    const s = get();
    const me = s.user?.email?.toLowerCase() ?? accounts.current()?.email?.toLowerCase();
    if (!me) return 'You are not signed in.';
    const pid = s.projectId;
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === pid);
    if (!board) return 'That board is not one of yours to change.';
    const from = board.contractValue;
    if (from == null) return 'Set a contract value before adjusting it.';
    if (newValue == null || !(newValue > 0)) return 'Enter the new contract value.';
    const why = reason.trim();
    if (!why) return 'Give a reason for the scope change.';
    if (newValue === from) return 'The new value is the same as the current one.';

    // WHO MAY RECORD A SCOPE CHANGE — gated like a payment (A1, Vinnie 8 Oct 2026):
    // the board's designated approver, or an admin/manager standing in. A general
    // editor cannot — that was the bug (anyone with editSettings could record it).
    const myRole: Role = amStaff()
      ? 'admin'
      : effectiveRole({ email: me, admins: s.access.admins, defaults: s.roles, on: s.projectRoles, legacy: s.legacyRoles, projectId: pid });
    const isAdmin = myRole === 'admin' || can(myRole, 'editSettings');
    const approver = board.payApprover?.trim().toLowerCase();
    const iAmApprover = !!approver && approver === me;
    if (!iAmApprover && !isAdmin) {
      return 'Only the scope approver, or a manager or admin standing in, can record a scope change.';
    }
    // SEPARATION OF DUTIES. The designated approver records cleanly. An admin
    // standing in (no approver set, or a different one) is a control exception:
    // allowed but DOCUMENTED, mirroring the invoice SoD override — unless STRICT
    // SoD is on, which reserves scope approval to the designated approver.
    const sodOverride = !iAmApprover;
    if (sodOverride && s.meta.requireSeparateScopeApprover === true) {
      return approver
        ? 'Separation of duties is on — only the designated scope approver can record this change.'
        : 'Separation of duties is on — set a scope approver before changing the contract value.';
    }

    // The baseline follows the change — in full, or keeping a deliberate
    // contingency when the caller passes an explicit new baseline (#435 B).
    const baseline = newBaseline != null && newBaseline > 0 ? newBaseline : newValue;
    const docTrim = documentRef?.trim();

    const entry: ContractAdjustment = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      from,
      to: newValue,
      reason: why,
      by: me,
      at: Date.now(),
      baseline,
      ...(sodOverride ? { sodOverride: true } : {}),
      ...(docTrim ? { documentRef: docTrim } : {}),
    };
    const contractAdjustments = [...(board.contractAdjustments ?? []), entry];

    // The baseline follows the approved scope change — written together so the two
    // never disagree.
    await backend.patch(boardPath(), {
      contractValue: newValue,
      budget: baseline,
      contractAdjustments,
    });
    const applyLocal = (p: Project): Project =>
      p.id === pid ? { ...p, contractValue: newValue, budget: baseline, contractAdjustments } : p;
    set((st) => ({
      projects: st.projects.map(applyLocal),
      archivedProjects: st.archivedProjects.map(applyLocal),
    }));

    // The decision goes to the audit/History trail, attributed, like approving a
    // payment. AuditEntry has no structured reason field, so the reason, the
    // stand-in (when there is one) and any signed-variation reference ride in the
    // sentence — so the trail answers "on what basis", not just the amounts (4b).
    const stand = sodOverride ? ' without a separate approver' : '';
    const baseNote = baseline !== newValue ? `; baseline held at ${fmtMoney(baseline)}` : '';
    await get().recordAudit(
      'project',
      `approved a contract-value change from ${fmtMoney(from)} to ${fmtMoney(newValue)}${stand}${baseNote} — ${why}${docTrim ? ` (signed variation: ${docTrim})` : ''}`,
      { projectId: pid }
    );
    return '';
  },

  setAssistantVersions: async (on) => {
    if (sampleBlocks()) return;
    await backend.patch(boardPath(), { assistantVersions: on });
    // Recorded by the database on the row — record_board_control_change (0167).
  },

  /**
   * RECORD A RELEASE — Option B, which is the one that works everywhere.
   *
   * The build number is worked out here rather than typed, because it is a
   * count rather than a decision: it is the primary key, and asking somebody to
   * type a key is asking them to collide with one. The VERSION is the decision
   * and stays theirs — see src/releases.ts for the switch that governs it.
   */
  cutRelease: async (draft) => {
    if (sampleBlocks()) return '';
    const builds = get().builds;
    const problem = releaseProblem(draft, builds);
    if (problem) return problem;
    const record = toBuild(draft, builds);
    /*
     * ADDRESSED BY THE NUMBER, LABELLED WITH THE "b". A build has no id column —
     * `id` is "b8", which is the number with a letter in front, and the seam
     * strips it on the way in and puts it back on the way out. The PATH has to
     * carry the number itself.
     *
     * This was written as `builds/b8` and every release refused: the path parser
     * read "b8" as NaN, sent null, and Postgres declined the row. Vinnie:
     * *"when clicking 'record 1.2.0' the screen is stuck saying recording…"* —
     * because the throw below was never caught either.
     */
    const id = `b${record.build}`;
    /*
     * PREPARED, OR CUT AT ONCE — #194, after Vinnie's correction on 21 August.
     *
     * Declaring what is in a release no longer cuts it: cutting is what
     * approval earns. But a board that names nobody as signing work off has
     * nothing to wait for, so recording a release there still means it is out,
     * in one act, exactly as before. Any other reading would hand every
     * customer who does not work this way a ladder with no rungs — they cannot
     * ask for sign-off with an empty panel, so they could never reach signed
     * off, so they could never cut anything.
     */
    /* On a slots board this is whether any slot produces somebody to sign — not
       counting the developer, whose attestation is not a sign-off to wait for.
       A rank/flat board keeps reading the panel. */
    /* THE RELEASE'S OWN POLICY decides whether anybody signs — the same 'release'
       resolution `requestBuildSignoff` raises against, so the cut-gate and the
       raise never disagree about whether a release has anybody to wait for. */
    const cutPolicy = { ...policyForType(get().meta.signoffPolicy, 'release'), named: get().meta.signatories ?? [] };
    /* WHETHER ANYBODY SIGNS — resolved the SAME way the raise does, so a stage
       board is read through its stages, a slots board through its slots (minus the
       developer's own attestation), and only a rank/flat board reads the named
       panel. Before this a STAGE board fell through to `thePanel`, which is empty
       on the stage model, so every stage release was read as "nobody signs" and
       CUT + GONE-LIVE the instant it was recorded — the release sign-off could
       then never be completed (the witness's blocker). `sheetFor` covers slots and
       stages; `!attested` drops the slots developer and leaves every stage row. */
    const cutSheet =
      usesSlots(cutPolicy) || usesStages(cutPolicy)
        ? sheetFor(cutPolicy, theWorld(get()), theDeveloper(get()))
        : null;
    const nobodySigns = cutSheet
      ? cutSheet.filter((r) => !r.attested).length === 0
      : thePanel(get().meta).length === 0;
    const at = Date.now();
    try {
      await backend.set(`${inProject(get().projectId).builds}/${record.build}`, {
        id,
        ...record,
        ...(nobodySigns ? { cutAt: at, wentLiveAt: at } : {}),
      });
    } catch (e) {
      /*
       * A REFUSED WRITE HAS TO REACH THE PERSON. Somebody has just typed a
       * version and pressed a button; leaving them on "Recording…" for ever
       * tells them nothing and loses what they typed. Same argument as
       * commentError.
       */
      return (
        (e as { message?: string })?.message ||
        'That release could not be saved. Nothing was recorded — try again.'
      );
    }
    /*
     * WRITTEN TO THE TRAIL, because a release is a claim about the world that
     * somebody outside the team may act on. Who said a version went out, and
     * when they said it, is the kind of thing that matters exactly once and
     * cannot be reconstructed afterwards.
     */
    void get().recordAudit(
      'project',
      `recorded release ${record.version}${record.items.length ? ` — ${record.items.length} item${record.items.length === 1 ? '' : 's'}` : ', carrying no changes'}`
    );
    return '';
  },

  pullRelease: async (buildId, pull) => {
    if (sampleBlocks()) return '';
    const builds = get().builds;
    const build = builds.find((b) => b.id === buildId);
    const problem = pullProblem(pull, build, builds);
    if (problem) return problem;
    const at = Date.now();
    const who = get().user?.email ?? 'someone';
    try {
      // By its NUMBER, not its "b8" label — see the note in cutRelease.
      await backend.patch(
        `${inProject(get().projectId).builds}/${build!.build}`,
        toPulled(pull, who, at)
      );
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That could not be saved. The release is unchanged — try again.'
      );
    }
    /*
     * THE TRAIL MATTERS MORE HERE THAN FOR CUTTING ONE. Taking a release back is
     * the event somebody reconstructs afterwards — who decided, when, and what
     * reason was given at the time rather than the one remembered later.
     */
    void get().recordAudit(
      'project',
      `marked ${build!.version || `build ${build!.build}`} ${PULLED_LABEL[pull.kind].toLowerCase()} — ${pull.why.trim()}`
    );
    return '';
  },

  putReleaseBack: async (buildId) => {
    const build = get().builds.find((b) => b.id === buildId);
    if (!build) return 'That release is not on this board any more.';
    if (!build.pulled) return 'That release was not marked as taken back.';
    try {
      /*
       * CLEARED, NOT DELETED-AND-REWRITTEN. Empty strings rather than nulls to
       * match how every other optional text field on a build reads back.
       */
      /*
       * NULL, NOT AN EMPTY STRING. `pulled` carries a check constraint — null,
       * 'withdrawn' or 'rolledback' — and '' is none of those, so this write was
       * refused every time and "Put it back" did nothing at all. The other four
       * are plain text columns where '' is the app's own reading of "not set".
       */
      await backend.patch(`${inProject(get().projectId).builds}/${build.build}`, {
        pulled: null,
        pulledWhy: '',
        pulledBy: '',
        pulledAt: null,
        pulledBack: '',
      });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That could not be saved. The release is unchanged — try again.'
      );
    }
    void get().recordAudit(
      'project',
      `put ${build.version || `build ${build.build}`} back — it was not withdrawn after all`
    );
    return '';
  },

  setReleaseDevices: async (buildId, devices) => {
    if (sampleBlocks()) return '';
    const build = get().builds.find((b) => b.id === buildId);
    if (!build) return 'That release is not on this board any more.';
    const installed = deviceText(deviceList(deviceText(devices)));
    if (installed === (build.installed ?? '')) return '';
    try {
      await backend.patch(`${inProject(get().projectId).builds}/${build.build}`, { installed });
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That could not be saved. The release is unchanged — try again.'
      );
    }
    /*
     * `installedTold` IS NOT TOUCHED HERE, and that is the ownership rule doing
     * its job rather than an omission. A person adding the Fold has said nothing
     * about what the tool reported, and a person REMOVING something the tool
     * reported has contradicted the ledger — which the next push will restate,
     * because the ledger is where that correction belongs. See mergeDevices.
     */
    const was = deviceList(build.installed);
    const now = deviceList(installed);
    const added = now.filter((d) => !was.some((w) => w.toLowerCase() === d.toLowerCase()));
    const gone = was.filter((d) => !now.some((n) => n.toLowerCase() === d.toLowerCase()));
    const name = build.version || `build ${build.build}`;
    void get().recordAudit(
      'project',
      [
        added.length ? `added ${added.join(', ')} to ${name}` : '',
        gone.length ? `took ${gone.join(', ')} off ${name}` : '',
      ]
        .filter(Boolean)
        .join(', and ')
    );
    return '';
  },

  /**
   * TELL THE PERSON WHO REPORTED IT THAT IT MOVED — if anybody did, and only
   * about the two moves that are any of their business.
   *
   * THE MIDDLE OF OUR PROCESS IS NOT NEWS. A card dragged to In Progress while
   * somebody triages on a Sunday would be an email that says nothing and trains
   * them to ignore the next one. Fixed and closed are the two outcomes a person
   * who reported a fault is actually waiting on, and everything else is us.
   *
   * QUIET WHEN NOBODY REPORTED IT, which is almost every card on almost every
   * board. The reporter is looked up rather than assumed, and the common answer
   * is "nobody" — see loadReporter, which remembers that too.
   */
  tellReporter: async (id, status) => {
    if (status !== 'done' && status !== 'wontdo') return;
    await get().loadReporter(id);
    if (!get().reporters[id]) return;

    /*
     * CLOSING NEEDS WORDS, so it stops here and asks for them. A bare "Closed"
     * to somebody who did us a favour by reporting a problem is the rudest
     * thing this system could send — worse than saying nothing.
     *
     * And if they cancel, nothing is sent. That is deliberate: the rule is that
     * a bare notice may not go out, not that somebody must be made to explain
     * themselves. Silence is a worse outcome than a good sentence and a better
     * one than a cold notice.
     */
    if (status === 'wontdo') {
      set({ tellingWhyClosed: id });
      return;
    }

    /*
     * FIXED SENDS ITSELF. It is the same message every time, it is the one
     * thing they are waiting to hear, and making it depend on somebody
     * remembering to type it is how it stops happening at forty tickets.
     */
    await get()
      .answerReport(id, WHEN_IT_IS_FIXED, 'fixed')
      .catch(() => {
        /*
         * SWALLOWED ON PURPOSE, and this is the one place in this file where
         * that is right. Nobody asked for this message — they moved a card. A
         * dialog about an email they did not know was being sent, on top of a
         * board move that worked, would be noise about somebody else's problem.
         * The server latches only on success, so the next move tries again.
         */
      });
  },

  /** Cancel telling them, having been asked why the ticket is being closed. */
  sayNothingAboutClosing: () => set({ tellingWhyClosed: '' }),

  myReports: async () => (backend.myReports ? backend.myReports() : []),

  tellThemWhyClosed: async (text) => {
    const id = get().tellingWhyClosed;
    if (!id) return { emailed: false };
    const said = await get().answerReport(id, text, 'closed');
    set({ tellingWhyClosed: '' });
    return said;
  },

  /**
   * WRITTEN DOWN AND KEPT BEFORE IT IS REMOVED.
   *
   * This used to be one line — delete the document — which made it the only act
   * on the board that left no record anywhere. The card went, its own history
   * went with it, and afterwards there was no way to know it had existed.
   *
   * The copy is written FIRST and the delete only follows if that succeeded. In
   * the other order, a refused or failed write would take the issue with it and
   * leave nothing, which is the exact outcome this exists to prevent.
   */
  deleteIssue: async (id) => {
    if (sampleBlocks()) return;
    const issue = get().issues.find((i) => i.id === id);
    if (issue) {
      // NO COPY, NO DELETE. Written first, and if that write is refused this
      // throws and the issue stays. The first version of this ran the delete
      // whether the record succeeded or not — which is how the very first test
      // deletion went through with nothing kept, exactly the outcome the copy
      // exists to prevent. The rules were rejecting the entry at the time, so
      // the "safeguard" removed an issue and recorded nothing.
      await get().recordAudit('issue', describeDeletion(issue), {
        snapshot: snapshotOf(issue),
        projectId: get().projectId,
      });
    }
    await backend.remove(issuePath(id));
    set((s) => ({ selectedId: s.selectedId === id ? null : s.selectedId }));
  },

  /**
   * The issue again, exactly as it was, written back where it came from.
   *
   * Deliberately NOT removing the record of the deletion afterwards: this
   * collection cannot be edited by anyone, and a trail that tidied itself up
   * once you undid something would be a trail you could not trust. Both facts
   * are true — it was deleted, and it was put back — and both stay.
   */
  restoreIssue: async (entry) => {
    const issue = issueFromSnapshot(entry.snapshot);
    if (!issue) throw new Error('That record does not carry a copy of the issue.');
    const where = entry.projectId || get().projectId;
    if (sampleBlocks(where)) return;
    await backend.set(`${inProject(where).issues}/${issue.id}`, issue);
    await get().recordAudit('issue', `restored ${issue.ref} — "${issue.title}"`);
  },

  recordBulk: (what, issues) => {
    // One issue is not a bulk change, and calling it one would bury the real
    // ones. It is already recorded on its own card.
    if (issues.length < 2) return;
    void get().recordAudit('issue', describeBulk(what, issues));
  },

  /**
   * POST IT, AND SHOW IT AT ONCE.
   *
   * This used to write and wait for the watcher to bring it back, which is a
   * round trip at best and — until the watcher was told to listen to the
   * comments table at all — a page reload at worst. Either way the box emptied
   * and nothing appeared, which reads as the comment having been lost.
   *
   * So it goes on screen first and is written second. If the write is refused it
   * is TAKEN BACK OFF and the reason is kept, because a comment sitting on screen
   * that was never saved is worse than one that visibly failed — it is believed.
   *
   * The watcher replaces the whole list when it fires, so the real row quietly
   * supersedes this one; they differ only in the id, which nothing here shows.
   */
  addComment: (id, author, text, replyTo) => {
    if (sampleBlocks()) return;
    const comment: Comment = {
      id: uid(),
      author,
      text,
      createdAt: Date.now(),
      ...(replyTo ? { parentId: replyTo.parentId, parentAuthor: replyTo.parentAuthor } : {}),
    };
    const showing = (list: Issue[]) =>
      list.map((i) => (i.id === id ? { ...i, comments: [...(i.comments ?? []), comment] } : i));
    set((s) => ({ issues: showing(s.issues), commentError: '' }));

    void backend
      .append(issuePath(id), 'comments', [comment])
      .then(() => backend.patch(issuePath(id), { updatedAt: Date.now() }))
      .catch((e: BackendError) => {
        set((s) => ({
          issues: s.issues.map((i) =>
            i.id === id
              ? { ...i, comments: (i.comments ?? []).filter((c) => c.id !== comment.id) }
              : i
          ),
          commentError: e?.message || 'it could not be saved',
        }));
      });
  },

  /**
   * REACT, AND SHOW IT AT ONCE — then let the row confirm it.
   *
   * A reaction toggles: leaving one you already have takes it off. Both go on
   * screen first and are written second (like a comment), so the press feels
   * instant; if the write is refused it is put back the way it was. The watcher
   * replaces the whole list when it fires, so the real rows supersede these. A
   * reaction is matched by its natural tuple — there is no id — so "mine" is
   * (this entry, this emoji, me).
   */
  addReaction: (issueId, targetId, emoji) => {
    if (sampleBlocks()) return;
    const me = (get().user?.email ?? '').toLowerCase();
    if (!me) return;
    const mineHere = (r: Reaction) =>
      r.issueId === issueId && r.targetId === targetId && r.reactor === me;
    const same = (r: Reaction) => mineHere(r) && r.emoji === emoji;
    if (get().reactions.some(same)) return; // already mine — pressing again is removeReaction
    // ONE REACTION PER PERSON PER ENTRY — picking another replaces the one you had
    // (Vinnie 6 Oct: a person cannot leave more than one). Take the previous off,
    // here and on the server, before adding the new, so the entry only ever shows
    // one of yours.
    const previous = get().reactions.filter((r) => mineHere(r) && r.emoji !== emoji);
    const mine: Reaction = { issueId, targetId, emoji, reactor: me, at: Date.now() };
    set((s) => ({ reactions: [...s.reactions.filter((r) => !mineHere(r)), mine] }));
    /*
     * SEQUENCED, SO "BEFORE" IS TRUE — and the rollback tells the truth about
     * what the server still holds.
     *
     * These were fired off together and unawaited, each with its own catch. If a
     * REMOVE failed while the ADD succeeded, the catch put the old reaction back
     * on screen while the new row was already written — so the entry showed TWO
     * reactions from one person, the database held two, and it stayed that way:
     * the watcher replaces the list from the server, and the server really did
     * have both. The one rule this code exists to keep was broken by its own
     * error handling, and only another press would clear it.
     *
     * Now the previous come off FIRST and are genuinely awaited, so the new one
     * is never added unless room was actually made for it. On any failure the
     * state is rebuilt from what the server is known to still hold: the previous
     * reactions that were NOT successfully removed, and never the new one, which
     * did not land. So a half-finished swap leaves the entry as it was rather
     * than holding both.
     *
     * No invariant is invented here either. If `previous` somehow held more than
     * one, the rollback restores however many survive rather than silently
     * dropping to one — local state should mirror the server, not tidy it.
     */
    void (async () => {
      const gone: Reaction[] = [];
      try {
        for (const p of previous) {
          await backend.removeReaction(issueId, targetId, p.emoji, me);
          gone.push(p);
        }
        await backend.addReaction(issueId, targetId, emoji, me);
      } catch {
        const held = previous.filter((p) => !gone.includes(p));
        set((s) => ({ reactions: [...s.reactions.filter((r) => !mineHere(r)), ...held] }));
      }
    })();
  },

  removeReaction: (issueId, targetId, emoji) => {
    if (sampleBlocks()) return;
    const me = (get().user?.email ?? '').toLowerCase();
    if (!me) return;
    const same = (r: Reaction) =>
      r.issueId === issueId && r.targetId === targetId && r.emoji === emoji && r.reactor === me;
    const had = get().reactions.find(same);
    if (!had) return;
    set((s) => ({ reactions: s.reactions.filter((r) => !same(r)) }));
    void backend.removeReaction(issueId, targetId, emoji, me).catch(() => {
      set((s) => ({ reactions: [...s.reactions, had] }));
    });
  },

  replyFromInbox: async (boardId, issueId, author, text) => {
    const body = text.trim();
    if (!body) return 'Write a reply first.';
    const comment: Comment = { id: uid(), author, text: body, createdAt: Date.now() };
    // Build the path from the call-out's OWN board, not the one open behind My
    // Work — this is the whole point of replying in place across boards.
    const path = `${inProject(boardId).issues}/${issueId}`;
    try {
      await backend.append(path, 'comments', [comment]);
      await backend.patch(path, { updatedAt: Date.now() });
    } catch (e) {
      return (e as BackendError)?.message || 'That reply could not be sent.';
    }
    // If the card happens to be on the board open behind My Work, show the reply
    // there at once so the card and the inbox never disagree.
    if (boardId === get().projectId) {
      set((s) => ({
        issues: s.issues.map((i) =>
          i.id === issueId ? { ...i, comments: [...(i.comments ?? []), comment] } : i
        ),
      }));
    }
    return '';
  },

  openCardInline: (boardId, issueId) => {
    // PAINT AT ONCE from the light card My Work already holds (`portfolioIssues`),
    // THEN fetch the FULL card (its comments nested, in one `get`) by id and overlay
    // it — no setProject, so none of the board-switch subscription load. Seeding the
    // light card is what makes the open feel INSTANT: the header, facts, status and
    // blocker draw from data already in hand, and only the conversation waits on the
    // fetch (Vinnie, 25 Oct — "I like the info, but want it instantly"; the panel used
    // to blank to a full "Opening the card…" state for the whole round-trip). When My
    // Work does not hold the card — e.g. opened from a notification for a board not in
    // the portfolio — the seed is null and the panel shows its loader exactly as before.
    const seed = (get().portfolioIssues.get(boardId) ?? []).find((i) => i.id === issueId) ?? null;
    set({
      inlineCardBoard: boardId,
      inlineCardId: issueId,
      inlineCard: seed,
      inlineCardLoading: true,
      inlineCardError: '',
    });
    void (async () => {
      try {
        const doc = await backend.get(`${inProject(boardId).issues}/${issueId}`);
        // A later open, or a close, supersedes this one — drop a stale arrival so
        // the panel never flips to a card the person has already moved off.
        if (get().inlineCardId !== issueId || get().inlineCardBoard !== boardId) return;
        set({
          inlineCard: (doc as unknown as Issue) ?? null,
          inlineCardLoading: false,
          inlineCardError: doc ? '' : 'That card could not be opened.',
        });
      } catch (e) {
        if (get().inlineCardId !== issueId || get().inlineCardBoard !== boardId) return;
        set({
          inlineCardLoading: false,
          inlineCardError: (e as BackendError)?.message || 'That card could not be opened.',
        });
      }
    })();
  },

  closeCardInline: () =>
    set({ inlineCardBoard: null, inlineCardId: null, inlineCard: null, inlineCardLoading: false, inlineCardError: '' }),

  postUpdateInline: async (input) => {
    const boardId = get().inlineCardBoard;
    const id = get().inlineCardId;
    if (!boardId || !id) return;
    if (sampleBlocks(boardId)) return;
    const before = get().inlineCard ?? (get().portfolioIssues.get(boardId) ?? []).find((i) => i.id === id);
    if (!before) return;
    const me = get().user?.email ?? '';
    // The SAME helper postStatusUpdate uses — the entry, the standing note, the
    // move and its started-stamp are byte-for-byte what the card's own board would
    // have written.
    const built = buildStatusUpdate(before, input, me, Date.now());
    if (!built) return;
    const { stamped, moved } = built;
    // Reflect it everywhere the card is shown: the panel, the My Work agenda
    // (portfolioIssues — a move re-buckets the row), and the board behind it when
    // that board is the one open.
    const applyPatch = (patch: Partial<Issue>) =>
      set((s) => {
        const upd = (i: Issue) => (i.id === id ? { ...i, ...patch } : i);
        const pf = new Map(s.portfolioIssues);
        const cards = pf.get(boardId);
        if (cards) pf.set(boardId, cards.map(upd));
        return {
          inlineCard: s.inlineCard && s.inlineCard.id === id ? { ...s.inlineCard, ...patch } : s.inlineCard,
          portfolioIssues: pf,
          issues: boardId === s.projectId ? s.issues.map(upd) : s.issues,
        };
      });
    applyPatch(stamped);
    set({ saveProblem: null });
    try {
      await backend.patch(`${inProject(boardId).issues}/${id}`, stamped);
    } catch (e) {
      // Put the card back as it was — the box the person typed into is on screen.
      applyPatch({
        standsLog: before.standsLog,
        stands: before.stands,
        status: before.status,
        startedAt: before.startedAt,
        updatedAt: before.updatedAt,
      });
      const why = e instanceof Error ? e.message : String(e);
      const which = before.ref ?? 'the card';
      set({ saveProblem: `Your status update on ${which} was not saved. ${why}` });
      throw e;
    }
    // The active-board reporter flow is only valid when the card's board is the one
    // open behind the panel; otherwise the full card on that board sends the notice.
    if (moved && boardId === get().projectId) void get().tellReporter(id, moved);
  },

  addCommentInline: async (author, text, replyTo) => {
    const boardId = get().inlineCardBoard;
    const id = get().inlineCardId;
    if (!boardId || !id) return 'This card is not open.';
    if (sampleBlocks(boardId)) return '';
    const body = text.trim();
    if (!body) return 'Write a reply first.';
    const comment: Comment = {
      id: uid(),
      author,
      text: body,
      createdAt: Date.now(),
      ...(replyTo ? { parentId: replyTo.parentId, parentAuthor: replyTo.parentAuthor } : {}),
    };
    const path = `${inProject(boardId).issues}/${id}`;
    // Comments live on the issue, not in the light portfolio row — reflect it in
    // the panel (and the board if open), take it back off if the write is refused.
    const addEverywhere = (present: boolean) =>
      set((s) => {
        const upd = (i: Issue) =>
          i.id === id
            ? {
                ...i,
                comments: present
                  ? [...(i.comments ?? []), comment]
                  : (i.comments ?? []).filter((c) => c.id !== comment.id),
              }
            : i;
        return {
          inlineCard: s.inlineCard && s.inlineCard.id === id ? upd(s.inlineCard) : s.inlineCard,
          issues: boardId === s.projectId ? s.issues.map(upd) : s.issues,
          commentError: '',
        };
      });
    addEverywhere(true);
    try {
      await backend.append(path, 'comments', [comment]);
      await backend.patch(path, { updatedAt: Date.now() });
    } catch (e) {
      addEverywhere(false);
      return (e as BackendError)?.message || 'That reply could not be sent.';
    }
    return '';
  },

  /**
   * Takes a comment off the card without erasing it.
   *
   * The whole array is rewritten rather than the one entry, because comments
   * live inside the issue document and there is no way to address one of them.
   * That is also why this marks rather than removes: an array that never shrinks
   * is one the security rules can allow a reviewer to write, so somebody can
   * withdraw their own words without needing rights over the whole issue.
   */
  removeComment: (id, commentId, by) => {
    if (sampleBlocks()) return;
    const issue = get().issues.find((i) => i.id === id);
    if (!issue) return;
    const gone = (issue.comments ?? []).find((c) => c.id === commentId);
    if (!gone) return;
    const now = Date.now();
    // Show it gone at once.
    set((s) => ({
      issues: s.issues.map((i) =>
        i.id === id
          ? {
              ...i,
              comments: (i.comments ?? []).map((c) =>
                c.id === commentId ? { ...c, removed: true, removedBy: by, removedAt: now } : c
              ),
            }
          : i
      ),
      commentError: '',
    }));
    /*
     * ONE ROW, NOT THE WHOLE ARRAY — the fix proven on 4 Oct 2026. Marking a
     * comment removed is an UPDATE of that single comment (set removed_at /
     * removed_by), which `comments_remove` allows for its author and for an
     * admin. The old path rewrote the entire comments array through `patch`,
     * which on Postgres is a delete-then-insert: there is no delete policy on
     * comments, and the insert policy only lets you write your OWN words, so it
     * could neither remove the row nor put other people's comments back. A
     * targeted update is what the "your words are yours to withdraw" policy was
     * written for.
     */
    void backend
      .editComment(issuePath(id), commentId, { removedAt: now, removedBy: by })
      .catch((e: BackendError) => {
        // Put it back on screen — a withdrawal that quietly did not take is the
        // one thing that must never happen without saying so.
        set((s) => ({
          issues: s.issues.map((i) =>
            i.id === id
              ? { ...i, comments: (i.comments ?? []).map((c) => (c.id === commentId ? gone : c)) }
              : i
          ),
          commentError: e?.message || 'it could not be removed',
        }));
      });
    // The card it was on already knows who took it down. Nowhere else did —
    // so seeing whether comments were being removed across the board meant
    // opening every card in turn, which is not a thing anybody does.
    //
    // By the name they chose, not the one guessed from their address.
    // nameFromEmail turned vsnlweb@gmail.com into "Vsnlweb" while every other
    // screen said "Vinnie Narace".
    void get().recordAudit(
      'comment',
      describeCommentRemoval(issue.ref, displayName(gone.author, get().profiles))
    );
  },

  editComment: (id, commentId, text) => {
    if (sampleBlocks()) return;
    const issue = get().issues.find((i) => i.id === id);
    if (!issue) return;
    const was = (issue.comments ?? []).find((c) => c.id === commentId);
    if (!was) return;
    const me = get().user?.email ?? '';
    const clean = text.trim();
    // Nothing to do if the words did not change, or were emptied — a comment is
    // withdrawn through Remove, not by saving it blank.
    if (!clean || clean === was.text) return;
    const now = Date.now();
    set((s) => ({
      issues: s.issues.map((i) =>
        i.id === id
          ? {
              ...i,
              comments: (i.comments ?? []).map((c) =>
                c.id === commentId ? { ...c, text: clean, editedAt: now, editedBy: me } : c
              ),
            }
          : i
      ),
      commentError: '',
    }));
    void backend
      .editComment(issuePath(id), commentId, { text: clean, editedAt: now, editedBy: me })
      .catch((e: BackendError) => {
        // Put the words back exactly as they were — an edit that silently did not
        // save would leave the writer believing a correction landed.
        set((s) => ({
          issues: s.issues.map((i) =>
            i.id === id
              ? { ...i, comments: (i.comments ?? []).map((c) => (c.id === commentId ? was : c)) }
              : i
          ),
          commentError: e?.message || 'the edit could not be saved',
        }));
      });
    void get().recordAudit(
      'comment',
      describeCommentEdit(issue.ref, displayName(was.author, get().profiles), was.author.toLowerCase() === me.toLowerCase())
    );
  },

  editStatusUpdate: async (id, entryId, text, nextStep) => {
    if (sampleBlocks()) return;
    const before = get().issues.find((i) => i.id === id);
    if (!before) return;
    const log = before.standsLog ?? [];
    const target = log.find((e) => e.id === entryId);
    if (!target) return;
    const me = get().user?.email ?? '';
    const clean = text.trim();
    // An update is narration — the words can be reworded but never blanked.
    if (!clean) return;
    // The next step can be changed, cleared or left alone. `undefined` means the
    // caller did not touch it (a comment edit has no next step); a string sets it,
    // and an empty string clears it. Saving goes ahead when the words OR the next
    // step changed — editing just the next step is a real edit too.
    const nextGiven = nextStep !== undefined;
    const nextClean = (nextStep ?? '').trim();
    const textChanged = clean !== target.text;
    const nextChanged = nextGiven && nextClean !== (target.nextStep ?? '');
    if (!textChanged && !nextChanged) return;
    const now = Date.now();
    const standsLog = log.map((e) =>
      e.id === entryId
        ? {
            ...e,
            text: clean,
            editedAt: now,
            editedBy: me,
            ...(nextGiven ? { nextStep: nextClean || undefined } : {}),
          }
        : e
    );
    // The card's standing note is the newest words, so re-derive it in case the
    // entry just edited is the latest one — the same rule posting an update uses.
    const latestText = [...standsLog].reverse().find((e) => e.text)?.text ?? before.stands ?? '';
    const stamped: Partial<Issue> = { standsLog, stands: latestText, updatedAt: now };
    set({
      issues: get().issues.map((i) => (i.id === id ? { ...i, ...stamped } : i)),
      saveProblem: null,
    });
    try {
      await backend.patch(issuePath(id), stamped);
    } catch (e) {
      set({ issues: get().issues.map((i) => (i.id === id ? before : i)) });
      const why = e instanceof Error ? e.message : String(e);
      const which = before.ref ?? 'the card';
      remember('broke', `an edit to a status update on ${which} was not saved — ${why}`);
      set({ saveProblem: `Your edit to the update on ${which} was not saved. ${why}` });
      throw e;
    }
    void get().recordAudit(
      'issue',
      describeUpdateEdit(before.ref, displayName(target.by, get().profiles), target.by.toLowerCase() === me.toLowerCase())
    );
  },

  removeStatusUpdate: async (id, entryId, by) => {
    if (sampleBlocks()) return;
    const before = get().issues.find((i) => i.id === id);
    if (!before) return;
    const log = before.standsLog ?? [];
    const target = log.find((e) => e.id === entryId);
    // A status MOVE is the card's real history and is never removable — only a
    // note or a plain update (no status) can be taken down. The UI withholds the
    // button for a move; this is the same rule, held at the store. An already
    // withdrawn entry is left alone rather than re-stamped.
    if (!target || target.status || target.removed) return;
    const now = Date.now();
    // Withdrawn, not deleted — flagged like a removed comment, so an admin can
    // still review what was taken down and by whom while everyone else is shown
    // nothing. standsLog is a column we rewrite (the same patch editStatusUpdate
    // makes), so the flag just rides along in that rewrite; there is no rows-with-
    // a-removal-policy table here. The plain removal is recorded in the audit
    // trail below as well.
    const remover = (by ?? '').trim().toLowerCase();
    const standsLog = log.map((e) =>
      e.id === entryId ? { ...e, removed: true, removedAt: now, removedBy: remover } : e
    );
    // The card's standing note is the newest words still SHOWN — skip the one just
    // withdrawn (and any earlier withdrawal), the same way the feed does.
    const latestText = [...standsLog].reverse().find((e) => e.text && !e.removed)?.text ?? '';
    const stamped: Partial<Issue> = { standsLog, stands: latestText, updatedAt: now };
    set({
      issues: get().issues.map((i) => (i.id === id ? { ...i, ...stamped } : i)),
      saveProblem: null,
    });
    try {
      await backend.patch(issuePath(id), stamped);
    } catch (e) {
      // Put it back — a withdrawal that quietly did not take is the one thing
      // that must never happen without saying so.
      set({ issues: get().issues.map((i) => (i.id === id ? before : i)) });
      const why = e instanceof Error ? e.message : String(e);
      const which = before.ref ?? 'the card';
      remember('broke', `removing a status update on ${which} was not saved — ${why}`);
      set({ saveProblem: `The update on ${which} could not be removed. ${why}` });
      throw e;
    }
    void get().recordAudit(
      'issue',
      describeUpdateRemoval(
        before.ref,
        displayName(target.by, get().profiles),
        target.by.toLowerCase() === remover,
        !!target.quiet
      )
    );
  },

  /**
   * THE CUSTOMER'S LOGO — #195, migration 0063.
   *
   * SMALL, AND ONLY THE TWO KINDS A PDF CAN HOLD. pdf-lib embeds PNG and JPEG
   * and nothing else, so an SVG accepted here would be a logo that uploads
   * happily and then stops every document from being produced. The cap is
   * about the file it ends up in: a two-megabyte photograph on the first page
   * of every change request is a document nobody can email.
   */
  uploadProjectLogo: async (file) => {
    const kind = file.type === 'image/png' ? 'png' : file.type === 'image/jpeg' ? 'jpg' : '';
    if (!kind) return 'The logo has to be a PNG or a JPEG — those are what a PDF can carry.';
    if (file.size > 2_000_000) return 'That logo is over 2MB. A smaller one keeps the document light enough to email.';

    const board = idFrom('board', get().projectId, md5.md5);
    /* Named by the board rather than by the file, so replacing a logo replaces
       it rather than leaving the old one behind, unreferenced and unreachable. */
    const key = `${board}/logo.${kind}`;
    try {
      await backend.putFile(key, file, file.type);
    } catch (e) {
      return (e as { message?: string })?.message || 'That logo could not be uploaded.';
    }
    get().setMeta({ logoPath: key, logoType: kind });
    void get().recordAudit('project', 'changed the logo on this board');
    return '';
  },

  projectLogoUrl: async () => {
    const path = get().meta.logoPath;
    if (!path) return '';
    try {
      return await backend.fileUrl(path);
    } catch {
      return '';
    }
  },

  setMeta: (patch) => {
    if (sampleBlocks()) return;
    // Show it at once, then write. Waiting for the database to echo back meant a
    // settings field could visibly revert while the write was still in flight,
    // and a box that undoes your typing reads as broken rather than slow.
    set((s) => ({ meta: { ...s.meta, ...patch } }));
    void backend.patch(boardPath(), patch);
    // A settings change goes in the trail like an access change does — one entry
    // per field that moved, naming both halves — but the database writes it now,
    // on the row itself: record_board_settings_change (migration 0166) mirrors
    // describeSettings for all fourteen fields, so a change made by any writer,
    // not only this one, is recorded once. See describeSettings for the night
    // that made this necessary.
  },

  setNumbering: async (usePrefix) => {
    if (sampleBlocks()) return;
    const { meta, issues } = get();
    const key = (meta.key || '').trim();
    /* The project key writes the prefix; with no key there is nothing to prefix
       with, so "use the key" quietly falls back to plain rather than stamping a
       bare "-267" onto every card. The Settings control disables the choice in
       that state too, so this is only a floor. */
    const prefix = usePrefix && key ? `${key}-` : '';
    const width = 0;
    /* An empty or bare-'#' prefix both mean "plain" — compare them the same way
       so re-choosing the scheme already in use is a no-op rather than a rewrite. */
    const curPrefix = meta.refPrefix && meta.refPrefix !== '#' ? meta.refPrefix : '';
    if (curPrefix === prefix && (meta.refWidth ?? 0) === width) return;

    const scheme = { prefix: prefix || '#', width, next: 0 };
    /* Re-label every card from the number it already carries, so the board reads
       as one scheme. Same order as `issues`, so the diff below lines up. */
    const relabelled = issues.map((it) => {
      const n = numberFromRef(it.ref);
      if (n == null) return it;
      /* Carry the whole token, not just the number: a bug's "B" is part of its
         identity (B4 ⇄ FDD-B4), so re-deriving the label from the bare integer
         would drop it and could collide bug B4 with ordinary #4 on FDD-4. */
      const ref = refFor(scheme, n, isBugRef(it.ref));
      return ref === it.ref ? it : { ...it, ref };
    });

    // Show it at once — the scheme and every new label — then write.
    set((s) => ({ meta: { ...s.meta, refPrefix: prefix, refWidth: width }, issues: relabelled }));
    void backend.patch(boardPath(), { refPrefix: prefix, refWidth: width });
    await Promise.all(
      relabelled
        .filter((it, i) => it.ref !== issues[i].ref)
        .map((it) => backend.patch(issuePath(it.id), { ref: it.ref })),
    );
    // Recorded by the database on the row — record_board_control_change (0167),
    // from ref_prefix: the project key (e.g. ACME-1) or plain numbers (e.g. #1).
  },

  // Access changes write the whole document, which is what the rules validate.
  // They also refuse to let you strand the board: you must remain an admin on it.
  writeAccess: async (emails, admins) => {
    /*
     * REFLECTED AT ONCE, then written. The People screen reads each person's
     * admin flag and role from `access`, and the only thing that otherwise moves
     * `access` is the allowlist watcher echoing this very write back — a round
     * trip that rewrites the whole people list, so until it returned the row sat
     * on its old state for as long as the write took: seconds on a small
     * workspace, minutes on a populated one. Every caller (grant admin, add,
     * remove) hands in the complete desired lists derived from the current
     * `access`, so the optimistic value equals what the echo will carry and the
     * row does not flicker. Roll back if the write is refused — the same shape as
     * setSuspended.
     */
    const prev = get().access;
    set({ access: { emails, admins } });
    try {
      // Written in whatever shape the document is already in — see src/access.ts.
      await backend.set(`${C.access}/allowlist`, accessWrite(emails, admins, get().legacyRoles));
    } catch (err) {
      set({ access: prev });
      throw err;
    }
  },

  addToProject: async (projectId, email, role, person) => {
    const e = email.trim().toLowerCase();
    if (!e.includes('@')) return;
    const { access } = get();
    // New to the workspace? They need to be able to sign in before being able
    // to open anything.
    if (!access.emails.includes(e)) await get().addPerson(e, role, person);

    const project = get().projects.find((p) => p.id === projectId);
    const named = project?.members ?? [];
    /*
     * A PROJECT WITH NOBODY NAMED IS OPEN TO EVERYONE, so adding the first
     * person to it would silently do nothing — they can already open it, and
     * the list would still read as open. Naming them means naming everybody
     * who can open it today, or adding one person would quietly shut everybody
     * else out.
     */
    const next = named.length === 0 ? [...new Set([...access.emails, e])] : [...new Set([...named, e])];
    await get().setProjectMembers(projectId, next);
  },

  removeFromProject: async (projectId, email) => {
    const e = email.trim().toLowerCase();
    const project = get().projects.find((p) => p.id === projectId);
    const { access } = get();
    // Same reasoning in reverse: an open project has no list to remove from,
    // so it becomes everybody who has access today, except them.
    const from = (project?.members ?? []).length === 0 ? access.emails : project!.members!;
    await get().setProjectMembers(projectId, from.filter((m) => m.toLowerCase() !== e));
  },

  /**
   * ADD SOMEBODY, AND ACTUALLY TELL THEM.
   *
   * This used to write three rows and stop. The person existed, could sign in,
   * and had no idea any of it had happened — somebody had to message them
   * separately and explain what FlightDeck was. Half of "adding a person" was
   * being done by hand and nothing said so.
   *
   * The invitation is tried first because it is the part that can be refused:
   * only an admin may invite (#359 — a board manager no longer qualifies), and
   * nobody may hand out more than they hold. Writing the rows first and then
   * discovering that would leave somebody added who should not have been.
   *
   * If invitations are not possible at all — the Firebase copy, which has no
   * server — it falls back to exactly what it did before rather than failing.
   * Live still runs that way.
   */
  /**
   * SEND SOMEBODY'S INVITATION AGAIN.
   *
   * The state this exists for: added weeks ago, never turned up. Until now the
   * only remedy was to remove them and add them back, which is a destructive
   * action performed for a clerical reason — and it takes their role, their
   * board access and their place in the trail with it.
   *
   * IT IS THE SAME CALL AS ADDING THEM, deliberately. `invite` already does the
   * right thing for somebody who exists: it sends a sign-in link rather than an
   * invitation, and says so in `reason`. Writing a second path would be two
   * things to keep in step for one difference that has already been handled.
   *
   * NOTHING IS WRITTEN. They are already in the list with the role they were
   * given, and a resend that quietly reset either would be a surprise.
   */
  resendInvite: async (email) => {
    const e = email.trim().toLowerCase();
    if (!get().access.emails.includes(e)) return;
    const out = await accounts.invite(e, get().roles[e] ?? 'viewer', {
      returnTo: window.location.origin,
    });
    set({ lastInviteNote: out.reason ?? '' });
    await get().recordAudit('invite', `sent ${e} their invitation again`);
  },
  /*
   * A RESET, not a resend. The same server call the add flow uses for a
   * temporary password is a reset when the account already exists — a new
   * temporary password and `password_set_at` cleared, so the old password
   * stops working and the choose-your-own screen is the only way back in. The
   * role and boards are passed as they already stand, so nothing about their
   * access changes; only the credential does. The temporary password is handed
   * back to be shown once and stored nowhere.
   */
  resetPassword: async (email) => {
    const e = email.trim().toLowerCase();
    if (!get().access.emails.includes(e)) return '';
    const out = await accounts.invite(e, get().roles[e] ?? 'viewer', {
      returnTo: window.location.origin,
      tempPassword: true,
    });
    set({ lastInviteNote: out.reason ?? '' });
    await get().recordAudit('invite', `reset ${e}'s password`);
    return out.tempPassword ?? '';
  },

  /*
   * CORRECTING AN ADDRESS — the one identity change the browser cannot make.
   *
   * The address is the identity: the people primary key, the auth login, and the
   * same string denormalised across every table that names a person. So this is a
   * server call (change-email), gated admin-only there exactly as invite is, which
   * re-keys the auth login and calls the one transactional RPC. Nothing is written
   * from here — a refusal is the server's, surfaced as its own words. The profiles
   * watcher brings the corrected row back; the address a person signs in with is
   * not a value this store writes by hand.
   */
  changeEmail: async (oldEmail, newEmail) => {
    const from = oldEmail.trim().toLowerCase();
    const to = newEmail.trim().toLowerCase();
    if (!from || !to) return 'An address is missing.';
    if (from === to) return 'That is already their address.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) return 'That does not look like an email address.';
    if (get().access.emails.includes(to)) return 'Somebody with that address is already in this workspace.';
    if (!accounts.changeEmail) return 'This workspace cannot correct an address yet.';
    try {
      await accounts.changeEmail(from, to);
    } catch (err) {
      return err instanceof Error ? err.message : String(err);
    }
    await get().recordAudit('invite', `corrected ${from}'s address to ${to}`);
    return '';
  },

  /*
   * DEACTIVATING A PERSON — a reversible pause, not a removal (0208).
   *
   * suspended_at is a moment, so turning it on stamps now and turning it off
   * writes null; every role and board is left exactly as it stands, which is what
   * makes this reversible. The app gates sign-in on the column in the same place
   * it gates password_set_at, so a suspended person reaches a "paused" screen and
   * no further while everything about their access is kept. Optimistic like
   * setPersonProfile, and the trail records both directions.
   */
  setSuspended: async (email, suspended) => {
    const e = email.trim().toLowerCase();
    const prev = get().profiles;
    const when = suspended ? Date.now() : null;
    set({ profiles: { ...prev, [e]: { ...(prev[e] ?? {}), suspendedAt: when } as Profile } });
    try {
      await backend.patch(`${C.profiles}/${e}`, { suspendedAt: when });
    } catch (err) {
      set({ profiles: prev });
      throw err;
    }
    // NOT recorded here. record_person_change (0208) writes the deactivate/
    // reactivate line on the people UPDATE, word for word — the same reasoning as
    // role and admin changes: a rule a browser can skip is not a rule, so the
    // trigger is the one that stays, and a browser copy would double-log.
  },

  requestToAdd: async ({ boardId, kind, email, displayName, role, note }) => {
    const me = get().user?.email?.toLowerCase() ?? '';
    if (!me) return 'You are not signed in.';
    const e = email.trim().toLowerCase();
    if (!e.includes('@')) return 'That is not an email address.';
    /*
     * ALREADY ASKED — G1, #359. One pending ask per person per board: a second
     * would let two admins each approve one and add the same person twice. The
     * database enforces this with a partial unique index (0079); this is the
     * kind word for it, caught before the write so the asker sees "already
     * requested" and not a raw constraint error.
     */
    if (get().boardRequests.some((r) => r.boardId === boardId && r.email === e && r.status === 'pending'))
      return 'Someone has already asked for that address on this board — it is waiting on an admin.';
    /* Already ON THIS BOARD — Gap 3, #359. Asking for someone who is already a
       member does nothing but put a no-op decision in front of an admin. Best
       effort: only what the board's roles show here, which for a person the
       asker can see is enough. */
    if (kind === 'person' && get().projectRoles[e]?.[boardId])
      return 'They are already on this board.';
    /* A uuid, because Postgres keys this table on one and reads it back as one —
       the app's own short ids would be hashed on the way in and not match. */
    const id = crypto.randomUUID();
    try {
      await backend.set(`${C.boardRequests}/${id}`, {
        id,
        boardId,
        kind,
        email: e,
        displayName: displayName.trim(),
        role,
        note: (note ?? '').trim(),
        requestedBy: me,
        status: 'pending',
        createdAt: Date.now(),
      });
    } catch (err) {
      /* The index caught a race the check above did not — two asks for the same
         address landing at once. Same answer, from the far side. */
      const said = (err as BackendError)?.message ?? '';
      if (/board_requests_one_pending|duplicate key|unique/i.test(said))
        return 'Someone has already asked for that address on this board — it is waiting on an admin.';
      return 'That request could not be filed. Try again.';
    }
    /* The row is what the app shows; the email is a nudge on top. Fired and not
       awaited-for-success — a mail that does not leave must not fail the filing,
       because the in-app queue already carries it. See notify-request (#359). */
    accounts.notifyRequest(id).catch(() => {});
    /* Recorded so the trail says who asked for what. */
    await get().recordAudit(
      'invite',
      `asked an admin to add ${e}${kind === 'assistant' ? ' as an assistant' : ''}`,
      { projectId: boardId }
    );
    return '';
  },

  decideRequest: async (id, approved, reason, grantRole) => {
    const req = get().boardRequests.find((r) => r.id === id);
    if (!req) return 'That request is no longer here.';
    /* Only a pending request is a decision to make. If realtime has already
       shown it decided — another admin got there first — say so rather than
       writing over their answer (G3). */
    if (req.status !== 'pending') return 'That request has already been decided.';
    const me = get().user?.email?.toLowerCase() ?? '';
    /* The admin may hand out a different role from the one asked for (G9); it
       is still capped at member by the table, and the picker offers no more. */
    const role = grantRole ?? req.role;
    const path = `${C.boardRequests}/${id}`;
    const NO = 'That request has already been decided.';
    /* Reflect the decision in the queue AT ONCE, rather than waiting for the
       realtime round-trip to bring it back. Without this the card sat as
       `pending` after a decision that had actually landed — so it looked like
       nothing happened, and a second click hit a row already decided and said
       so. The write is still the truth; realtime later confirms the same
       values. Kept in step with the exact values written below. */
    const patchLocal = (changes: Partial<BoardRequest>) =>
      set((s) => ({ boardRequests: s.boardRequests.map((r) => (r.id === id ? { ...r, ...changes } : r)) }));
    const now = Date.now();

    /*
     * ⚠️ THE DECISION IS A CLAIM — decided EXACTLY ONCE (G3).
     *
     * The local check above catches the ordinary case; this is the one that
     * catches two admins acting in the same second, before realtime has told
     * either. `patchIf` moves the row only while it is still `pending`, so the
     * first admin wins and the second's guard no longer matches — it changes
     * nothing and is told the request is already decided. Crucially the CLAIM
     * comes BEFORE any side effect, so a losing admin never adds the person or
     * sends the invitation: the request they saw was already answered.
     */
    if (!approved) {
      const declineReason = (reason ?? '').trim();
      const claimed = await backend.patchIf(
        path,
        { status: 'pending' },
        { status: 'declined', decidedBy: me, decidedAt: now, declineReason }
      );
      if (claimed) {
        patchLocal({ status: 'declined', decidedBy: me, decidedAt: now, declineReason });
        /* Tell the requester it was decided — Gap 2. The row already carries it;
           this is the nudge on top, so its failure never fails the decision. */
        accounts.notifyDecision(id).catch(() => {});
      }
      return claimed ? '' : NO;
    }

    /*
     * AN ASSISTANT IS ONLY APPROVED; THE REQUESTER FINISHES IT — so the one-time
     * password never passes through the admin. Claiming to `approved` is the
     * whole of the admin's part. The role rides on the row because the requester
     * reads it back at fulfilment, so a lifted role must be written.
     */
    if (req.kind === 'assistant') {
      const claimed = await backend.patchIf(
        path,
        { status: 'pending' },
        { status: 'approved', role, decidedBy: me, decidedAt: now }
      );
      if (claimed) {
        patchLocal({ status: 'approved', role, decidedBy: me, decidedAt: now });
        /* Approved — tell the requester so they know to come and finish it (Gap 2). */
        accounts.notifyDecision(id).catch(() => {});
      }
      return claimed ? '' : NO;
    }

    /*
     * A PERSON IS ADDED OUTRIGHT — but the add is a side effect, so the claim
     * comes first. Claim to `approved`; only the admin who won adds them; then
     * finalise to `fulfilled`. If the add fails, hand the claim back to
     * `pending` so the request can be tried again rather than sticking as
     * done-but-not-added — the same retry safety the old order had, without its
     * race.
     */
    const claimed = await backend.patchIf(
      path,
      { status: 'pending' },
      { status: 'approved', role, decidedBy: me, decidedAt: now }
    );
    if (!claimed) return NO;
    patchLocal({ status: 'approved', role, decidedBy: me, decidedAt: now });
    try {
      await get().addPerson(req.email, role, { first: req.displayName, last: '' }, { boards: [{ id: req.boardId, role }] });
    } catch (err) {
      await backend.patchIf(path, { status: 'approved' }, { status: 'pending' }).catch(() => {});
      /* The add failed — hand it back to pending, in the queue and on screen, so
         it can be tried again rather than stranded as approved-but-not-added. */
      patchLocal({ status: 'pending', decidedBy: '', decidedAt: null });
      /* RETURNED, NOT THROWN. A thrown error left the caller's button stuck on
         "Approving…" with nothing said — the card flashed back to pending and
         looked broken. Returned as the problem string every other refusal uses,
         it un-sticks the button and shows why. */
      return (err as { message?: string })?.message || 'They could not be added — the request is still open to try again.';
    }
    await backend.patchIf(path, { status: 'approved' }, { status: 'fulfilled' });
    patchLocal({ status: 'fulfilled', decidedBy: me, decidedAt: now });
    /* Added and done — tell the requester their colleague is in (Gap 2). */
    accounts.notifyDecision(id).catch(() => {});
    return '';
  },

  clearRequest: (id) => {
    /* A view convenience, not a delete — the row stays for the admin's Decided
       log. Persisted per browser and mirrored into the store so the nav badge
       and the list drop it together. */
    set({ requestsCleared: markRequestCleared(id, Date.now()) });
  },

  setCover: async ({ delegate, delegator, until = null, reason = '', active }) => {
    const me = get().user?.email?.toLowerCase() ?? '';
    if (!me) return 'Sign in to arrange cover.';
    const forWhom = (delegator ?? me).trim().toLowerCase();
    const nominee = delegate.trim().toLowerCase();
    if (!nominee) return 'Choose who signs while you are away.';
    if (nominee === forWhom) return 'Someone cannot cover themselves — choose a different teammate.';
    /* Covering somebody else is an admin act: only an admin may do it, and they
       must say why. The database enforces both (the RLS write policy and the
       reason CHECK, 0130); this is the near side of the same rules, so the message
       is friendly rather than a raw refusal. */
    const forSomeoneElse = forWhom !== me;
    if (forSomeoneElse && get().role !== 'admin')
      return 'Only an admin can arrange cover for someone else.';
    if (forSomeoneElse && !reason.trim())
      return 'Give a reason for arranging this cover.';
    /* Reuse the person's existing cover row so this CHANGES it rather than leaving
       a second — a person has at most one standing cover. Turning an old one back
       on keeps its created time. */
    const existing = get().signoffCover.find((c) => c.delegator === forWhom);
    const id = existing?.id ?? crypto.randomUUID();
    /* Keep the cover's on/off state unless the caller means to turn it on. So
       changing the end date or swapping the nominee on a PAUSED cover leaves it
       paused — it never silently re-grants the right to sign for someone. A fresh
       cover (no existing row) starts on. */
    const nextActive = active ?? existing?.active ?? true;
    const row = {
      id,
      delegator: forWhom,
      delegate: nominee,
      until: until ?? null,
      active: nextActive,
      /* Only carried when arranging for someone else; the guard clears it for a
         self-set cover anyway (set_by_admin is false there). */
      reason: forSomeoneElse ? reason.trim() : '',
      createdAt: existing?.createdAt ?? Date.now(),
    };
    try {
      await backend.set(`${C.signoffCover}/${id}`, row);
    } catch (err) {
      const said = (err as BackendError)?.message ?? '';
      if (/signoff_cover_not_self/i.test(said))
        return 'Someone cannot cover themselves — choose a different teammate.';
      if (/signoff_cover_reason_when_admin/i.test(said))
        return 'Give a reason for arranging this cover.';
      return 'That cover could not be set. Try again.';
    }
    /* Shown at once; set_by / set_by_admin are the database's to fill (the guard),
       and realtime brings the finished row back over the top of this. */
    set((s) => ({
      signoffCover: [
        { ...row, setBy: me, setByAdmin: forSomeoneElse } as SignoffCover,
        ...s.signoffCover.filter((c) => c.id !== id),
      ],
    }));
    /* The History line — "asked …"/"arranged …" — is the database's now
       (record_cover_change, 0162); written once, wherever the row is written. */
    return '';
  },

  clearCover: async (delegator) => {
    const me = get().user?.email?.toLowerCase() ?? '';
    if (!me) return 'Sign in to change cover.';
    const forWhom = (delegator ?? me).trim().toLowerCase();
    if (forWhom !== me && get().role !== 'admin')
      return 'Only an admin can change cover for someone else.';
    const existing = get().signoffCover.find((c) => c.delegator === forWhom && c.active);
    /* Nothing on means nothing to do — a quiet success, not an error. */
    if (!existing) return '';
    try {
      /* Kept, not deleted (active=false), so the person's last nominee is
         remembered when they turn cover back on. */
      await backend.patch(`${C.signoffCover}/${existing.id}`, { active: false });
    } catch {
      return 'That cover could not be turned off. Try again.';
    }
    set((s) => ({
      signoffCover: s.signoffCover.map((c) => (c.id === existing.id ? { ...c, active: false } : c)),
    }));
    /* "turned off …" is written by the database now (record_cover_change, 0162). */
    return '';
  },

  withdrawRequest: async (id) => {
    const req = get().boardRequests.find((r) => r.id === id);
    if (!req) return '';
    if (req.status !== 'pending') return 'That request has already been decided.';
    /* The delete policy (0079) only lets the asker remove their own pending
       row, so this is refused server-side for anyone else — the button is just
       the near side of that. */
    await backend.remove(`${C.boardRequests}/${id}`);
    /* Gone at once — a withdrawal is the asker taking it back, so it leaves the
       list rather than lingering; realtime confirms the delete after. */
    set((s) => ({ boardRequests: s.boardRequests.filter((r) => r.id !== id) }));
    await get().recordAudit('invite', `withdrew the request to add ${req.email}`, {
      projectId: req.boardId,
    });
    return '';
  },

  fulfilAssistantRequest: async (id) => {
    const req = get().boardRequests.find((r) => r.id === id);
    if (!req) return '';
    /*
     * THE ACCOUNT IS MINTED ON THE REQUESTER'S BEHALF, and the password comes
     * back here. addPerson carries `requestId` to the invite function, which is
     * the one thing that lets a non-admin add anybody — and only ever this one
     * assistant, on this one board, at the role the admin approved, because the
     * server reads all of that from the request row and ignores the rest. The
     * function marks the request `fulfilled` itself once the account exists.
     */
    const password = await get().addPerson(
      req.email,
      req.role,
      { first: req.displayName, last: '' },
      { assistant: true, boards: [{ id: req.boardId, role: req.role }], requestId: id }
    );

    /*
     * REGISTER IT ON THE BOARD, so it shows on the Assistants screen with its
     * name and its "may answer" switch — G1, #359. The account now exists, but
     * `meta.assistants` is what LISTS it, and the direct Assistants flow writes
     * this same entry (see AssistantAccount). Without it the assistant works —
     * it can sign in and push — but is invisible to manage, which is a pain the
     * moment somebody wants to turn it on or take it off.
     *
     * ⚠️ ONLY WHEN THE BOARD IT IS FOR IS THE ONE OPEN. `setMeta` writes the
     * OPEN board's meta (boardPath → projectId); the requester finishes the
     * request on that board's own screen, so this holds — but the guard means a
     * finish from anywhere else registers the account and simply skips the
     * listing rather than writing it onto the wrong board.
     */
    if (password && get().projectId === req.boardId) {
      const meta = get().meta;
      const key =
        (req.displayName || req.email.split('@')[0])
          .toLowerCase()
          .replace(/[^a-z0-9-]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'assistant';
      get().setMeta({
        assistants: {
          ...(meta.assistants ?? {}),
          [key]: {
            ...(meta.assistants?.[key] ?? {}),
            name: req.displayName || req.email,
            email: req.email,
            addedAt: meta.assistants?.[key]?.addedAt ?? Date.now(),
            pusher: 'assistant',
          },
        },
      });
    }
    /* Mark it done in the queue at once — the invite function set `fulfilled`
       server-side; reflecting it here moves the card out of the awaiting lane
       without waiting on realtime. */
    if (password) set((s) => ({ boardRequests: s.boardRequests.map((r) => (r.id === id ? { ...r, status: 'fulfilled' } : r)) }));
    return password;
  },

  addPerson: async (email, role, person, options) => {
    const e = email.trim().toLowerCase();
    if (!e.includes('@')) return '';
    const { access, writeAccess } = get();
    /*
     * ALREADY HERE MEANS THERE IS NOTHING TO ADD — except when the point is to
     * change WHAT they are.
     *
     * An assistant that predates the Assistants tab is already a row on the
     * guest list, put there by the migration or by somebody adding it as a
     * person because that was the only way. Issuing it credentials from the
     * Assistants tab has to convert that row rather than refuse it, and this
     * line refused it: `claude-cli@flightdeck.build` sat in `access.emails`, so
     * the button returned an empty password and the screen reported that no
     * password came back — a dead button with a puzzling excuse.
     *
     * The screen asks before converting somebody who is already listed as a
     * person, because the other way to arrive here is a mistyped address, and
     * an unasked conversion would take a colleague off People and access.
     */
    if (access.emails.includes(e) && !options?.assistant) return '';

    let told = false;
    let handed = '';
    try {
      const out = await accounts.invite(e, role, {
        returnTo: window.location.origin,
        firstName: person?.first || undefined,
        lastName: person?.last || undefined,
        boards: options?.boards,
        tempPassword: options?.tempPassword,
        companyRole: options?.companyRole,
        assistant: options?.assistant,
        requestId: options?.requestId,
      });
      told = out.emailed;
      handed = out.tempPassword ?? '';
      /*
       * SAY WHEN NOBODY WAS TOLD, AND WHY.
       *
       * The first person added this way was added correctly and sent nothing,
       * and the only record of that was one word in the audit trail. The screen
       * said "added" — so the natural conclusion was that an invitation had
       * gone out, and the invitation was the entire point of this path.
       *
       * Held on the store rather than thrown, because it is not a failure: they
       * ARE added. It is the difference between what happened and what somebody
       * would reasonably assume happened.
       */
      /*
       * WHENEVER THERE IS SOMETHING TO SAY, not only when nothing was sent.
       *
       * Somebody who already had an account is sent a SIGN-IN LINK rather than
       * an invitation — which counts as emailed, so this said nothing at all and
       * the screen read as an ordinary add. The admin has no way to know the
       * person got a different message from the one they expected, which is the
       * kind of thing you find out when they reply asking what this is about.
       */
      set({ lastInviteNote: out.reason ?? '' });
      // The invitation writes the rows itself, on the server, where it can be
      // sure the caller was allowed to. Doing it again here would be a second
      // opinion from the side that is not entitled to one.
      if (out.added) {
        /*
         * The name the admin typed, or the address until they set their own.
         * Written here rather than on the server because a profile is the app's
         * shape and the invite is about the ACCOUNT — and because somebody
         * renaming themselves later must not be overwritten by an invitation.
         *
         * ⚠️ NOT ON THE FULFILMENT PATH. Writing a profile is writing the
         * `people` table, whose WITH CHECK is admin-only — the #356 wall. The
         * requester finishing an assistant is not an admin, so this would throw
         * after the account was already made and the password already handed
         * back. It is skipped for them: an assistant's shown name lives in
         * `meta.assistants` (the board they run, which they may write), not on
         * the people row, so nothing is lost. The server trigger
         * (people_record_change) still records the add either way.
         */
        if (!options?.requestId) {
          const shown =
            person?.first || person?.last
              ? [person.first, person.last].filter(Boolean).join(' ')
              : nameFromEmail(e);
          await backend.patch(`${C.profiles}/${e}`, {
            name: shown,
            ...(person?.first ? { firstName: person.first } : {}),
            ...(person?.last ? { lastName: person.last } : {}),
            ...(options?.title ? { title: options.title } : {}),
            // The invite function already wrote company_role on the people row
            // (the same server call as the board grants). Mirroring it into the
            // local profile here means the roster shows the Vendor tag the instant
            // the add returns, rather than after the profiles watcher echoes back.
            ...(options?.companyRole ? { companyRole: options.companyRole } : {}),
          });
        }
        /*
         * THE TRAIL SAYS A PASSWORD WAS ISSUED, AND NEVER WHAT IT WAS.
         *
         * Somebody else knowing a way into an account is exactly the thing an
         * audit trail exists to record — and writing the password into that
         * trail would put it somewhere permanent and readable, which is the
         * whole failure this is trying not to be.
         */
        await get().recordAudit(
          'invite',
          `invited ${e} as ${role}` +
            (handed ? ' with a temporary password' : '') +
            (told ? '' : ' (no email sent)')
        );
        return handed;
      }
    } catch (err) {
      // A refusal is the person's answer and must reach them. Anything else —
      // offline, a function that is not deployed — falls through to the old way.
      const reason = (err as BackendError)?.code;
      if (reason === 'invite-refused') throw err;
    }

    await writeAccess([...access.emails, e], access.admins);
    await backend.set(`${C.roles}/${e}`, { role });
    // Seeded with a name derived from the address, so an invited person is never
    // shown to everyone else as a raw email while they wait to sign in.
    await backend.patch(`${C.profiles}/${e}`, { name: nameFromEmail(e) });
    /*
     * ⚠️ NOT RECORDED HERE — #336. `people_record_change` (0068) writes
     * `added <email> as <role>` on the insert, word for word, so writing it
     * from the browser as well put two identical lines in the trail a second
     * apart. The trigger is the one that stays: 0068's whole argument is that a
     * rule a script can skip is not a rule, and the browser is exactly what a
     * script skips. Every removal in this commit is the same reasoning.
     */
    /*
     * The old path adds the rows from the browser and cannot issue a password —
     * that needs the service key, which only the function holds. Empty is the
     * honest answer, and the screen says so rather than showing a blank box
     * where a password should be.
     */
    return '';
  },

  setMyPassword: async () => {
    const me = get().user?.email?.toLowerCase();
    if (!me) return;
    await backend.patch(`${C.profiles}/${me}`, { passwordSetAt: Date.now() });
  },

  setPersonRole: async (email, role) => {
    const e = email.toLowerCase();
    // Optimistic: show the new default role at once instead of waiting on the
    // realtime echo and its full re-read of the people table (a ~0.5-1s round-trip).
    // The roles watcher reconciles to the same value when its broadcast lands; a
    // rejected write rolls back and the caller surfaces the error.
    const prev = get().roles;
    set({ roles: { ...prev, [e]: toRole(role) as ProjectRole } });
    recomputeMyRole();
    try {
      await backend.patch(`${C.roles}/${e}`, { role });
    } catch (err) {
      set({ roles: prev });
      recomputeMyRole();
      throw err;
    }
    /* The trigger writes `set <email> to <role> by default` — #336. */
  },

  /**
   * WHAT SOMEBODY MAY DO ON ONE BOARD.
   *
   * ONE DOCUMENT PER BOARD, at roles/{email}/on/{projectId}.
   *
   * It was a map inside the person's roles document first, to save a read.
   * That could not be secured: one document holding every board's answer means
   * a manager of ONE board writes the entry for all of them, and the rule that
   * would have to check which entry moved cannot be written — rules hand back
   * the changed keys as a Set and cannot take an element out of one.
   *
   * With the board in the PATH the rule is one line, and a manager of one
   * board cannot reach another's rather than being trusted not to.
   */
  setRoleOn: async (email, projectId, role) => {
    const e = email.toLowerCase();
    // Optimistic: move the row the instant the change is made, rather than waiting
    // on the 'on' watcher's realtime echo + full re-read of board_roles (a ~0.5-1s
    // round-trip). The role controls read straight from projectRoles (PersonAccess),
    // so this is what makes them feel instant. The echo reconciles to the same value
    // when it lands; a rejected write rolls back and the caller surfaces the error.
    const prev = get().projectRoles;
    set({ projectRoles: { ...prev, [e]: { ...(prev[e] ?? {}), [projectId]: toRole(role) } } });
    recomputeMyRole();
    try {
      await backend.set(`${C.roles}/${e}/on/${projectId}`, { role });
    } catch (err) {
      set({ projectRoles: prev });
      recomputeMyRole();
      throw err;
    }
    /* `board_roles_record_change` writes `set <email> to <role> on <board>`,
       and looks the board's name up itself rather than trusting the caller to
       have it — a script setting a role had no name to send. #336. */
  },

  /**
   * Put a board's answer back to the person's default.
   *
   * NOT THE SAME AS SETTING IT TO WHAT THE DEFAULT HAPPENS TO BE TODAY. Left
   * as an explicit value it stops following the default, so changing somebody
   * from reviewer to member later would silently miss this board.
   */
  clearRoleOn: async (email, projectId) => {
    const e = email.toLowerCase();
    // Optimistic, the mirror of setRoleOn: drop this board's own answer at once so
    // the row falls back to the default role immediately, rather than after the
    // echo's round-trip. Reconciled by the 'on' watcher; rolled back on a rejected
    // delete.
    const prev = get().projectRoles;
    const next = { ...prev };
    if (next[e]) {
      const inner = { ...next[e] };
      delete inner[projectId];
      if (Object.keys(inner).length) next[e] = inner;
      else delete next[e];
    }
    set({ projectRoles: next });
    recomputeMyRole();
    try {
      await backend.remove(`${C.roles}/${e}/on/${projectId}`);
    } catch (err) {
      set({ projectRoles: prev });
      recomputeMyRole();
      throw err;
    }
    /* The same trigger, on the delete: `returned <email> to their default role
       on <board>`. #336. */
  },

  setPersonAdmin: async (email, isAdmin) => {
    const e = email.toLowerCase();
    const { access, user, writeAccess } = get();
    if (e === user?.email?.toLowerCase()) return; // never change your own admin
    const admins = isAdmin
      ? [...new Set([...access.admins, e])]
      : access.admins.filter((x) => x !== e);
    await writeAccess(access.emails, admins);
    /* `made <email> an admin` / `removed <email> as an admin` comes off the
       `is_admin` change in `people_record_change`. This is the pair Vinnie
       actually saw: four presses, eight lines. #336. */
  },

  /**
   * A BOARD OF YOUR OWN.
   *
   * IT NAMES ITSELF BEFORE IT IS WRITTEN, and the id carries a random tail —
   * see projectId. Two people starting a board called "Website" on the same day
   * would otherwise land on the same document, and writing over a document that
   * already exists is not an error, so the second one would quietly take the
   * first one's issues.
   *
   * WRITTEN, THEN OPENED, IN THAT ORDER. Switching first would clear the board
   * and start listeners on a document that does not exist yet — which reads on
   * screen as a board that failed to load rather than one being made.
   */
  createProject: async (name, key, def) => {
    const me = get().user?.email ?? '';
    // Hold the HASHED id the board reads back as, not the slug it is minted from:
    // idFrom hashes makeProjectId's slug to the uuid every read of the board carries,
    // and the current board, the stored choice and the list filter all compare against
    // that uuid. Holding the slug left a new board unfindable by its own id — no
    // go-live, no budget, and a reload fell to the first board. A uuid passes through
    // idFrom unchanged, so every write below still hits the same row. Full story:
    // project-board-id-slug-vs-uuid-lesson + test-a-project-round-trips.
    const id = idFrom('board', makeProjectId(name, Math.random().toString(36).slice(2, 7)), md5.md5);
    const doc_ = newProjectDoc({ id, name, key, owner: me, now: Date.now(), def });
    await backend.set(`${C.projects}/${id}`, doc_);
    /*
     * THE PERSON WHO MADE IT RUNS IT, AND THE SCREEN SAYS SO.
     *
     * Owning a board already granted everything through the rules, so this
     * changes nothing about what they CAN do. What it changes is what every
     * screen shows: without it, somebody who started a board saw themselves
     * listed as a reviewer on it — their default — while quietly being able to
     * change its settings and delete its issues. The screen disagreeing with
     * the rules is the exact fault "view as" was built to find, and here it
     * was, in the feature that makes boards.
     *
     * Manager rather than admin: admin is a WORKSPACE rank — adding people,
     * appointing other admins — and making a board is not a reason to be
     * handed the workspace. Manager is the top of the per-board ladder and is
     * what "runs this board" means.
     */
    await backend.set(`${C.roles}/${me.toLowerCase()}/on/${id}`, { role: 'manager' });
    /*
     * ── THE PHASES THE PROJECT STARTS WITH — #377 ────────────────────────────
     *
     * Written HERE rather than on a trigger (unlike the types below), and the
     * difference is what makes that safe: `issues.kind` has a foreign key onto
     * types, so a board with none can hold no card at all — which is why types
     * must exist however a board is made. `issues.phase` is NULLABLE and "No
     * phase" is a valid answer, so a board made by a script without phases is a
     * perfectly ordinary board, not a broken one. That lets the wizard do what it
     * needs to: let somebody SHAPE the phases before the board exists and write
     * exactly that set. `def.phases` is the wizard's edited list; with none given
     * (Start blank, or the old two-field path) the type's template is seeded,
     * with target days spread across the start → go-live span when both are set.
     */
    const phaseDrafts = def?.phases ?? seedPhases(def?.projectType, def?.startDate, def?.goLive);
    await Promise.all(
      phaseDrafts.map((ph, i) => {
        const pid = uid();
        return backend.set(`${inProject(id).phases}/${pid}`, {
          id: pid,
          name: ph.name.trim(),
          position: i,
          status: 'planned' as const,
          ...(ph.targetEnd ? { targetEnd: ph.targetEnd } : {}),
          createdAt: Date.now(),
        });
      })
    );
    /*
     * ⚠️ THE FOUR TYPES A NEW BOARD STARTS WITH ARE NOT WRITTEN HERE — #302.
     *
     * They were, for about an hour, and it was the wrong place: `issues.kind`
     * has a foreign key onto them since 0072, so a board made by ANYTHING else
     * — a fixture, a seed script, `stand-up-production.mjs`, an importer nobody
     * has written yet — could not hold a single card. Six checks failed at once
     * saying so.
     *
     * Migration 0073 puts it on a trigger against `boards`, so making a board
     * IS making its types, whoever makes it. Same argument as 0068: a rule a
     * script can skip is not a rule.
     */
    /*
     * Recorded before the switch, while it is still certain what was made.
     * Named as its own board rather than whichever one happened to be open —
     * recordAudit stamps the CURRENT project, and this concerns the new one.
     */
    await get().recordAudit('project', `started the board ${doc_.name} (${doc_.key})`, {
      projectId: id,
    });
    // Hold the switch against the projects watcher until the new board reaches
    // the list over realtime — otherwise it falls back to the first board and
    // the person lands on someone else's project. Cleared when it arrives.
    awaitingProjectId = id;
    get().setProject(id);
    return id;
  },

  archiveProject: async (id, archived) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    if (Boolean(board.archived) === archived) return ''; // already where it is being sent

    /*
     * ⚠️ YOU MAY NOT PUT THE LAST ONE AWAY.
     *
     * Nought boards is the brand-new-company case, and the app answers it by
     * offering to set a workspace up — see the projects watcher, and #324,
     * where being shown that screen by mistake read as being asked to make a
     * new board. Archiving your way into it would be the same fault with a
     * button attached: the board is still there, the trail still has it, and
     * the only screen offered is the one for people who have nothing.
     *
     * Refused here rather than hidden, because the control being greyed out
     * with no reason given is how somebody concludes the feature is broken.
     */
    if (archived && s.projects.length <= 1) {
      return 'This is the only board you have. Make another one before putting this one away.';
    }

    /*
     * RECORDED BEFORE THE WRITE, and against the board it concerns rather than
     * whichever one happens to be open — recordAudit stamps the CURRENT
     * project, and archiving is very often done while standing on it. The
     * entry has to name the board being put away, and after the write there
     * may be no board of that name in `projects` to name.
     */
    await get().recordAudit(
      'project',
      archived ? `put the board ${board.name} away` : `brought the board ${board.name} back`,
      { projectId: id }
    );

    await backend.patch(`${C.projects}/${id}`, { archived });

    /*
     * STANDING ON A BOARD THAT HAS JUST BEEN PUT AWAY.
     *
     * The watcher falls back on its own when the current board leaves the
     * list, but it picks `projects[0]` and it does so a round trip later — so
     * the screen sits on an archived board in between, with its issues still
     * on it. Move first and let the watcher agree, rather than the other way
     * round.
     */
    if (archived && get().projectId === id) {
      const next = get().projects.find((p) => p.id !== id);
      if (next) get().setProject(next.id);
    }
    return '';
  },

  removeSamples: async () => {
    const samples = get().projects.filter((p) => p.sample && !p.archived);
    if (samples.length === 0) return ''; // nothing to remove

    /*
     * ARCHIVE THE PAIR, THE SAME WAY archiveProject DOES — but across the two of
     * them, and WITHOUT its "you may not put the last one away" guard. That guard
     * stops a REAL board being archived into the empty-company state; removing the
     * samples is allowed to reach that state on purpose, because a tenant may end
     * up with only the examples and remove them, and the board picker's "No boards
     * yet / Start your first board" (plus the one quiet "Or bring back the
     * examples") is the designed landing, not a fault.
     *
     * The projects watcher (watchCollection C.projects) is left to split the
     * archived pair out of `projects` — the SAME single source the board switcher
     * and the Settings row read — so the removal reflects there without a second,
     * optimistic copy that a realtime echo would briefly revert (the flicker that
     * a hand-maintained array here caused).
     */
    for (const b of samples) {
      await get().recordAudit('project', `put the sample board ${b.name} away`, { projectId: b.id });
      await backend.patch(`${C.projects}/${b.id}`, { archived: true });
    }

    // Standing on one just put away → step onto a real board now, rather than sit
    // on an archived board until the watcher catches up (the move archiveProject
    // makes for the same reason). With no real board left there is nowhere to go:
    // the watcher empties `projects` and App shows the Welcome/empty state.
    if (samples.some((p) => p.id === get().projectId)) {
      const next = get().projects.find((p) => !p.sample && !p.archived);
      if (next) get().setProject(next.id);
    }
    return '';
  },

  restoreSamples: async () => {
    const samples = get().archivedProjects.filter((p) => p.sample);
    if (samples.length === 0) return ''; // nothing to bring back

    // Unarchive the pair — the rows were only put away, never deleted, so this
    // brings back exactly the authored examples (no half-edited leftover, since a
    // sample can't be edited). Only adds; it cannot lose anything, so no confirm.
    // The projects watcher brings the pair back into `projects` and, when they
    // were the only boards (restored from the empty Welcome screen), its own
    // "current board is gone — fall back" step lands the screen on one of them,
    // so there is nothing to move by hand here.
    for (const b of samples) {
      await get().recordAudit('project', `brought the sample board ${b.name} back`, { projectId: b.id });
      await backend.patch(`${C.projects}/${b.id}`, { archived: false });
    }

    get().showFlash('The sample boards are back.');
    return '';
  },

  updateProject: async (id, patch) => {
    if (sampleBlocks(id)) return '';
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';

    // Drop keys set to undefined so a caller can pass a partial without clearing
    // fields it did not mean to touch; an empty patch is a no-op, not a write.
    const clean = Object.fromEntries(
      Object.entries(patch).filter(([, v]) => v !== undefined)
    ) as typeof patch;
    if (Object.keys(clean).length === 0) return '';

    await backend.patch(`${C.projects}/${id}`, clean);

    // Reflect the change locally the instant it is written, exactly as the ledger
    // edits do (setInvoicesLocal). Before this, updateProject wrote only to the
    // backend and left the board in the store untouched, waiting for the realtime
    // echo to refresh it — which lands a second or more later. So a saved forecast
    // (or any scalar) went to the database correctly but the screen kept showing
    // the old number until the echo arrived, and read as "Save didn't persist it".
    // Merging the written fields in now makes the save show at once; the echo then
    // confirms the same value.
    const applyLocal = (p: Project): Project => (p.id === id ? { ...p, ...clean } : p);
    set((s) => ({
      projects: s.projects.map(applyLocal),
      archivedProjects: s.archivedProjects.map(applyLocal),
    }));

    // A one-line trail entry, named for the field that changed where it is one
    // the audit reads at a glance — the go-live is the date a sponsor asks about.
    const what =
      'goLiveAnchor' in clean
        ? clean.goLiveAnchor
          ? `anchored the Go-Live to ${
              clean.goLiveAnchor.kind === 'phase'
                ? 'the end of a phase'
                : clean.goLiveAnchor.kind === 'milestone'
                  ? 'a milestone'
                  : 'a fixed date'
            }`
          : 'cleared the Go-Live anchor'
        : 'goLive' in clean
        ? clean.goLive
          ? `set the Go-Live date to ${asDate(clean.goLive)}`
          : 'cleared the Go-Live date'
        : 'contractValue' in clean
          ? clean.contractValue != null
            ? `set the contract value to ${fmtMoney(clean.contractValue)}`
            : 'cleared the contract value'
          : 'changed the project details';
    await get().recordAudit('project', what, { projectId: id });
    return '';
  },

  putProjectOnHold: async (id, reason, pickUp) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const why = reason.trim();
    // `held_at` is a real timestamp so the age can say "on hold 14 days"; the
    // seam turns the millisecond count into timestamptz on the way in.
    await backend.patch(`${C.projects}/${id}`, {
      heldAt: Date.now(),
      holdReason: why,
      holdPickup: pickUp.trim(),
    });
    await get().recordAudit('project', why ? `put the project on hold — ${why}` : 'put the project on hold', {
      projectId: id,
    });
    return '';
  },

  liftProjectHold: async (id) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    // Null clears the stamp (presence is the on/off); the two text fields empty.
    await backend.patch(`${C.projects}/${id}`, { heldAt: null, holdReason: '', holdPickup: '' });
    await get().recordAudit('project', 'took the project off hold', { projectId: id });
    return '';
  },

  addBudgetLine: async (id, line, file) => {
    if (sampleBlocks(id)) return '';
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    // If the caller attached the supplier invoice at add time, check it BEFORE the
    // line is written — a bad file must not leave a dangling, document-less line.
    if (file) { const why = whyNot(file); if (why) return why; }
    // The whole array is written back, the way stakeholders are; a new id keeps
    // the line addressable for settling or removing it later.
    const newId = crypto.randomUUID();
    const next = [...(board.invoices ?? []), { ...line, id: newId }];
    // Update the local board FIRST. The projects list is refreshed by the realtime
    // watcher, which lands a moment after the write — so without this a second add
    // fired before the echo would read the pre-add array and clobber the line just
    // added. Setting it here means each add builds on the last; the echo confirms.
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    // Draw the amount in the LINE's own currency (a TT$ commitment reads as
    // TT$, not the base $) — fmtIn falls back to the base symbol for a line with
    // no currency of its own.
    const money = fmtIn(line.currency, line.amount);
    const kind = line.status === 'paid' ? 'invoice' : 'commitment';
    await get().recordAudit(
      'project',
      `added a ${line.pool} ${kind}${line.vendor ? ` — ${line.vendor}` : ''}${money ? ` (${money})` : ''}`,
      { projectId: id },
    );
    // Attach the invoice the same way the drawer does (storage upload + records it
    // as received), now the line is addressable. Its own guard re-checks the file.
    if (file) { const err = await get().attachInvoiceFile(id, newId, file); if (err) return err; }
    return '';
  },

  updateBudgetLine: async (id, lineId, patch) => {
    if (sampleBlocks(id)) return '';
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const lines = board.invoices ?? [];
    const existing = lines.find((l) => l.id === lineId);
    if (!existing) return 'That budget line is gone.';
    // FIELD-LOCK — once a line is approved (or paid), the FIGURES the approval
    // attests to (amount, vendor, currency, FX rate) are read-only, so an approval
    // can never be edited out from under. The invoice DATE and REFERENCE are not
    // part of what is attested — they are housekeeping that often needs correcting
    // after the fact — so they stay editable (by a planner) on a settled line.
    // A status change (e.g. moving it back to committed) is still allowed; that is
    // how the figures are re-opened. Invoice-approval governance, 2 Oct.
    const settled = existing.approvedAt != null || existing.status === 'approved' || existing.status === 'paid';
    if (settled) {
      const LOCKED: (keyof InvoiceLine)[] = ['amount', 'poAmount', 'vendor', 'currency', 'fxRate'];
      const touched = LOCKED.some(
        (f) =>
          f in patch &&
          JSON.stringify((patch as unknown as Record<string, unknown>)[f]) !==
            JSON.stringify((existing as unknown as Record<string, unknown>)[f]),
      );
      if (touched) {
        return 'This line is approved — its amount, vendor, currency and exchange rate are locked. Move it back to committed to change them, which re-opens its approval.';
      }
    }
    // RE-OPENING — moving a line back to committed unwinds everything after it (the
    // acceptance and the approval), so a re-opened line genuinely starts over and
    // cannot be paid on a stale approval. jsonb drops the undefined keys on write.
    const reopen: Partial<InvoiceLine> =
      patch.status === 'committed'
        ? {
            receivedAt: undefined,
            acceptedBy: undefined,
            acceptedAt: undefined,
            acceptance: undefined,
            approvedBy: undefined,
            approvedAt: undefined,
            approvalBasis: undefined,
            approvalCoverFor: undefined,
            approvalReason: undefined,
            sodOverride: undefined,
            approvedBelowDelivery: undefined,
            approvalRequestedAt: undefined,
            approvalRequestedBy: undefined,
            paidAt: undefined,
            paymentValueDate: undefined,
            paymentValueDateLog: undefined,
          }
        : {};
    const next = lines.map((l) => (l.id === lineId ? { ...l, ...patch, ...reopen } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    const line = next.find((l) => l.id === lineId)!;
    const who = line.vendor || 'budget';
    const what =
      'status' in patch
        ? patch.status === 'paid'
          ? `marked the ${who} line paid`
          : `moved the ${who} line back to committed`
        : 'changed a budget line';
    await get().recordAudit('project', what, { projectId: id });
    return '';
  },

  removeBudgetLine: async (id, lineId) => {
    if (sampleBlocks(id)) return '';
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const next = (board.invoices ?? []).filter((l) => l.id !== lineId);
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    await get().recordAudit('project', 'removed a budget line', { projectId: id });
    return '';
  },

  correctLine: async (id, lineId, field, to, reason) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    const why = reason.trim();
    if (!why) return 'Say why this line is being reclassified.'; // a reason is required at every tier
    if (field === 'pool' && to !== 'capex' && to !== 'opex') return 'A pool is capex or opex.';
    const from = field === 'pool' ? line.pool : field === 'milestone' ? (line.milestone ?? '') : (line.phase ?? '');
    if (String(from) === String(to)) {
      return field === 'pool'
        ? 'That is already the line’s pool.'
        : field === 'milestone'
          ? 'That is already what the line is billed against.'
          : 'That is already the line’s phase.';
    }
    const now = Date.now();
    const me = accounts.current()?.email?.toLowerCase() ?? '';
    const correction: Correction = {
      id: crypto.randomUUID(),
      at: now,
      by: me,
      field,
      from: String(from),
      to: String(to),
      reason: why,
      status: correctionNeedsApproval(line) ? 'pending' : 'applied',
    };
    const applyNow = correction.status === 'applied';
    const applied: Partial<InvoiceLine> = applyNow
      ? field === 'pool'
        ? { pool: to as 'capex' | 'opex' }
        : field === 'milestone'
          ? { milestone: to || undefined }
          : { phase: to || undefined }
      : {};
    const patch: Partial<InvoiceLine> = { corrections: [...(line.corrections ?? []), correction], ...applied };
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    // Name resolution per field so the audit reads in words, not ids. A milestone
    // or phase id becomes its name; '' reads "none"/"Unassigned".
    const nameOf = (v: string) =>
      field === 'phase'
        ? s.phases.find((p) => p.id === v)?.name ?? 'Unassigned'
        : field === 'milestone'
          ? (v ? get().milestones.find((m) => m.id === v)?.name ?? 'a milestone' : 'none')
          : v;
    const what = field === 'pool' ? 'pool' : field === 'milestone' ? 'billed-against milestone' : 'phase';
    const fromName = nameOf(correction.from);
    const toName = nameOf(correction.to);
    await get().recordAudit(
      'project',
      applyNow
        ? `reclassified the ${line.vendor || 'budget'} line’s ${what} (${fromName} → ${toName}) — ${why}`
        : `requested a reclassification of the ${line.vendor || 'budget'} line’s ${what} (${fromName} → ${toName}), awaiting approval — ${why}`,
      { projectId: id },
    );
    return '';
  },

  setBilledAgainst: async (id, lineId, milestoneId) => {
    if (sampleBlocks(id)) return '';
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    // Gated to the admin/manager (planning) tier — re-linking what a line is billed
    // against is a governance/classification input to the schedule-of-values roll-up,
    // not a general editor's to set. The drawer hides the picker behind mayPlan; the
    // store action is reachable directly, so it carries the same guard as the sibling
    // setDeliveryBasis write path.
    const me = s.user?.email?.toLowerCase() ?? accounts.current()?.email?.toLowerCase() ?? '';
    const myRole: Role = amStaff()
      ? 'admin'
      : effectiveRole({ email: me, admins: s.access.admins, defaults: s.roles, on: s.projectRoles, legacy: s.legacyRoles, projectId: id });
    if (!(myRole === 'admin' || can(myRole, 'editSettings'))) {
      return 'Only a manager or admin can change what a line is billed against.';
    }
    // A paid line's classification is frozen behind a governed correction — the
    // money has moved, so re-linking it needs a reason and (if it was paid) review.
    if (line.status === 'paid')
      return 'This line is paid — use Reclassify to change what it is billed against.';
    const to = milestoneId || undefined;
    if ((line.milestone ?? undefined) === to) return '';
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, milestone: to } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    const name = to ? get().milestones.find((m) => m.id === to)?.name ?? 'a milestone' : 'nothing';
    await get().recordAudit(
      'project',
      `set the ${line.vendor || 'budget'} line to bill against ${name}`,
      { projectId: id },
    );
    return '';
  },

  setDeliveryBasis: async (id, lineId, basis) => {
    if (sampleBlocks(id)) return '';
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    // Gated to the admin/manager (planning) tier — the basis decides what the frozen
    // delivery evidence measures, which drives the delivery nudge, so it is a
    // governance input, not a general editor's to set. Matches the drawer's `mayPlan`
    // gate on the picker and the write-path gates on setPaymentValueDate/setDeliveryNudge.
    const me = s.user?.email?.toLowerCase() ?? accounts.current()?.email?.toLowerCase() ?? '';
    const myRole: Role = amStaff()
      ? 'admin'
      : effectiveRole({ email: me, admins: s.access.admins, defaults: s.roles, on: s.projectRoles, legacy: s.legacyRoles, projectId: id });
    if (!(myRole === 'admin' || can(myRole, 'editSettings'))) {
      return 'Only a manager or admin can change what a line’s delivery is tracked against.';
    }
    // The delivery evidence is frozen onto the line when it is accepted, so the basis
    // is only editable up to that point.
    if (line.acceptedAt != null) {
      return 'The delivery basis is fixed once the line is accepted — its evidence is frozen then.';
    }
    const to = basis && basis.id ? basis : undefined;
    if (JSON.stringify(line.deliveryBasis ?? null) === JSON.stringify(to ?? null)) return '';
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, deliveryBasis: to } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    const label = !to
      ? 'nothing'
      : to.kind === 'phase'
        ? s.phases.find((p) => p.id === to.id)?.name ?? 'a phase'
        : to.kind === 'epic'
          ? to.id
          : s.milestones.find((m) => m.id === to.id)?.name ?? 'a milestone';
    await get().recordAudit(
      'project',
      `set the ${line.vendor || 'budget'} line’s delivery to track ${label}`,
      { projectId: id },
    );
    return '';
  },

  decideCorrection: async (id, lineId, correctionId, approve, reason) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    const corr = (line.corrections ?? []).find((c) => c.id === correctionId);
    if (!corr) return 'That reclassification is gone.';
    if (corr.status !== 'pending') return 'That reclassification has already been decided.';
    const me = accounts.current()?.email?.toLowerCase() ?? '';
    const myRole: Role = amStaff()
      ? 'admin'
      : effectiveRole({ email: me, admins: s.access.admins, defaults: s.roles, on: s.projectRoles, legacy: s.legacyRoles, projectId: id });
    if (!(myRole === 'admin' || can(myRole, 'editSettings'))) return 'Only an admin or manager can decide a reclassification.';
    // Separation of duties — the person who raised it may not decide it.
    if (corr.by && corr.by === me) return 'You raised this reclassification, so someone else must decide it.';
    const say = (reason ?? '').trim();
    if (!approve && !say) return 'Say why the reclassification is rejected.';
    const now = Date.now();
    const decided: Correction = {
      ...corr,
      status: approve ? 'approved' : 'rejected',
      decidedBy: me,
      decidedAt: now,
      ...(approve ? {} : { decideReason: say }),
    };
    const applied: Partial<InvoiceLine> = approve
      ? corr.field === 'pool'
        ? { pool: corr.to as 'capex' | 'opex' }
        : corr.field === 'milestone'
          ? { milestone: corr.to || undefined }
          : { phase: corr.to || undefined }
      : {};
    const patch: Partial<InvoiceLine> = {
      corrections: (line.corrections ?? []).map((c) => (c.id === correctionId ? decided : c)),
      ...applied,
    };
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    const what = corr.field === 'pool' ? 'pool' : corr.field === 'milestone' ? 'billed-against milestone' : 'phase';
    await get().recordAudit(
      'project',
      approve
        ? `approved the reclassification of the ${line.vendor || 'budget'} line’s ${what}`
        : `rejected the reclassification of the ${line.vendor || 'budget'} line’s ${what} — ${say}`,
      { projectId: id },
    );
    return '';
  },

  /*
   * THE ACCEPTANCE GATE — one step forward at a time, each recording who/when.
   *
   * Each finds the line, refuses a move made out of order (so the gate holds on
   * the write path, not only in the drawer), writes the whole array back like the
   * other ledger actions, and files a plain-English audit line. `payBudgetLine`
   * is the enforced gate: it refuses unless the line is BOTH accepted AND approved.
   */
  receiveBudgetLine: async (id, lineId) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    if (line.status !== 'committed') return 'Only a committed line can be recorded as received.';
    const patch: Partial<InvoiceLine> = { status: 'received', receivedAt: Date.now() };
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    await get().recordAudit('project', `recorded the ${line.vendor || 'budget'} invoice as received`, { projectId: id });
    return '';
  },

  acceptBudgetLine: async (id, lineId) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    if (line.status !== 'received') return 'Only a received line can be accepted.';
    const now = Date.now();
    const me = accounts.current()?.email?.toLowerCase() ?? '';
    // Freeze the delivery evidence for the line's basis (phase, epic or milestone)
    // AS IT STANDS NOW, so it never re-counts if the board moves later. A line with
    // no basis records a plain acceptance.
    const acceptance = buildDeliveryEvidence(
      lineDeliveryBasis(line),
      {
        cards: s.issues,
        phaseName: (pid) => s.phases.find((p) => p.id === pid)?.name,
        milestones: s.milestones.map((m) => ({ key: m.id, name: m.name, actualDay: m.actualDay ?? undefined })),
      },
      now,
    ) ?? { phaseId: '', total: 0, done: 0, delivered: 0, dropped: 0, at: now };
    const patch: Partial<InvoiceLine> = { status: 'accepted', acceptedBy: me, acceptedAt: now, acceptance };
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    await get().recordAudit('project', `accepted the ${line.vendor || 'budget'} line — work verified`, { projectId: id });
    return '';
  },

  requestPayApproval: async (id, lineId) => {
    if (sampleBlocks(id)) return '';
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    if (line.status !== 'accepted') return 'Only an accepted line can be sent for approval.';
    // THE UPLOAD GATE — the supplier invoice must be attached before approval is
    // sought, so no decision is ever asked for on a line with no document behind it.
    if (!line.invoiceFile) return 'Attach the supplier invoice before sending it for approval.';
    const approver = (line.approver ?? board.payApprover ?? '').trim();
    const now = Date.now();
    const me = accounts.current()?.email?.toLowerCase() ?? '';
    const patch: Partial<InvoiceLine> = { approvalRequestedAt: now, approvalRequestedBy: me };
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    await get().recordAudit(
      'project',
      approver
        ? `sent the ${line.vendor || 'budget'} line to ${approver} for approval`
        : `sent the ${line.vendor || 'budget'} line for approval`,
      { projectId: id },
    );
    return '';
  },

  approveBudgetLine: async (id, lineId, reason) => {
    if (sampleBlocks(id)) return '';
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    if (line.status !== 'accepted') return 'Only an accepted line can be approved for payment.';
    // THE UPLOAD GATE — no approval without the supplier invoice behind it.
    if (!line.invoiceFile) return 'Attach the supplier invoice before this line can be approved.';
    // WHO MAY APPROVE — the designated approver, their active cover, or an admin;
    // the accepter cannot, unless an admin overrides with a reason. Enforced here
    // AND in the database (migration 0136), so a hidden button is never the whole
    // of it.
    const auth = get().payApprovalFor(id, lineId);
    if (!auth || !auth.allowed) return auth?.blockedWhy || 'You may not approve this line.';
    const said = (reason ?? '').trim();
    // THE DELIVERY NUDGE (#483) — a NOTE, never a block. If the board runs the
    // nudge and the frozen delivery evidence is below its bar, the approver may
    // still approve, but must say why (option b): the reason is kept on the line
    // beside the recorded shortfall.
    const share = line.acceptance ? deliveredShare(line.acceptance) : 1;
    const below = deliveryNudgeTrips(board.deliveryNudge, share);
    // A reason is required to approve in the approver's place / override SoD, and
    // to approve ahead of the board's delivery bar.
    if ((auth.needsReason || below) && !said) {
      if (auth.needsReason) {
        return auth.sodOverride
          ? 'You verified this work — an admin may still approve it, but must say why. Give a reason.'
          : 'Approving in the approver’s place needs a reason — say why you are standing in.';
      }
      return 'This payment is below your board’s delivery bar — say why you’re approving it ahead of delivery.';
    }
    const now = Date.now();
    const me = accounts.current()?.email?.toLowerCase() ?? '';
    const belowDelivery = below
      ? { at: now, by: me, share, pct: board.deliveryNudge!.pct, reason: said }
      : undefined;
    const patch: Partial<InvoiceLine> = {
      status: 'approved',
      approvedBy: me,
      approvedAt: now,
      approvalBasis: auth.basis,
      // Bind the approval to THIS invoice: record the fingerprint it was given
      // against, so a later file replacement voids the stamp (see payStampState).
      ...(line.invoiceFile?.hash ? { approvalFileHash: line.invoiceFile.hash } : {}),
      ...(auth.coverFor ? { approvalCoverFor: auth.coverFor } : {}),
      ...(auth.needsReason ? { approvalReason: said } : {}),
      ...(auth.sodOverride ? { sodOverride: true } : {}),
      ...(belowDelivery ? { approvedBelowDelivery: belowDelivery } : {}),
    };
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    const how =
      auth.sodOverride ? ' — overriding separation of duties'
      : auth.basis === 'cover' ? ` as delegated cover for ${auth.coverFor}`
      : auth.basis === 'admin' && said ? ' in the approver’s place'
      : '';
    await get().recordAudit(
      'project',
      `approved the ${line.vendor || 'budget'} line for payment${how}${said ? ` (${said})` : ''}`,
      { projectId: id },
    );
    return '';
  },

  payApprovalFor: (id, lineId) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    const line = (board?.invoices ?? []).find((l) => l.id === lineId);
    if (!board || !line) return null;
    const me = accounts.current()?.email?.toLowerCase() ?? '';
    const myRole: Role = amStaff()
      ? 'admin'
      : effectiveRole({ email: me, admins: s.access.admins, defaults: s.roles, on: s.projectRoles, legacy: s.legacyRoles, projectId: id });
    const isAdmin = myRole === 'admin' || can(myRole, 'editSettings');
    const now = Date.now();
    return approvalAuthority({
      line,
      projectApprover: board.payApprover,
      me,
      isAdmin,
      hasActiveCoverFrom: (approver) => {
        const a = (approver ?? '').trim().toLowerCase();
        return !!a && s.signoffCover.some((c) => c.active && c.delegate === me && c.delegator === a && (c.until == null || c.until >= now));
      },
    });
  },

  raisePaymentChain: async (id, lineId) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    if (line.status !== 'accepted') return 'Only an accepted line can be sent for authorisation.';
    // THE UPLOAD GATE — the supplier invoice must be attached before authorisation
    // is sought, the same rule the single-approver path and the database (0182) hold.
    if (!line.invoiceFile) return 'Attach the supplier invoice before sending it for authorisation.';
    // Already raised — nothing to do. The chain is frozen once and never re-shuffled.
    if (line.payChain && line.payChain.length > 0) return '';
    const policy = policyForProject(s.paymentApproval, id);
    const world = theWorld(s);
    // Refuse a payment the chain cannot answer — no chain set (first-run), an empty
    // stage, a stage that resolves to nobody — in the words the screen shows.
    const problem = paymentPolicyProblem(policy, world);
    if (problem) return problem;
    const baseAmount = lineBaseAmount(line, s.currency, s.fxRates);
    const chain = resolvePaymentChain(policy!, baseAmount, world);
    if (chain.length === 0)
      return 'No approver resolves for this payment. Check the payment-approval chain in Settings.';
    const now = Date.now();
    const me = accounts.current()?.email?.toLowerCase() ?? '';
    const patch: Partial<InvoiceLine> = {
      payChain: chain,
      approvalRequestedAt: now,
      approvalRequestedBy: me,
    };
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    await get().recordAudit(
      'project',
      `sent the ${line.vendor || 'budget'} line for payment authorisation`,
      { projectId: id },
    );
    return '';
  },

  paymentAuthorityFor: (id, lineId) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    const line = (board?.invoices ?? []).find((l) => l.id === lineId);
    if (!board || !line || !line.payChain || line.payChain.length === 0) return null;
    const me = accounts.current()?.email?.toLowerCase() ?? '';
    const myRole: Role = amStaff()
      ? 'admin'
      : effectiveRole({ email: me, admins: s.access.admins, defaults: s.roles, on: s.projectRoles, legacy: s.legacyRoles, projectId: id });
    // Admin here means ONLY the SoD-override tier — the engine never lets an admin
    // sign a stage they are not a named signer of (paymentAuthority has no fallback).
    const isAdmin = myRole === 'admin' || can(myRole, 'editSettings');
    return paymentAuthority({ chain: line.payChain, acceptedBy: line.acceptedBy, me, isAdmin });
  },

  authorisePaymentStage: async (id, lineId, answer, reason) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    if (!line.payChain || line.payChain.length === 0)
      return 'This payment has no approval chain to authorise.';
    const auth = get().paymentAuthorityFor(id, lineId);
    if (!auth || !auth.allowed) return auth?.blockedWhy || 'You may not authorise this payment.';
    const said = (reason ?? '').trim();
    // A reason is required ONLY to override separation of duties (an admin authorising
    // work they themselves verified) — recorded on the row as a control exception.
    if (auth.needsReason && !said)
      return 'You verified this work — an admin may still authorise it, but must say why. Give a reason.';
    const now = Date.now();
    const me = accounts.current()?.email?.toLowerCase() ?? '';
    // Sign MY unsigned row on the stage open right now — and only that row.
    const chain: PaymentChainRow[] = line.payChain.map((r) =>
      r.step === auth.stageId && (r.who.email ?? '').toLowerCase() === me && !r.signedAt
        ? {
            ...r,
            signedAt: now,
            answer,
            ...(said ? { reason: said } : {}),
            ...(auth.sodOverride ? { sodOverride: true } : {}),
          }
        : r,
    );
    const rejected = answer === 'rejected';
    const settled = chainState(chain) === 'authorised';
    const patch: Partial<InvoiceLine> = { payChain: chain };
    // The line becomes approved only when the WHOLE chain settles — the moment the
    // last required stage is met. The database (0182) refuses an 'approved' write
    // while any required stage is still outstanding, whatever this believes.
    if (settled) {
      patch.status = 'approved';
      patch.approvedBy = me;
      patch.approvedAt = now;
      if (line.invoiceFile?.hash) patch.approvalFileHash = line.invoiceFile.hash;
    }
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    await get().recordAudit(
      'project',
      rejected
        ? `declined payment of the ${line.vendor || 'budget'} line${said ? ` (${said})` : ''}`
        : settled
          ? `gave the final authorisation — the ${line.vendor || 'budget'} line is approved for payment${auth.sodOverride ? ' — overriding separation of duties' : ''}${said ? ` (${said})` : ''}`
          : `authorised a stage of the ${line.vendor || 'budget'} line's payment${auth.sodOverride ? ' — overriding separation of duties' : ''}${said ? ` (${said})` : ''}`,
      { projectId: id },
    );
    return '';
  },

  previewPaymentChain: (id, lineId) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    const line = (board?.invoices ?? []).find((l) => l.id === lineId);
    if (!board || !line) return null;
    const policy = policyForProject(s.paymentApproval, id);
    const world = theWorld(s);
    const problem = paymentPolicyProblem(policy, world);
    if (problem || !policy) return { rows: [], problem: problem || 'No payment approver is set.' };
    const baseAmount = lineBaseAmount(line, s.currency, s.fxRates);
    return { rows: resolvePaymentChain(policy, baseAmount, world), problem: '' };
  },

  remindPaymentChain: async (id, lineId) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    if (!line.payChain || line.payChain.length === 0) return 'This payment has no approval chain yet.';
    const open = openPaymentStage(line.payChain);
    if (open === null) return 'This payment is already fully authorised — there is no one to remind.';
    // The people the open stage is still waiting on, by name — the reminder names
    // them, the same audit-only channel the single-approver path uses for now.
    const waiting = line.payChain
      .filter((r) => r.step === open && !r.signedAt && !!(r.who.email ?? '').trim())
      .map((r) => displayName((r.who.email ?? '').toLowerCase(), s.profiles));
    const who = waiting.length > 0 ? waiting.join(' and ') : 'the next approver';
    await get().recordAudit(
      'project',
      `reminded ${who} to authorise the ${line.vendor || 'budget'} line`,
      { projectId: id },
    );
    return '';
  },

  payBudgetLine: async (id, lineId) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    if (line.status === 'paid') return '';
    // THE GATE — real money only moves on work that was accepted AND approved.
    if (!canMarkPaid(line)) return 'This line must be accepted and approved before it can be marked paid.';
    // THE RETENTION CEILING — while the project holds a retention rate, total paid
    // can never reach the full budget: rate% must stay withheld. Refuse a payment
    // that would push paid spend past budget − retention, and say by how much, so
    // the retention is never quietly paid away.
    const ceiling = retentionCeiling(board, { base: s.currency, rates: s.fxRates });
    const lineBase = Number.isFinite(line.amount) ? lineBaseAmount(line, s.currency, s.fxRates) : 0;
    const rblock = retentionPayBlock(ceiling, lineBase);
    if (rblock.blocked) {
      const money = (n: number) => fmtIn(s.currency, Math.round(n));
      return (
        `Paying this would take total paid to ${money(ceiling.paidTotal + lineBase)}, past the ` +
        `${money(ceiling.payCeiling)} ceiling. Retention of ${ceiling.rate}% (${money(ceiling.retentionAmount)}) ` +
        `must stay withheld against the ${money(ceiling.budget)} budget — ${money(rblock.over)} too much to pay now.`
      );
    }
    const now = Date.now();
    const patch: Partial<InvoiceLine> = { status: 'paid', paidAt: now };
    // A foreign line freezes at the current rate — the rate the money moved at.
    if (line.currency && line.currency !== s.currency) {
      const frozen = s.fxRates[line.currency] ?? line.fxRate;
      if (frozen != null) patch.fxRate = frozen;
    }
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    await get().recordAudit('project', `marked the ${line.vendor || 'budget'} line paid`, { projectId: id });
    return '';
  },

  setPaymentValueDate: async (id, lineId, value, reason) => {
    if (sampleBlocks(id)) return '';
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    // The finance value date only means anything once real money has moved.
    if (line.status !== 'paid') return 'A finance payment date can only be set once the line is paid.';
    // LEDGER-WRITE TIER — admin or manager, the tier that governs the ledger.
    const me = accounts.current()?.email?.toLowerCase() ?? '';
    const myRole: Role = amStaff()
      ? 'admin'
      : effectiveRole({ email: me, admins: s.access.admins, defaults: s.roles, on: s.projectRoles, legacy: s.legacyRoles, projectId: id });
    if (!(myRole === 'admin' || can(myRole, 'editSettings'))) {
      return 'Only an admin or manager can set the finance payment date.';
    }
    const v = (value ?? '').trim();
    if (!v) return 'Give the date Finance recorded the payment on.';
    // HARD RULE — a payment cannot have cleared before it was authorised. (A future
    // date is allowed; the drawer warns before saving one, so it is not blocked here.)
    if (paymentValueDateIssue(line, v, civilDay(Date.now())) === 'before-approval') {
      return `The finance payment date can’t be before the line was approved (${humanDay(civilDay(line.approvedAt!))}).`;
    }
    const prev = line.paymentValueDate;
    if (prev === v) return '';
    // A CORRECTION — changing an already-recorded date — must say why.
    const why = (reason ?? '').trim();
    if (prev && !why) return 'Changing a recorded finance payment date needs a reason — say why.';
    const edit: PaymentValueDateEdit = { at: Date.now(), by: me, ...(prev ? { from: prev } : {}), to: v, reason: why };
    const patch: Partial<InvoiceLine> = {
      paymentValueDate: v,
      paymentValueDateLog: [...(line.paymentValueDateLog ?? []), edit],
    };
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    await get().recordAudit(
      'project',
      prev
        ? `corrected the ${line.vendor || 'budget'} line’s finance payment date (${humanDay(prev)} → ${humanDay(v)})${why ? ` — ${why}` : ''}`
        : `recorded the ${line.vendor || 'budget'} line’s finance payment date (${humanDay(v)})`,
      { projectId: id },
    );
    return '';
  },

  attachInvoiceFile: async (id, lineId, file) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    if (!board) return 'That board is not one of yours to change.';
    const line = (board.invoices ?? []).find((l) => l.id === lineId);
    if (!line) return 'That budget line is gone.';
    const why = whyNot(file);
    if (why) return why;
    // A PAID line is settled money moved against this exact document — its invoice
    // cannot be swapped out. A re-classification goes through the correction path, not
    // a quiet file replacement.
    if (line.status === 'paid') {
      return 'This line is paid — its invoice can no longer be replaced.';
    }
    // File FIRST, then the row — a failed upload must not leave a line pointing at
    // a document that is not there. Keyed board-first for the storage policy (0048).
    const boardKey = idFrom('board', id, md5.md5);
    const key = fileKey(boardKey, `invoice-${lineId}`, file.name);
    // The fingerprint binds a pay approval to THIS document (same mechanism as a
    // signed document): taken from the bytes here, before the upload, so the line
    // records what the approver will have authorised.
    const hash = await fingerprint(file);
    try {
      await backend.putFile(key, file, file.type);
    } catch (e) {
      return (
        (e as { message?: string })?.message ||
        'That file could not be uploaded. Nothing was changed — try again.'
      );
    }
    const now = Date.now();
    const patch: Partial<InvoiceLine> = { invoiceFile: { path: key, name: file.name, size: file.size, addedAt: now, hash } };
    // TAMPER RULE — replacing the invoice on an already-approved line voids its pay
    // approval: the authority was given against a different document, so the stamp no
    // longer binds and the line drops back to accepted to be approved again. (A
    // same-bytes re-attach would hash identically and leave the approval standing, but
    // we re-open on any replace for a clear, auditable trail.)
    const wasApproved = line.status === 'approved';
    if (wasApproved) {
      patch.status = 'accepted';
      patch.approvedBy = undefined;
      patch.approvedAt = undefined;
      patch.approvalBasis = undefined;
      patch.approvalCoverFor = undefined;
      patch.approvalReason = undefined;
      patch.sodOverride = undefined;
      patch.approvalFileHash = undefined;
      patch.approvalRequestedAt = undefined;
      patch.approvalRequestedBy = undefined;
    }
    // Attaching the invoice IS receiving it: a committed line advances to received
    // in the same action, so there is no separate "Mark received" click. Only from
    // committed (a later attach/replace never moves the line back), and the manual
    // receiveBudgetLine still covers a line received without a document.
    const advanced = line.status === 'committed';
    if (advanced) { patch.status = 'received'; patch.receivedAt = now; }
    const next = (board.invoices ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
    setInvoicesLocal(set, id, next);
    await backend.patch(`${C.projects}/${id}`, { invoices: next });
    await get().recordAudit(
      'project',
      wasApproved
        ? `replaced the invoice on the ${line.vendor || 'budget'} line with ${file.name} — the pay approval was voided and the line must be approved again`
        : advanced
          ? `attached ${file.name} to the ${line.vendor || 'budget'} line and recorded it as received`
          : `attached ${file.name} to the ${line.vendor || 'budget'} line`,
      { projectId: id },
    );
    return '';
  },

  budgetLineFileUrl: async (id, lineId) => {
    const board = [...get().projects, ...get().archivedProjects].find((p) => p.id === id);
    const line = board?.invoices?.find((l) => l.id === lineId);
    if (!line?.invoiceFile?.path) return '';
    try {
      return await backend.fileUrl(line.invoiceFile.path);
    } catch {
      return '';
    }
  },

  authorityToPayUrl: async (id, lineId) => {
    const s = get();
    const board = [...s.projects, ...s.archivedProjects].find((p) => p.id === id);
    const line = board?.invoices?.find((l) => l.id === lineId);
    if (!board || !line || !line.approvedAt) return '';
    const basis = payStampKind(line);
    const designated = (line.approver ?? board.payApprover ?? '').trim();
    const nameOf = (email?: string) => displayName(email ?? '', s.profiles);
    const approver =
      basis === 'cover'
        ? nameOf(line.approvalCoverFor ?? designated)
        : nameOf(designated || line.approvedBy);
    // Phase names load per board, so a line on another board resolves pool-only —
    // which is all the finance classification needs anyway.
    const phaseName = line.phase ? s.phases.find((p) => p.id === line.phase)?.name ?? '' : '';
    const classification = `${line.pool === 'capex' ? 'Capital' : 'Operating'}${phaseName ? ` · ${phaseName}` : ''}`;

    // ── STAMP A COPY OF THE INVOICE (Vinnie, 3 Oct) ──────────────────────────
    // "View authority to pay" shows the actual uploaded invoice with the
    // authorisation stamped on page 1. We fetch the stored bytes and stamp a copy
    // in memory — the stored original (and the fingerprint the approval binds to)
    // is never written. A PDF or a PNG/JPG scan can be stamped; anything else
    // returns null and we fall through to the standalone authority page below.
    if (line.invoiceFile?.path) {
      try {
        const address = await backend.fileUrl(line.invoiceFile.path);
        const bytes = await (await fetch(address)).arrayBuffer();
        const stamped = await stampInvoiceToPay(bytes, line.invoiceFile.name ?? 'invoice', {
          reference: payStampRef(line),
          basis,
          approver,
          approvedBy: nameOf(line.approvedBy),
          approvedOn: asStampDate(line.approvedAt),
          approvedTime: asStampTime(line.approvedAt),
          amount: fmtIn(line.currency ?? s.currency, line.amount),
          invoiceHash: line.invoiceFile.hash ?? '',
        });
        if (stamped) return URL.createObjectURL(new Blob([stamped as BlobPart], { type: 'application/pdf' }));
      } catch {
        /* Couldn't fetch or stamp — fall through to the standalone page. */
      }
    }

    // The workspace mark, if there is one — fetched here so the page can carry it.
    let logo: { bytes: ArrayBuffer; type: 'png' | 'jpg' } | undefined;
    if (s.meta.logoPath) {
      try {
        const address = await get().projectLogoUrl();
        if (address) {
          logo = {
            bytes: await (await fetch(address)).arrayBuffer(),
            type: s.meta.logoType === 'jpg' ? 'jpg' : 'png',
          };
        }
      } catch {
        /* a missing mark is a blemish, not a failure — the page is the point */
      }
    }
    try {
      const bytes = await authorityToPayPdf({
        board: board.name,
        reference: payStampRef(line),
        supplier: line.vendor ?? '',
        amount: fmtIn(line.currency ?? s.currency, line.amount),
        poNumber: line.poNumber,
        invoiceRef: line.invoiceRef,
        description: line.description,
        classification,
        basis,
        approver,
        approvedBy: nameOf(line.approvedBy),
        exceptionReason: basis === 'exception' ? line.approvalReason : undefined,
        approvedOn: asStampDate(line.approvedAt),
        approvedTime: asStampTime(line.approvedAt),
        invoiceName: line.invoiceFile?.name ?? '',
        invoiceHash: line.invoiceFile?.hash ?? '',
        version: __APP_VERSION__,
        logo,
      });
      return URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
    } catch {
      return '';
    }
  },

  /*
   * THE WORKSPACE CURRENCY (0117) — and the exchange rates, working calendar and
   * payment-approval chain that ride the same tenant row.
   *
   * READ for the workspace IN VIEW. `resolveWorkspaceId` resolves the workspace
   * the address scopes the client to (my_tenant()), not the home named in the
   * token — see its note; keying this by the home id meant a non-home workspace
   * read nothing and fell silently to defaults. It points money.ts at the
   * currency and holds the values in the store so the admin controls can show
   * what is set.
   *
   * ABSENT vs FAILED, kept apart. A row that reads back with a blank or missing
   * value means the workspace never chose — the default stands, quietly, which
   * is correct. But NO readable row (null) or a thrown read is a FAILURE: we do
   * not know the settings, so defaulting silently would be a wrong-but-plausible
   * calendar/currency — the misleading class. That case is logged rather than
   * swallowed (a visible "settings unavailable" cue is mocked separately), so it
   * can never again look like a healthy default.
   */
  loadWorkspaceCurrency: async () => {
    const wid = await resolveWorkspaceId();
    if (!wid) return;
    try {
      const row = await backend.get(`${C.workspace}/${wid}`);
      if (!row) {
        console.warn(
          `[workspace] settings row unreadable for ${wid} — currency, rates, calendar and payment approval left at defaults; NOT applying silently as if chosen.`,
        );
        return;
      }
      const code = typeof row?.currency === 'string' && row.currency ? row.currency : DEFAULT_CURRENCY;
      setActiveCurrency(code);
      // The rate table is stored as jsonb; keep only sane positive numbers so a
      // malformed value can never divide a total to nonsense.
      //
      // READ THE FIELD NAME, NOT THE COLUMN. The seam (toFields in backend/paths)
      // renames every column to its app field on the way out, so `fx_rates` and
      // `payment_approval` arrive here as `fxRates` and `paymentApproval`. Reading
      // the snake-case column names returned undefined — fx rates silently never
      // loaded, and the whole payment-approval chain (0182) read back as "no chain
      // set", so it could be configured but never engaged. Both are app fields.
      const rawRates = row?.fxRates && typeof row.fxRates === 'object' ? row.fxRates : {};
      const fxRates: Record<string, number> = {};
      for (const [k, v] of Object.entries(rawRates)) {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) fxRates[k] = n;
      }
      // The payment-approval chain rides on the same tenant row (0182). Kept as
      // stored; `payment-approval.ts` reads it forward and treats a malformed or
      // absent value as "no chain set" (payments blocked), so no shaping here.
      const pa = row?.paymentApproval;
      const paymentApproval: StoredPaymentApproval | null =
        pa && typeof pa === 'object' ? (pa as StoredPaymentApproval) : null;
      // The working calendar rides on the same tenant row (0216). Shaped to a sane
      // { week, holidays } or left as the Mon–Fri default, so a malformed or absent
      // value can never make the working-day engine misread a day. Field name, not
      // column — the seam hands back `workCalendar` (see fx note above).
      const wc = row?.workCalendar;
      const workCalendar: WorkCalendar = (() => {
        if (!wc || typeof wc !== 'object') return DEFAULT_CALENDAR;
        const rawWeek = Array.isArray((wc as WorkCalendar).week) ? (wc as WorkCalendar).week : [];
        const week = rawWeek.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);
        const rawHols = Array.isArray((wc as WorkCalendar).holidays) ? (wc as WorkCalendar).holidays : [];
        const holidays: Holiday[] = rawHols
          .filter((h): h is Holiday => !!h && typeof h.name === 'string' && typeof h.date === 'string')
          .map((h) => ({ name: h.name, date: h.date, ...(h.until ? { until: h.until } : {}), ...(h.annual ? { annual: true } : {}) }));
        return { week: week.length ? week : DEFAULT_CALENDAR.week, holidays };
      })();
      set({ currency: code, fxRates, paymentApproval, workCalendar });
    } catch (e) {
      // A thrown read is a FAILURE, not a chosen default — logged, not swallowed
      // (see the note above). The defaults stay so the board still works.
      console.warn('[workspace] could not read settings; left at defaults:', (e as Error)?.message ?? e);
    }
  },

  /*
   * THE PORTFOLIO ROLL-UP, READ ON DEMAND. Two group queries fetch every board's
   * issues and phases the caller may see (RLS scopes them the same way the board
   * switcher does), bucketed by board, then `buildPortfolio` derives a row each
   * from the projects already in state, in the workspace's base currency. Any
   * board with no work data yet still gets a row (green, 0%). Errors are held on
   * the state, not thrown — the screen shows them and offers a retry.
   */
  loadPortfolio: async () => {
    set({ portfolioLoading: true, portfolioError: undefined });
    try {
      // The window the movement read covers — the same 7 days the "Moved" column,
      // the by-person "closed this week" and the drawer marker all read against.
      const now = Date.now();
      const sinceMs = now - 7 * 86_400_000;
      const [issueDocs, phaseDocs, moveDocs, pulseDocs, milestoneDocs, msPulseDocs, docDocs, areaDocs, lessonDocs, typeDocs, riskDocs] = await Promise.all([
        backend.readGroup('issues'),
        backend.readGroup('phases'),
        // The recent status moves to attach — the light issue read carries no
        // history, so movement would otherwise read blank. Absent on a backend
        // that cannot do it; the derivations then fall back to the milestone
        // signals (closed/blocked/aging) without erroring.
        backend.readMovesSince ? backend.readMovesSince(sinceMs) : Promise.resolve([]),
        // The daily health pulses (0122), for the trend arrow and history strip.
        // Tolerant: a backend or a tenant with none yet reads empty rather than
        // failing the whole roll-up — a project with no pulse simply reads "New".
        backend.readGroup('portfolio_pulse').catch(() => []),
        // The standalone milestones and their daily forecast snapshots (0124),
        // for the Milestones lens, the roll-up's next-milestone cell and the
        // drill's slip sparkline. Same tolerance — a tenant with none reads empty.
        backend.readGroup('milestones').catch(() => []),
        backend.readGroup('milestone_pulse').catch(() => []),
        // Every board's documents (0102), read only to lift each charter's Risk
        // register onto the roll-up — so a captured risk reaches the steering pack
        // instead of being stranded in the charter. Tolerant: a backend that
        // cannot do the group read, or a tenant with no documents, reads empty and
        // the roll-up simply carries no charter risks.
        backend.readGroup('documents').catch(() => []),
        // Every board's AREAS (the by-area lens groups cards across boards by
        // area NAME, which needs each board's own id→name list). Tolerant: a
        // backend that cannot group-read, or a tenant with none, reads empty and
        // the lens simply shows every card as "no area".
        backend.readGroup('areas').catch(() => []),
        // Every board's lessons (RLS hands back workspace-wide lessons plus the
        // board lessons on boards the person can open), for the steering pack's
        // Learning & closure roll-up (#491). Tolerant: a tenant with none, or a
        // backend that cannot group-read, reads empty and the roll-up carries no
        // forward lessons.
        backend.readGroup('lessons').catch(() => []),
        // Every board's issue TYPES (#435) — so each board's Run-vs-Change split
        // derives through its OWN type→means map, not the open board's. RLS hands
        // back the types of every board the viewer can open (same scope as areas).
        // Tolerant: a backend that cannot group-read, or a tenant with none, reads
        // empty and each card falls to its derived default (Run).
        backend.readGroup('issue_types').catch(() => []),
        // Every board's RISK REGISTER (0193). The register is the single source of
        // truth for a project's risks, so the roll-up and the steering pack read a
        // board's risks from HERE when it has any, and fall back to the charter's
        // authored Risk-register table only for a board that has no register yet
        // (a legacy charter). Tolerant: a backend that cannot group-read, or a
        // tenant with none, reads empty and the fallback stands.
        backend.readGroup('risks').catch(() => []),
      ]);
      // Group the moves by the issue they belong to, so each card carries only
      // its own recent transitions.
      const movesByIssue = new Map<string, Activity[]>();
      for (const m of moveDocs) {
        const arr = movesByIssue.get(m.parentId);
        if (arr) arr.push(m as unknown as Activity);
        else movesByIssue.set(m.parentId, [m as unknown as Activity]);
      }
      const issuesByBoard = new Map<string, Issue[]>();
      for (const d of issueDocs) {
        // Attach this card's recent moves (oldest first, as the board does) — the
        // transition log the movement read fetched, onto the light issue row.
        const issue = d as unknown as Issue;
        const moves = movesByIssue.get(issue.id);
        if (moves) issue.activity = moves.sort((a, b) => a.at - b.at);
        const arr = issuesByBoard.get(d.parentId);
        if (arr) arr.push(issue);
        else issuesByBoard.set(d.parentId, [issue]);
      }
      const phasesByBoard = new Map<string, Phase[]>();
      for (const d of phaseDocs) {
        const arr = phasesByBoard.get(d.parentId);
        if (arr) arr.push(d as unknown as Phase);
        else phasesByBoard.set(d.parentId, [d as unknown as Phase]);
      }
      // Each board's areas, kept by board — the by-area lens resolves a card's
      // local area id to its name through the list for the card's own board.
      const areasByBoard = new Map<string, Area[]>();
      for (const d of areaDocs) {
        const arr = areasByBoard.get(d.parentId);
        if (arr) arr.push(d as unknown as Area);
        else areasByBoard.set(d.parentId, [d as unknown as Area]);
      }
      // Each board's issue types, kept by board — so the Run-vs-Change split for a
      // board classifies its cards through that board's own type→means map (#435).
      const issueTypesByBoard = new Map<string, IssueType[]>();
      for (const d of typeDocs) {
        const t = d as unknown as IssueType;
        const arr = issueTypesByBoard.get(d.parentId);
        if (arr) arr.push(t);
        else issueTypesByBoard.set(d.parentId, [t]);
      }
      // The pulses, kept by board — the trend layer sorts them itself.
      const pulsesByBoard = new Map<string, Pulse[]>();
      for (const d of pulseDocs) {
        const p = d as unknown as Pulse;
        const arr = pulsesByBoard.get(d.parentId);
        if (arr) arr.push(p);
        else pulsesByBoard.set(d.parentId, [p]);
      }
      // The standalone milestones and their forecast snapshots, kept by board —
      // the derivation (projectMilestones) orders and folds them with the go-live
      // and the promoted phases.
      const milestonesByBoard = new Map<string, MilestoneRow[]>();
      for (const d of milestoneDocs) {
        const m = d as unknown as MilestoneRow;
        const arr = milestonesByBoard.get(d.parentId);
        if (arr) arr.push(m);
        else milestonesByBoard.set(d.parentId, [m]);
      }
      const msPulsesByBoard = new Map<string, MilestonePulse[]>();
      for (const d of msPulseDocs) {
        const p = d as unknown as MilestonePulse;
        const arr = msPulsesByBoard.get(d.parentId);
        if (arr) arr.push(p);
        else msPulsesByBoard.set(d.parentId, [p]);
      }
      // Each board's risks, lifted to the portfolio's CharterRisk rows here (the
      // pure portfolio layer never reads a document or the register). THE REGISTER
      // IS THE SINGLE SOURCE: when a board has a risk register (0193), the roll-up
      // and the steering pack read its OPEN risks, worst-first, through
      // risksForCharter — the same rows the charter now renders. A board with NO
      // register yet falls back to its charter's authored Risk-register table, so a
      // legacy charter keeps reporting until its risks are moved into the register.
      const registerRisksByBoard = new Map<string, Risk[]>();
      for (const d of riskDocs) {
        const r = d as unknown as Risk;
        const arr = registerRisksByBoard.get(d.parentId);
        if (arr) arr.push(r);
        else registerRisksByBoard.set(d.parentId, [r]);
      }
      const charterRisksByBoard = new Map<string, CharterRisk[]>();
      const profilesForOwner = get().profiles;
      for (const [boardId, risks] of registerRisksByBoard) {
        const rows = risksForCharter(risks, (who) => displayName(who, profilesForOwner));
        if (rows.length) charterRisksByBoard.set(boardId, rows);
      }
      for (const d of docDocs) {
        const doc = d as unknown as Doc;
        if (doc.kind !== 'charter') continue;
        // A board with a register has already been read from it above — the charter
        // table is only the fallback, so skip it when the register spoke.
        if (registerRisksByBoard.has(d.parentId)) continue;
        const rows = charterRisks(doc.charter);
        if (!rows.length) continue;
        const arr = charterRisksByBoard.get(d.parentId);
        if (arr) arr.push(...rows);
        else charterRisksByBoard.set(d.parentId, rows);
      }
      const s = get();
      // EACH BOARD'S DEFINITION OF DONE, so the Portfolio counts a card's ticked
      // checks exactly as the card face does (effectiveProgress reads meta.doneChecks).
      // Without it the roll-up's Project %, its earned-value cost read and its schedule
      // health ran DoD-blind for EVERY board — a card showing 50% on its face counted
      // as 0% in the roll-up, understating progress and skewing the EVM EAC (EV = BAC ×
      // %). The board-list docs carry done_checks (camelCased by the seam); a board with
      // none maps to nothing and reads typed %s only, exactly as before. (H1, 29 Oct —
      // the same class as the comment at the board-meta snapshot: doneChecks is easy to
      // miss in an aggregation path.)
      const doneChecksByBoard = new Map<string, readonly string[]>();
      for (const p of s.projects) {
        const dc = (p as { doneChecks?: readonly string[] }).doneChecks;
        if (dc && dc.length) doneChecksByBoard.set(p.id, dc);
      }
      // LEARNING & CLOSURE (#491). Lift the signed after-action reports and the
      // recommendation-bearing lessons to plain inputs here — the pure pack layer
      // reads no document and no rich text, exactly as the charter risks are lifted
      // above. The composer windows these to the pack's cycle and words them; a
      // report froze its own numbers at close, so nothing is re-derived.
      const projectName = new Map(s.projects.map((p) => [p.id, p.name] as const));
      const portfolioClosures: PackClosureInput[] = [];
      for (const d of docDocs) {
        const doc = d as unknown as Doc;
        if (doc.kind !== 'aar' || doc.state !== 'signed' || !doc.aar) continue;
        const a = doc.aar;
        const benefits = a.benefits?.length
          ? a.benefits.reduce(
              (acc, b) => {
                if (b.status === 'realised') acc.realised += 1;
                else if (b.status === 'partial') acc.partial += 1;
                else acc.notyet += 1;
                return acc;
              },
              { realised: 0, partial: 0, notyet: 0 },
            )
          : undefined;
        const rec = a.sustain.find((l) => l.kind === 'recommendation');
        portfolioClosures.push({
          project: projectName.get(d.parentId) ?? '',
          // The after-action report is frozen once signed, so its last-written
          // moment is when it was closed; createdAt is the fallback.
          closedAt: doc.updatedAt ?? doc.createdAt,
          outcome: richText(a.actual),
          scheduleDays: a.variance?.scheduleDays,
          budgetBaseline: a.variance?.budgetBaseline,
          forecast: a.variance?.forecast,
          benefits,
          lessonsLogged: a.sustain.length,
          carryForward: rec ? richText(rec.text) : undefined,
        });
      }
      const portfolioLessons: PackLessonInput[] = (lessonDocs as unknown as Lesson[])
        .filter((l) => hasRecommendation(l))
        .map((l) => ({
          createdAt: l.createdAt,
          title: l.title,
          recommendation: richText(l.recommendation),
          kind: asLessonKind(l.kind),
          source: l.boardId ? projectName.get(l.boardId) ?? 'A project' : 'Workspace',
        }));
      const rows = buildPortfolio(
        s.projects,
        issuesByBoard,
        phasesByBoard,
        { base: s.currency, rates: s.fxRates },
        // Resolve whoever is responsible to a shown name — a project manager's
        // typed name stays, a board creator's email becomes their name — so the
        // Owner column and its filter never show a raw address, and the same
        // person never appears twice (once as a name, once as an email). `now`
        // and `sinceMs` match the move read's window exactly.
        { now, sinceMs, resolveName: (who) => displayName(who, s.profiles), charterRisksByBoard, issueTypesByBoard, doneChecksByBoard, calendar: s.workCalendar, milestonesByBoard },
      );
      // The same cards, read by the person instead of the project — who is
      // accountable and what they are doing. Reads the assignee on each card,
      // attributes a one-person board's unset cards to that member, and sets the
      // unowned work apart. Same name resolution, so a person never shows twice.
      const accountability = buildAccountability(s.projects, issuesByBoard, {
        now,
        resolveName: (who) => displayName(who, s.profiles),
        cal: s.workCalendar,
      });
      set({
        portfolio: rows,
        accountability,
        portfolioIssues: issuesByBoard,
        portfolioPhases: phasesByBoard,
        portfolioAreas: areasByBoard,
        portfolioPulses: pulsesByBoard,
        portfolioMilestones: milestonesByBoard,
        milestonePulses: msPulsesByBoard,
        portfolioClosures,
        portfolioLessons,
        portfolioLoading: false,
        portfolioAt: Date.now(),
      });
    } catch (e) {
      set({
        portfolioLoading: false,
        portfolioError: (e as Error)?.message || 'Could not load the portfolio.',
      });
    }
  },

  livePortfolioIssues: () => {
    return backend.watchGroup(
      'issues',
      (docs) => {
        // Preserve each card's recent moves — the batch loadPortfolio attaches
        // them for the Portfolio's movement column, and this lighter live read
        // carries only the card rows, so without this a refresh would blank it.
        const keptActivity = new Map<string, Activity[] | undefined>();
        for (const arr of get().portfolioIssues.values())
          for (const i of arr) keptActivity.set(i.id, i.activity);
        // Attach kept moves to the incoming rows, and remember each card's board
        // so the stale-read guard below can carry a just-saved local copy forward
        // without losing which board it buckets into.
        const boardOf = new Map<string, string>();
        const incoming: Issue[] = [];
        for (const d of docs) {
          const issue = d as unknown as Issue;
          if (!issue.activity) {
            const moves = keptActivity.get(issue.id);
            if (moves) issue.activity = moves;
          }
          boardOf.set(issue.id, d.parentId);
          incoming.push(issue);
        }
        // The same guard the board watcher uses: a card this client just saved
        // keeps its newer local copy until this tenant-wide read catches up, so a
        // bulk assign made on a board does not flash back here either. Shares the
        // one pendingCardWrites map, so a card is guarded on both surfaces at once.
        const current: Issue[] = [];
        for (const arr of get().portfolioIssues.values()) for (const i of arr) current.push(i);
        const { issues, cleared } = carryPendingCardWrites(incoming, current, pendingCardWrites, Date.now());
        for (const id of cleared) pendingCardWrites.delete(id);
        const byBoard = new Map<string, Issue[]>();
        for (const issue of issues) {
          const board = boardOf.get(issue.id);
          if (board === undefined) continue; // only ever a card from this snapshot
          const arr = byBoard.get(board);
          if (arr) arr.push(issue);
          else byBoard.set(board, [issue]);
        }
        set({ portfolioIssues: byBoard, portfolioAt: Date.now() });
      },
      // Tolerant: a backend that cannot group-watch simply doesn't live-refresh;
      // the batch-loaded cache is left as it was rather than being cleared.
      () => {}
    );
  },

  /*
   * READ THIS BOARD'S FLOW HISTORY, ONCE, WHEN THE DIAGRAM IS SHOWN.
   *
   * The status-change log is the only honest source for a cumulative-flow
   * diagram — it records every stage a card passed through and when, which no
   * date stamp on the card can reconstruct (a card carries when it started and
   * finished, never when it entered review). The board read attaches only a few
   * days of it; this asks for the whole window the diagram needs.
   *
   * readMovesSince is tenant-wide and windowed, so this keeps only the current
   * board's cards and only the window. It is cached per board: opening Insights
   * twice reads once, switching boards reads the new one. A backend that cannot
   * do the read (or a mid-read board switch) leaves an empty history rather than
   * an error, and the diagram says it has nothing to draw yet.
   */
  loadFlowMoves: async () => {
    // Keyed by what is being looked at: one board, or all of them. Switching the
    // board, or the scope, changes the key and so refetches; opening Insights
    // twice on the same view reads once.
    const scope = get().insightsScope;
    const keyFor = () => (get().insightsScope === 'all' ? '*all*' : get().meta.key);
    const key = keyFor();
    if (get().flowMovesBoard === key && get().flowMoves) return;
    if (!backend.readMovesSince) {
      set({ flowMoves: [], flowMovesBoard: key, flowMovesLoading: false });
      return;
    }
    set({ flowMovesLoading: true, flowMovesBoard: key });
    try {
      const since = Date.now() - FLOW_WINDOW_WEEKS * 7 * 86_400_000;
      const raw = await backend.readMovesSince(since);
      // The view changed while the read was in flight — the answer is for the
      // wrong board or scope now, so drop it and let the new call stand.
      if (keyFor() !== key) return;
      // On a single board, keep only its cards; across all boards, keep them all
      // (readMovesSince is already tenant-wide and windowed).
      const mine = scope === 'all' ? null : new Set(get().issues.map((i) => i.id));
      const moves: FlowMove[] = (raw as unknown as Array<{
        parentId: string; at: number; fromStatus?: IssueStatus; toStatus?: IssueStatus;
      }>)
        .filter((r) => (mine === null || mine.has(r.parentId)) && r.toStatus != null)
        .map((r) => ({ issueId: r.parentId, at: r.at, fromStatus: r.fromStatus, toStatus: r.toStatus as IssueStatus }));
      set({ flowMoves: moves, flowMovesLoading: false });
    } catch {
      set({ flowMoves: [], flowMovesLoading: false });
    }
  },

  setInsightsScope: (scope) =>
    set((s) => {
      writeInsightsView({ scope, window: s.insightsWindow });
      // All boards needs every board's cards — pull the roll-up in if it is not
      // already in hand. Flow only; the portfolio owns RAG, schedule and budget.
      if (scope === 'all' && s.portfolioIssues.size === 0 && !s.portfolioLoading) void get().loadPortfolio();
      return { insightsScope: scope };
    }),
  setInsightsWindow: (window) =>
    set((s) => {
      writeInsightsView({ scope: s.insightsScope, window });
      return { insightsWindow: window };
    }),

  /*
   * CHANGE it — an admin only. Applied AT ONCE (the symbol and the stored code)
   * so the person who changed it sees every amount redraw immediately, then
   * written to the tenant row. The database is the real guard: it refuses the
   * write to anyone but an admin of this workspace, and the column privilege
   * refuses any column but `currency`. On refusal the optimistic change is rolled
   * back so the screen never shows a change that did not take.
   */
  setWorkspaceCurrency: async (code) => {
    const wid = await resolveWorkspaceId();
    if (!wid) return;
    const was = get().currency;
    setActiveCurrency(code);
    set({ currency: code });
    try {
      await backend.patch(`${C.workspace}/${wid}`, { currency: code });
      // A workspace-wide change an admin made — traceable in Security activity,
      // like every other admin act, named by its currency not its code alone.
      // The database writes that line on the tenant now — record_currency_change
      // (migration 0167) — so a change made by any writer is recorded once.
    } catch (e) {
      setActiveCurrency(was);
      set({ currency: was });
      throw e;
    }
  },

  /*
   * CHANGE ONE CURRENCY'S RATE — an admin only, the same guard as the currency.
   * The whole map is written back (the column holds the table); a rate of null or
   * ≤ 0 removes that currency's rate. Optimistic so committed foreign lines redraw
   * at once; rolled back on refusal. Changing a rate is a workspace setting change,
   * so it is recorded in Security activity like the currency.
   */
  setWorkspaceRate: async (code, rate) => {
    const wid = await resolveWorkspaceId();
    if (!wid) return;
    const was = get().fxRates;
    const next = { ...was };
    if (rate != null && Number.isFinite(rate) && rate > 0) next[code] = rate;
    else delete next[code];
    set({ fxRates: next });
    try {
      await backend.patch(`${C.workspace}/${wid}`, { fxRates: next });
      const c = currencyByCode(code);
      await get().recordAudit(
        'settings',
        rate != null && rate > 0
          ? `set the ${c.name} (${c.code}) exchange rate to ${rate}`
          : `cleared the ${c.name} (${c.code}) exchange rate`,
      );
    } catch (e) {
      set({ fxRates: was });
      throw e;
    }
  },

  /**
   * SAVE THE WORKSPACE PAYMENT-APPROVAL CHAIN (0182) — the whole stored shape
   * ({ default, overrides }) at once, the way the Payment-approval screen edits it.
   * Optimistic, rolled back on refusal; the column-privilege guard on `tenants`
   * keeps the write to admins (same as the currency/rate writes). Returns '' on
   * success, or a sentence to show.
   */
  setPaymentApproval: async (stored) => {
    const wid = await resolveWorkspaceId();
    if (!wid) return 'This workspace is not one you can change.';
    const was = get().paymentApproval;
    set({ paymentApproval: stored });
    try {
      await backend.patch(`${C.workspace}/${wid}`, { paymentApproval: stored });
      await get().recordAudit('settings', 'changed who authorises payments');
      return '';
    } catch (e) {
      set({ paymentApproval: was });
      return e instanceof Error ? e.message : 'That change could not be saved.';
    }
  },

  /**
   * SAVE THE WORKSPACE WORKING CALENDAR (0216) — the whole { week, holidays } shape at
   * once, the way the Working-days settings edit it. Optimistic, rolled back on
   * refusal; the column-privilege guard on `tenants` keeps the write to admins (the
   * same as the currency/rate/approval writes).
   */
  setWorkCalendar: async (cal) => {
    const wid = await resolveWorkspaceId();
    if (!wid) return;
    const was = get().workCalendar;
    set({ workCalendar: cal });
    try {
      await backend.patch(`${C.workspace}/${wid}`, { workCalendar: cal });
      await get().recordAudit('settings', 'changed the working calendar');
    } catch (e) {
      set({ workCalendar: was });
      throw e;
    }
  },

  previewAs: (email) => {
    const s = get();
    const seen = {
      email,
        // Worked out as THEY are — their default, their role on this board,
        // their groups — which is the whole question being asked.
        role: effectiveRole({
          email,
          admins: s.access.admins,
          defaults: s.roles,
          on: s.projectRoles,
          legacy: s.legacyRoles,
        projectId: s.projectId,
      }),
    };
    set({ viewing: seen, shownRole: seen.role });
  },

  // Back to your own eyes. `role` never moved, so there is nothing to restore.
  stopPreview: () => set({ viewing: null, shownRole: get().role }),

  setProject: (id) => {
    if (id === get().projectId) return;
    // Switching off the board we were waiting on cancels the hold, so the
    // watcher's normal fallback works again (see awaitingProjectId).
    if (id !== awaitingProjectId) awaitingProjectId = null;
    writeProjectChoice(id);
    // Clear here, not in the listener setup. Showing one project's work under
    // another project's name is the worst outcome of a switch, and it must not
    // depend on a listener having started — if the data never arrives, empty is
    // honest and stale is a lie.
    set({
      projectId: id,
      issues: [],
      builds: [],
      signoffs: [],
      dismissedSignoffs: [],
      documents: [],
      loaded: false,
      selectedId: null,
      creating: false,
      search: '',
    });
    // The one place your role is worked out — see recomputeMyRole. The roles
    // listener does NOT fire on a switch, because nothing about the roles
    // document changed; without this the screen would carry the previous
    // board's rights until something unrelated reloaded them.
    recomputeMyRole();
    // A preview is a role ON A BOARD, so switching board has to recompute it
    // too — otherwise it keeps showing the previous board's answer, which is
    // the exact class of fault this feature exists to find.
    if (get().viewing) get().previewAs(get().viewing!.email);
    // The workspace listeners are untouched; only the project's own data reopens.
    if (denialHandler) startProjectListening(denialHandler);
  },

  // Always about the person doing it — the rules refuse anything else.
  recordAudit: async (kind, what, extra = {}) => {
    const me = get().user?.email?.toLowerCase();
    if (!me) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    await backend.set(`${C.audit}/${id}`, {
      id,
      at: Date.now(),
      who: me,
      kind,
      what,
      // ONLY WHERE IT MEANS SOMETHING, AND ONLY WHEN IT IS REALLY KNOWN.
      //
      // Read from the loaded list of projects rather than from the board's own
      // details, because those details are still the app's starting values at
      // the moment somebody signs in — which is how twelve sign-ins came to be
      // stamped "FD", a project that has never existed. If the real one is not
      // known yet, nothing is recorded rather than something untrue.
      ...(stampsProject(kind)
        ? (() => {
            const key = get().projects.find((p) => p.id === get().projectId)?.key;
            return key ? { project: key } : {};
          })()
        : {}),
      // Spread last and only where present — an undefined value is refused by
      // the database outright, so an absent extra has to be genuinely absent.
      ...extra,
    });
  },

  createGroup: async (name) => {
    const id = makeGroupId(name, Math.random().toString(36).slice(2, 7));
    await backend.set(`${C.groups}/${id}`, { id, name: name.trim() });
    // The trail line is the groups trigger's — see recordAudit, #336.
    return id;
  },

  renameGroup: async (groupId, name) => {
    await backend.patch(`${C.groups}/${groupId}`, { name: name.trim() });
    // The trail line is the groups trigger's — see recordAudit, #336.
  },

  /**
   * REMOVING A GROUP TAKES IT OFF EVERYTHING FIRST.
   *
   * Deleting only the group document would leave its id sitting on boards and
   * on people — harmless to look at, and a board still reserved for a group
   * that no longer exists is a board nobody can open and nothing explains. So
   * the references go first and the group itself last: interrupted half way,
   * the result is a group nobody is in, which is visible and fixable.
   */
  deleteGroup: async (groupId) => {
    for (const [email, ids] of Object.entries(get().groupsOf) as [string, string[]][])
      if (ids.includes(groupId))
        await backend.patch(`${C.roles}/${email}`, { groups: ids.filter((g) => g !== groupId) });
    for (const p of get().projects)
      if ((p.groups ?? []).includes(groupId))
        await backend.patch(`${C.projects}/${p.id}`, {
          groups: (p.groups ?? []).filter((g) => g !== groupId),
        });
    await backend.remove(`${C.groups}/${groupId}`);
    // The trail line is the groups trigger's — see recordAudit, #336.
  },

  setInGroup: async (email, groupId, isIn) => {
    const e = email.toLowerCase();
    const now = groupsFor(get().groupsOf, e);
    const next = isIn ? [...new Set([...now, groupId])] : now.filter((g) => g !== groupId);
    await backend.patch(`${C.roles}/${e}`, { groups: next });
    /* NO TRAIL LINE. `record_group_membership` on `group_people` writes this
       sentence — but nothing ever inserts that row (see TEAMS_ARE_CONNECTED),
       so the store's copy was the only one, recording a thing that did not
       happen. It comes back with the mapping, not before it. #337. */
  },

  setProjectGroups: async (projectId, groupIds) => {
    const p = get().projects.find((x) => x.id === projectId);
    await backend.patch(`${C.projects}/${projectId}`, { groups: [...new Set(groupIds)] });
    const names = groupIds.map((g) => get().groups.find((x) => x.id === g)?.name ?? g);
    await get().recordAudit(
      'project',
      names.length
        ? `set who may open ${p?.name ?? projectId} to include the ${names.join(', ')} group${names.length > 1 ? 's' : ''}`
        : `took every group off ${p?.name ?? projectId}`,
      { projectId }
    );
  },

  setPersonProfile: async (email, patch) => {
    const e = email.toLowerCase();
    // Optimistic: merge the patch into the local profile at once (e.g. a company-role
    // change) so the control updates instantly rather than after the profiles
    // watcher's echo + full re-read. The echo overwrites with the stored document
    // when it lands; a rejected write rolls back.
    const prev = get().profiles;
    set({ profiles: { ...prev, [e]: { ...(prev[e] ?? {}), ...patch } as Profile } });
    try {
      await backend.patch(`${C.profiles}/${e}`, patch);
    } catch (err) {
      set({ profiles: prev });
      throw err;
    }
  },

  notificationSwitches: async (email) => {
    if (!backend.notificationPrefs) return [];
    try {
      return await backend.notificationPrefs(email);
    } catch {
      /* A read that fails leaves every switch at its default (on), which is the
         safe reading of "we could not find a mute". */
      return [];
    }
  },

  signInTimes: async () => {
    if (!backend.lastSignInTimes) return [];
    try {
      return await backend.lastSignInTimes();
    } catch {
      /* A read that fails (not an admin, or the RPC is absent) simply shows the
         roster its dashes — it is a nicety, not something to surface an error for. */
      return [];
    }
  },

  muteKind: async (kind, muted) => {
    if (!backend.setNotificationMuted) return 'This workspace cannot change notifications yet.';
    try {
      await backend.setNotificationMuted(kind, muted);
      return '';
    } catch (e) {
      return (e as { message?: string })?.message || 'That could not be saved. Try again.';
    }
  },

  adminSetKind: async (email, kind, on, lock) => {
    if (!backend.adminSetNotification) return 'This workspace cannot change notifications yet.';
    try {
      await backend.adminSetNotification(email, kind, on, lock);
      await get().recordAudit(
        'project',
        on
          ? `turned "${kind}" notifications back on for ${email}${lock ? ' and locked it on' : ''}`
          : `let ${email} choose their "${kind}" notifications again`
      );
      return '';
    } catch (e) {
      return (e as { message?: string })?.message || 'That could not be saved. Try again.';
    }
  },

  setProjectMembers: async (projectId, members) => {
    const prevProjects = get().projects;
    const before = prevProjects.find((p) => p.id === projectId);
    // The one gate every route to this list goes through — screen, script or
    // importer. See checkedMembers: the value that broke the live board never
    // came through a field, so a check on the field would not have caught it.
    const checked = checkedMembers(members);
    /*
     * REFLECTED AT ONCE, then written — the board-access lists read
     * `project.members`, and only the projects watcher otherwise moves it, so
     * without this an add or remove sat on its old state until the write echoed
     * back: the same lag writeAccess carried for workspace admin, here for
     * adding/removing somebody on a board. `checked` is the value the echo will
     * carry, so the optimistic state matches it and the list does not flicker;
     * roll back if the write is refused.
     */
    set({ projects: prevProjects.map((p) => (p.id === projectId ? { ...p, members: checked } : p)) });
    try {
      await backend.patch(`${C.projects}/${projectId}`, { members: checked });
    } catch (err) {
      set({ projects: prevProjects });
      throw err;
    }
    // Recorded, because who can open a board is exactly the kind of change
    // that is invisible afterwards and impossible to explain. Named as the
    // project it concerns rather than whichever one happens to be open.
    const name = before?.name ?? projectId;
    await get().recordAudit(
      'project',
      checked.length === 0
        ? `opened ${name} to everyone in the workspace`
        : `set who may open ${name} to ${checked.length} ${checked.length === 1 ? 'person' : 'people'}: ${checked.join(', ')}`
    );
  },


  removePerson: async (email) => {
    const e = email.toLowerCase();
    const { access, user, writeAccess } = get();
    if (e === user?.email?.toLowerCase()) return; // never remove yourself
    await writeAccess(
      access.emails.filter((x) => x !== e),
      access.admins.filter((x) => x !== e)
    );
    // The role and profile documents are deliberately left: their old comments
    // still reference this address and should keep reading as a name.
    /* `removed <email> from the workspace` comes off the DELETE in
       `people_record_change` — the last of the five in #336. */
  },

  signOutEverywhere: async () => {
    const me = get().user?.email?.toLowerCase();
    if (!me) return;
    // A moment, not a list. Anything that started before it goes; anything
    // after is by definition a fresh sign-in and stays — so this cannot race
    // somebody signing in on another machine while it is being pressed.
    await backend.patch(`${C.profiles}/${me}`, { signOutAfter: Date.now() });
  },

  /*
   * ALWAYS ONE OF YOUR OWN. The rule on `sessions` is `lower(email) = me()`,
   * so an administrator cannot end somebody else's — and the comment on that
   * rule says why: a list of a person's machines is not workspace information.
   *
   * NOTHING HERE WRITES THE TRAIL ANY MORE — 0068. Removing the row IS the
   * event, and the trigger on `sessions` writes the line from the device on the
   * row rather than from anything this knows. What is lost is the wording:
   * from the database, ending a session here and pressing Sign out are the
   * same delete. What is gained is that neither can be skipped.
   */
  endSession: async (id) => {
    const here = id === deviceId();
    await backend.remove(`${C.sessions}/${id}`);
    if (here) void accounts.signOut();
  },

  markNotificationRead: async (id) => {
    const n = get().notifications.find((x) => x.id === id);
    if (!n || n.readAt != null) return; // already settled — leave it alone
    const at = Date.now();
    // Optimistic: the panel settles the instant it is opened, and the badge
    // drops with it, rather than waiting on the round trip. The listener will
    // land the same value; if the write is refused the row simply reads unread
    // again on the next snapshot.
    set((s) => ({
      notifications: s.notifications.map((x) => (x.id === id ? { ...x, readAt: at } : x)),
    }));
    try {
      await backend.patch(`${C.notifications}/${id}`, { readAt: at });
    } catch (e) {
      remember('broke', `a notification could not be marked as read — ${(e as BackendError)?.message || String(e)}`);
    }
  },

  markNotificationDone: async (id) => {
    const n = get().notifications.find((x) => x.id === id);
    if (!n || n.handledAt != null) return; // already handled — leave it alone
    const at = Date.now();
    const readAt = n.readAt ?? at; // handling a call-out also reads it
    // Optimistic — the row leaves the inbox at once; the listener lands the same
    // stamps, and a refused write simply reads unhandled again on the next snapshot.
    set((s) => ({
      notifications: s.notifications.map((x) => (x.id === id ? { ...x, handledAt: at, readAt } : x)),
    }));
    try {
      await backend.patch(`${C.notifications}/${id}`, { handledAt: at, readAt });
    } catch (e) {
      remember('broke', `a notification could not be marked as done — ${(e as BackendError)?.message || String(e)}`);
    }
  },

  markNotificationActive: async (id) => {
    const n = get().notifications.find((x) => x.id === id);
    if (!n || n.handledAt == null) return; // not handled — nothing to undo
    // Optimistic — the row returns to "Needs my action" (unhandled, still read);
    // a refused write simply reads handled again on the next snapshot.
    set((s) => ({
      notifications: s.notifications.map((x) => (x.id === id ? { ...x, handledAt: null } : x)),
    }));
    try {
      await backend.patch(`${C.notifications}/${id}`, { handledAt: null });
    } catch (e) {
      remember('broke', `a notification could not be restored — ${(e as BackendError)?.message || String(e)}`);
    }
  },

  markAllNotificationsRead: async () => {
    const unread = get().notifications.filter((x) => x.readAt == null);
    if (unread.length === 0) return;
    const at = Date.now();
    set((s) => ({
      notifications: s.notifications.map((x) => (x.readAt == null ? { ...x, readAt: at } : x)),
    }));
    // One write per row — there is no bulk update across the seam, and the count
    // a person carries is small. Settled independently so one refusal does not
    // strand the rest.
    await Promise.all(
      unread.map((x) =>
        backend.patch(`${C.notifications}/${x.id}`, { readAt: at }).catch((e: unknown) => {
          remember('broke', `a notification could not be marked as read — ${(e as BackendError)?.message || String(e)}`);
        })
      )
    );
  },

  signOutUser: () => {
    /*
     * This browser is going, so its entry goes with it. Left behind, the list
     * would fill with machines nobody is signed in on — which is the fastest
     * way to make a security screen worth ignoring.
     *
     * ── IN THAT ORDER, AND WAITED FOR ─────────────────────────────────────
     *
     * These were two `void` calls started together, and started together they
     * race: signing out destroys the token the deletion needs, so whenever the
     * sign-out won, the deletion was sent with a dead token and refused. The
     * entry stayed. The comment above described a behaviour the code did not
     * reliably have.
     *
     * Removing the row FIRST and waiting for it is the whole fix. If it fails
     * the sign-out still happens — being unable to tidy up is not a reason to
     * keep somebody signed in, and a stale row is a smaller harm than a button
     * that does nothing.
     */
    void (async () => {
      try {
        await backend.remove(`${C.sessions}/${deviceId()}`);
      } catch (e) {
        remember('broke', `this browser could not be taken off your devices — ${(e as BackendError)?.message || String(e)}`);
      }
      await accounts.signOut();
    })();
  },

  resendVerification: async () => {
    const u = accounts.current();
    if (u) await accounts.sendVerification();
  },

  // Clicking the emailed link flips the flag on Google's side only — the app still holds
  // the old sign-in token, so reload the user AND force a fresh token before the database
  // rules will see the address as confirmed.
  refreshVerification: async () => {
    // Re-reads the account and takes a fresh token — see Accounts.refresh for
    // why doing only the first is the bug that makes somebody verify twice.
    const { emailVerified } = await accounts.refresh();
    if (!emailVerified) return false;
    set({ user: accounts.current(), emailVerified: true });
    startListening();
    /*
     * ── AFTER startListening, NOT BEFORE — #305 ────────────────────────────
     *
     * The write needs a token the database accepts, and `verified()` is in
     * every rule on the audit table. Until this moment the address was not
     * verified, so the entry would be refused by the very condition it is
     * recording the end of. Swallowed for the same reason as the sign-in entry
     * beneath it: somebody has just got in, and an error about the recording of
     * that is not something they can act on.
     */
    void useApp
      .getState()
      .recordAudit('access', 'confirmed their email address')
      .catch(() => {});
    return true;
  },
}));

// ── Auth + live data wiring ─────────────────────────────────────────────

/** Which project this person was last looking at, kept per browser. */
function readProjectChoice(): string {
  try {
    return localStorage.getItem(PROJECT_KEY) || FIRST_PROJECT_ID;
  } catch {
    return FIRST_PROJECT_ID;
  }
}

function writeProjectChoice(id: string): void {
  try {
    localStorage.setItem(PROJECT_KEY, id);
  } catch {
    /* private browsing — the choice just will not persist */
  }
}

/** Kept so a project switch can reuse the same refusal handling. */
let denialHandler: ((err: BackendError) => void) | null = null;

let unsubscribers: (() => void)[] = [];
let projectUnsubscribers: (() => void)[] = [];

/*
 * CARDS THIS CLIENT JUST SAVED, WAITING FOR THE SERVER TO CATCH UP — id → the
 * `updatedAt` stamp set by updateIssue. The issues watcher consults this so a
 * lagged read-back right after a burst of writes (a bulk "Assign to") cannot
 * repaint a card with the value it had BEFORE the write; see
 * src/issue-reconcile.ts. Kept module-level, not in store state, because a bulk
 * assign writes many cards in one tick and this must not fan out a re-render per
 * card — it is watcher plumbing, not UI state. Entries clear themselves once a
 * read carries the write (or a short safety window lapses); cleared wholesale on
 * a board switch so the new board's reads are never second-guessed.
 */
const pendingCardWrites = new Map<string, number>();

function stopProjectListening() {
  projectUnsubscribers.forEach((u) => u());
  projectUnsubscribers = [];
  pendingCardWrites.clear();
}

function stopListening() {
  stopProjectListening();
  unsubscribers.forEach((u) => u());
  unsubscribers = [];
}

/**
 * The listeners that belong to ONE project. Torn down and rebuilt when the
 * project changes — the workspace listeners (who is who, what they may do, the
 * history) survive a switch untouched, because none of that is per-project.
 */
/**
 * Keep a picture of the board as it stands, for the next cold start.
 *
 * READ FROM THE STORE RATHER THAN PASSED IN, so that whichever of the five
 * listeners answered last writes a COMPLETE snapshot rather than its own part
 * of one. They arrive within a few hundred milliseconds of each other and the
 * write waits two seconds, so in practice this runs once with everything in it.
 */
function keepBoardForNextTime(): void {
  const s = useApp.getState();
  if (!s.loaded || s.denied) return;
  const who = s.user?.email?.toLowerCase() ?? '';
  /*
   * KEYED ON THE REMEMBERED CHOICE, NOT ON THE STORE'S IDEA OF IT, and the two
   * are not the same thing at the moment that matters.
   *
   * `projectId` starts at a placeholder and only becomes a real board once the
   * board list has arrived — a network round trip later. A snapshot filed under
   * the placeholder is filed under a name the next cold start will never look
   * up, which is exactly the bug this line was written to fix: everything
   * worked, the snapshot was written, and it was never found again.
   *
   * Refusing to file it until the two agree also throws away the empty snapshot
   * the placeholder would otherwise leave behind — a board of no cards, ready
   * to be painted over the real one.
   */
  const choice = readProjectChoice();
  if (!who || !choice || s.projectId !== choice) return;
  rememberBoard({
    who,
    project: choice,
    at: Date.now(),
    issues: s.issues,
    builds: s.builds,
    signoffs: s.signoffs,
    documents: s.documents,
    sprints: s.sprints,
    projects: s.projects,
  });
}

function startProjectListening(onDenied: (err: BackendError) => void) {
  stopProjectListening();
  /*
   * ── SHOW THE BOARD THAT WAS THERE, WHILE THE REAL ONE IS FETCHED ──────────
   *
   * Blanking here is right when the board is CHANGING — the old cards must not
   * sit under the new board's name, which is a fault this codebase has shipped
   * before. It is wrong on a cold start, where the board is the same one and
   * the only thing the blank achieves is "Loading the board…" for two seconds
   * in a window the browser threw away while it was in the background.
   *
   * `recall` tells the two apart by holding whose board it was and which board
   * it was: a switch does not match and blanks as before, a cold start matches
   * and paints. See src/board-cache.ts for why it is only ever a picture.
   */
  const before = useApp.getState();
  const remembered = recallBoard(before.user?.email?.toLowerCase() ?? '', readProjectChoice());
  useApp.setState(
    remembered
      ? {
          issues: remembered.issues,
          builds: remembered.builds,
          signoffs: remembered.signoffs,
          documents: remembered.documents,
          sprints: remembered.sprints,
          /* A snapshot written before boards were kept has none — treated as
             not-yet-known, which waits rather than drawing a board with none. */
          projects: remembered.projects ?? [],
          loaded: true,
        }
      : { issues: [], builds: [], signoffs: [], dismissedSignoffs: [], documents: [], loaded: false }
  );
  // The decision log is not in the recalled snapshot, so it is unknown until its own
  // watcher answers — reset here on every (re)subscribe so the Decisions tab waits
  // rather than flashing its never-logged empty state on a board that has decisions.
  useApp.setState({ decisionsLoaded: false });
  useApp.setState({ raidLoaded: false });

  // Reset per project. The first snapshot only primes this — otherwise opening a
  // project would announce every comment ever written in it, which is how people
  // learn to switch notifications off.
  const seenComments = new Set<string>();
  let primed = false;

  projectUnsubscribers.push(
    backend.watchDoc(
      boardPath(),
      (raw) => {
        if (raw) {
          /*
           * NAMED ONCE, HERE, AND NOWHERE ELSE.
           *
           * The backend hands back plain data — every value `unknown`, because
           * it cannot know what any particular document holds and pretending
           * otherwise is how a boundary becomes decoration. This is where a
           * board document is claimed to be a board document, and it is the
           * only claim made about it: the field-by-field copy below is what
           * actually decides what reaches the app.
           */
          const d = raw as Partial<ProjectMeta> & { members?: string[] };
          // EVERY FIELD ProjectMeta DECLARES HAS TO BE LISTED HERE.
          //
          // This is copied field by field rather than spread, so that a stray
          // value in the document cannot become part of the app's state. The
          // cost is that a field added to the type and forgotten here is simply
          // never loaded — it saves, the write succeeds, and then this snapshot
          // comes back without it and wipes it from the screen.
          //
          // That is not hypothetical. `doneChecks` was missed, so the whole
          // definition-of-done feature loaded empty on every real board: the
          // list looked blank after a reload and the gate never fired. Nothing
          // caught it, because the screen tests set this state directly and
          // never come through here. test-wiring.ts now compares this list
          // against the type and fails when they drift apart.
          useApp.setState({
            meta: {
              name: d.name ?? '',
              key: d.key ?? '',
              nextNumber: d.nextNumber ?? 1,
              /*
               * Absent on every board but an imported one, and absent MEANS
               * '#' with no padding — see ProjectMeta.refPrefix. Listed here
               * because the comment above is right: caught by
               * test-wiring.ts before it ever reached a board, which is the
               * only reason it is not another doneChecks.
               */
              refPrefix: d.refPrefix,
              refWidth: d.refWidth,
              repoUrl: d.repoUrl ?? '',
              projectOwnerName: d.projectOwnerName ?? '',
              projectOwnerPosition: d.projectOwnerPosition ?? '',
              projectOwnerEmail: d.projectOwnerEmail ?? '',
              /* Absent MEANS true — see ProjectMeta.ownerSignsOff. `?? true` here
                 would be the same answer written as though it were a default. */
              ownerSignsOff: d.ownerSignsOff,
              requestedOn: d.requestedOn ?? '',
              signatories: d.signatories ?? [],
              signatoriesKnown: d.signatoriesKnown ?? [],
              /* ⚠️ ABSENT MEANS NO RULE, and no rule is not the same as an
                 empty one written down: a board that has never been near this
                 screen must never start raising sign-offs. Left off this list
                 the field saved and then vanished on the next snapshot, which
                 is exactly what test-columns caught. #332. */
              signoffPolicy: d.signoffPolicy,
              source: d.source,
              sourceName: d.sourceName,
              cadence: d.cadence,
              /* Absent means project — see ProjectMeta.deliveryMode. Listed here
                 or the field saves and then vanishes on the next snapshot (#332). */
              deliveryMode: d.deliveryMode,
              /* Absent = unset cost basis — see ProjectMeta.costBasis (0219). Listed
                 here or the field saves and then vanishes on the next snapshot. */
              costBasis: d.costBasis,
              /* Absent reads as 'follow' — see ProjectMeta.cardDefaults (A). Listed
                 here or the board override saves then vanishes on the next snapshot. */
              cardDefaults: d.cardDefaults,
              /* Absent/false means the In Staging column is off — see
                 ProjectMeta.stagingEnabled. Listed here or it saves then
                 vanishes on the next snapshot (#332). */
              stagingEnabled: d.stagingEnabled,
              /* Absent/false means the board keeps Done and scrolls to it — see
                 ProjectMeta.hideDoneInStaging (0183). Listed here or it saves
                 then vanishes on the next snapshot (#332). */
              hideDoneInStaging: d.hideDoneInStaging,
              /* Absent/false means the backlog is not shown as a board column — see
                 ProjectMeta.backlogOnBoard (0218). Listed here or it saves then
                 vanishes on the next snapshot (#332). */
              backlogOnBoard: d.backlogOnBoard,
              /* Absent/false means the completion odds are a PM-and-above read — see
                 ProjectMeta.showOddsToEveryone (0220). Listed here or it saves then
                 vanishes on the next snapshot (#332). */
              showOddsToEveryone: d.showOddsToEveryone,
              /* Delivery-signal audiences — 0225 (supersede the 0222 booleans, kept
                 for back-compat). Who sees the schedule read and who sees the project
                 cost. Listed here or they save then vanish on the next snapshot (#332). */
              scheduleSignalAudience: d.scheduleSignalAudience,
              budgetSignalAudience: d.budgetSignalAudience,
              deliverySignalEnabled: d.deliverySignalEnabled,
              costInSignal: d.costInSignal,
              /* The go-live -> in-service handoff — 0192 / #435 surface 2. Absent
                 until a project is marked live; its presence makes the board read
                 "in service". Listed here or it saves then vanishes (#332). */
              actualGoLive: d.actualGoLive,
              deliverySnapshot: d.deliverySnapshot,
              /* Absent means soft SoD (a stand-in is documented, not blocked) —
                 see ProjectMeta.requireSeparateScopeApprover. Listed here or it
                 saves then vanishes on the next snapshot (#332). */
              requireSeparateScopeApprover: d.requireSeparateScopeApprover,
              /* Absent means soft dependency gate (an early start is warned, not
                 blocked) — see ProjectMeta.depHardBlock. Listed here or it saves
                 then vanishes on the next snapshot (#332). */
              depHardBlock: d.depHardBlock,
              /* Absent reads as ON — the card size/date assists show by default;
                 an admin can turn them off per board (0205). Listed here or it
                 saves then vanishes on the next snapshot (#332). */
              cardAssists: d.cardAssists,
              /* Absent reads as ON — the Insights Data quality panel shows by
                 default; an admin can turn it off per board (0205). Listed here
                 or it saves then vanishes on the next snapshot (#332). */
              dataQualityPanel: d.dataQualityPanel,
              /* Null means "use the app defaults" — a board's own idle windows
                 (0213). Listed here or it saves then vanishes on the next snapshot
                 (#332). */
              idleWindows: d.idleWindows ?? null,
              /* Null means "use the app defaults" — a board's own checklist prompts
                 (0214). Listed here or it saves then vanishes on the next snapshot
                 (#332). */
              checklistPrompts: d.checklistPrompts ?? null,
              assistantVersions: d.assistantVersions,
              doneChecks: d.doneChecks,
              keptByAssistant: d.keptByAssistant,
              assistantMayAnswer: d.assistantMayAnswer,
              assistants: d.assistants,
              setUpAt: d.setUpAt,
              tracksDevices: d.tracksDevices,
              /* The customer's logo, for the documents this board produces. */
              logoPath: d.logoPath,
              logoType: d.logoType,
            },
            members: d.members ?? [],
          });
        }
      },
      onDenied
    )
  );

  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).issues,
      (docs) => {
        const fromServer = docs as unknown as Issue[];
        const state = useApp.getState();
        const me = state.user?.email?.toLowerCase() ?? '';

        const when = (state.profiles[me]?.notifyWhen ?? 'all') as NotifyWhen;
        for (const incoming of collectNew(fromServer, seenComments, me, primed, mentions)) {
          if (shouldNotify(incoming, when)) {
            show(incoming, state.profiles, (id) => useApp.getState().openIssue(id));
          }
        }
        primed = true;
        // Don't let a lagged read-back revert a card this client just saved: keep
        // the newer local copy until the server carries the write (or a short
        // safety window lapses). src/issue-reconcile.ts; notifications above still
        // read the raw server snapshot, so this only affects what is shown.
        const { issues, cleared } = carryPendingCardWrites(
          fromServer,
          useApp.getState().issues,
          pendingCardWrites,
          Date.now(),
        );
        for (const id of cleared) pendingCardWrites.delete(id);
        useApp.setState({ issues, loaded: true, denied: false });
        keepBoardForNextTime();
      },
      { onError: onDenied }
    )
  );
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).builds,
      (docs) => {
        const builds = (docs as unknown as Build[]).sort((a, b) => b.build - a.build);
        useApp.setState({ builds });
        keepBoardForNextTime();
      },
      { onError: onDenied }
    )
  );
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).signoffs,
      (docs) => {
        /*
         * In sheet order, because that is how a sign-off is read and because
         * the order carries seniority.
         *
         * A row is against a release OR a document — migration 0045 — so
         * neither key is on every row. Releases sort by number and documents
         * by id, and the two groups are kept apart rather than interleaved by
         * whichever key happened to be absent. `?? 0` on the build would have
         * put every document's signatures ahead of build 1's.
         */
        /*
         * A WITHDRAWN ROW IS NOT ON THE PANEL — 0058.
         *
         * Taking a request back used to delete these rows. It now stamps them,
         * so that somebody holding a link is told the request was withdrawn
         * rather than being turned away as a stranger. Nothing above this line
         * should have to know that: to every screen in the app a withdrawn row
         * is gone, exactly as it was before.
         */
        const all = docs as unknown as Signoff[];
        const bySheet = (a: Signoff, b: Signoff) =>
          (a.build == null ? 1 : 0) - (b.build == null ? 1 : 0) ||
          (a.build ?? 0) - (b.build ?? 0) ||
          (a.documentId ?? '').localeCompare(b.documentId ?? '') ||
          /* ⚠️ SPRINTS SORT TOO, OR TWO SHEETS INTERLEAVE. A sprint row has
             no build and no document, so without this every sprint's rows
             compared equal on all three and fell through to `seq` — putting
             the first signatory of one fortnight next to the first signatory
             of another, in one run of rows nothing could tell apart. #332. */
          (a.sprintId ?? '').localeCompare(b.sprintId ?? '') ||
          a.seq - b.seq;
        const signoffs = all.filter((r) => !r.withdrawnAt).sort(bySheet);
        /*
         * DISMISSED REFUSALS, KEPT APART — #<0094>. A dismissed row is a
         * withdrawn row, so the active `signoffs` above already excludes it and
         * nothing that reasons about a panel ever sees it. It is kept here only
         * for the one screen that shows a settled refusal — the Sign-offs list —
         * so the reason is not lost when the item is cleared off the open panel.
         */
        const dismissedSignoffs = all.filter((r) => r.dismissedAt).sort(bySheet);
        useApp.setState({ signoffs, dismissedSignoffs });
        keepBoardForNextTime();
      },
      { onError: onDenied }
    )
  );
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).documents,
      (docs) => {
        /*
         * Newest first, and a superseded version directly under the one that
         * replaced it — which is what `supersedes` is read for. Sorting by
         * date alone would scatter the history of one requirement through the
         * list, and the history is the point.
         */
        const incoming = docs as unknown as Doc[];
        /*
         * KEEP A JUST-CREATED ROW A STALE SNAPSHOT HAS NOT CAUGHT UP TO — see
         * `pendingDocs`. Keyed into a Map by id so the result can NEVER carry the
         * same document twice, whatever the snapshot or current state hold (a
         * concat could, and briefly did — a freshly created row flashed in the
         * list twice). The snapshot wins for any id it carries; a pending id the
         * snapshot DOES carry is now confirmed and leaves the pending set; a
         * pending id it does NOT carry is lag, so the optimistic row from current
         * state is kept so the open editor does not collapse. A deleted draft is
         * dropped from pending in `deleteDocument`, so a real removal is never
         * resurrected here.
         */
        const byId = new Map<string, Doc>();
        for (const d of incoming) byId.set(d.id, d);
        const current = useApp.getState().documents;
        for (const id of [...pendingDocs]) {
          if (byId.has(id)) {
            pendingDocs.delete(id);
          } else {
            const row = current.find((d) => d.id === id);
            if (row) byId.set(id, row);
            else pendingDocs.delete(id);
          }
        }
        const documents = [...byId.values()].sort((a, b) => b.createdAt - a.createdAt);
        useApp.setState({ documents });
        keepBoardForNextTime();
      },
      { onError: onDenied }
    )
  );
  /*
   * A BOARD ON FLOW STILL WATCHES THIS, and it costs nothing: the collection is
   * empty, so the listener returns nothing and the screens that read it are not
   * offered. Subscribing only when the setting is on would mean the setting
   * being turned on has to also start a listener — one more thing to get right,
   * in the one place where getting it wrong looks like "sprints do not work".
   */
  /*
   * ── THE BOARD'S TYPES — #302, and it watches for the reason areas do ──────
   *
   * Somebody adding a type in Settings must see it appear on the board screen
   * in their other tab, and so must everybody else. Without this the table is
   * readable but silent, and the failure reads as "adding a type does not
   * work" — the wrong diagnosis, and the expensive kind of wrong.
   */
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).issueTypes,
      (docs) => {
        const types = docs as unknown as IssueType[];
        /*
         * ⚠️ AN EMPTY ANSWER LEAVES THE FALLBACK STANDING. Every board has four
         * types from migration 0072, so an empty list here is not a board with
         * no types — it is a read that came back with nothing, and drawing that
         * would empty the picker and strip the type off every card on screen.
         */
        if (types.length) useApp.setState({ issueTypes: types });
      },
      { onError: () => useApp.setState({ issueTypes: ORIGINAL_TYPES }) }
    )
  );
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).areas,
      (docs) => {
        const server = docs as unknown as Area[];
        useApp.setState((s) => {
          // Carry a just-created area forward if the server snapshot predates its
          // insert, dropping the pending mark once the server echoes it. By id, so
          // the server wins and no duplicate survives; a non-pending row is never
          // carried, so ordinary deletes/renames from elsewhere still take effect.
          const { areas, stillPending } = carryPendingAreas(server, s.areas, s.pendingAreaIds);
          return { areas, areasProblem: '', pendingAreaIds: stillPending };
        });
        keepBoardForNextTime();
      },
      /*
       * THE MESSAGE, NOT AN EMPTY LIST — #289 again. A board with no areas and
       * a board whose areas could not be read look identical if a failure sets
       * this to []. One of those is somebody's ordinary Tuesday and the other
       * is a fault nobody will ever be told about.
       */
      {
        onError: (e: BackendError) =>
          useApp.setState({
            areas: [],
            areasProblem: e?.message || 'the areas on this board could not be read',
          }),
      }
    )
  );
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).labels,
      (docs) => {
        useApp.setState({ labels: docs as unknown as Label[], labelsProblem: '' });
        keepBoardForNextTime();
      },
      // The message, not an empty list — a board with no labels and a board whose
      // labels could not be read must not look identical. Same reasoning as areas.
      {
        onError: (e: BackendError) =>
          useApp.setState({
            labels: [],
            labelsProblem: e?.message || 'the labels on this board could not be read',
          }),
      }
    )
  );
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).phases,
      (docs) => {
        useApp.setState({ phases: docs as unknown as Phase[], phasesProblem: '' });
        keepBoardForNextTime();
      },
      // The message, not an empty list — a board with no phases and a board whose
      // phases could not be read must not look identical. Same reasoning as areas.
      {
        onError: (e: BackendError) =>
          useApp.setState({
            phases: [],
            phasesProblem: e?.message || 'the phases on this board could not be read',
          }),
      }
    )
  );
  /* The active board's standalone milestones — #374 + milestone payments. They
     arrive live like phases so the Plan register, its schedule read and the
     Budget schedule-of-values update together. A refused or empty read leaves
     them empty rather than throwing anyone off the board. */
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).milestones,
      (docs) => {
        useApp.setState({ milestones: docs as unknown as MilestoneRow[] });
        keepBoardForNextTime();
      },
      { onError: () => useApp.setState({ milestones: [] }) }
    )
  );
  /* The active board's decision log — 3.19 Learning & Governance spine (D1). Arrives
     live like milestones so a logged, decided or superseded decision shows for
     everyone at once. A refused or empty read leaves it empty rather than throwing
     anyone off the board. */
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).decisions,
      (docs) => {
        /* Keep a just-logged/superseded decision a stale snapshot has not caught up
           to — see pendingDecisions. Keyed into a Map by id so the result never carries
           one twice; the snapshot wins for any id it holds (confirming and clearing the
           pending one), and a pending id it lacks is lag, so the optimistic row is kept.
           Mirrors the documents watcher. */
        const incoming = docs as unknown as Decision[];
        const byId = new Map<string, Decision>();
        for (const d of incoming) byId.set(d.id, d);
        const current = useApp.getState().decisions;
        for (const id of [...pendingDecisions]) {
          if (byId.has(id)) pendingDecisions.delete(id);
          else {
            const row = current.find((d) => d.id === id);
            if (row) byId.set(id, row);
            else pendingDecisions.delete(id);
          }
        }
        useApp.setState({ decisions: [...byId.values()], decisionsLoaded: true });
        keepBoardForNextTime();
      },
      { onError: () => useApp.setState({ decisions: [], decisionsLoaded: true }) }
    )
  );
  /* The active board's RAID register — Risks, Assumptions, Issues, Dependencies
     (0193). Four collections, each arriving live like decisions so a logged item
     shows for everyone at once; a just-added row is kept from a stale first snapshot
     via its pending set (as decisions do). A refused or empty read leaves the register
     empty rather than throwing anyone off the board. `raidLoaded` flips true on the
     first snapshot of any of them, which is enough for the screen's empty state. */
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).risks,
      (docs) => {
        const incoming = docs as unknown as Risk[];
        const byId = new Map<string, Risk>();
        for (const d of incoming) byId.set(d.id, d);
        const current = useApp.getState().risks;
        for (const id of [...pendingRisks]) {
          if (byId.has(id)) pendingRisks.delete(id);
          else { const row = current.find((r) => r.id === id); if (row) byId.set(id, row); else pendingRisks.delete(id); }
        }
        useApp.setState({ risks: [...byId.values()], raidLoaded: true });
      },
      { onError: () => useApp.setState({ risks: [], raidLoaded: true }) }
    )
  );
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).assumptions,
      (docs) => {
        const incoming = docs as unknown as Assumption[];
        const byId = new Map<string, Assumption>();
        for (const d of incoming) byId.set(d.id, d);
        const current = useApp.getState().assumptions;
        for (const id of [...pendingAssumptions]) {
          if (byId.has(id)) pendingAssumptions.delete(id);
          else { const row = current.find((a) => a.id === id); if (row) byId.set(id, row); else pendingAssumptions.delete(id); }
        }
        useApp.setState({ assumptions: [...byId.values()], raidLoaded: true });
      },
      { onError: () => useApp.setState({ assumptions: [], raidLoaded: true }) }
    )
  );
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).registerIssues,
      (docs) => {
        const incoming = docs as unknown as RegisterIssue[];
        const byId = new Map<string, RegisterIssue>();
        for (const d of incoming) byId.set(d.id, d);
        const current = useApp.getState().registerIssues;
        for (const id of [...pendingRegisterIssues]) {
          if (byId.has(id)) pendingRegisterIssues.delete(id);
          else { const row = current.find((i) => i.id === id); if (row) byId.set(id, row); else pendingRegisterIssues.delete(id); }
        }
        useApp.setState({ registerIssues: [...byId.values()], raidLoaded: true });
      },
      { onError: () => useApp.setState({ registerIssues: [], raidLoaded: true }) }
    )
  );
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).dependencies,
      (docs) => {
        const incoming = docs as unknown as Dependency[];
        const byId = new Map<string, Dependency>();
        for (const d of incoming) byId.set(d.id, d);
        const current = useApp.getState().dependencies;
        for (const id of [...pendingDependencies]) {
          if (byId.has(id)) pendingDependencies.delete(id);
          else { const row = current.find((d) => d.id === id); if (row) byId.set(id, row); else pendingDependencies.delete(id); }
        }
        useApp.setState({ dependencies: [...byId.values()], raidLoaded: true });
      },
      { onError: () => useApp.setState({ dependencies: [], raidLoaded: true }) }
    )
  );
  /* The Plan view's two collections — #377. Steps and history arrive live like
     phases, so signing a step off shows for everyone at once. A refused or empty
     read leaves them empty rather than throwing anybody off the board — the
     board opens fine before its phases have any steps. */
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).phaseSteps,
      (docs) => {
        useApp.setState({ phaseSteps: docs as unknown as Step[] });
        keepBoardForNextTime();
      },
      { onError: () => useApp.setState({ phaseSteps: [] }) }
    )
  );
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).phaseEvents,
      (docs) => {
        useApp.setState({ phaseEvents: docs as unknown as PhaseEvent[] });
        keepBoardForNextTime();
      },
      { onError: () => useApp.setState({ phaseEvents: [] }) }
    )
  );
  projectUnsubscribers.push(
    backend.watchCollection(
      inProject(useApp.getState().projectId).sprints,
      (docs) => {
        useApp.setState({ sprints: docs as unknown as Sprint[] });
        keepBoardForNextTime();
      },
      // Not `onDenied`. A board with sprints turned off has none, and treating
      // an empty or refused read here as "you are not allowed in" would throw
      // somebody off a board they can perfectly well open.
      { onError: () => useApp.setState({ sprints: [] }) }
    )
  );
}

/**
 * ── WHETHER THIS PERSON IS TURNED AWAY FROM THIS WORKSPACE ──────────────────
 *
 * ON POSTGRES, BEING REFUSED LOOKS EXACTLY LIKE AN EMPTY WORKSPACE. Firestore
 * refused a stranger outright, so `onDenied` fired and the "signed in but not
 * approved" screen appeared. A row-level policy does not refuse — it hides the
 * rows and returns nothing, which is the right behaviour and why the security
 * suite treats the two differently. But it left nothing to trigger denial: a
 * person on nobody's list saw an empty board, waited on "Loading the board…",
 * and the screen written to explain their situation could never appear.
 *
 * So membership is decided here, from the list itself, which works the same on
 * either database. An empty list is NOT denial — that is a brand new workspace,
 * and its first admin belongs in onboarding rather than being turned away.
 *
 * ── ⚠️ AN ASSISTANT IS NOT IN THAT LIST, AND NEVER WAS — #304, one gate on ──
 *
 * `readAccess` builds the access list with `is_assistant = false`, on purpose:
 * the People screen is a list of colleagues and an assistant is not one.
 * `writeAccess` already knows that — it scopes its delete the same way, with a
 * note saying that treating an assistant as "here but not mentioned" would
 * silently revoke the tracker push.
 *
 * This check did not know it. An assistant is therefore NEVER in `known`, so
 * absence — which for a person means "not approved" — means nothing at all for
 * an assistant, and reading it as denial turned them away from a board they are
 * a full member of. `board_people`, `board_roles`, the profile and every policy
 * said yes; this one line said no. The screen it lands on offers "start a
 * workspace of your own", which is how it was reported by Vinnie on 1 September
 * 2026: *"the account was let in but it could not access the board — it was
 * asked to create a new board."*
 *
 * ── AND IT IS ASKED BY WHOEVER ARRIVES LAST, WHICH IS THE REST OF THE FIX ───
 *
 * It used to be one line inside the access watcher, so it ran exactly once —
 * when the access list landed — reading whatever else happened to be in the
 * store at that instant. Three separate answers decide this and they arrive in
 * no fixed order: the sign-in, the access list, and the profiles. Judging on
 * the state at one arbitrary moment is why this looked intermittent rather than
 * broken: a second visit usually had the list in hand before the account, `me`
 * was still empty, the check was skipped, and everything looked fine. A FIRST
 * visit puts the Terms of use in front of the board for as long as it takes to
 * read them — so the account was long since attached, the list arrived after
 * it, and it lost every single time.
 *
 * Calling it from every watcher that changes one of the three answers makes the
 * order stop mattering: whoever is last has the whole picture. It only ever
 * SETS denial, never clears it, so the existing paths that decide somebody is
 * refused are untouched by this.
 */
function decideWhetherIAmTurnedAway(): void {
  const s = useApp.getState();
  const me = s.user?.email?.toLowerCase() ?? '';
  /* Not until the sign-in is attached — an empty address matches nothing and
     would turn everybody away for the moment before it arrives. */
  if (!me) return;
  /* A visit is not in the list it is being checked against, and must not be. */
  if (amStaff()) return;
  /* Absent is not "no". If the profiles have not arrived nobody is judged yet,
     and this runs again when they do — the same rule the password gate follows
     one screen earlier. */
  if (!s.profilesKnown) return;
  if (s.profiles[me]?.assistant === true) return;
  const known = (s.access.emails ?? []).map((e) => e.toLowerCase());
  if (known.length === 0 || known.includes(me)) return;
  forgetBoard();
  useApp.setState({ denied: true, loaded: false, issues: [] });
}

function startListening() {
  // A refusal means one of two things — an unconfirmed email, or an email that is
  // confirmed but not on the access list. Re-check with the server before deciding,
  // so an out-of-date sign-in token can never be reported as "not approved".
  const onDenied = async (err: BackendError) => {
    // Asked of the boundary rather than compared against one database's own
    // string — see BackendError. Everything that is not a refusal is a blip.
    if (!err.refused) return;
    stopListening();
    if (accounts.current()) {
      // Re-reads the account and takes a fresh token in one — see
      // Accounts.refresh. Doing only the first is what makes somebody verify
      // twice: the flag flips on the provider's side and the database keeps
      // reading the old token.
      const { emailVerified } = await accounts.refresh();
      if (!emailVerified) {
        useApp.setState({ emailVerified: false, denied: false, loaded: false, issues: [] });
        return;
      }
      // Confirmed since this token was issued? Then try again.
      if (!useApp.getState().emailVerified) {
        useApp.setState({ emailVerified: true });
        startListening();
        return;
      }
    }
    forgetBoard();
    useApp.setState({ denied: true, loaded: false, issues: [] });
    void recordRefusal();
  };

  unsubscribers.push(
    backend.watchCollection(
      C.projects,
      (docs) => {
        const me = accounts.current()?.email?.toLowerCase() ?? '';
        /*
         * THE BOARD LIST, and this is what showed "Who you are" to the staff
         * account on a workspace full of boards. The database returned them —
         * boards_read is `verified() and tenant_id = my_tenant()`, both true —
         * and then this filter dropped every board reserved for named people,
         * because a visitor is on nobody's list. Nought boards is the
         * brand-new-company case, so the app offered to set one up.
         */
        const iAmAdmin = iAmAdminHere(useApp.getState().access.admins, me);
        const mine = (docs as unknown as Project[])
          // A project reserved for particular people is not offered to anybody
          // else — the rules refuse its issues either way, and a name in the
          // switcher that opens an empty board is worse than no name at all.
          .filter((p) => canOpenProject(p, me, iAmAdmin, useApp.getState().groupsOf))
          .sort((a, b) => a.name.localeCompare(b.name));
        /*
         * ARCHIVED BOARDS ARE SPLIT OFF, NOT DROPPED — #327.
         *
         * The filter used to end here, and `archived` was read in exactly one
         * place in the whole app: this line. Nothing ever wrote it, so a board
         * could not be put away; and had anything written it, the board would
         * have disappeared with nowhere to bring it back from.
         */
        const projects = mine.filter((p) => !p.archived);
        const archivedProjects = mine.filter((p) => p.archived);
        useApp.setState({ projects, archivedProjects, projectsKnown: true });
        // A board we just made has now reached the list — stop holding the
        // choice; from here it is an ordinary member of the list like any other.
        if (awaitingProjectId && projects.some((p) => p.id === awaitingProjectId)) {
          awaitingProjectId = null;
        }
        // The remembered project may have been archived, removed, or belong to
        // someone else's workspace entirely. Fall back rather than showing
        // nothing — UNLESS it is a board we just created and are still waiting to
        // arrive, which is not missing, only late (see awaitingProjectId).
        const current = useApp.getState().projectId;
        if (
          projects.length > 0 &&
          current !== awaitingProjectId &&
          !projects.some((p) => p.id === current)
        ) {
          useApp.getState().setProject(projects[0].id);
        }
      },
      {
        /*
         * A FAILED ANSWER IS STILL AN ANSWER — #252, and the half I got wrong.
         *
         * The profiles read already said so; this one did not, and `onDenied`
         * returns silently on anything that is not a refusal. So one blip here
         * left `projectsKnown` false for good, the setup screen could never
         * speak, and somebody with no projects fell through to a board with
         * nothing on it — where before the fix they would at least have been
         * offered the wizard. That trades a flash for a blank page, which is
         * the worse of the two and the opposite of the point.
         *
         * Set FIRST, so it stands whatever onDenied goes on to decide.
         */
        onError: (err: BackendError) => {
          useApp.setState({ projectsKnown: true });
          return onDenied(err);
        },
      }
    )
  );

  unsubscribers.push(
    backend.watchDoc(
      `${C.access}/allowlist`,
      (doc_) => {
        const d = doc_ ?? {};
        const emails = (d.emails ?? []) as string[];
        const legacy = (d.roles ?? {}) as Record<string, string>;
        // Includes anyone the older role map marks as an admin. Without this the
        // list is empty on such a board, and every caller — including the one
        // that adds people — reasons from a list that is simply wrong.
        const admins = [
          ...new Set([
            ...((d.admins ?? []) as string[]).map((a) => a.toLowerCase()),
            ...adminsFromRoles(legacy),
          ]),
        ];
        useApp.setState({ access: { emails, admins }, legacyRoles: legacy });
        recomputeMyRole();

        /* Whoever arrives last decides — see decideWhetherIAmTurnedAway. */
        decideWhetherIAmTurnedAway();
      },
      onDenied
    )
  );

  // Anybody may read their OWN trail — `is_admin() OR lower(who) = me()` — and
  // an admin may read the workspace's. The screen filters to your own address
  // either way; People and access is where a workspace's is read.
  unsubscribers.push(
    backend.watchCollection(
      C.audit,
      (docs) => {
        const audit = (docs as unknown as AuditEntry[]).sort((a, b) => b.at - a.at);
        useApp.setState({ audit, auditProblem: '' });
      },
      /*
       * A SECURITY SCREEN MUST NOT SAY "NOTHING RECORDED" WHEN IT WAS REFUSED,
       * which is the same fault as the devices list carried — see #289. The
       * comment that stood here said non-admins are simply refused; the policy
       * says otherwise, and has for some time: `is_admin() OR lower(who) = me()`.
       * Anybody may read their own trail. So a refusal here is a real fault
       * rather than the expected state, and hiding it as an empty list hid it.
       */
      {
        onError: (err) =>
          useApp.setState({
            audit: [],
            auditProblem: (err as BackendError)?.message || 'the list could not be read',
          }),
      }
    )
  );

  // The admin-only origin of each sign-in — IP and an approximate place (0150).
  // One listener for the whole workspace, like reactions; RLS (audit_origin_read)
  // returns rows to an admin and nothing to anyone else, so a non-admin's list is
  // simply empty and the drill shows no origin block. A refused read is not a
  // fault to surface here — it is the ordinary state for most of the workspace —
  // so, unlike the trail above, an error just leaves the list empty.
  unsubscribers.push(
    backend.watchCollection(
      C.auditOrigin,
      (docs) => useApp.setState({ auditOrigin: docs as unknown as AuditOrigin[] }),
      { onError: () => useApp.setState({ auditOrigin: [] }) }
    )
  );

  unsubscribers.push(
    backend.watchCollection(
      C.roles,
      (docs) => {
        const roles: Roles = {};
        const groupsOf: GroupsOf = {};
        for (const d of docs) {
          roles[d.id] = toRole(d.role) as ProjectRole;
          // Kept on the person rather than on the group, so a permission rule
          // reads it for free — see src/groups.ts for why that mattered.
          if (Array.isArray(d.groups)) groupsOf[d.id] = d.groups as string[];
        }
        useApp.setState({ roles, groupsOf });
        recomputeMyRole();
      },
      { onError: () => useApp.setState({ roles: {}, groupsOf: {} }) }
    )
  );

  unsubscribers.push(
    backend.watchCollection(
      C.groups,
      (docs) =>
        useApp.setState({
          groups: (docs as unknown as Group[]).sort((a, b) => a.name.localeCompare(b.name)),
        }),
      { onError: () => useApp.setState({ groups: [] }) }
    )
  );

  /*
   * REQUESTS TO ADD SOMEBODY — #359. One listener for the whole workspace; RLS
   * hands each person only what they may see (their own asks, or all of them if
   * they administer). Newest first, the order an admin's queue reads in.
   */
  unsubscribers.push(
    backend.watchCollection(
      C.boardRequests,
      (docs) =>
        useApp.setState({
          boardRequests: (docs as unknown as BoardRequest[]).sort((a, b) => b.createdAt - a.createdAt),
        }),
      { onError: () => useApp.setState({ boardRequests: [] }) }
    )
  );

  /*
   * A PERSON'S CALL-OUTS — track A, #388. One listener for the whole workspace,
   * not one per board: the bell gathers every board they are on. RLS narrows the
   * rows to the ones they own (notifications_own_read, 0104), so this asks for
   * "notifications" and is handed only this person's. Newest first, the order the
   * panel reads in.
   */
  unsubscribers.push(
    backend.watchCollection(
      C.notifications,
      (docs) =>
        useApp.setState({
          notifications: (docs as unknown as Notification[]).sort(
            (a, b) => b.createdAt - a.createdAt
          ),
        }),
      { onError: () => useApp.setState({ notifications: [] }) }
    )
  );

  /*
   * REACTIONS ON THE FEED — migration 0143. One listener for the whole workspace,
   * like the call-outs above: RLS hands back only reactions on boards the person
   * can open, and the card and My Work merge them onto the feed by the entry id
   * each points at. A reaction left on one screen shows on another at once.
   */
  unsubscribers.push(
    backend.watchCollection(
      C.reactions,
      (docs) => useApp.setState({ reactions: docs as unknown as Reaction[] }),
      { onError: () => useApp.setState({ reactions: [] }) }
    )
  );

  /*
   * THE LESSONS REGISTER — 3.19 Learning & Governance spine (L1). One listener for
   * the whole workspace, like reactions: RLS hands back workspace-wide lessons plus
   * board lessons on boards the person can open. The Lessons tab filters to its
   * board; the library shows them all. A lesson added on one screen shows on another
   * at once. A refused or empty read leaves the register empty rather than throwing.
   */
  unsubscribers.push(
    backend.watchCollection(
      C.lessons,
      (docs) => {
        /* Keep a just-recorded lesson a stale snapshot has not caught up to — see
           pendingLessons. Same reconcile as the decisions watcher. kind is coerced
           so a legacy 'recommendation' row (pre-3.19-redesign) never reaches the UI
           as a kind it has no label or tint for. */
        const incoming = (docs as unknown as Lesson[]).map((l) => ({
          ...l,
          kind: asLessonKind(l.kind as unknown as string),
        }));
        const byId = new Map<string, Lesson>();
        for (const l of incoming) byId.set(l.id, l);
        const current = useApp.getState().lessons;
        for (const id of [...pendingLessons]) {
          if (byId.has(id)) pendingLessons.delete(id);
          else {
            const row = current.find((l) => l.id === id);
            if (row) byId.set(id, row);
            else pendingLessons.delete(id);
          }
        }
        useApp.setState({ lessons: [...byId.values()] });
      },
      { onError: () => useApp.setState({ lessons: [] }) }
    )
  );

  /*
   * WHO COVERS WHOM WHILE A SIGNER IS AWAY — configurable sign-offs R1 (0130).
   * One listener for the whole workspace, not one per board: a cover spans every
   * board the person is on, and a sign-off sheet on any board needs to know who
   * may sign for whom. RLS keeps the read to the tenant (signoff_cover_read).
   */
  unsubscribers.push(
    backend.watchCollection(
      C.signoffCover,
      (docs) =>
        useApp.setState({
          signoffCover: (docs as unknown as SignoffCover[]).sort((a, b) => b.createdAt - a.createdAt),
        }),
      { onError: () => useApp.setState({ signoffCover: [] }) }
    )
  );

  /*
   * WHAT EACH PERSON MAY DO ON EACH BOARD — every one of them, in one listener.
   *
   * A collection-group query, because these live at roles/{email}/on/{board}
   * and the alternative is one listener per person per board. It needs its own
   * rule matching the GROUP as well as the path; a query is refused otherwise,
   * and the refusal arrives as an empty result rather than an error.
   *
   * SPARSE, AND THAT IS THE POINT. A board appears here only once it has been
   * given an answer of its own. Everybody else falls back to their default
   * role, which is what let this ship without migrating anybody.
   */
  unsubscribers.push(
    backend.watchGroup(
      'on',
      (docs) => {
        const projectRoles: ProjectRoles = {};
        for (const d of docs) {
          if (!d.parentId) continue;
          (projectRoles[d.parentId] ??= {})[d.id] = toRole(d.role);
        }
        useApp.setState({ projectRoles });
        recomputeMyRole();
      },
      () => useApp.setState({ projectRoles: {} })
    )
  );

  unsubscribers.push(
    backend.watchCollection(
      C.profiles,
      (docs) => {
        const profiles: Profiles = {};
        for (const d of docs) profiles[d.id] = d as unknown as Profile;
        // profilesOk: true ONLY here, on a read that actually returned — this is
        // the signal the terms and password gates trust (see profilesOk above).
        useApp.setState({ profiles, profilesKnown: true, profilesOk: true });
        /*
         * WHOEVER ARRIVES LAST DECIDES. The profiles carry the one fact that
         * says an assistant is not a missing colleague, so this landing is a
         * reason to ask again — and on a first sign-in, behind the terms, this
         * is usually the arrival that comes last. See the function.
         */
        decideWhetherIAmTurnedAway();
        /*
         * OBEYING "SIGN OUT EVERYWHERE".
         *
         * Every browser running this app is subscribed here, which is what
         * makes the instruction reach them at all. Compared against when THIS
         * browser signed in: started before the instruction, it goes.
         *
         * That includes the browser the button was pressed in, which is
         * correct — "everywhere" includes here, and a screen that signed out
         * every machine except the one you were looking at would be lying in
         * its title.
         */
        const me = accounts.current()?.email?.toLowerCase();
        const mine = me ? profiles[me] : undefined;
        /*
         * ── THE VIEWS THEY SAVED, ARRIVING WITH THEIR PROFILE — #301 ──────
         *
         * This is what "follows them to another machine" is, in one line: the
         * views are on the person, so signing in anywhere reads them here.
         *
         * ⚠️ ONLY WHEN THEY DIFFER. This listener fires on every heartbeat of
         * every browser on the board. Setting an equal-but-new array each time
         * would hand Zustand a fresh reference, and the backlog would re-derive
         * its views — and its whole list — several times a minute for nothing.
         */
        /* Through normalizeFilters, so a view saved before a filter field
           existed — `phase`, #377 — arrives in the current full shape rather
           than throwing the moment the backlog reads f.phase.length. The
           equality check still holds: normalisation is deterministic, so a
           heartbeat that changed nothing still stringifies the same. */
        const theirs = (mine?.savedViews ?? []).map((v) => ({
          ...v,
          filters: normalizeFilters(v.filters),
        }));
        if (JSON.stringify(theirs) !== JSON.stringify(useApp.getState().savedViews)) {
          useApp.setState({ savedViews: theirs });
        }
        /* The Portfolio's own saved views + the pinned default, same treatment:
           normalised on the way in (so a view saved before a field existed still
           parses) and only set when they actually differ, to keep the reference
           stable across heartbeats. */
        const pvTheirs = normalizeSavedViews(mine?.portfolioViews);
        if (JSON.stringify(pvTheirs) !== JSON.stringify(useApp.getState().portfolioViews)) {
          useApp.setState({ portfolioViews: pvTheirs });
        }
        const pvDefault = mine?.portfolioDefault ?? '';
        if (pvDefault !== useApp.getState().portfolioDefault) {
          useApp.setState({ portfolioDefault: pvDefault });
        }
        const here = useApp.getState().sessions.find((x) => x.id === deviceId());
        if (mine && here && shouldSignOut(here.startedAt, mine.signOutAfter)) {
          /*
           * ── THE RECEIVING END OF "SIGN OUT EVERYWHERE" — #305 ─────────────
           *
           * A separate event from the pressing end, and the more interesting
           * one: this browser is being closed by a decision taken somewhere
           * else, and the person sitting in front of it did nothing. Recorded
           * from here because this is the only place that knows it happened.
           *
           * GUARDED, because this runs inside a listener. Signing out is not
           * instant, and the listener can fire again in the gap — without the
           * flag one sign-out writes a line every time the profile row moves.
           */
          if (!leavingAfterSignOutElsewhere) {
            leavingAfterSignOutElsewhere = true;
            void (async () => {
              try {
                await backend.remove(`${C.sessions}/${deviceId()}`);
              } catch {
                /* a stale row is a smaller harm than staying signed in */
              }
              await accounts.signOut();
            })();
          }
        }
      },
      // A board with no profiles yet is normal, not a failure — names simply
      // fall back to the address until someone sets one.
      {
        onError: () => {
          /*
           * profilesKnown: true, but profilesOk: FALSE. The turned-away decision
           * below still wants "a failed answer is an answer" — a genuine stranger
           * must be told, not held at the board for ever — so profilesKnown flips.
           * But the terms and password gates must NOT read this empty map as
           * "agreed to nothing / set no password": that is what ejected
           * established people to the Terms of use on a transient realtime error.
           * profilesOk stays false, so those gates fall through to the board the
           * browser kept instead of throwing an accepted person back to the door.
           */
          useApp.setState({ profiles: {}, profilesKnown: true, profilesOk: false });
          decideWhetherIAmTurnedAway();
        },
      }
    )
  );

  /*
   * Where this person is signed in, narrowed to their own address.
   *
   * THIS COMMENT USED TO CLAIM SOMETHING THE CODE DID NOT DO — that a broader
   * query would be "refused outright rather than quietly filtered". It was not
   * narrowed at all: the whole table came back and the filter was applied in
   * JavaScript afterwards. A witness found it on 26 August 2026. The filter is
   * now put to the database as well as applied on the way back, and both halves
   * are deliberate — see readMany, where the reason each exists is written.
   */
  {
    const me = accounts.current()?.email?.toLowerCase() ?? '';
    if (me) {
      unsubscribers.push(
        backend.watchCollection(
          C.sessions,
          (docs) =>
            useApp.setState({ sessions: docs as unknown as Session[], sessionsProblem: '' }),
          {
            where: { field: 'who', equals: me },
            /*
             * THE LIST IS EMPTIED AND THE REASON IS KEPT. Emptying it is still
             * right — stale rows from before a refusal are worse than none —
             * but the screen now has something to say instead of inventing a
             * reassurance nobody checked.
             */
            onError: (err) =>
              useApp.setState({
                sessions: [],
                sessionsProblem:
                  (err as BackendError)?.message || 'the list could not be read',
              }),
          }
        )
      );
    }
  }

  // Kept so a project switch can reuse the same refusal handling.
  denialHandler = onDenied;
  // The project's own data. Without this nothing ever sets `loaded`, and the
  // board waits for issues that were never asked for.
  startProjectListening(onDenied);
}

accounts.onChange(async (user) => {
  stopListening();
  /*
   * ── AND PAINTED BEFORE THE SIGN-IN CHECK COMES BACK ──────────────────────
   *
   * Restoring in startProjectListening alone still flashed "Loading the board…"
   * for about 800ms, because everything between here and there is a network
   * round trip: is this address confirmed, is this person still in a workspace.
   * The snapshot cannot wait for that and be the thing that removes the wait.
   *
   * NOTHING IS TRUSTED THAT WAS NOT TRUSTED BEFORE. The session is already in
   * hand — this runs BECAUSE somebody is signed in — and the snapshot is only
   * handed back when it carries that same address and the board they are on.
   * If the answer that arrives a moment later is no, every one of those paths
   * calls forgetBoard() and clears the screen.
   *
   * The board it looks for is the REMEMBERED choice rather than the store's
   * `projectId`, which is still a placeholder this early — see the note on
   * keepBoardForNextTime, where filing it under that placeholder was what made
   * the whole thing look like it did not work.
   */
  const kept = user ? recallBoard(user.email?.toLowerCase() ?? '', readProjectChoice()) : null;
  useApp.setState({
    user,
    denied: false,
    stale: false,
    /*
     * NOBODY IS KNOWN UNTIL THEY HAVE BEEN ASKED ABOUT — #252. Whoever was
     * here before has just been replaced, and their answers are not this
     * person's. Left standing, the terms would be drawn over somebody whose
     * own details had not arrived, which is the fault this pair exists to end.
     */
    profilesKnown: false,
    profilesOk: false,
    projectsKnown: false,
    /*
     * CONFINEMENT GOES WITH THE PERSON, TOO. This fires only on a real change
     * of address — the onChange dedupe (supabase-auth.ts) suppresses a token
     * refresh or a tab regaining focus for the SAME person — so role, access
     * and the role maps standing here are the PREVIOUS person's, and must not
     * be drawn over the one arriving. Dropped to least privilege, exactly as
     * #252 above drops their profiles and their board: the sidebar now starts
     * confined and never flashes a row the new person cannot open. recompute-
     * MyRole widens it the instant their own access list lands, and a fresh
     * load already begins from here. [session-state-leaks-across-signin]
     */
    role: 'viewer',
    shownRole: 'viewer',
    access: EMPTY_ACCESS,
    roles: {},
    projectRoles: {},
    legacyRoles: {},
    ...(kept
      ? {
          issues: kept.issues,
          builds: kept.builds,
          signoffs: kept.signoffs,
          documents: kept.documents,
          sprints: kept.sprints,
          projects: kept.projects ?? [],
          loaded: true,
        }
      : { loaded: false, issues: [] }),
  });

  if (!user) {
    /* SIGNING OUT TAKES THE PICTURE WITH IT. The next person at this machine
       must not be handed the last one's board while their own is fetched. */
    forgetBoard();
    /* A "Not yet" on the go-live handoff was this sign-in's, not the machine's:
       forget it so the next person to sign in gets the prompt once, as they
       should, rather than inheriting the last person's dismissal (#435). */
    handoffDismissed.clear();
    useApp.setState({ authReady: true, emailVerified: false });
    return;
  }

  // The sign-in token carries a snapshot of the confirmed flag, which goes stale the
  // moment the address is confirmed elsewhere. Ask the server before deciding.
  let verified = user.emailVerified;
  if (!verified) {
    try {
      verified = (await accounts.refresh()).emailVerified;
    } catch {
      /* offline — fall back to whatever the token said */
    }
  }

  useApp.setState({ authReady: true, emailVerified: verified });
  // Unconfirmed accounts are refused by the database rules, so don't even open a listener.
  if (verified) {
    /*
     * ASKED, NOT INFERRED. On Postgres a stranger sees an empty workspace rather
     * than a refusal, and an empty workspace is also what the very first admin
     * sees before they set one up — the same emptiness wanting opposite screens.
     * Each database answers this in its own terms; see Backend.amIAMember.
     *
     * A blip is not an answer. If the question itself fails, carry on and let
     * the listeners decide: locking somebody out of their own workspace because
     * the network hiccuped is a worse mistake than showing them a board a moment
     * before refusing it.
     */
    /*
     * STAFF ARE NEVER TURNED AWAY — see 0014. `amIAMember` asks whether there is
     * a row for this person in this workspace, and a support visit deliberately
     * has none, so it answered "no" and the screen said "signed in but not
     * approved for this board" for every customer. The database disagrees:
     * my_tenant() has already honoured the staff mark by the time anything is
     * read, so the board behind that message was loading perfectly well.
     */
    let member = amStaff() || (await backend.amIAMember().catch(() => true));

    /*
     * A SIGN-IN THAT HAS GONE OUT OF DATE, TOLD APART FROM NOT BEING INVITED.
     *
     * Both look identical from here: `amIAMember` reads `people`, whose rule is
     * `tenant_id = my_tenant()`, so a token that cannot resolve a workspace
     * returns nought rows — exactly what a stranger gets. On 17 August that put
     * the only administrator in front of "signed in but not approved for this
     * board" on a board they own, minutes after migrations 0010-0014 changed how
     * a workspace is decided. Their token had been minted before the change and
     * could not answer the new question.
     *
     * `workspaces()` can tell the two apart because it runs as its owner and is
     * hard-wired to the caller — it is the one thing that answers "which
     * workspaces are you in" WITHOUT going through my_tenant(). So: a person
     * with workspaces but no rows has a token problem, and a person with neither
     * was never invited.
     *
     * FIXED WITHOUT ASKING, WHERE IT CAN BE. Exchanging the refresh token mints
     * a new one carrying whatever is true now, which is the same thing signing
     * out and back in does and costs the person nothing. Only if that still
     * cannot reach the workspace is anybody interrupted.
     */
    if (!member) {
      const mine = await accounts.workspaces().catch(() => []);
      /*
       * SWITCHED OFF IS NOT OUT OF DATE, and it arrives here looking identical.
       *
       * A suspended workspace is one my_tenant() will not name (0028), so
       * `people` returns nought rows — the same nothing a stale token returns,
       * and the same nothing a stranger gets. Told apart here because
       * `workspaces()` does not go through my_tenant() and carries the flag.
       *
       * Without this the customer whose account was suspended for an unpaid
       * invoice is told to sign in again, does, and is told to sign in again —
       * which is the frightened-customer failure suspension exists to avoid.
       * Reaching here with a suspended workspace means EVERY workspace they
       * have is off: with a working one anywhere, my_tenant() would have
       * chosen it and they would never have got this far.
       */
      const off = mine.find((w) => w.suspended);
      if (off) {
        forgetBoard();
        useApp.setState({
          suspended: { name: off.name, why: off.suspendedWhy },
          authReady: true,
          loaded: false,
          issues: [],
        });
        return;
      }
      if (mine.length) {
        await accounts.refresh().catch(() => {});
        member = await backend.amIAMember().catch(() => true);
        if (!member) {
          forgetBoard();
          useApp.setState({ stale: true, authReady: true, loaded: false, issues: [] });
          return;
        }
      }
    }

    if (!member) {
      forgetBoard();
      useApp.setState({ denied: true, authReady: true, loaded: false, issues: [] });
      return;
    }
    startListening();
    // Point money formatting at this workspace's currency (0117). Fire and
    // forget: a wrong symbol is cosmetic, and its own failure is handled inside.
    void useApp.getState().loadWorkspaceCurrency();
    // Once per browser session. Firebase re-announces the user on every page
    // load and every hourly token renewal, and recording those would bury the
    // real sign-ins under noise.
    const email = user.email?.toLowerCase() ?? '';
    /*
     * ── THE LINE IS NO LONGER WRITTEN HERE — 0068 ─────────────────────────
     *
     * This used to write `signin` into the trail itself. The database does it
     * now, off the session row, so what is left is reporting the FACT: this is
     * a fresh sign-in rather than a reload. Same condition as before, same once
     * per tab, and the entry comes out the same — it is simply no longer this
     * client's job to know that a sign-in deserves a line.
     */
    const fresh = Boolean(email) && !alreadyLoggedThisSession(email);
    if (fresh) markSessionLogged(email);
    if (email) {
      /*
       * A REFUSAL HERE IS NOT A FAULT — it means this person is in no workspace,
       * which the access listener above decides and shows properly. Fired and
       * forgotten, this threw "not a member of any workspace" into nothing: an
       * unhandled exception on every sign-in by somebody with no access, and the
       * first thing the browser console said about a state the app handles.
       */
      void noteThisDevice(email, fresh).catch((e: BackendError) => {
        if (!e?.refused) throw e;
      });
    }
  }
});

/**
 * This browser, on the list of where you are signed in.
 *
 * `startedAt` is only set the first time this device appears, and left alone
 * afterwards — it is when this browser signed in, and a page reload is not a
 * new sign-in. It is also what "sign out everywhere" compares against, so
 * refreshing it on every load would let a browser outlive the instruction to
 * close simply by being reloaded.
 *
 * ── WHY IT PATCHES AND THEN SETS, RATHER THAN DOING EITHER ALONE ──────────
 *
 * THIS WAS ONE CALL, and moving house broke it. Under Firestore it was
 * `setDoc(ref, …, { merge: true })`, which updates a document or creates it as
 * required. The move to Postgres turned that into `backend.patch`, and a patch
 * is an UPDATE: it can change a row that is there and it can never make one.
 *
 * So from the day of the move, no browser was ever ADDED to this list. The
 * only rows in it are the ones Firestore had already written; every sign-in
 * since has updated nothing at all. The screen was not empty because the list
 * was broken — it was empty because nothing was being written to read.
 *
 * PATCH FIRST, because the ordinary case is a browser already on the list, and
 * a patch is the call that leaves `startedAt` alone. If it reports that it
 * changed nothing, this browser is not on the list yet, and `set` puts it
 * there whole — `startedAt` included, and genuinely now, because this is the
 * first time this device has been seen.
 *
 * `startedAt` NO LONGER COMES FROM THE STORE, which is the other half of the
 * repair. It used to be decided by looking this device up in `sessions`, the
 * very list that was empty — so on the day the list came back, a reload would
 * have looked like a fresh sign-in and let this browser outlive "sign out
 * everywhere". The two calls now carry that distinction between them, and
 * neither has to consult a list to know which case it is in.
 */
async function noteThisDevice(email: string, fresh = false): Promise<void> {
  const id = deviceId();
  const now = Date.now();
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const parts = describeDeviceParts(ua);
  // The method was left by the auth layer at the moment it signed in; it is
  // taken once, and only matters on a fresh sign-in (a reload has none). The
  // parts are written every time — they describe the device, not the event.
  const method = fresh ? takeSignInMethod() : '';
  const row = {
    id,
    who: email,
    device: describeDevice(ua),
    deviceKind: parts.deviceKind,
    os: parts.os,
    browser: parts.browser,
    userAgent: ua,
    where: whereFrom(),
    lastSeen: now,
    ...(fresh && method ? { method } : {}),
    /*
     * ONLY ON A FRESH SIGN-IN, and deliberately not `startedAt` — 0068.
     *
     * The trigger turns a move in this field into a line of History. Reusing
     * `startedAt` would have done the same job and broken "sign out everywhere"
     * on the way: that compares against `startedAt`, so refreshing it in a new
     * tab would let a browser survive being closed.
     *
     * On a reload this is absent, so the patch below leaves it where it was and
     * nothing is written — which is right, because a reload is not a sign-in.
     */
    ...(fresh ? { signedInAt: now } : {}),
  };
  try {
    await backend.patch(`${C.sessions}/${id}`, row);
  } catch (e) {
    if ((e as BackendError)?.code !== 'changed-nothing') return couldNotNoteDevice(e);
    try {
      await backend.set(`${C.sessions}/${id}`, { ...row, startedAt: now });
    } catch (second) {
      couldNotNoteDevice(second);
    }
  }
}

/**
 * A LIST OF MACHINES IS NOT WORTH FAILING A SIGN-IN OVER. It is worth saying.
 *
 * The old comment here was right about the first half and wrong about the
 * second: it swallowed the reason whole. Between that silence and a reader
 * that turned every refusal into an empty list, this feature could stop
 * working for three weeks in front of people looking straight at it.
 *
 * So the sign-in still succeeds — nobody is kept out of the app because we
 * could not write down which browser they used — and the reason goes into the
 * trail, where the broken-screen report and `problems` will both find it.
 */
function couldNotNoteDevice(e: unknown): void {
  const why = (e as BackendError)?.message || String(e);
  remember('broke', `this browser could not be added to your devices — ${why}`);
}
